// ===== REPORT CONFIGURATION SYSTEM =====
// Config-driven approach: define form fields per report, render dynamically

import type { SelectedDayMenu } from './components/selectors/CreditDayMenuSelector';
import type { SelectedCreditItemUsage } from './components/selectors/CreditItemUsageSelector';
import { useGlobalStore } from '../../stores/globalStore';
import { toLocalDateString } from '../../utils/localDate';
import { formatDateOnly, formatDateOnlyRange, formatDayCountRange, addDaysDateOnly, parseDateOnly, toLocalWireDateTime } from '../../utils/dateOnly';
import { floorUnitNames, residentsInFloorUnits } from './floorUnitResidents';
import { collapseSingleCheckedFacility, filterTrayTicketPatients } from './trayTicketPatients';
import { RETIRED_REPORT_SLUGS, currentReportPKey } from './retiredReports';
import { fetchPastNourishmentLabelTypes, SHEET_LABEL_TYPES } from '../../api/residentsApi';
import { buildMealtimeOptions, facilitySettingsToMealtimeFacility } from './components/selectors/mealtimeOptions';
import type { TempMenuSupport } from '../../utils/tempMenu';

export type FieldType = 'date' | 'dateRange' | 'month' | 'dropdown' | 'radio' | 'checkbox' | 'number' | 'text' | 'multiCheckbox' | 'mealtimesPerDay' | 'residentSelect' | 'patientLabelSelect' | 'residentMultiplesSelect' | 'setSelect' | 'floorUnitSelect' | 'menuCycleSelect' | 'recipeSelect' | 'employeeSelect' | 'specialNeedSelect' | 'disciplineSelect' | 'driGroupSelect' | 'dietTypeSelect' | 'consistencySelect' | 'nutrientSelect' | 'assessmentSetupTypeRadio' | 'patientLookup' | 'menuCycleInfo' | 'creditDayMenuGrid' | 'creditItemUsageGrid' | 'customDietButton' | 'daysBetween' | 'foodGroupTree' | 'productionAreaGrid' | 'dietTypeConsistencyGrid' | 'trayTicketImage' | 'trayTicketNote' | 'weightTriggerGrid' | 'filterSelect' | 'facilityTree' | 'corporateFacilitySelect' | 'itemSelect' | 'planOfCareDomainSelect';

export interface FieldOption {
  /** Boolean is permitted because several radio groups map directly to
   *  boolean wire fields (e.g. `PrintAlternates`, `IsLegal`) where the
   *  two choices ARE false/true rather than codes. The radio renderer in
   *  GenericReportForm compares with `===` and keys off `String(value)`,
   *  so all three primitive types round-trip safely. */
  value: string | number | boolean;
  label: string;
}

export interface ReportFieldConfig {
  key: string;
  label: string;
  type: FieldType;
  defaultValue: unknown;
  /** Dropdown/radio choices. Either a static array OR a zero-arg
   *  function that returns the array at render time. Use a
   *  function when the list depends on the current wall-clock
   *  date (e.g. "last 12 months") — module-load precomputation
   *  can drift if the app runs across midnight of a month
   *  boundary. */
  options?: FieldOption[] | (() => FieldOption[]);
  required?: boolean;
  min?: number;
  max?: number;
  /** For `date` fields: restricts the DateBox picker's max to
   *  `formValues[fromField] + days`. When the sibling field is
   *  empty the picker falls back to unrestricted. Mirrors WinForms
   *  patterns like `ucDeEnd.DateMax = ucDeStart.EditValue.AddDays(9)`
   *  (10-day span cap on Production - Grocery List, .vb:376). */
  maxOffsetDaysFrom?: { fromField: string; days: number };
  /** For `date` fields: symmetric partner to `maxOffsetDaysFrom` —
   *  restricts the DateBox picker's min to `formValues[fromField] + days`.
   *  Mirrors WinForms `ucDeEnd.DateMin = ucDeStart.EditValue`
   *  (Prep/Pull .vb:310, Grocery List .vb:375) which pins End
   *  no-earlier-than Start at the picker level. */
  minOffsetDaysFrom?: { fromField: string; days: number };
  /** For `date` fields on the two past-report generators: clamp the picker to the
   *  dates that actually have saved snapshots, and print a note under it saying
   *  so. Without it the operator can pick any day and only finds out there is
   *  nothing to reprint after the report has been queued and come back empty.
   *
   *  The bounds move as rows age out under the facility's `Census_AutoDeleteDays`
   *  retention, so they are read from the server rather than assumed. `kind`
   *  picks which census rows are counted: nourishment label rows carry
   *  `Menu_Header_ForeignKeyLink = -999999`, tray ticket rows do not, and a day
   *  with one but not the other is outside the other's range. */
  snapshotDateBounds?: { kind: 'trayTicket' | 'nourishmentLabel' };
  /**
   * Why this field is greyed out, shown as a tooltip on the disabled
   * control. Only needed when the reason cannot be derived: an
   * `enabledWhen: { key, value }` explains itself ("Available when X
   * is Y"), but an `enabledWhen: { check }` predicate is opaque.
   * A disabled control with no explanation is a dead end for the user.
   */
  disabledReason?: string;
  /** For `date` fields: switch the picker into month/year mode.
   *  Mirrors WinForms `MonthEdit.YearMonthOnly = True` on Weight -
   *  Matrix (`.vb:104`). DateBox uses `displayFormat = "MMMM yyyy"`
   *  and the spin buttons shift by one month instead of one day. */
  yearMonthOnly?: boolean;
  /** For `date` fields (typically paired with `yearMonthOnly`):
   *  render a computed inline suffix showing the END month of an
   *  N-month window that starts at the picked month. Mirrors
   *  WinForms `lblDateUntil.Text = " - {MonthName(start-1)} {start.Year+1}"`
   *  on Weight - Matrix (`.vb:110, 807`) — a 12-month window
   *  (`endMonthOffset: 11`) yields " - July 2026" when the picker
   *  reads "August 2025". Renders as static text after the picker
   *  (and spin buttons). */
  endMonthOffset?: number;
  /** For `date` fields: explicit pixel width for the inner
   *  DateBox editor (falls back to `100%` of the flex-parent).
   *  Distinct from `width` on FieldConfig which clamps the whole
   *  date-spin container (label + chevrons + editor + suffix).
   *  Use `dateBoxWidth` when you want the editor input itself to
   *  grow independent of the container. */
  dateBoxWidth?: number;
  /** For `daysBetween` fields: the two sibling date field keys the
   *  count is computed against. Renders as read-only inline text
   *  showing "(N Day)" or "(N Days)" — matches WinForms
   *  `SetDaysLabel` at
   *  `ucDietech_Report_Generator_Production_GroceryList.vb:417-425`. */
  startField?: string;
  endField?: string;
  /** For dateRange: the end-date key */
  endKey?: string;
  endLabel?: string;
  /** Conditionally show this field when another field has a specific value */
  /** Show/hide condition. Two supported shapes:
   *  - `{ key, value }` — show when the sibling field equals value
   *    (single-condition; original shape).
   *  - `{ check }` — arbitrary predicate over all form values.
   *    Use for OR/complex conditions the single-key form can't
   *    express (e.g. Special Devices' mealtimes list which
   *    depends on BOTH Order AND ReportType). */
  visibleWhen?:
    | { key: string; value: unknown }
    | { check: (values: Record<string, unknown>) => boolean };
  /** Fieldset group label. Fields sharing a group render inside the
   *  same bordered box with the group name as the legend. Mirrors the
   *  WinForms "Report Type / Date / Mealtimes / Order / Print /
   *  Options" section headers with a horizontal rule underneath. */
  group?: string;
  /** Re-invoke this field's `defaultValue` function whenever any of
   *  the listed sibling field keys changes. Used for cross-field
   *  reactivity like WinForms Meal Acceptance's Daily/Weekly radio
   *  resetting the Date when clicked. The `defaultValue` function
   *  receives `(facilitySettings, currentValues)` in this case so
   *  it can branch on the trigger field's current value. */
  recomputeWhen?: string[];
  /** Re-invoke `defaultValue` once the residents list resolves.
   *  Enables the census-fallback pattern from
   *  `ucDietech_Report_Generator_Recipe_MenuCycle.vb:132-138`
   *  where the field falls back to
   *  `ResidentManager.GetActiveResidentCount` when the facility
   *  `Recipe_Census` setting is 0. Read the count via the 3rd
   *  `ctx` argument to `defaultValue` — `ctx.activeResidentCount`. */
  recomputeOnResidents?: boolean;
  /** For `setSelect` / `residentSelect` fields: when the picker
   *  becomes visible with an empty value, auto-check every option.
   *  Mirrors the WinForms Meal Acceptance default where Specific
   *  Cart / Specific Patient starts with all rows selected so the
   *  user only has to deselect the exceptions.
   *
   *  NOTE: For `dietTypeSelect` the wiring is different — this
   *  flag maps to `defaultToHouse` on `DietTypeSelector`, matching
   *  WinForms Nutritional Analysis's `Fill_dtDietTypes` behavior
   *  (auto-checks facility `FKey_Diet_Type_House` only, not every
   *  row). Use `defaultCheckAll` below when you want true CheckAll
   *  behavior on diet/consistency pickers. */
  defaultSelectAll?: boolean;
  /** For `setSelect`: show each {set}'s active resident count on the date in this form field (a Residents
   *  column; {set_plural} with none are muted). */
  showResidentCountsFor?: string;
  /** For `dietTypeSelect` / `consistencySelect`: check every row on
   *  initial load. Mirrors WinForms
   *  `clbDietTypes.CheckAll()` / `clbConsist.CheckAll()` at
   *  `ucDietech_Report_Generator_Posting_ExtensionSheets.vb:165-166`.
   *  Distinct from `defaultSelectAll` above, which for `dietTypeSelect`
   *  means "check facility house diet only". */
  defaultCheckAll?: boolean;
  /** For `dietTypeSelect` / `consistencySelect`: render a
   *  "Select Active" button in the selector header that checks each
   *  listed row a resident's current diet order uses and unchecks the
   *  rest (DT_ResidentDietFlucs). Mirrors WinForms
   *  `btnActiveDiets_Click` / `btnActiveConsist_Click` at
   *  `ucDietech_Report_Generator_Posting_ExtensionSheets.vb:1002-1017`. */
  showSelectActive?: boolean;
  /** For `dietTypeSelect` / `consistencySelect`: a form value kept true while every row on the
   *  list is checked, false otherwise. A schedule saves it with its settings (GenericReportForm
   *  handleSchedule), so one saved with everything checked prints every column of each run's
   *  menu cycle (ScheduledReports/ExtensionSheetColumns.cs), not only the ones listed when it was
   *  saved. Not sent by Generate: the generator's settings class has no such key. */
  allCheckedKey?: string;
  /** Assigns this field to a named tab within its `group`. When any
   *  field in a group has `tab`, the group renders as a MUI Tab
   *  strip and each field is shown on its own tab. Mirrors the
   *  WinForms XtraTabControl pattern (Monthly Acceptance's
   *  Mealtimes / Floor-Units / Residents tabs). Fields in the same
   *  group without a `tab` render above the tab strip. */
  tab?: string;
  /** Width clamp (px). Semantics vary by field type:
   *   - `dropdown` / `month`: HARD clamp (fixed width, no shrinking).
   *     Used for compact inline controls like Beverage - By Resident's
   *     meal-of-day dropdown at 65px next to the Tray Line radio.
   *   - `setSelect` (and other selectors when needed): SOFT max-width.
   *     The grid can still shrink on narrow viewports. Used to keep
   *     Beverage - By Set's cart list from stretching the full
   *     main column. */
  width?: number;
  /** For `dietTypeConsistencyGrid`: sibling form-field key that
   *  holds an explicit `PKey_Menu_Header`. The grid uses this
   *  directly if set. Otherwise falls back to `dateKey` below. */
  menuHeaderKey?: string;
  /** For `dietTypeConsistencyGrid`: sibling form-field key that
   *  holds a date value. The selector resolves the menu header
   *  from that date via `useMenuCycleInfo` — matches WinForms
   *  `ucMenuCycleInfo.DateToUse` → `MenuCycleInUseInfo.PKey_Menu_Header`
   *  chain. */
  dateKey?: string;
  /** Field is rendered always but disabled unless the referenced
   *  sibling field currently matches `value`. Mirrors WinForms
   *  companion-control enable/disable (e.g. Beverage - By Resident
   *  `cmbTrayLineOrder` is enabled only when the Tray Line radio
   *  is checked, via `chkSortTrayline_CheckedChanged`). Distinct
   *  from `visibleWhen` — the control keeps its layout footprint
   *  so its row doesn't reflow when the condition toggles. */
  enabledWhen?:
    | { key: string; value: unknown }
    | { check: (values: Record<string, unknown>) => boolean };
  /** Force a selector-type field (`setSelect` / `residentSelect` /
   *  `floorUnitSelect` / `menuCycleSelect` / `recipeSelect`) to
   *  render INLINE in the main column instead of being routed to
   *  the right-hand side column. Used when the selector IS the
   *  primary content of the form (e.g. Beverage - By Set where the
   *  cart list sits directly below the Date group, matching the
   *  WinForms `lcgSets` layout with no side panel). Fields inside
   *  a `tab` are already inline; this flag is for the non-tab case. */
  inline?: boolean;
  /** For `setSelect` / `residentSelect` / `floorUnitSelect` fields: when the
   *  report is in multi-facility mode, render ONE picker per checked facility
   *  inside a tab strip instead of a single picker for the current facility.
   *
   *  Ports ucDietech_Report_Generator_Tabbed_ClbSet / _Tabbed_ResidentList /
   *  _Tabbed_ClbUnits, which WinForms swaps in for `ucClbSets` / `gvTrayTickets` /
   *  `ucClbFloorUnit` by flipping layout-item visibility
   *  (ProductionTally.vb:166-190).
   *
   *  Changes the field's VALUE SHAPE while multi-facility is active: `number[]`
   *  becomes `Record<facilityId, number[]>`. `buildSettings` must flatten it with
   *  `toPositionalLists` - the DTO's parallel arrays are index-aligned, not keyed. */
  multiFacility?: boolean;
  /** For `mealtimesPerDay` fields: build the checklist from facility settings
   *  instead of a static `options` array, porting `Fill_clbMeals`
   *  (ucDietech_Report_Generator_clbMealtime.vb:70-140).
   *
   *  Gets all nine ordinals right (including PM2=7 / HS2=8), drops mealtimes the
   *  facility has not named, honours the facility's `Nourish_*_SortOrder` display
   *  order, applies the EAM/UseProduction rule, and in multi-facility mode unions
   *  across the checked facilities - falling back to the generic English label
   *  where two facilities name the same mealtime differently.
   *
   *  `includeNourishments` may name a facility-settings key (e.g.
   *  `'pT_ShowNourishments'`) to read the flag per facility, matching
   *  `.IncludeNourishments = g_facilitySettings.PT_ShowNourishments(...)`. */
  mealtimeOptions?: {
    includeMain: boolean;
    includeNourishments: boolean | string;
    showAllNourishmentInLite?: boolean;
    includeCustom?: boolean;
  };
  /** For `multiCheckbox` mealtime lists keyed by ordinal (Breakfast=0 ...
   *  EAM=3, AM=4, PM=5, HS=6): names a facility-settings flag. When the flag
   *  is false, the nourishment options (value >= 3) are not offered, matching
   *  WinForms `.IncludeNourishments = g_facilitySettings.<Flag>(facility)`.
   *  The posting menus read `menuCycle_ShowNourishments` (#2003). */
  nourishmentsSetting?: string;
  /** For `facilityTree` fields: the form key holding the checked facility ids that
   *  every `multiFacility` selector on this report tabs over. Defaults to
   *  `LstFacilityMultiple`. */
  facilityTreeKey?: string;
  /** For `facilityTree` fields: `corporate` shows the tree when the facility is
   *  corporate (not on PT_ShowMultiFacility), offers every facility the user has
   *  and starts with all of them checked - FacilityTree.Init's IsCorporate branch,
   *  where every row is added with Selected = True. Used by Production - Grocery
   *  List (.vb:122). Defaults to the multi-facility gate. */
  facilityTreeGate?: 'multiFacility' | 'corporate';
  /** For `menuCycleInfo` fields: name of another form field
   *  whose value (a date) drives the fetched menu-cycle info.
   *  Mirrors the WinForms `ucMenuCycleInfo.DateToUse = editValue`
   *  binding in Cost Analysis - Menu (`.vb:280-290`). */
  watchField?: string;
  /** For `menuCycleInfo` fields: facility-settings key whose
   *  value is passed to the server as `FKey_Menu_Header_Override`.
   *  Mirrors the WinForms
   *  `.FKey_Menu_Header_Override = g_facilitySettings.FKey_Menu_Header_Employee(...)`
   *  wiring in Employee Production Tally
   *  (`ucDietech_Report_Generator_Employee_ProductionTally.vb:124-126`).
   *  Names a camelCased property on the facility-settings store
   *  (e.g. `fKey_Menu_Header_Employee`). */
  overrideFacilitySettingKey?: string;
  /** For fields that need more than one sibling's value (e.g.
   *  `creditItemUsageGrid` watches Start / End / Group all at
   *  once). Keys are role names the renderer knows about; values
   *  are sibling field keys in the same form. */
  watchGroup?: Record<string, string>;
  /** For `residentSelect` fields: renders extra info columns
   *  (Diet Order, Unit, Room, TF) alongside Name — matches the
   *  WinForms Credits report picker layout at
   *  `ucDietech_Report_Generator_Credits_CurrentMonth.vb:271-304`.
   *  Off by default; the default single-Name column stays. */
  showInfoColumns?: boolean;
  /** For `residentSelect` fields: adds a boolean "Static" column
   *  between Diet Order and Unit (requires `showInfoColumns`).
   *  Mirrors WinForms Posting Selective Menu
   *  `colStaticSelectiveMenu` at Designer.vb:484-494. */
  showStaticColumn?: boolean;
  /** For `residentSelect` fields: suppress the trailing TF (Tube
   *  Feed) column. Requires `showInfoColumns`. Used by Selective
   *  Menu (pKey 2) whose WinForms Designer grid ends at Room —
   *  see `ucDietech_Report_Generator_SelectiveMenu.Designer.vb`. */
  hideTFColumn?: boolean;
  /** For `residentSelect` fields: narrow the roster to residents
   *  with `tF_Tube_Feed = true`. Mirrors WinForms
   *  `ucClbResident.TubeFeedOnly = True` at
   *  `ucDietech_Report_Generator_TubeFeedFillable.vb:104`. */
  filterTubeFeedOnly?: boolean;
  /** For `residentSelect` fields: gate the 3 mealtime location
   *  columns (Loc B / Loc L / Loc S) on a facility settings key.
   *  Value must be a `tblFacility` boolean column name (camelCase
   *  first-char lower, e.g. `'tT_ShowLocationColumns'`). Mirrors
   *  WinForms `ucDietech_Report_Generator_TrayTicket.vb:165-183`
   *  which reads `g_facilitySettings.TT_ShowLocationColumns` and
   *  toggles colB_Location / colL_Location / colD_Location. The
   *  column captions use the facility's meal-name abbreviations
   *  (first char of Breakfast / Lunch / Dinner names). */
  showLocationColumnsFromFacility?: string;
  /** For `residentSelect` fields: filter the resident list to only
   *  Selective-Menu-eligible patients per WinForms
   *  `Fill_dtResidents` at
   *  `ucDietech_Report_Generator_Posting_SelectiveMenu.vb:508`.
   *  Reads sibling fields for `StartDate` (Date), `NumberOfWeeks`
   *  (int), and `MealtimesInt` (int[]) to build the request. Field
   *  names default to `StartDate` / `NumberOfWeeks` /
   *  `MealtimesInt` — override when the config uses different keys. */
  selectiveMenuEligible?: {
    startDateField?: string;
    /** Weeks-based mode (Posting - Selective Menu, pKey 12):
     *  `endDate = startDate + numberOfWeeks × 7`. */
    numberOfWeeksField?: string;
    /** Days-based mode (Selective Menu, pKey 2):
     *  `endDate = startDate + numberOfDays - 1`. If both `days`
     *  and `weeks` fields are set, `days` wins. */
    numberOfDaysField?: string;
    mealtimesField?: string;
  };
  /** For `residentSelect` fields: filter the resident list to only
   *  Posting - Specific Resident-eligible patients per WinForms
   *  `Fill_dtResidents` at
   *  `ucDietech_Report_Generator_Posting_SpecificResident.vb:490-583`.
   *  Uses `SetMealtimeAttendance(StartDate.DayOfWeek, ...)` + guest
   *  recurrence filter. Reads the sibling `StartDate` (Date) field.
   *  WinForms fills the list once, in Init, before any mealtime is
   *  checked (.vb:155), so the attendance filter is skipped: the grid
   *  shows all facility patients + guests filtered by StartDate
   *  recurrence. */
  postingSpecificResidentEligible?: {
    startDateField?: string;
  };
  /** For `residentSelect` fields: swap the data source for the Tray
   *  Tickets picker — regulars + guests whose recurrence intersects
   *  the report's date range. Mirrors WinForms `Fill_dtPatientGrid`
   *  at `ucDietech_Report_Generator_TrayTicket.vb:1010-1122` which
   *  loops guest recurrence over `[StartDate, StartDate + NumberOfDays - 1]`
   *  and drops guests whose recurrence doesn't match ANY day in the
   *  range. `startDateField` names the sibling that holds the report
   *  Start Date (defaults to `StartDate`); `numberOfDaysField`
   *  names the sibling that holds the day-count (defaults to
   *  `NumberOfDays`). */
  trayTicketEligible?: {
    startDateField?: string;
    numberOfDaysField?: string;
  };
  /** For `residentSelect` fields: list only the residents who have a SAVED tray
   *  ticket for the chosen date and mealtimes, read from the census snapshots
   *  (src/docs/migrations/PastTrayTickets-migration.md). Unlike
   *  `trayTicketEligible` this watches a mealtime field too, because the answer
   *  changes with it. `dateField` defaults to `StartDate`, `mealtimesField` to
   *  `Mealtimes`. */
  pastTrayTicketEligible?: {
    dateField?: string;
    mealtimesField?: string;
  };
  /** For `residentSelect` fields: list only the residents who have SAVED
   *  nourishment labels for the chosen date and mealtimes, read from the census
   *  snapshots (src/docs/migrations/PastTrayTickets-migration.md, Nourishment
   *  Labels section).
   *
   *  Not interchangeable with `pastTrayTicketEligible`: that one reads the tray
   *  ticket census rows and this one the nourishment rows, which carry
   *  `Menu_Header_ForeignKeyLink = -999999`. A resident with a saved tray ticket
   *  for the same meal is not offered here. `dateField` defaults to `StartDate`,
   *  `mealtimesField` to `Mealtimes`. */
  pastNourishmentLabelEligible?: {
    dateField?: string;
    mealtimesField?: string;
  };
  /** For `residentSelect` fields: list the Med Pass - Summary grid - residents
   *  and guests (bulk-order guests too) with no guest-schedule check, never
   *  bulk-order residents. WinForms `Fill_dtPatientGrid`
   *  (ucDietech_Report_Generator_MedPassSummary.vb:503-547). */
  medPassSummaryGrid?: boolean;
  /** For `residentSelect` fields: enables the "disabled patient"
   *  affordance — rows without a Diet Order or whose diet type
   *  matches the facility NPO diet type get grayed out, are
   *  non-selectable, and (when the Info column is on) surface an
   *  info icon with a tooltip. Mirrors the Nutritional Analysis
   *  patient grid at
   *  `ucDietech_Report_Generator_NutritionalAnalysis.vb:747-769`. */
  disableEmptyDietPatients?: boolean;
  /** For `residentSelect` fields: enable the "Predefined Patient
   *  List" toolbar (dropdown + New/Save/Delete/Reload/Rename
   *  buttons) above the grid header. Value is the WinForms
   *  `ResidentSelect` enum: 0=SelectiveMenuPosting,
   *  1=SpecificResidentPosting, 2=SelectMenu. Mirrors
   *  `luResidents` @ ucDietech_Report_Generator_Posting_
   *  SpecificResident.vb:617-877. */
  predefinedListType?: number;
  /** For `residentSelect` fields: append "(Deleted)" or
   *  "(Discharged)" after the resident's name.
   *
   *  For the past reports, whose resident list is read from saved
   *  census rows rather than from tblPatient, so months later it
   *  still lists residents who have since been discharged, deleted,
   *  or purged from the system entirely. Deleted outranks
   *  discharged. */
  showResidentStatus?: boolean;
  /** For `residentSelect` fields: drop ticked residents that are no
   *  longer in the roster when it re-queries (past reports re-ask per
   *  date and mealtime). Keeps the header count and the submitted
   *  list in step with what the grid shows. */
  pruneSelectionToRoster?: boolean;

  // ─── filterSelect (Production Tally "Predefined Filters" combo) ──
  /** Bidirectional map between fields on a saved
   *  `TblPrd_TallyFilter` row and this report's form keys. The
   *  filterSelect field uses it to hydrate the form on filter load
   *  AND collect the form snapshot on filter save. */
  filterFieldMap?: {
    mealtimes?: string;         // "0,1,2;0,1,2" ↔ List<int[]>
    selectedSets?: string;      // "1,2,3" ↔ number[]
    printRecipes?: string;      // bool ↔ bool
    reportType?: string;        // int ↔ (int or string via decode/encode)
    days?: string;
    foodGroupIds?: string;      // number[] ↔ number[]
    productionAreaIds?: string; // number[] ↔ number[]
    /** Derived from `selectedSets` emptiness on load — WinForms
     *  .vb:665 sets `radPrintAll.Checked = True` first, then
     *  .vb:727 overrides to `radSpecificSet.Checked = True` when
     *  SelectedSets non-empty. This key gets the resulting
     *  'all' or 'set' string. */
    printMode?: string;
  };
  /** Keys the form should clear + lock when the user picks more
   *  than one filter (WinForms .vb:764-780 disables Mealtimes /
   *  Food Groups / Production Areas / Sets). Each key is set to
   *  `[]` in the patch; special-case: the `filterFieldMap.mealtimes`
   *  key resets to `[[]]` to keep mealtimesPerDay's shape valid. */
  filterMultiLockKeys?: string[];
  /** Convert the filter row's `reportType` int into whatever the
   *  form's ReportType radio value is (e.g. 0 → "extended"). */
  filterReportTypeDecode?: (n: number) => unknown;
  /** Inverse of `filterReportTypeDecode` — encode the form's
   *  ReportType radio value back to the int stored on the filter. */
  filterReportTypeEncode?: (v: unknown) => number;
  /** For `employeeSelect` fields: renders the Employee ID column
   *  alongside Name — matches the WinForms Employee Tray Tickets
   *  grid at
   *  `ucDietech_Report_Generator_Employee_TrayTickets.vb:447-458`. */
  showIdColumn?: boolean;
  /** When true, forces this field to start on a new flex row
   *  inside its group. Renders a zero-height `flex-basis: 100%`
   *  spacer before the field so the surrounding
   *  `.report-form-group-body { display: flex; flex-wrap: wrap; }`
   *  wraps. Useful for laying out multi-checkbox rows where two
   *  short items should sit inline and the next pair should
   *  break to a new line — e.g. the Print group in Posting -
   *  Daily Menu (AS + count on line 1, Include-SM + Show Dates
   *  on line 2). */
  newRow?: boolean;
  /** For `radio` fields: layout direction of the option row.
   *  Defaults to `'row'` (horizontal, matches Report Type / Filter
   *  / Order By radios). Use `'column'` for long option lists
   *  where a horizontal row would wrap awkwardly — e.g. Birthday's
   *  Month radios (12 vertically stacked, matching WinForms).
   *  For `multiCheckbox` fields: `'twoColumn'` renders items in a
   *  2-column column-major flow (items 1..N/2 in the left column,
   *  N/2+1..N in the right). Matches WinForms tree-list dialogs
   *  where subitems appear beneath a parent-labeled column. */
  layout?: 'row' | 'column' | 'twoColumn' | 'threeCol';
  /** For `multiCheckbox` fields with `layout: 'twoColumn'`:
   *  clamp the first column's width in pixels. The right column
   *  takes the remainder. Useful when the left-column labels are
   *  short and the right column needs the extra breathing room —
   *  e.g. Cardex Options where the right column holds the
   *  Progress Notes parent + indented children. */
  firstColumnMaxWidth?: number;
  /** For `multiCheckbox` fields: declare parent/child tree
   *  relationships between options. Each entry names a parent
   *  option value and its children. Parent values are derived
   *  from children (checked = all children checked, indeterminate
   *  = some but not all). Clicking a parent toggles all children
   *  as a group. Parent values are stripped from the wire payload
   *  in `buildSettings` since they're UI-only sentinels — matches
   *  the standard WinForms tree-list dialog behavior. */
  treeParents?: Array<{ parentValue: string | number; childValues: Array<string | number> }>;
  /** For `specialNeedSelect` fields: names a sibling field whose
   *  value determines multi-select mode. When
   *  `formValues[key] === value` the grid switches to multi-select
   *  checkboxes; otherwise single-row selection. Mirrors WinForms
   *  `radTypeSummary_EditValueChanging` in Special Needs which
   *  toggles `gv.OptionsSelection.MultiSelect` based on the Type
   *  radio (`.vb:451-471`). */
  multiSelectWhen?: { key: string; value: unknown };
  /** When two or more adjacent groups share the same `groupRow`
   *  key, they render inside a shared horizontal flex row instead
   *  of stacking vertically. Used to place two separate fieldsets
   *  side-by-side (e.g. Nutrition - Analysis's "DRI Group" and
   *  "Print" panels sit on the same visual row). Every field
   *  in a given group must have the same `groupRow` — the value
   *  is read from the first field of each grouped entry. */
  groupRow?: string;
  /** Compute the fieldset header (the label that reads
   *  ".report-form-group-header") from current form values,
   *  overriding the static `group` string. When multiple fields
   *  share a group, the FIRST field's `getGroupHeader` (if any)
   *  wins. Return the raw string; the renderer applies token
   *  substitution the same way it does for `group`. Used by
   *  Extension Sheet's Custom Diet field to reflect the current
   *  selection ("Custom Diet - Regular, Puree" once saved). */
  getGroupHeader?: (formValues: Record<string, unknown>) => string;
  /** Hover tooltip text — mirrors WinForms `.ToolTip = "…"` or
   *  `.SuperTip.Items.Add(...)` on report-generator controls.
   *  For `checkbox` fields the field renderer wraps the
   *  `<FormControlLabel>` in a MUI `<Tooltip>`; other field types
   *  are free to consume this and render their own tooltip. Keep
   *  the string short — WinForms tooltips run one sentence. */
  tooltip?: string;
  /** For `number` fields: name of a sibling date field whose
   *  menu-cycle-in-use `NumberOfWeeksIncycle` becomes the runtime
   *  max. Mirrors the WinForms
   *  `ucNumberOfWeeks.MaxValue = ucMenuCycleInfo.MenuCycleInUseInfo.NumberOfWeeksIncycle`
   *  pattern (e.g. `Posting_GeneralMenu.vb:141` /
   *  `Posting_GeneralMenu2.vb:126`) so the user can't spin past
   *  what the current menu cycle supports. `field.max` still acts
   *  as a hard ceiling — the effective max is
   *  `min(cycleWeeks, field.max)`. */
  dynamicMaxFromMenuCycle?: string;
  /** For `multiCheckbox` fields: per-option max width (px). Caps
   *  each checkbox+label item so a wide label doesn't stretch
   *  the group. Used by Nutrition - Analysis's Mealtimes to keep
   *  each row narrow when a facility relabels Breakfast/Lunch/
   *  Dinner to longer strings. */
  itemMaxWidth?: number;
}

/**
 * Per-group layout, declared on the config instead of hand-written
 * into `Reports.css` under a `[data-report-slug]` selector.
 *
 * Every one of these used to be a slug-specific CSS rule. The values
 * are unchanged — they are just delivered through data attributes and
 * custom properties now, so a rule can be written once and any report
 * can opt into it. See the `.report-form-group[data-density]` /
 * `[data-cols]` rules in `Reports.css`.
 */
export interface ReportGroupLayout {
  /**
   * Tight vertical rhythm for checkbox-dense groups — zeroes the
   * MUI label padding and shrinks the checkbox hit padding, so ten
   * checkboxes in a fieldset do not run the card off the page.
   */
  density?: 'compact';
  /**
   * Lay the group body out as a CSS grid instead of the default
   * wrapping flex row. Any valid `grid-template-columns` value:
   * `'minmax(0, 150px) 1fr'` for a label column plus a filler,
   * `'150px 200px 150px'` for three fixed columns.
   *
   * In grid mode the last child spans every column, and the
   * zero-height `newRow` spacers the flex layout needs are hidden
   * (a grid breaks rows on its own).
   */
  cols?: string;
  /** `gap` override for the group body, e.g. `'2px 8px'`. */
  gap?: string;
  /**
   * Vertical padding on the checkbox hit box inside a compact group.
   * Defaults to `1px`. Report families differ by a pixel or two and
   * this carries the exact value, so moving off the per-slug CSS
   * changed no spacing anywhere.
   */
  checkboxPad?: string;
  /**
   * Zero MUI's default label margin. Needed when the body is a grid:
   * the label's negative left margin otherwise knocks the columns
   * out of alignment.
   */
  flushLabels?: boolean;
}

/**
 * Card-level layout. Same rationale as `ReportGroupLayout`: these
 * were `:has()`-driven width overrides keyed on the slug.
 */
export interface ReportCardLayout {
  /**
   * `'wide'` drops the card's width cap and lets it size to its
   * content. For reports whose form is genuinely wider than the
   * default column (Tray Tickets, Selective Menu).
   */
  width?: 'wide';
  /** Fixed width for the main (left) column above 1200px. */
  mainWidth?: string;
  /** Fixed width for the side (right) selector column above 1200px. */
  sideWidth?: string;
}

export interface ReportConfig {
  slug: string;
  reportName: string;
  description?: string;
  /** FKey_RS_Report from tblRS_Report - used for generic queue.
   *  Static default; a config may override at submit time via
   *  `resolveFkeyReport`. */
  fKey_RS_Report: number;
  /** Optional resolver that computes `fKey_RS_Report` from the
   *  current form values. Used by reports that share a single
   *  card but submit to different server pkeys based on a radio
   *  choice — Management Team - Chargeable maps Labels/List/
   *  Summary → 30/29/28. Returning any of the sibling pKeys is
   *  fine; the queue endpoint routes by that number. */
  resolveFkeyReport?: (values: Record<string, unknown>) => number;
  /** Whether the report can print the user's Temp Menu (see `TempMenuSupport`). Leave it out
   *  when every menu lookup of the generator follows the menu, start date and menu day the
   *  form sends. Set `false` (or a check on the values) where the generator works the menu
   *  out from the date on the server: until the render hosts read the Temp Menu, the form
   *  then sends the active cycle only, so the report never mixes the two menus. */
  tempMenuSupported?: TempMenuSupport;
  /** Specific API endpoint name (e.g., 'Birthday') - uses POST /api/Report/{endpoint}/{facilityId} */
  apiEndpoint?: string;
  /** If set, renders a custom form component instead of the generic one */
  customComponent?: 'ProductionTally';
  fields: ReportFieldConfig[];
  /** Per-group layout, keyed by the group name (the fieldset legend). */
  groupLayout?: Record<string, ReportGroupLayout>;
  /** Card-level layout (width caps, fixed column widths). */
  layout?: ReportCardLayout;
  /** Build human-readable dateFor string from form values */
  buildDateFor: (values: Record<string, unknown>) => string;
  /** Translate the form's React-flavored field values into the
   *  wire shape the API's settings class expects (WinForms
   *  MealAcceptanceSettings, etc.). Second arg is the raw
   *  facility settings object — use it for wire fields that are
   *  read from `g_facilitySettings` in the WinForms
   *  `Print_Handler` (e.g. `UseProduction`). Third arg is a small
   *  context object with derived facility data that isn't part of
   *  `facilitySettings` (e.g. `facilitySetCount` for Beverage -
   *  Total's `AllSetsSelected` flag). Both extra args are
   *  optional; omit for configs that don't need them. */
  buildSettings?: (
    values: Record<string, unknown>,
    facilitySettings: Record<string, unknown> | null,
    ctx?: {
      /** Distinct facility-set (cart) count. Used by
       *  `Beverage - Total.AllSetsSelected`. */
      facilitySetCount?: number;
      /** Flattens multi-facility form state into the positional arrays the
       *  *Settings DTOs expect. Pass the `facilityTree` field's checked ids and
       *  one `multiFacility` selector's `Record<facilityId, number[]>` value;
       *  get back `ids` / `names` / `selections`, all index-aligned.
       *
       *  `ids` is null when the report is not in multi-facility mode — send the
       *  nulls straight through, that is what makes the server generator take
       *  its single-facility path. Call once per `multiFacility` selector; the
       *  `ids` / `names` come back identical each time. */
      resolveMultiFacility?: (
        checkedIds: number[] | undefined,
        perFacility: Record<string, unknown> | undefined,
      ) => { ids: number[] | null; names: string[] | null; selections: number[][] | null };
      /** The merged mealtime (name, ordinal) list — WinForms `GetMealNames()`, which
       *  is what `lstMealtime_MultipleNames` carries. NOT per-facility: it is the
       *  union across the checked facilities, generic-labelled where two facilities
       *  name a mealtime differently. The generator filters it per facility via
       *  GetAllMealNames (NourishmentLabels.vb:47-53).
       *
       *  Pass the same `mealtimeOptions` spec the report's `mealtimesPerDay` field
       *  declares, so the names sent match the checklist the user ticked. */
      resolveMealtimeNames?: (
        checkedIds: number[] | undefined,
        spec: {
          includeMain: boolean;
          includeNourishments: boolean | string;
          showAllNourishmentInLite?: boolean;
          includeCustom?: boolean;
        },
      ) => Array<{ item1: string; item2: number }> | null;
      /** Resolves a patient PKey to their diet-order string —
       *  same DT_Name/C_Name join `PatientDAL` populates on the
       *  server. Used by `Cost Analysis - Menu.StrDF`. Returns
       *  empty string if the patient isn't in the residents cache
       *  or has no diet-flucs row. */
      resolvePatientDietOrder?: (patientId: number) => string;
      /** Converts a floorUnitSelect value (unit keys on a unit-mapping
       *  facility, floor numbers otherwise - see floorUnitResidents.ts)
       *  into the "Floor,Wing" or "Floor,*" string list WinForms
       *  `ucClb_FloorUnit.GetCheckedFloorWing_CommaSep()` produces.
       *  Used by `Cost Analysis - Nourishments.LstFloorUnits`.
       *  Format depends on the facility's UseUnitMapping setting:
       *  - `UseUnitMapping = true`  → "1,A" / "2,B" / etc.
       *  - `UseUnitMapping = false` → "1,*" / "2,*" / etc. */
      resolveFloorWing?: (pKeys: number[]) => string[];
      /** Display names for a Floor/Unit selection — mirrors WinForms
       *  Tray Tickets `.vb:485-491`. When facility UseUnitMapping is
       *  on, names come from the `Unit` column; otherwise the raw
       *  floor number is the display string. Used by Tray Tickets
       *  to populate `LstFloorUnitNames`. */
      resolveFloorUnitNames?: (pKeys: number[]) => string[];
      /** Returns every active primary-resident PKey — the same filter
       *  WinForms `SQL_Patient_Where_Generator` + `p.FKey_Patient IS NULL`
       *  + `p.IsBulkOrder = 0` + `p.ActAsTemplate = 0` produces.
       *  Used by `Credits - End of Month Balance` when "All" is
       *  picked — the server iterates the list and would NRE on null.
       *  Filters applied on the client (mirrors the WinForms SQL):
       *  `!isDeleted && !discharged && !actAsTemplate && !isBulkOrder
       *   && fKey_Patient == null` (fKey_Patient = guest-of link). */
      resolveAllResidentPKeys?: () => number[];
      /** Returns the `PKey_Menu_Header` for the given ISO date by
       *  reading the React Query cache the sibling `menuCycleInfo`
       *  display populates. Used by `Dish Up Labels` and
       *  `Employee Production Tally` — WinForms reads the
       *  equivalent via
       *  `ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header`.
       *  <p>
       *  Optional `override` matches the sibling `menuCycleInfo`
       *  field's `overrideField` — pass the same value so the
       *  resolver taps the correct cache entry (queryKey tail
       *  includes the override). Returns `undefined` when no
       *  fetch has completed yet — the caller decides whether to
       *  block submit or send 0. */
      resolvePkeyMenuHeader?: (dateIso: string | null, override?: number | null) => number | undefined;
      /** Companion to `resolvePkeyMenuHeader` — returns the raw
       *  `Menu_Name` string for the same cached menu-cycle-info
       *  payload. Used by report generators that echo the menu
       *  name in the settings payload (e.g. Nutrition - Analysis
       *  sends `Menu_Name` for the printed report title). */
      resolveMenuName?: (dateIso: string | null, override?: number | null) => string;
      /** Returns the 1-based `MenuDayToUse` for the current
       *  cycle-in-use on `dateIso`. Reads the same cache entry as
       *  `resolvePkeyMenuHeader`. Used by Selective Menu (pKey 2)
       *  and Recipes - Menu Cycle to send
       *  `MenuCycle_MenuDayToUse` on the wire — WinForms reads it
       *  from `ucMenuCycleInfo.MenuCycleInUseInfo.MenuDayToUse`. */
      resolveMenuDayToUse?: (dateIso: string | null, override?: number | null) => number | undefined;
      /** Returns the menu cycle's own `Start_Date` (`yyyy-MM-dd`)
       *  for the cycle in use on `dateIso`. NOT the report StartDate —
       *  this is when the underlying menu cycle began. Mirrors
       *  WinForms `ucMenuCycleInfo.MenuCycleInUseInfo.Start_Date`,
       *  used by Tray Tickets (`.vb:634`) to feed
       *  `TrayTicketsSettings.MenuStartDate`. */
      resolveMenuStartDate?: (dateIso: string | null, override?: number | null) => string | null;
      /** Lookup a menu-header's display `Menu_Name` by its
       *  `PKey_Menu_Hdr`. Reads the `useMenuCycles` cache the
       *  `menuCycleSelect` field populates. Used by Recipes -
       *  Ingredients for Menu Cycle to send `MenuName` alongside
       *  `FKey_Menu_Header` (WinForms sends `luMenuHeader.Text`). */
      resolveMenuHeaderName?: (pKey: number) => string;
      /** Returns the full name ("Last, First") for a patient PKey
       *  from the residents cache. Used by Nutrition - Analysis's
       *  `LstPatientNames` wire field (WinForms fills this from
       *  the selected grid rows at .vb:333). */
      resolvePatientFullName?: (patientId: number) => string;
      /** Returns the display Name for a diet-type PKey from the
       *  useDietTypes cache. Used by Nutrition - Analysis's
       *  `StringDietConsist` builder (WinForms iterates
       *  `clbDietTypes.CheckedItems` and joins names at
       *  .vb:291-300). */
      resolveDietTypeName?: (pKey: number) => string;
      /** Returns the display Name for a consistency PKey from
       *  the useConsistencies cache. Used by Nutrition -
       *  Analysis's `StringDietConsist` builder (WinForms joins
       *  clbConsist names at .vb:302-310). */
      resolveConsistencyName?: (pKey: number) => string;
      /** Returns the display Name for a DRI Group PKey from the
       *  useDRIGroups cache. Used by Nutrition - Analysis's
       *  `StringDRIName` wire field (WinForms reads
       *  `luFKey_DRIGroup.Text` at .vb:289). */
      resolveDriGroupName?: (pKey: number) => string;
      /** Display Name for a Special Needs PKey, from the list the
       *  `specialNeedSelect` picker loaded. Special Needs sends it as
       *  `LstSpecialNeedsName` (WinForms reads `rowFocused("Name")`,
       *  ucDietech_Report_Generator_SpecialNeeds.vb:380). */
      resolveSpecialNeedName?: (pKey: number) => string;
      /** Report colours (`Argb_Back` / `Argb_Fore`, signed ARGB ints) of a
       *  diet type / consistency, from the lists the pickers loaded. Used by
       *  the Extension Sheet Excel columns. Null when the row is not cached. */
      resolveDietTypeColors?: (pKey: number) => { back: number; fore: number } | null;
      resolveConsistencyColors?: (pKey: number) => { back: number; fore: number } | null;
      /** Comma-separated display names for checked set NUMBERS
       *  (e.g. "Cart 1, Cart 3"), reading from the useFacilitySets
       *  cache. Mirrors WinForms `ucClbSets.GetCheckedSetsString`
       *  used by Production Tally .vb:412 to populate the DTO's
       *  `SetsSelectedString` field. */
      resolveSetsSelectedString?: (setNumbers: number[]) => string;
      /** True when the user has interacted with a non-filter-toolbar
       *  form field since the last filter Load / Save / Clear.
       *  Only Production Tally reads it — mapped to the WinForms
       *  `_filterOverride` boolean which tells the server whether
       *  to use FORM data vs each picked filter's own DB rows
       *  (ProductionTally.vb:180). */
      hasUserEdited?: boolean;
    },
  ) => Record<string, unknown>;
  /** Report-specific pre-submit validation. Return `null` to
   *  proceed, or a user-facing error message to abort. Mirrors
   *  WinForms `ValidateForm` in each generator (e.g. "at least
   *  one set must be checked when Print Specific Set is chosen"). */
  validate?: (values: Record<string, unknown>) => string | null;
  /** Async server-side preparation invoked BETWEEN `validate` and
   *  `buildSettings`. Return extra fields merged into the
   *  buildSettings output; return an object with `error` to abort
   *  submission with a toast. Used by Nutritional Assessment
   *  Template — the report needs a fresh `PKeyPatient_AssesmentHeader`
   *  row created server-side before the queue submission (matches
   *  the WinForms
   *  `frmDietech_Resident_Assessment_Wizard.Show(rowNew, patient)`
   *  step at .vb:302-320). */
  preSubmit?: (
    values: Record<string, unknown>,
    ctx: {
      facilityId: number;
      userId: number;
      /** False when the report cannot use the Temp Menu for these values
       *  (`tempMenuSupported`): menu lookups made here use the active cycle too. */
      useTempMenu?: boolean;
      /** Facility settings snapshot from `useFacilitySettings`.
       *  Null when the query hasn't resolved. Same shape /
       *  case-insensitive lookup semantics as `_fs` in
       *  `buildSettings`. */
      facilitySettings?: Record<string, unknown> | null;
      /** Pre-print Non-Returned Selective Menu confirmation dialog
       *  used by Tray Tickets (pKey 4). Fetches the pending-selective
       *  rows from the server, and when non-empty pops a MUI dialog
       *  matching WinForms `frmDietech_TT_SelectiveMenu_Notify`. The
       *  promise resolves to:
       *    • `rejections[]` — the un-checked rows the user wants to
       *      SKIP (goes on the wire as `SelectiveRejectionPatientList`);
       *    • `'cancel'` — the user clicked Cancel; caller should
       *      short-circuit with `{ error: '' }` to abort silently. */
      showSelectiveMenuNonReturnedDialog: (request: {
        startDate: string;
        numberOfDays: number;
        pKey_Menu_Header: number;
        uniqueMealtimes: number[];
        setList?: number[] | null;
        patientList?: number[] | null;
        /** The facility whose residents are checked - several-facility
         *  Tray Tickets checks each one in turn. Defaults to the current one. */
        facilityId?: number;
      }) => Promise<Array<{ PKey_Patient: number; Mealtime: number; MenuDate: string }> | 'cancel'>;
      /** Active-resident pool used by Tray Tickets' client-side
       *  filters (Special Needs "Do Not Print TT" + Empty-diet-order
       *  + Floor/Unit resolution). Mirrors the rows WinForms's
       *  `Fill_dtPatientGrid` yields at `.vb:1010-1122`, minus the
       *  guest-recurrence pass — guests are excluded here in the
       *  React port (see the same limitation on the Non-Returned
       *  Selective Menu dialog). */
      activePatients?: Array<{
        pKey_Patient: number;
        floor: string | null;
        wing: string | null;
        dietOrder: string | null;
      }>;
      /** Unit-mapping table (already loaded via
       *  `useReferenceData`). Used to translate patient
       *  Floor + Wing → PKey_Unit_Mapping for Floor/Unit filtering
       *  when the facility runs UseUnitMapping mode. */
      units?: Array<{
        pKey_Unit_Mapping: number;
        floor: string | null;
        wing: string | null;
        unit?: string | null;
      }>;
      /** Async fetch (or cache-read) of the facility's Special
       *  Needs "Do Not Print Tray Ticket" patient PKeys. Uses the
       *  same React Query key as `useDoNotPrintTTPatientIds`, so a
       *  fresh request only fires when the cache is cold. Mirrors
       *  WinForms `Fill_dtSpecialNeedsPatientDoNotPrint` at
       *  `.vb:1125-1141`. */
      fetchDoNotPrintTTPatientIds?: (facilityId?: number) => Promise<number[]>;
      /** Same "Last, First" resolver `buildSettings` uses — exposed
       *  in preSubmit so that when the client-side patient filters
       *  trim SpecificResident's picked list, the overriding
       *  `LstPatientNames` stays in sync with the surviving PKeys. */
      resolvePatientFullName?: (pKey: number) => string;
      /** Range-aware active-patient pool for Tray Tickets. Mirrors
       *  WinForms `Fill_dtPatientGrid`: regulars + guests whose
       *  recurrence matches ANY day in `[startIso, startIso + days - 1]`.
       *  Uses the same React Query cache as the picker grid so it's
       *  instant when the grid was already rendered. Returns the
       *  same trimmed shape as `activePatients`. Falls back to the
       *  guest-excluding cache when startIso is null. */
      fetchTrayTicketPatientPool?: (startIso: string | null, numberOfDays: number, facilityId?: number) => Promise<Array<{
        pKey_Patient: number;
        floor: string | null;
        wing: string | null;
        dietOrder: string | null;
      }>>;
      /** Another facility's Floor/Unit setup - its UseUnitMapping and
       *  unit rows. Multi-facility reports resolve each checked
       *  facility's floor/unit tab against that facility's own setup
       *  (TrayTicket.vb:494-526). */
      fetchFloorUnitSetup?: (facilityId: number) => Promise<{
        useUnitMapping: boolean;
        units: Array<{ pKey_Unit_Mapping: number; floor: string | null; wing: string | null; unit?: string | null }>;
      }>;
      /** Another facility's settings record (GET facility/GetById) - several-
       *  facility Tray Tickets reads each checked facility's selective menu
       *  warnings (TrayTicket.vb:600). */
      fetchFacilitySettingsFor?: (facilityId: number) => Promise<Record<string, unknown> | null>;
      /** The Med Pass - Summary grid rows (residents and guests) for the
       *  current facility - the rows Specific Floor/Unit walks
       *  (MedPassSummary.vb:234-260). Same cache as the picker. */
      fetchMedPassSummaryPatients?: () => Promise<Array<{
        pKey_Patient: number;
        floor: string | null;
        wing: string | null;
      }>>;
      /** Launch the shared `<LabelSelectorDialog>` and await the
       *  user's choice. Used by the four label-report configs
       *  (Nourishment / MedPass / Resident / Dish-Up) to mirror
       *  the WinForms `frmLabelSelector*` modal shown before
       *  print. Resolves to a 0-based label-skip count (0 =
       *  start at label 1, no skips) or `'cancel'` when the
       *  user hit Close — callers should return `{ error: '' }`
       *  to abort silently. */
      showLabelSelector: (
        variant: import('../../components/shared/LabelSelectorDialog').LabelSelectorVariant,
      ) => Promise<number | 'cancel'>;
      /** React Query cache access — for reading loaded query data
       *  that a preSubmit needs (e.g. Production Tally reads the
       *  cached TallyFilter names to build `TallyFilterString`). */
      queryClient?: import('@tanstack/react-query').QueryClient;
    },
  ) => Promise<Record<string, unknown> | { error: string }>;
}

/** The desktop's message when Restaurant Style has nothing to print from: no paper
 *  layout and no Selective Touch layout (ucDietech_Report_Generator_SelectiveMenu.vb,
 *  SMReportLayoutFromTouch.HasPrintableLayout). Since 2026-10-25 a facility without
 *  the paper layout prints from its Selective Touch screens. */
export const SELECTIVE_MENU_NO_RESTAURANT_LAYOUT =
  'This facility has no Restaurant Style layout and no Selective Touch layout to print it from. Set up Selective Touch screens, or choose Standard.';

/** WinForms' message when the facility has no menu categories set up for
 *  printing selective menus (ucDietech_Report_Generator_SelectiveMenu.vb:793). */
export const SELECTIVE_MENU_NO_MENU_CATEGORIES =
  'There are no menu categories defined for printing selective menus.';

/** g_facilitySettings.IsCorporate for the report's facility. */
/**
 * Production Tally "Each {set} on its own pages": two or more {set_plural} picked, one facility, no saved filters,
 * not the Combined type (which adds every section into one summary). The API sends such a print to the Linux
 * renderer from any connection (ReportPoolRouting.NeedsLinuxRenderer): the Windows pool's older renderer ignores
 * the flag and adds every picked {set} into one tally.
 */
export function productionTallyEachSetOnOwnPagesAvailable(v: Record<string, unknown>): boolean {
  return v.PrintMode === 'set'
    && Array.isArray(v.SetList) && v.SetList.length > 1
    && v.ReportType !== 'combined'
    && (((v.LstFilters as number[] | undefined) ?? []).length === 0)
    && (((v.LstFacilityMultiple as number[] | undefined) ?? []).length === 0);
}

function isCorporateFacility(fs?: Record<string, unknown> | null): boolean {
  const settings = fs ?? (useGlobalStore.getState().facilitySettings as unknown as Record<string, unknown> | null);
  return Boolean((settings as { isCorporate?: boolean } | null)?.isCorporate);
}

/**
 * The facility a Recipes report is for: the corporate facility picker's choice (luFacility) when
 * the facility is corporate and one is picked, else this facility - as the desktop's
 * `If _isCorporate Then CInt(luFacility.EditValue) Else FKey_Facility`.
 */
export function corporateFacilityFor(picked: unknown, facilityId: number): number {
  const id = Number(picked);
  return isCorporateFacility() && Number.isFinite(id) && id > 0 ? id : facilityId;
}

// ===== SHARED OPTION LISTS =====

const MONTH_OPTIONS: FieldOption[] = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

/** Last 12 completed months as dropdown options — value is the
 *  1..12 offset (1 = last month, 12 = one year ago) matching the
 *  WinForms `_dtMonths` PKey scheme at
 *  `ucDietech_Report_Generator_Credits_EndOfMonth.vb:389-391`.
 *  Label is "MonthName YYYY" (e.g. "June 2026"). Recomputed on
 *  every call so long-running sessions that cross a month
 *  boundary don't drift — WinForms's `Fill_dtMonths` runs on
 *  every `Init()` for the same reason.
 */
function last12MonthsOptions(): FieldOption[] {
  const now = new Date();
  const monthName = (m: number) => (
    ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'][m]
  );
  const opts: FieldOption[] = [];
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ value: i, label: `${monthName(d.getMonth())} ${d.getFullYear()}` });
  }
  return opts;
}

/** This month and the twelve before it (offsets 0..12), newest first: the
 *  months the resident Credits tab offers (WinForms ledger month picker). */
function thisAndLast12MonthsOptions(): FieldOption[] {
  const now = new Date();
  const opts: FieldOption[] = [];
  for (let i = 0; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ value: i, label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }) });
  }
  return opts;
}

/** Resolve a `LAST_12_MONTHS_OPTIONS` offset (1..12) to the first
 *  day of that month — same math WinForms does in the Print block
 *  at `ucDietech_Report_Generator_Credits_EndOfMonth.vb:331-332`. */
function offsetToMonthStart(offset: number): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - offset, 1);
}

const MEAL_OPTIONS: FieldOption[] = [
  { value: 'Breakfast', label: 'Breakfast' },
  { value: 'Lunch', label: 'Lunch' },
  { value: 'Dinner', label: 'Dinner' },
];



const RESIDENT_LIST_TYPE_OPTIONS: FieldOption[] = [
  { value: 0, label: 'Grid - By Room' },
  { value: 1, label: 'Grid - By Name' },
  { value: 2, label: 'Grid - By Floor/Unit (Name)' },
  { value: 3, label: 'Grid - By Floor/Unit (Room)' },
  { value: 8, label: 'Extended - By Room' },
  { value: 9, label: 'Extended - By Name' },
  { value: 10, label: 'Extended - By Floor/Unit (Room)' },
  { value: 11, label: 'Extended - By Floor/Unit (Name)' },
  { value: 16, label: 'Grid - By Age' },
];

const ALLERGY_GROUP_TYPE_OPTIONS: FieldOption[] = [
  { value: 0, label: 'Ingredients & Items by Allergy' },
  { value: 1, label: 'Allergy by Resident' },
  { value: 2, label: 'Resident by Allergy' },
  { value: 3, label: 'Ingredients by Allergy' },
  { value: 4, label: 'Items by Allergy' },
];


// Note: `ALTERNATE_TYPE_OPTIONS` was removed with the duplicate
// pKey-11 config that was its only consumer. The Posting Menu configs
// that survive declare their AlternateType radio options inline.

// Note: previous `RESIDENT_MULTIPLES_TYPE_OPTIONS` and
// `CARDEX_SECTION_OPTIONS` constants were removed — the Multiples
// config now defines its inline options (Cardex/List radio +
// string-valued Cardex Sections multiCheckbox) directly under the
// `resident-multiples` slug so the shape matches WinForms
// `ResidentMultiplesSettings` exactly.

const MEAL_SUMMARY_TYPE_OPTIONS: FieldOption[] = [
  { value: 0, label: 'By Hot/Cold' },
  { value: 1, label: 'By Meal' },
  { value: 2, label: 'By Set' },
  { value: 3, label: 'Total' },
];

const MANAGEMENT_TEAM_SORT_OPTIONS: FieldOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'room', label: 'Room' },
];

// ===== HELPER FUNCTIONS =====

/**
 * Converts PascalCase to kebab-case - mirrors C# TblRS_Report.PascalToKebab exactly.
 * "TrayTickets" → "tray-tickets", "AIMChanges" → "aim-changes"
 */
export function pascalToKebab(name: string | undefined | null): string | undefined {
  if (!name) return undefined;
  // Insert hyphen before uppercase letters preceded by a lowercase letter or digit
  let result = name.replace(/([a-z0-9])([A-Z])/g, '$1-$2');
  // Handle consecutive uppercase followed by lowercase (e.g. "AIMChanges" → "AIM-Changes")
  result = result.replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2');
  return result.toLowerCase().replace(/ /g, '-').replace(/_/g, '-');
}

const today = () => toLocalDateString();
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return toLocalDateString(d);
};
const weekAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toLocalDateString(d);
};
const threeMonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return toLocalDateString(d);
};

/**
 * VB `CInt` of a Double: nearest integer, halves to even (banker's rounding).
 * The recipe yield defaults are `GetNearest5(CInt(census * 0.75))`
 * (ucDietech_Report_Generator_Recipe_Book.vb:323, Recipe_MenuCycle.vb:576);
 * truncating instead gave 55 for a census of 77 where WinForms gives 60.
 */
export function vbCInt(x: number): number {
  const r = Math.round(x);
  return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}

/** M/D/YYYY for a date-only form value (never shifted through UTC). */
const formatDate = (val: unknown): string => formatDateOnly(val);

function formatDateRange(start: unknown, end: unknown): string {
  return formatDateOnlyRange(start, end);
}

/** A range whose server `DateFor` prints one date when start = end. */
function formatDateRangeSameDay(start: unknown, end: unknown): string {
  return formatDateOnlyRange(start, end, { collapseSameDay: true });
}

/**
 * Weekly Posting Menu footer = the server's `DateFor`:
 * `StartDate - StartDate.AddDays(NumberOfWeeks * 7 + endAdjust)`.
 * General (PostingMenuGeneralSettings.vb) uses `- 1`; Diet Specific,
 * Selective Menu, Specific Resident and General 2 do not.
 */
function formatWeeksRange(start: unknown, weeks: unknown, endAdjust: number): string {
  if (!parseDateOnly(start)) return '';
  const w = Math.max(1, Math.trunc(Number(weeks)) || 1);
  return formatDateRange(start, addDaysDateOnly(start, w * 7 + endAdjust));
}

/**
 * Med Pass footer = `MedPassSettings.DateForRange`: By Floor/Unit (0) and
 * Total (2) cover the week `DateFor .. DateFor + 6`; List and Labels one day.
 */
function formatMedPassDateFor(v: Record<string, unknown>): string {
  const type = Number(v.ReportTypeSelected ?? 0);
  if ((type === 0 || type === 2) && parseDateOnly(v.DateFor)) {
    return formatDateRange(v.DateFor, addDaysDateOnly(v.DateFor, 6));
  }
  return formatDate(v.DateFor);
}

/**
 * Census footer. Census.vb walks `NumberOfMonths` forward from
 * Month/Year, so a multi-month census reads "September 2026 - November
 * 2026" (the server's own text, CensusSettings.ToString, puts the count on
 * a second line: "September 2026 / Number Of Months - 3").
 */
function formatCensusMonths(v: Record<string, unknown>): string {
  const first = `${formatMonth(v.Month)} ${v.Year}`;
  const months = Math.trunc(Number(v.NumberOfMonths)) || 1;
  const month = Number(v.Month);
  const year = Number(v.Year);
  if (months <= 1 || !month || !year) return first;
  const last = new Date(year, month - 1 + months - 1, 1);
  return `${first} - ${formatMonth(last.getMonth() + 1)} ${last.getFullYear()}`;
}

function formatMonth(val: unknown): string {
  const m = val as number;
  return MONTH_OPTIONS.find(o => o.value === m)?.label ?? '';
}

/**
 * WinForms `GetFacilityMenuNotes(FKey_Facility, PKey_Menu_Header)`: the
 * facility's menu notes for the menu cycle in use on `startDate`, sent as
 * `StrNotesHTML` by the General (.vb:350) and Diet Specific (.vb:308)
 * posting menus. Route: `GET /api/MenuFacility/Notes/{fac}/{menuHeader}`
 * (MenuFacilityDAL.GetNotes, `SELECT Note FROM tblMenu_Facility_Notes`).
 * WinForms swallows errors and sends String.Empty; so does this (`{}`
 * leaves buildSettings' '' in place).
 */
async function fetchFacilityMenuNotes(facilityId: number, startDate: unknown, useTempMenu = true): Promise<Record<string, unknown>> {
  const d = startDate;
  const iso: string | null = d instanceof Date
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.substring(0, 10) : null);
  if (!iso) return {};
  try {
    const [{ fetchActiveMenuCycleInfo }, { default: apiClient }] = await Promise.all([
      import('../../api/selectiveTouchApi'),
      import('../../api/client'),
    ]);
    // The notes of the menu the report prints (the Temp Menu only where the report uses it).
    const info = await fetchActiveMenuCycleInfo(facilityId, iso, null, useTempMenu);
    const menuHeaderId = info?.pKey_Menu_Header ?? 0;
    if (menuHeaderId <= 0) return {};
    const { data } = await apiClient.get<Array<{ note?: string; notes?: string }>>(
      `/api/MenuFacility/Notes/${facilityId}/${menuHeaderId}`,
    );
    // ExecuteScalar: the first row's Note.
    const first = (data ?? [])[0];
    return { StrNotesHTML: (first?.note ?? first?.notes ?? '') as string };
  } catch {
    return {};
  }
}

// ===== TEMP MENU SUPPORT (see TempMenuSupport in utils/tempMenu.ts) =====
// Until the render hosts read the Temp Menu (VB MenuCycleTempOverride), a server-side
// lookup of the menu by date returns the facility's active cycle. These checks keep a
// report on the active cycle wherever its generator makes such a lookup, so it never
// prints the Temp Menu's items on the active cycle's days (or an empty grid).

/** The generator works the menu out from the date for all or part of the report. */
const TEMP_MENU_NOT_SUPPORTED = false as const;

/** Posting menus: the nourishment rows (MealtimesInt 3-6) come from
 *  NourishmentProductionWrapper, which looks the menu up by date
 *  (NourishmentProductionWrapper.vb:30), while the meals use the menu sent. */
export function postingMenuUsesTempMenu(v: Record<string, unknown>): boolean {
  return !((v.MealtimesInt as number[] | undefined) ?? []).some((m) => Number(m) >= 3);
}


/** Tray Tickets: the multi-facility run looks each facility's menu up by date
 *  (TrayTickets.vb:380), and the multi-day run by resident name (facility "group by Name",
 *  specific residents, more than one day) takes the menu sent with the date's menu day
 *  (TrayTickets.vb:1132-1135). Every other run uses the menu, day and start date sent. */
export function trayTicketsUseTempMenu(raw: Record<string, unknown>, fs: Record<string, unknown> | null): boolean {
  const v = collapseSingleCheckedFacility(raw);
  if (((v.LstFacilityMultiple as number[] | undefined) ?? []).length > 0) return false;
  const production = Boolean(fs?.useProduction);
  const groupByName = Number(fs?.tT_MultipleDayPrintType) === 1;
  const residents = String(v.PrintMode ?? 'all') === 'resident';
  const days = Number(v.NumberOfDays ?? 1);
  return !(production && groupByName && residents && days > 1);
}

// ===== REPORT CONFIGS =====

/**
 * Fieldset layout shared by the Posting Menu family.
 *
 * Was a `[data-report-slug^="posting-menu-"]` prefix selector in
 * Reports.css — which quietly applied to any future slug starting
 * with "posting-menu" and was invisible from the config. Declared
 * explicitly per report now; three of the eight override the label
 * column width, and those spread this and replace `Print`.
 */
const POSTING_MENU_FIELDSET: ReportGroupLayout = {
  density: 'compact', checkboxPad: '2px', cols: 'minmax(0, 150px) 1fr', gap: '4px 8px',
};
const POSTING_MENU_GROUP_LAYOUT: Record<string, ReportGroupLayout> = {
  Print: POSTING_MENU_FIELDSET,
};
/**
 * Only two of the eight Posting Menu reports actually have an Options
 * fieldset. The CSS prefix selector styled `[data-group="Options"]`
 * for all of them, which was harmless but hid the fact — declaring it
 * per report made the six dead branches visible immediately.
 */
const POSTING_MENU_GROUP_LAYOUT_WITH_OPTIONS: Record<string, ReportGroupLayout> = {
  ...POSTING_MENU_GROUP_LAYOUT,
  Options: POSTING_MENU_FIELDSET,
};

/**
 * Posting menu designs (docs/posting-menus/html-engine.md; Jeff 2026-11-03 "build all"). A design is a choice on an
 * existing posting menu, here Posting - Daily Menu (13) and Daily Menu - Full Day (8): Classic is the report as it
 * always printed and stays the default, and a Classic print sends exactly the settings it sent before.
 * Design C, today's menu poster: one day per page, the facility logo and accent color (Settings >
 * Posting Menu > Designs), and these options. Printed as HTML by the report renderer;
 * where its HTML engine is missing the classic layout prints.
 */
export const POSTING_DESIGN_CLASSIC = 0;
export const POSTING_DESIGN_POSTER = 1;
const isPosterDesign = (v: Record<string, unknown>) => Number(v.Design ?? POSTING_DESIGN_CLASSIC) === POSTING_DESIGN_POSTER;
const POSTING_DESIGN_FIELDS: ReportFieldConfig[] = [
  { key: 'Design', label: 'Design', type: 'dropdown', defaultValue: POSTING_DESIGN_CLASSIC, group: 'Design', width: 280,
    options: [
      { value: POSTING_DESIGN_CLASSIC, label: 'Classic' },
      { value: POSTING_DESIGN_POSTER, label: "Today's menu poster" },
    ],
    tooltip: "Today's menu poster prints each day on one page in large print, with your logo and color." },
  { key: 'PosterQrLink', label: 'QR code link', type: 'text', defaultValue: '', group: 'Design', newRow: true,
    visibleWhen: { key: 'Design', value: POSTING_DESIGN_POSTER },
    tooltip: 'A web address printed as a QR code at the foot of the poster, such as your menu page. Leave it blank for no QR code.' },
  { key: 'PosterNamesAsEntered', label: 'Print item names exactly as entered', type: 'checkbox', defaultValue: false,
    group: 'Design', newRow: true, visibleWhen: { key: 'Design', value: POSTING_DESIGN_POSTER },
    tooltip: 'Off: names in capitals print in title case, without a portion code such as (1).' },
];
/** The design settings a daily posting menu adds to its wire: nothing for Classic. */
export function postingDesignSettings(v: Record<string, unknown>): Record<string, unknown> {
  if (!isPosterDesign(v)) return {};
  return {
    Design: POSTING_DESIGN_POSTER,
    PosterQrLink: String(v.PosterQrLink ?? '').trim(),
    PosterNamesAsEntered: Boolean(v.PosterNamesAsEntered),
  };
}
/**
 * Design B, the large-print week, on Posting - General Menu (14): each meal's main dish, one side and the alternate in
 * large type, a week on one page, on 11x17, legal or letter landscape. Classic stays the default and sends exactly what
 * it sent before. The server prints it for production facilities, where the renderer can draw it; otherwise the
 * classic weekly grid prints.
 */
export const POSTING_DESIGN_LARGE_PRINT = 2;
const LARGE_PRINT_DESIGN_FIELDS: ReportFieldConfig[] = [
  { key: 'Design', label: 'Design', type: 'dropdown', defaultValue: POSTING_DESIGN_CLASSIC, group: 'Design', width: 280,
    options: [
      { value: POSTING_DESIGN_CLASSIC, label: 'Classic' },
      { value: POSTING_DESIGN_LARGE_PRINT, label: 'Large print week' },
    ],
    tooltip: "Large print week shows each meal's main dish, one side and the alternate in large type, a week on one page. For facilities that use production." },
  { key: 'LargePrintPaper', label: 'Paper', type: 'dropdown', defaultValue: 0, group: 'Design', width: 200, newRow: true,
    visibleWhen: { key: 'Design', value: POSTING_DESIGN_LARGE_PRINT },
    options: [
      { value: 0, label: '11x17 (best)' },
      { value: 1, label: 'Legal' },
      { value: 2, label: 'Letter' },
    ],
    tooltip: 'The large-print week prints landscape. 11x17 gives the largest type.' },
];
/** The design settings the General posting menu adds to its wire: nothing for Classic. */
export function postingLargePrintSettings(v: Record<string, unknown>): Record<string, unknown> {
  if (Number(v.Design ?? POSTING_DESIGN_CLASSIC) !== POSTING_DESIGN_LARGE_PRINT) return {};
  return { Design: POSTING_DESIGN_LARGE_PRINT, LargePrintPaper: Number(v.LargePrintPaper ?? 0) };
}
/** Paper size and dates are the poster's own (letter portrait, the date always printed). */
const notPosterDesign = { check: (v: Record<string, unknown>) => !isPosterDesign(v) };

// Tray Tickets paper types. WinForms fills cmbPaperType per facility
// (ucDietech_Report_Generator_TrayTicket.vb:287-304) and sends the index.
const TRAY_TICKET_PAPER_PRODUCTION: FieldOption[] = [
  { value: 0, label: '1 Part - Continuous' },
  { value: 1, label: '1 Part - 4"x4"' },
  { value: 2, label: '3 Part' },
  { value: 3, label: '4 Part' },
  { value: 4, label: '3 Part - Landscape' },
  { value: 5, label: 'Raw Data (xlsx)' },
];
const TRAY_TICKET_PAPER_BASIC: FieldOption[] = [
  { value: 0, label: '3 Part' },
  { value: 1, label: '4 Part' },
  { value: 2, label: '6 Part' },
];
function isProductionFacility(fs?: Record<string, unknown> | null): boolean {
  const settings = fs ?? (useGlobalStore.getState().facilitySettings as unknown as Record<string, unknown> | null);
  return Boolean((settings as { useProduction?: boolean } | null)?.useProduction);
}
/** 1 Part (0, 1) and Raw Data (5) lock Collate / Start New Page off - on a
 *  production facility only; the lockout sits inside `If _useProduction`
 *  (cmbPaperType_EditValueChanging .vb:1718-1756), where 0 / 1 are 3 / 4 Part. */
function isOnePartOrRawPaper(paperType: unknown, fs?: Record<string, unknown> | null): boolean {
  return isProductionFacility(fs) && (paperType === 0 || paperType === 1 || paperType === 5);
}
function isRawDataPaper(paperType: unknown, fs?: Record<string, unknown> | null): boolean {
  return isProductionFacility(fs) && paperType === 5;
}

/**
 * Management Team (Chargeable / Special Request / Fiber / Protein / Thickener):
 * the residents sent are the checked residents that live in the checked
 * floors/units. WinForms `GetCheckResidentsBasedOnUnitsSelected`
 * (ucDietech_Report_Generator_ManagementTeam.vb:1001-1012) walks the checked
 * residents and drops any whose Floor/Wing is not in the Floor/Units tab, and
 * every Print branch sends that as PatientList (.vb:206, :233, :259, ...).
 * The generators only read PatientList, so without this the Floor/Units tab
 * did nothing.
 *
 * When nobody is left WinForms sends an empty list, and the generators read an
 * empty PatientList as "no filter": the Chargeable and Fiber / Protein / Thickener
 * SQL skips its `PKey_Patient IN (...)` clause when the list is empty
 * (ManagementTeamChargeableList.vb:49-54, ManagementTeamFPTList.vb:173-178, and
 * the same lines in each Label / Summary generator), and the production path
 * hands it to ProductionMenuGenerator, which fills every resident for an empty
 * list (ProductionGenerator.vb:975-976). So WinForms prints the whole facility
 * for a Floor/Units selection that holds none of the checked residents (Special
 * Request prints "no data" instead, Diet_Utils.vb:1416-1428). The website stops
 * with the message the tray-ticket and med-pass dialogs use rather than copy that.
 */
const managementTeamFloorUnitPreSubmit: NonNullable<ReportConfig['preSubmit']> = async (v, ctx) => {
  const checked = (v.PatientList as number[] | undefined) ?? [];
  if (checked.length === 0) return {}; // validate() already asked for one
  const fs = ctx.facilitySettings as { useUnitMapping?: boolean; resident_Plural?: string } | null;
  const checkedSet = new Set(checked);
  const located = (ctx.activePatients ?? []).filter((p) => checkedSet.has(p.pKey_Patient));
  const inUnits = new Set(residentsInFloorUnits(
    located,
    (v.lstFloorUnit as number[] | undefined) ?? [],
    Boolean(fs?.useUnitMapping),
    ctx.units ?? [],
  ));
  const kept = checked.filter((id) => inUnits.has(id));
  if (kept.length === 0) {
    const plural = String(fs?.resident_Plural ?? 'residents').toLowerCase();
    return { error: `There are not any ${plural} associated to the selected floors/units. Please widen selection.` };
  }
  return { PatientList: kept };
};

export const REPORT_CONFIGS: ReportConfig[] = [

  // ─── RESIDENT ───
  // ─── BIRTHDAY (pKey 16, fKey 16) ───────────────────────────────
  // WinForms source: ucDietech_Report_Generator_Birthday.vb.
  // Print_Handler at .vb:191-202 iterates a list of 12 monthly
  // radios and calls PrintReport(month) with the 1-based index of
  // the checked radio. PrintReport (.vb:310-322) builds:
  //   .FKey_Facility = FKey_Facility
  //   .Month         = month (1..12)
  //   report class   = Birthday (server pKey 16)
  //   detailedReportName = "Birthday" (fixed)
  // BirthdaySettings has ONLY these two fields — no photo/sort/etc.
  // React config drops the previous IncludePhoto / SortByUnit
  // fields since the server would ignore them.
  //
  // Init (.vb:92): current month's radio checked by default.
  // Validate (.vb:302-305): always returns True (one radio is
  // always checked).
  {
    slug: 'birthday',
    reportName: 'Birthday',
    fKey_RS_Report: 16,
    apiEndpoint: 'Birthday',
    fields: [
      // 12 vertical Month radios matching WinForms `radJan` …
      // `radDec`. Default = current month (1-based, so `getMonth() + 1`).
      // `layout: 'column'` uses the new vertical radio variant.
      { key: 'Month', label: '', type: 'radio', required: true, group: 'Month',
        defaultValue: () => new Date().getMonth() + 1,
        layout: 'column',
        options: MONTH_OPTIONS },
    ],
    buildDateFor: (v) => formatMonth(v.Month),
    // Mirrors PrintReport (.vb:314-317). Only Month + FKey_Facility
    // are wire fields; FKey_Facility is added by handleSubmit.
    buildSettings: (v) => ({
      Month: v.Month,
    }),
    // No validation in WinForms; a radio is always checked.
    validate: () => null,
  },
  // ─── FACILITY CENSUS (pKey 21, fKey 21) ────────────────────────
  // WinForms source: ucDietech_Report_Generator_Census.vb.
  // Print (.vb:274-290):
  //   .FKey_Facility  = FKey_Facility
  //   .Month          = cmbMonth.SelectedIndex + 1 (1-based)
  //   .MonthName      = cmbMonth.Text (English month name)
  //   .Year           = CInt(cmbYear.Text)
  //   .ShowDays       = chkShowDay.Checked
  //   .NumberOfMonths = spNumberOfMonths.Value
  //   report class    = Census (server pKey 21)
  //   detailedReportName = "Census"
  //
  // Init (.vb:69-98):
  //   cmbYear: current year → current-3 (4 years descending)
  //   Default selection = LAST month:
  //     If Now.Month = 1 (January) → December + (Now.Year - 1)
  //     Else                        → prior month + current year
  //   spNumberOfMonths default = 1 (Designer).
  //   chkShowDay default unchecked (Designer) — label
  //     "Show Day Breakdown".
  //
  // Validate (.vb:308-313): always returns True — no validation.
  {
    slug: 'census',
    // Banner uses the `{Facility}` token so it reads e.g.
    // "Hospital Census" per the facility's `Facility` label.
    reportName: '{Facility} Census',
    fKey_RS_Report: 21,
    apiEndpoint: 'Census',
    fields: [
      // Month dropdown — 1..12. Default = LAST month (WinForms
      // .vb:85-91). Function default evaluates at form init.
      { key: 'Month', label: '', type: 'dropdown',
        defaultValue: () => {
          const m = new Date().getMonth() + 1; // 1..12
          return m === 1 ? 12 : m - 1;
        },
        options: MONTH_OPTIONS, required: true,
        group: 'Timeframe', width: 150 },

      // Year dropdown — last 4 years descending. Default paired
      // with the Month above so January rolls back a year.
      { key: 'Year', label: '', type: 'dropdown',
        defaultValue: () => {
          const now = new Date();
          return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        },
        options: (() => {
          const currentYear = new Date().getFullYear();
          return [0, 1, 2, 3].map((offset) => ({
            value: currentYear - offset,
            label: String(currentYear - offset),
          }));
        })(),
        required: true, group: 'Timeframe', width: 120 },

      // Number of Months — spin, 1..12, default 1 (Designer).
      // Same inline pattern as Employee Charge Summary — flanking
      // chevron buttons. `width: 265` fits the "Number of Months:"
      // label + spin + chevrons on one line without extra slack.
      { key: 'NumberOfMonths', label: 'Number of Months:', type: 'number',
        defaultValue: 1, min: 1, max: 12, required: true,
        group: 'Timeframe', width: 265 },

      // Options — Show Day Breakdown checkbox (chkShowDay label
      // per Designer). Unchecked default.
      { key: 'ShowDays', label: 'Show Day Breakdown', type: 'checkbox',
        defaultValue: false, group: 'Options' },
    ],
    buildDateFor: (v) => formatCensusMonths(v),
    // Mirrors WinForms Print block (.vb:278-285). `MonthName` is
    // the English month name — derived from the Month value via
    // the same MONTH_OPTIONS list. `FKey_Facility` added by
    // GenericReportForm at submit.
    buildSettings: (v) => {
      const monthOpt = MONTH_OPTIONS.find((o) => o.value === Number(v.Month));
      return {
        Month: Number(v.Month),
        MonthName: monthOpt?.label ?? '',
        Year: Number(v.Year),
        ShowDays: Boolean(v.ShowDays),
        NumberOfMonths: Number(v.NumberOfMonths),
      };
    },
  },
  {
    slug: 'resident-list',
    reportName: 'Resident List',
    description: 'Complete resident roster with diet and allergy details',
    fKey_RS_Report: 27,
    apiEndpoint: 'Roster',
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'ReportTypeToPrint', group: 'Report Type', label: 'Report Type', type: 'dropdown', defaultValue: 0, options: RESIDENT_LIST_TYPE_OPTIONS, required: true },
      { key: 'SortByNameAfterFloorWing', group: 'Options', label: 'Sort by Name within Floor/Wing', type: 'checkbox', defaultValue: false },
      { key: 'SpecificResident', group: 'Options', label: 'Specific Residents Only', type: 'checkbox', defaultValue: false },
      { key: 'LstPatient', group: 'Selection', label: 'Select Residents', type: 'residentSelect', defaultValue: [], visibleWhen: { key: 'SpecificResident', value: true } },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // ResidentListSettings (Resident List/ResidentListSettings.vb). WinForms
    // prints this from the resident grid and always fills LstPatient with the
    // grid's rows (ucDietech_Resident_DataGrid.vb:1904-1906, 1938-1953):
    // there is no "everyone" flag. An empty list is not "all" on the server:
    // the Grid types return no data (ResidentListGrid.vb:135) and the Extended
    // types build `IN ()` (:611-615). So "Specific Residents Only" off sends
    // every active resident. `SpecificResident` is ours, not the class's.
    // ProgramName prints as the report title (ResidentListGrid.vb:439, 896).
    buildSettings: (v, _fs, ctx) => ({
      DateFor: v.DateFor,
      ProgramName: 'Dietech',
      ReportTypeToPrint: Number(v.ReportTypeToPrint ?? 0),
      SortByNameAfterFloorWing: Boolean(v.SortByNameAfterFloorWing),
      LstPatient: v.SpecificResident
        ? ((v.LstPatient as number[] | undefined) ?? [])
        : (ctx?.resolveAllResidentPKeys?.() ?? []),
    }),
    validate: (v) => {
      if (v.SpecificResident && ((v.LstPatient as number[] | undefined) ?? []).length === 0) {
        return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },
  // ─── MULTIPLES (pKey 44 canonical / pKey 45 hidden) ────────────
  // WinForms source: ucDietech_Report_Generator_Resident_Multiples.vb.
  // Two dispatcher pKeys share one card here — pKey 44 =
  // ResidentMultiplesList (radList), pKey 45 =
  // ResidentMultiplesCardex (radCardex). `resolveFkeyReport`
  // swaps at submit time based on the Report Type radio.
  //
  // Init (.vb:82-133):
  //   TabPageText = "{Resident} - Multiples"
  //   Column visibility per facility UseUnitMapping —
  //     UseUnitMapping = true  → Unit column visible, Floor/Wing hidden
  //     UseUnitMapping = false → Floor/Wing visible (indices 0/1), Unit hidden
  //   Diet Order column visibility per facility ShowResidentDFColumn.
  //   `Fill_dtPatient` at .vb:360-390 loads the grid via
  //     ResidentManager.SQL_Patient_Select_String(guestsOnly=False)
  //     WHERE IsDeleted=0 AND Discharged=0 AND ActAsTemplate=0
  //           AND IsBulkOrder=0 AND FKey_Facility=@fac AND FKey_Patient IS NULL
  //   Age computed per row via Age_Calc(BirthDate ?? Today).
  //
  // Print (.vb:306-321, PrintCardex .vb:451-484, PrintList .vb:486-519):
  //   Common:
  //     .FKey_Facility, .DateFor = Now.Date, .ProgramName = g_PRIME_Program_Name
  //     .LstPatient = selected rows' PKey_Patient
  //   List (dispatcher pKey 44):
  //     .ReportTypeToPrint = ResidentMultiplesSettings.ReportType.List (0)
  //     .SortByUnit = colUnit.SortIndex >= 0 when UseUnitMapping,
  //                   else (colFloor.SortIndex >= 0 AND colWing.SortIndex >= 0)
  //   Cardex (dispatcher pKey 45):
  //     .ReportTypeToPrint = CardexMultiples (1)
  //     .LstCardexSections = List<string> from frmDietech_RptGen_TreeList
  //     .IsLandscape       = tree-list dialog toggle
  //     Note: WinForms shows a modal frmDietech_RptGen_TreeList
  //     that lets the user pick cardex sections + landscape.
  //     React port renders those inline as multiCheckbox +
  //     checkbox — the same values reach the server.
  //
  // Cardex section values (per ResidentMultiplesSettings.ToString
  // at .vb:46-52) — filtered so value "3" is skipped by the
  // report (it's `Progress Notes`, superseded by 4 & 5):
  //   0 = "Cardex and Lab Data"
  //   1 = "Meal Pattern"
  //   2 = "{ProblemGoalApproach}"   (facility label token)
  //   4 = "Progress Notes - Date and Title"
  //   5 = "Progress Notes - Details"
  //
  // Validate (.vb:308-311): ≥1 resident selected.
  {
    slug: 'resident-multiples',
    reportName: '{Resident} - Multiples',
    // Static default = pKey 44 (List). resolveFkeyReport swaps
    // at submit based on the Report Type radio.
    fKey_RS_Report: 44,
    apiEndpoint: 'ResidentMultiplesList',
    // Dispatcher pKey per WinForms class constants:
    //   ResidentMultiplesList.reportPKey  = 44   (radio "List")
    //   ResidentMultiplesCardex.reportPKey = 45  (radio "Cardex")
    // Radio option values in this form: 0 = Cardex, 1 = List.
    resolveFkeyReport: (v) => (Number(v.ReportType) === 0 ? 45 : 44),
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 0,
        group: 'Report Type',
        options: [
          { value: 0, label: 'Cardex' },
          { value: 1, label: 'List' },
        ] },

      // ─── Cardex Options (visible when Report Type = Cardex) ────
      // Mirrors the WinForms tree-list dialog
      // `frmDietech_RptGen_TreeList`. The four top-level sections
      // map to the string values ResidentMultiplesCardex.vb reads
      // at .vb:114-145. `LstCardexSections` is a multiCheckbox for
      // the first three; Progress Notes is a parent checkbox with
      // two sub-options that appear only when the parent is on.
      //
      // Server-side value mapping (see .vb:114-145 for the reads,
      // .settings.vb:46-52 for the display labels):
      //   "0" → Cardex and Lab Data
      //   "1" → Meal Pattern
      //   "2" → Problem/Goal/Approach
      //   "4" → Progress Notes - Date and Title
      //   "5" → Progress Notes - Details (implies Print_Progress_Notes)
      // Note: value "3" ("Progress Notes" parent) is intentionally
      // NOT sent — the settings ToString filter at .settings.vb:49
      // skips it, and the cardex renderer only checks "4"/"5".
      // `layout: 'twoColumn'` flows the 6 options in column-major
      // order — left column: Cardex and Lab Data / Meal Pattern /
      // Problem·Goal·Approach; right column: Progress Notes
      // (parent) plus its two indented children (Date and Title /
      // Details).
      //
      // `treeParents` wires the standard tree-list dialog behavior:
      //   - Progress Notes ("pn") shows checked when both children
      //     are on, indeterminate when one is, unchecked when
      //     neither is. Its state is DERIVED from the child
      //     selections — "pn" itself never enters `selected`.
      //   - Clicking Progress Notes toggles both children together.
      //   - Default = every leaf checked (matches WinForms tree-list
      //     dialog which opens with all leaves selected).
      // WinForms `.settings.vb:49` skips value "3" (its analogue),
      // so no numeric section ID leaks through the parent slot.
      //
      // Labels for the two Progress Notes children use non-breaking
      // spaces ( ) for indentation — MUI FormControlLabel
      // preserves nbsp but collapses regular spaces.
      { key: 'LstCardexSections', label: '', type: 'multiCheckbox',
        defaultValue: ['0', '1', '2', '4', '5'],
        treeParents: [
          { parentValue: 'pn', childValues: ['4', '5'] },
        ],
        options: [
          { value: '0', label: 'Cardex and Lab Data' },
          { value: '1', label: 'Meal Pattern' },
          { value: '2', label: 'Problem/Goal/Approach' },
          { value: 'pn', label: 'Progress Notes' },
          { value: '4', label: '    Date and Title' },
          { value: '5', label: '    Details' },
        ],
        layout: 'twoColumn',
        firstColumnMaxWidth: 250,
        group: 'Cardex Options',
        visibleWhen: { key: 'ReportType', value: 0 } },

      // List-only control — hidden when Cardex is selected.
      { key: 'SortByUnit', label: 'Sort by {FloorOrUnit}', type: 'checkbox',
        defaultValue: false,
        group: 'List Options',
        visibleWhen: { key: 'ReportType', value: 1 } },

      // Resident grid — specialized selector mirrors WinForms
      // column set: Unit | Rm | B | Name | Prescriber | Diet Order |
      // Admit Date | Birth Date | Age | Weight.
      { key: 'LstPatient', label: '', type: 'residentMultiplesSelect',
        defaultValue: [], group: '{Resident_Plural}', inline: true,
        defaultSelectAll: true },
    ],
    // No user-facing date field — WinForms hard-codes DateFor to Now.
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // ResidentMultiplesSettings shape — mirrors WinForms
    // PrintCardex (.vb:464-478) and PrintList (.vb:490-508). We
    // emit EVERY field on the settings class so both report
    // classes' JSON deserializers see the full object.
    //
    // ReportTypeToPrint enum (.settings.vb:4-9):
    //   List             = 0   ← radio value 1 (List)
    //   CardexMultiples  = 1   ← radio value 0 (Cardex)
    //   Cardex           = 2   (not exposed here)
    //   MealPattern      = 3   (not exposed here)
    //
    // LstCardexSections comes from the multiCheckbox. Strip the
    // UI-only "pn" sentinel — WinForms server-side reads only the
    // numeric-string section IDs ("0","1","2","4","5") at
    // ResidentMultiplesCardex.vb:114-145. List path sends an empty
    // array — the List report class doesn't read this field.
    buildSettings: (v, fs) => {
      const isCardex = Number(v.ReportType) === 0;
      const raw = (v.LstCardexSections as string[] | undefined) ?? [];
      return {
        FKey_Facility: 0, // server fills from URL
        // Printed as the report title. Never the old product name.
        ProgramName: 'Dietech',
        DateFor: new Date(),
        ReportTypeToPrint: isCardex ? 1 : 0,
        LstPatient: (v.LstPatient as number[] | undefined) ?? [],
        LstCardexSections: isCardex ? raw.filter((s) => s !== 'pn') : [],
        StrResidentFullName: '',
        // frmDietech_RptGen_TreeList.vb:296 opens with
        // `radLandscape.Checked = Not Cardex_Portrait(facility)`.
        IsLandscape: isCardex && (fs as { cardex_Portrait?: boolean } | null)?.cardex_Portrait === false,
        SortByUnit: isCardex ? false : Boolean(v.SortByUnit),
      };
    },
    // Mirrors ValidateForm at .vb:308-311.
    validate: (v) => {
      const residents = (v.LstPatient as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  {
    slug: 'resident-discharge-summary',
    reportName: 'Resident Discharge Summary',
    fKey_RS_Report: 92,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true, endKey: 'EndDate', endLabel: 'End Date' },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  // ─── RESIDENT ASSESSMENT LIST (pKey 103, fKey 103) ─────────────
  // WinForms source: ucDietech_Report_Generator_ResidentAssessmentList.vb.
  // Print (.vb:253-278):
  //   .FKey_Facility        = _FKey_Facility
  //   .DateFor              = Now  (hard-coded — form has no date
  //                                  picker for DateFor)
  //   .OrderByName          = radOrderName.Checked
  //   .OrderByRoom          = radOrderRoom.Checked
  //   .StartNewPage         = chkStartNewPage.Checked
  //   .PrintGreaterThanDate = chkPrint.Checked
  //   .DateGreaterThan      = dePrintDate.DateTime
  //   .LstSpecificResident  = ucClbResident.GetCheckResidents
  //   report class          = ResidentAssessmentList (pKey 103 per
  //                                  ReportDispatcher.cs:125)
  //   queue title           = "{Resident} Assessment List"
  //                            (facility Resident label — .vb:277
  //                             `g_facilitySettings.Resident`)
  //
  // Init (.vb:67-89):
  //   radOrderRoom default checked (Designer default; radOrderRoom
  //     is the first radio in the group).
  //   radOrderRoom.Text = "{Room} Number" (.vb:77 —
  //     `g_facilitySettings.Room(FKey_Facility)`).
  //   chkStartNewPage default checked (matches supplied UI mockup).
  //   chkStartNewPage.Text swaps via UseUnitMapping — WinForms
  //     `ResidentAssessmentListSettings.ToString` at .vb:23-29 uses
  //     "Start New Page After Each Unit" (UseUnitMapping=true) vs
  //     "Start New Page After Each Floor". Same `{FloorOrUnit}`
  //     token pattern as the Skin report.
  //   chkPrint default unchecked.
  //   lblGreaterThan = "≥" (Unicode U+2265) — inline with the Print
  //     checkbox in the WinForms designer.
  //   dePrintDate default = Now.AddDays(-89) (.vb:84); enabled only
  //     when chkPrint checked (.vb:321).
  //   ucClbResident.ShowHeaderLabel = True — the patient list
  //     renders under a "Patient" tab header (matches mockup).
  //
  // Validate (.vb:308-316): at least one resident must be checked.
  {
    slug: 'resident-assessment-list',
    reportName: '{Resident} Assessment List',
    fKey_RS_Report: 103,
    apiEndpoint: 'ResidentAssessmentList',
    fields: [
      // Order radio — "{Room} Number" / "Name" pair. `{Room}` token
      // resolves to the facility's Room label (Room / Suite / Bed /
      // etc.) via GenericReportForm's substituteTokens.
      { key: 'Order', label: '', type: 'radio', defaultValue: 'room',
        group: 'Order',
        options: [
          { value: 'room', label: '{Room} Number' },
          { value: 'name', label: 'Name' },
        ] },

      // Options group — chkStartNewPage default TRUE per mockup;
      // chkPrint default FALSE; dePrintDate always visible but
      // disabled unless chkPrint is checked (mirrors WinForms
      // chkPrint_CheckedChanged at .vb:321-323).
      { key: 'StartNewPage', label: 'Start New Page After Each {FloorOrUnit}',
        type: 'checkbox', defaultValue: true, group: 'Options' },

      { key: 'PrintGreaterThanDate', label: 'Print ≥', type: 'checkbox',
        defaultValue: false, group: 'Options' },

      // dePrintDate — always rendered so the layout matches the
      // WinForms designer, but disabled until PrintGreaterThanDate
      // toggles on. Default = today - 89 (matches .vb:84).
      { key: 'DateGreaterThan', label: '', type: 'date',
        group: 'Options',
        defaultValue: () => {
          const d = new Date();
          d.setDate(d.getDate() - 89);
          return d;
        },
        enabledWhen: { key: 'PrintGreaterThanDate', value: true } },

      // Patient list — mirrors ucClbResident. All residents shown
      // checked by default (matches UI mockup). `inline: true`
      // pulls the selector OUT of the right-side column so it
      // flows below the Order + Options groups in the main column,
      // matching the WinForms designer where the resident checklist
      // sits below the Options band under a "Patient" header.
      { key: 'LstSpecificResident', label: '', type: 'residentSelect',
        defaultValue: [], group: 'Patient', inline: true,
        defaultSelectAll: true },
    ],
    // DateFor is a fixed Now() at print time — WinForms does NOT
    // expose a date picker for it. `buildDateFor` populates the
    // queue's "Date" column with today's date to match.
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print settings block (.vb:258-273).
    buildSettings: (v) => ({
      DateFor: new Date(),
      OrderByName: v.Order === 'name',
      OrderByRoom: v.Order === 'room',
      StartNewPage: Boolean(v.StartNewPage),
      PrintGreaterThanDate: Boolean(v.PrintGreaterThanDate),
      DateGreaterThan: v.DateGreaterThan,
      LstSpecificResident: (v.LstSpecificResident as number[] | undefined) ?? [],
    }),
    // Mirrors ValidateForm (.vb:308-316) — at least one resident.
    validate: (v) => {
      const residents = (v.LstSpecificResident as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  {
    slug: 'resident-assessment',
    reportName: 'Resident Assessment',
    fKey_RS_Report: 105,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'PrintAll', group: 'Options', label: 'Print All Residents', type: 'checkbox', defaultValue: true },
      { key: 'IsBlank', group: 'Options', label: 'Blank Form', type: 'checkbox', defaultValue: false },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },
  {
    slug: 'resident-data-grid',
    reportName: 'Resident Data Grid',
    fKey_RS_Report: 75,
    // 2026-10 redesign (ResidentDataGridLayout): by unit, then room (default:
    // portrait, unit bands, summary at the end) or one list sorted by name
    // (landscape). The desktop's Ctrl+Shift+P offers the same two choices; no
    // grid layout is sent (StrStreamMaster / StrStreamDetail are not read).
    fields: [
      { key: 'Order', label: '', type: 'radio', defaultValue: 'unit',
        group: 'Order',
        options: [
          { value: 'unit', label: 'By {FloorOrUnit}, then {Room}' },
          { value: 'name', label: 'Sorted by name' },
        ] },
    ],
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => ({
      DateFor: new Date(),
      SortByName: v.Order === 'name',
    }),
  },
  // ─── NUTRITIONAL ASSESSMENT TEMPLATE (pKey 104) ───────────────
  // WinForms source: ucDietech_Report_Generator_AssessmentTemplate.vb.
  // Print (.vb:276-337):
  //   .FKey_Facility               = _FKey_Facility
  //   .DateFor                     = Now
  //   .PKeyPatient_AssesmentHeader = wizard-created header row PKey
  //   .AssessmentName              = selected type's Name
  //   report class                 = AssessmentTemplate (pKey 104
  //                                  per ReportDispatcher.cs:126)
  //   queue title                  = "Assessment Template" (.vb:333)
  //
  // Init (.vb:83-98,343-384):
  //   Fetches facility-scoped assessment setup types (via
  //   `SQL_AssessmentSetupType_Facility_Select`), renders each as
  //   a stacked radio (first checked).
  //
  // The `PKeyPatient_AssesmentHeader` row is created server-side
  // via `POST /api/Assessment/CreateBlankHeaderForReport/{fac}`
  // inside `preSubmit` — mirrors the WinForms wizard flow at
  // `.vb:292-320` which inserts a blank header row before queueing.
  // The `AssessmentTemplate` report class deletes the row after
  // rendering (see AssessmentTemplate.vb:60-64), so no cleanup is
  // needed here.
  {
    slug: 'assessment-template',
    reportName: 'Nutritional Assessment Template',
    fKey_RS_Report: 104,
    apiEndpoint: 'AssessmentTemplate',
    fields: [
      { key: 'PKey_AssessmentSetupType_Facility', label: '',
        type: 'assessmentSetupTypeRadio', defaultValue: 0,
        group: 'Assessment Type' },
    ],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Create the temp `tblPatient_AssessmentHeader` row server-side
    // so the AssessmentTemplate report gets a valid PKey to render
    // against. The report class deletes the row on completion.
    preSubmit: async (v, { facilityId }) => {
      const pKey = Number(v.PKey_AssessmentSetupType_Facility ?? 0);
      if (pKey <= 0) return { error: 'Please select an assessment type.' };
      try {
        const apiClient = (await import('../../api/client')).default;
        const { API } = await import('../../api/endpoints');
        const { data } = await apiClient.post<{ pKey_Patient_AssessmentHeader: number; name: string }>(
          API.createBlankAssessmentHeaderForReport(facilityId),
          { PKey_AssessmentSetupType_Facility: pKey },
        );
        return {
          PKeyPatient_AssesmentHeader: data?.pKey_Patient_AssessmentHeader ?? 0,
          AssessmentName: data?.name ?? '',
        };
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to prepare assessment header.';
        return { error: msg };
      }
    },
    // Mirrors WinForms settings block (.vb:323-330). The
    // `PKeyPatient_AssesmentHeader` + `AssessmentName` fields are
    // merged in from `preSubmit`'s server response.
    buildSettings: () => ({
      DateFor: new Date(),
    }),
    validate: (v) => {
      if (!v.PKey_AssessmentSetupType_Facility) {
        return 'Please select an assessment type.';
      }
      return null;
    },
  },
  // ─── PATIENT LABELS (pKey 64, fKey 64) ─────────────────────────
  // WinForms source: ucDietech_Report_Generator_Resident_Labels.vb.
  //
  // Init (.vb:88-150):
  //   TabPageText           = "{Resident} Labels" (facility Resident label)
  //   radByRoomNumer.Text   = "{Room} Number"
  //   radSpecificSet.Text   = "Specific {SetName}"
  //   radPrintResident.Text = "Specific {Resident}"
  //   ucDeStart.DateInit    = today
  //   ucRadMealtimes        — B/L/D radio (default B)
  //   Order By default from facility `TT_PrintOrder`:
  //       0,3 → Name; 1,4 → Room Number; 2,5 → Tray Line.
  //
  // Print (.vb:509-583):
  //   .FKey_Facility          = _FKey_Facility
  //   .DateFor                = ucDeStart.EditValue
  //   .Mealtime               = ucRadMealtimes.GetSelectedMealtime (0/1/2)
  //   .OrderByName / ByRoom / ByTrayLine — one-of-three
  //   .AttendanceAll / Normal / SM       — one-of-three
  //   .PrintAll / PrintSpecificSet / PrintSpecificResident — one-of-three
  //   .LstSets  = selected sets (when PrintSpecificSet)
  //   .LstResident = selected residents (when PrintSpecificResident)
  //   .LabelCount  = labelsToSkip (from PRIMEServices label-selector
  //                                dialog — NOT ported; always 0)
  //   .LabelSizeType = facility ResidentList_Labels_Type or _B_Type
  //                     — server-derived; sending 0 is safe (server
  //                     re-reads the facility setting).
  //   .LabelType   = 0 (Labels A) / 1 (Labels B)
  //   report class = ResidentsLabels (dispatcher pKey 64)
  //   queue title  = "{Resident} Labels"
  //
  // Validate (.vb:466-486):
  //   - PrintSpecificSet     → ≥1 set required
  //   - PrintSpecificResident → ≥1 resident required
  {
    slug: 'resident-labels',
    reportName: '{Resident} Labels',
    fKey_RS_Report: 64,
    apiEndpoint: 'ResidentsLabels',
    fields: [
      // Report Type — radLablesA (default checked) / radLablesB.
      { key: 'LabelType', label: '', type: 'radio', defaultValue: 0,
        group: 'Report Type',
        options: [
          { value: 0, label: 'Labels A' },
          { value: 1, label: 'Labels B' },
        ] },

      // Date — single date picker (ucDeStart), defaults to today.
      { key: 'DateFor', label: '', type: 'date', defaultValue: () => today(),
        required: true, group: 'Date' },

      // Mealtimes — B / L / D radio (single-select). Labels resolve
      // through the facility's meal-name settings (Breakfast_Name /
      // Lunch_Name / Dinner_Name). WinForms uses
      // `g_facilitySettings.GetMainMealNames(FKey_Facility)` at
      // .vb:104 to populate the radio labels.
      { key: 'Mealtime', label: '', type: 'radio', defaultValue: 0,
        group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Order By — one-of-three radio. Default is derived from
      // facility TT_PrintOrder: 0/3 → Name, 1/4 → Room, 2/5 → Tray
      // Line. React port defaults to 'trayline' (matches supplied
      // UI screenshot); if the facility's TT_PrintOrder differs
      // it's overridable at the field level (users pick per-print).
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'trayline',
        group: 'Order By',
        options: [
          { value: 'name', label: 'Name' },
          { value: 'room', label: '{Room} Number' },
          { value: 'trayline', label: 'Tray Line' },
        ] },

      // Attendance — one-of-three radio. `All` is default per mockup.
      { key: 'Attendance', label: '', type: 'radio', defaultValue: 'all',
        group: 'Attendance',
        options: [
          { value: 'all', label: 'All' },
          { value: 'normal', label: 'Normal' },
          { value: 'sm', label: 'Selective Menu' },
        ] },

      // Print — one-of-three radio. `All` is default per mockup.
      // Set list appears under `Specific Cart` (facility SetName);
      // Resident list appears under `Specific Patient`. Both selectors
      // are `inline: true` so they render in the main column below
      // the Print radio when their corresponding value is chosen.
      { key: 'Print', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },

      { key: 'LstSets', label: 'Select {SetName_Plural}', type: 'setSelect',
        defaultValue: [], visibleWhen: { key: 'Print', value: 'set' },
        defaultSelectAll: true, group: 'Print', inline: true },

      // patientLabelSelect — specialized selector that filters
      // by day-of-week attendance columns
      // (`attend_Day_{B|L|D}{1..7}`), mirrors WinForms
      // `Fill_dtPatientGrid` at .vb:293-402. Reads DateFor,
      // Mealtime, and Attendance as sibling formValues (no
      // watchField prop — the selector case in
      // GenericReportForm.tsx forwards them directly).
      // Columns: guest-indicator icon + Name / Diet Order / Unit / {Room}.
      // Guest recurrence-based filtering (WinForms
      // `ValidMealtimesForGuestRecurrence`) is NOT ported yet —
      // guests pass through unfiltered by their own schedule.
      { key: 'LstResident', label: '', type: 'patientLabelSelect',
        defaultValue: [], visibleWhen: { key: 'Print', value: 'resident' },
        defaultSelectAll: true, group: 'Print', inline: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Pre-print label-selector dialog — WinForms
    // `ucDietech_Report_Generator_Resident_Labels.vb:509-538`.
    // Two facility settings feed the variant pick, keyed by the
    // Labels A/B radio in this form:
    //   • LabelType === 0 (Labels A) → `residentList_Labels_Type`
    //   • LabelType === 1 (Labels B) → `residentList_Labels_B_Type`
    // Master gate: `labelReports_UseLabelSelector`. Variant:
    //   labelType === 0 → A5195 · else → generic 30-up.
    preSubmit: async (v, { facilitySettings, showLabelSelector }) => {
      const fs = facilitySettings as {
        labelReports_UseLabelSelector?: boolean;
        residentList_Labels_Type?: number;
        residentList_Labels_B_Type?: number;
      } | null;
      if (!fs?.labelReports_UseLabelSelector) return {};
      const isLabelsA = Number(v.LabelType ?? 0) === 0;
      const labelType = isLabelsA
        ? (fs.residentList_Labels_Type ?? 0)
        : (fs.residentList_Labels_B_Type ?? 0);
      const variant = labelType === 0 ? 'A5195' : 'generic30';
      const result = await showLabelSelector(variant);
      // Close/Escape → silent cancel; abort submit without a toast.
      if (result === 'cancel') return { error: '' };
      return { LabelCount: result };
    },
    // Mirrors WinForms Print settings block (.vb:540-578).
    buildSettings: (v, fs) => {
      // Resolve `LabelSizeType` from the A/B radio + facility
      // settings — WinForms `.LabelSizeType = labelType` at
      // .vb:571 where `labelType` is the setting picked by the
      // A/B branch at .vb:513-517. This is ALWAYS sent regardless
      // of whether the label-selector dialog runs; only
      // LabelCount depends on the dialog. Sending 0 here made the
      // server default to Labels B rendering — hence the "Type A
      // selection but Type B output" bug.
      const isLabelsA = Number(v.LabelType ?? 0) === 0;
      const labelSizeType = isLabelsA
        ? Number((fs as { residentList_Labels_Type?: number } | null)?.residentList_Labels_Type ?? 0)
        : Number((fs as { residentList_Labels_B_Type?: number } | null)?.residentList_Labels_B_Type ?? 0);
      return {
        DateFor: v.DateFor,
        Mealtime: Number(v.Mealtime ?? 0),
        OrderByName:     v.OrderBy === 'name',
        OrderByRoom:     v.OrderBy === 'room',
        OrderByTrayLine: v.OrderBy === 'trayline',
        AttendanceAll:    v.Attendance === 'all',
        AttendanceNormal: v.Attendance === 'normal',
        AttendanceSM:     v.Attendance === 'sm',
        PrintAll:              v.Print === 'all',
        PrintSpecificSet:      v.Print === 'set',
        PrintSpecificResident: v.Print === 'resident',
        LstSets:     v.Print === 'set'      ? ((v.LstSets     as number[] | undefined) ?? []) : [],
        LstResident: v.Print === 'resident' ? ((v.LstResident as number[] | undefined) ?? []) : [],
        // Default 0 — overridden by the preSubmit hook above when
        // the label-selector dialog runs and the user picks a non-
        // first starting label. `preSubmitExtras` spreads on top of
        // wireSettings in GenericReportForm.handleSubmit so the
        // wire value wins.
        LabelCount: 0,
        LabelSizeType: labelSizeType,
        LabelType: Number(v.LabelType ?? 0),
      };
    },
    // Mirrors ValidateForm (.vb:466-486).
    validate: (v) => {
      if (v.Print === 'set') {
        const sets = (v.LstSets as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select at least one {SetName}.';
      }
      if (v.Print === 'resident') {
        const residents = (v.LstResident as number[] | undefined) ?? [];
        if (residents.length === 0) return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },

  // ─── DIET ORDER ───
  {
    slug: 'diet-order',
    reportName: 'Diet Order',
    description: 'Current diet orders grouped by diet order or by floor/unit',
    fKey_RS_Report: 57,
    apiEndpoint: 'DietOrder',
    // WinForms ucDietech_Report_Generator_DietOrder.vb: one Order radio
    // (By Diet Order, default per Designer.vb:124 / By Diet Order (Count
    // Only) / By Floor or By Unit, .vb:99-101), sent as three booleans
    // (.vb:314-316). DietOrder.vb:52 sorts on OrderByFloorUnit and :142
    // reads OrderByDietOrderCountOnly; there is no name/room/diet sort.
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'Order', group: 'Order', label: '', type: 'radio', defaultValue: 'dietOrder',
        options: [
          { value: 'dietOrder', label: 'By Diet Order' },
          { value: 'countOnly', label: 'By Diet Order (Count Only)' },
          { value: 'floor', label: 'By {FloorOrUnit}' },
        ] },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v) => {
      const order = String(v.Order ?? 'dietOrder');
      return {
        DateFor: v.DateFor,
        OrderByDietOrder: order === 'dietOrder',
        OrderByDietOrderCountOnly: order === 'countOnly',
        OrderByFloorUnit: order === 'floor',
      };
    },
  },
  // ─── DIET ORDER — CHANGES (pKey 53, fKey 53) ───────────────────
  // WinForms source: ucDietech_Report_Generator_EHR_DietOrder_Changes.vb.
  // Print (.vb:295-307):
  //   .FKey_Facility = _FKey_Facility
  //   .DateStart     = ucDate.EditValue
  //   .DateEnd       = ucDeEnd.EditValue
  //   report class   = DietOrderChanges (server pKey 53)
  //   detailedReportName = "Diet Order - Changes" (fixed English —
  //   no facility token)
  //
  // Init (.vb:72-95):
  //   ucDate (start): no label, DateMin = Now - 10 years,
  //                   DateInit = today - 7 days
  //   ucDeEnd:        DateMin = start, DateInit = today, label "To"
  //
  // Validate (.vb:342-346): always returns True — no validation.
  {
    slug: 'diet-order-changes',
    // Banner shows full name; sidebar card uses `nameForList:
    // 'Changes'` from staticReports so it reads as
    // "Diet Order → Changes" under the category header.
    reportName: 'Diet Order - Changes',
    fKey_RS_Report: 53,
    apiEndpoint: 'DietOrderChanges',
    fields: [
      // Date Range — bidirectional invariant `DateEnd >= DateStart`.
      // Start default = today - 7 (.vb:84); End default = today
      // (.vb:88).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) {
            const d = new Date();
            d.setDate(d.getDate() - 7);
            return d;
          }
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },
    ],
    buildDateFor: (v) => formatDateRange(v.DateStart, v.DateEnd),
    buildSettings: (v) => ({
      DateStart: v.DateStart,
      DateEnd: v.DateEnd,
    }),
  },
  // ─── DIET ORDER — TRAY LINE (pKey 56, fKey 56) ─────────────────
  // WinForms source: ucDietech_Report_Generator_DietOrder_TrayLine.vb.
  // Print (.vb:291-307):
  //   .FKey_Facility     = _FKey_Facility
  //   .DateFor           = ucDate.EditValue
  //   .OrderByDietOrder  = radByDietOrder.Checked  (radio arm 1)
  //   .OrderByFloor      = radFloor.Checked        (radio arm 2)
  //   .Mealtime          = ucRadMealtimes.GetSelectedMealtime
  //                        (single int — Mealtime enum:
  //                         Breakfast=1, Lunch=2, Dinner=4)
  //   .LstSets           = ucClbSets.GetCheckedSetsList
  //   report class       = DietOrderTrayLine (server pKey 56)
  //   detailedReportName = "Diet Type - Tray Line" (WinForms literal
  //   at .vb:305 — the settings class ToString says "Diet Order -
  //   Tray Line" though. TabPageText is "Diet Order - Tray Line".)
  //
  // Init (.vb:85-118):
  //   ucDate: no label, DateInit = today
  //   ucRadMealtimes: default depends on the control — radio group
  //     of 3 mealtimes (Breakfast/Lunch/Dinner). Default 1 (Breakfast).
  //   ucClbSets: multi-check list of facility sets
  //   radByDietOrder default checked (Designer)
  //   radFloor.Text = "By Unit" if facility UseUnitMapping, else
  //                   "By Floor" (.vb:109-113)
  //
  // Validate (.vb:361-374):
  //   - ≥1 set selected → "Select at least one {SetName}"
  //   - ≤17 sets selected → "Sorry, but you cannot select more
  //                         than 17 {SetName}s."
  {
    slug: 'diet-order-tray-line',
    reportName: 'Diet Order - Tray Line',
    fKey_RS_Report: 56,
    apiEndpoint: 'DietOrderTrayLine',
    fields: [
      // Print scope radio — mutually-exclusive radByDietOrder /
      // radFloor. Second label swaps via facility UseUnitMapping.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'diet',
        group: 'Print',
        options: () => {
          const fs = useGlobalStore.getState().facilitySettings as { useUnitMapping?: boolean } | null;
          const useUnitMapping = Boolean(fs?.useUnitMapping);
          return [
            { value: 'diet', label: 'By Diet Order' },
            { value: 'floor', label: useUnitMapping ? 'By Unit' : 'By Floor' },
          ];
        } },

      // Date — single date, defaults to today (.vb:96).
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Mealtime — WinForms `ucRadMealtimes` is a single-select
      // radio group of 3 mealtimes. Values are the INDEX returned
      // by `GetSelectedMealtime` at
      // `ucDietech_Reports_Generator_RadMealtime.vb:35-39`:
      // Breakfast=0, Lunch=1, Dinner=2. NOT the `Mealtime` enum
      // bit flags — this control emits a 0-based radio index that
      // the server settings class stores as `Mealtime As Integer`.
      // Labels use facility-scoped tokens so a facility that
      // relabels breakfast → "BreakfastM" gets the right text.
      { key: 'Mealtime', label: '', type: 'radio', defaultValue: 0,
        group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Sets — multi-select via `setSelect`, auto-selects all on
      // mount (WinForms opens with every checkbox on). Tab label
      // uses `{SetName}` singular to match the screenshot's
      // "Cart" header.
      { key: 'LstSets', label: '', type: 'setSelect',
        defaultValue: [],
        group: '{SetName}',
        inline: true,
        defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print settings block (.vb:295-302).
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      OrderByDietOrder: v.PrintMode === 'diet',
      OrderByFloor: v.PrintMode === 'floor',
      Mealtime: v.Mealtime,
      LstSets: (v.LstSets as number[] | undefined) ?? [],
    }),
    // Mirrors ValidateForm (.vb:361-374).
    validate: (v) => {
      const sets = (v.LstSets as number[] | undefined) ?? [];
      if (sets.length === 0) return 'Please select at least one {SetName}.';
      if (sets.length > 17) return 'Sorry, but you cannot select more than 17 {SetName_Plural}.';
      return null;
    },
  },
  // ─── CONSISTENCY — CENSUS (pKey 54, fKey 54) ───────────────────
  // WinForms source: ucDietech_Report_Generator_Consist_Census.vb.
  // Print (.vb:280-296):
  //   .FKey_Facility  = _FKey_Facility
  //   .DateFor        = ucDate.EditValue
  //   .ByDietConsist  = radByConsist.Checked
  //   .ByFloorUnit    = radFloor.Checked
  //   .PrimaryOnly    = chkPrimaryOnly.Checked
  //   .IsDietType     = False  (always — this generator is for
  //                     consistency, not diet type)
  //   report class    = DietOrderConsist (server pKey 54)
  //   detailedReportName = "Consistency - Census"
  //
  // Init (.vb:83-107):
  //   ucDate: no label, DateInit = today
  //   radByConsist default checked (Designer)
  //   radFloor.Text = "By Unit" when facility UseUnitMapping,
  //                   else "By Floor" (.vb:97-102)
  //   chkPrimaryOnly unchecked default (Designer) — label
  //                   "1st Consistency Only"
  //
  // Validate (.vb:350-353): always returns True — no validation.
  {
    slug: 'consistency-census',
    reportName: 'Consistency - Census',
    fKey_RS_Report: 54,
    apiEndpoint: 'DietOrderConsist',
    fields: [
      // Print scope radio — WinForms uses two mutually-exclusive
      // radios (radByConsist / radFloor). Value is 'consist' /
      // 'floor'; buildSettings converts to the twin booleans.
      // Second radio label swaps between "By Unit" and "By Floor"
      // based on the facility's UseUnitMapping setting — same as
      // WinForms .vb:97-102.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'consist',
        group: 'Print',
        options: () => {
          // Facility-scoped label resolved at render time via the
          // options-as-function pattern. Second radio's label
          // swaps between "By Unit" (UseUnitMapping) and
          // "By Floor" — WinForms .vb:97-102.
          const fs = useGlobalStore.getState().facilitySettings as { useUnitMapping?: boolean } | null;
          const useUnitMapping = Boolean(fs?.useUnitMapping);
          return [
            { value: 'consist', label: 'By Consistency' },
            { value: 'floor', label: useUnitMapping ? 'By Unit' : 'By Floor' },
          ];
        } },

      // Date group — single date, defaults to today (.vb:94).
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Options — "1st Consistency Only" (.Designer label for
      // chkPrimaryOnly). Unchecked default.
      { key: 'PrimaryOnly', label: '1st Consistency Only', type: 'checkbox',
        defaultValue: false, group: 'Options' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print settings block (.vb:284-291).
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      ByDietConsist: v.PrintMode === 'consist',
      ByFloorUnit: v.PrintMode === 'floor',
      PrimaryOnly: v.PrimaryOnly,
      IsDietType: false,
    }),
  },
  // ─── DIET TYPE — CENSUS (pKey 55, fKey 55) ─────────────────────
  // WinForms source: ucDietech_Report_Generator_DietType_Census.vb.
  // Print (.vb:285-300):
  //   .FKey_Facility  = _FKey_Facility
  //   .DateFor        = ucDate.EditValue
  //   .ByDietConsist  = radByDietOrder.Checked   (radio arm 1)
  //   .ByFloorUnit    = radFloor.Checked         (radio arm 2)
  //   .PrimaryOnly    = chkPrimaryOnly.Checked
  //   .IsDietType     = True   (this variant — vs pKey 54 which
  //                     sends False for the consistency variant)
  //   report class    = DietOrderDiet (server pKey 55)
  //   detailedReportName = "Diet Type - Census"
  //
  // Init (.vb:85-111):
  //   ucDate: no label, DateInit = today
  //   radByDietOrder default checked (Designer)
  //   radFloor.Text = "By Unit" when facility UseUnitMapping,
  //                   else "By Floor" (.vb:101-106)
  //   chkPrimaryOnly unchecked default — label "1st Diet Type Only"
  //
  // Validate: always returns True — no validation.
  {
    slug: 'diet-type-census',
    reportName: 'Diet Type - Census',
    fKey_RS_Report: 55,
    apiEndpoint: 'DietOrderDiet',
    fields: [
      // Print scope radio — mutually-exclusive radByDietOrder /
      // radFloor. Value 'diet' / 'floor'; buildSettings converts
      // to the twin booleans. Second label swaps between
      // "By Unit" and "By Floor" via UseUnitMapping.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'diet',
        group: 'Print',
        options: () => {
          const fs = useGlobalStore.getState().facilitySettings as { useUnitMapping?: boolean } | null;
          const useUnitMapping = Boolean(fs?.useUnitMapping);
          return [
            { value: 'diet', label: 'By Diet Order' },
            { value: 'floor', label: useUnitMapping ? 'By Unit' : 'By Floor' },
          ];
        } },

      // Date — single date, defaults to today (.vb:96).
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Options — "1st Diet Type Only" (.Designer label for
      // chkPrimaryOnly). Unchecked default.
      { key: 'PrimaryOnly', label: '1st Diet Type Only', type: 'checkbox',
        defaultValue: false, group: 'Options' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print settings block (.vb:288-295).
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      ByDietConsist: v.PrintMode === 'diet',
      ByFloorUnit: v.PrintMode === 'floor',
      PrimaryOnly: v.PrimaryOnly,
      IsDietType: true,
    }),
  },
  // ─── EHR — DIET ORDER CHANGES (pKey 118, fKey 118) ─────────────
  // WinForms source: ucDietech_Report_Generator_DietOrder_Changes.vb.
  // Print (.vb:265-276):
  //   .FKey_Facility = _FKey_Facility
  //   .PrintDate     = Now  (WinForms hard-codes current time —
  //                     .vb:270; form has no date picker)
  //   report class   = EhrDietOrderChanges (server pKey 118)
  //   detailedReportName = "{EHR_Title} - Integration - Diet Order - Changes"
  //                        (.vb:274 — passes facility EHR_Title
  //                        as prefix)
  //
  // Init: no field initialization beyond the title — form has no
  // configurable options. Same "no available options" pattern as
  // Prescriber.
  //
  // Validate: always returns True — no validation.
  {
    slug: 'ehr-diet-order-changes',
    // Banner reads e.g. "EHR Mike Integration - Diet Order -
    // Changes" — matches the sidebar category label "{EHR_Title}
    // Integration" so the card leaf-strip in AllReports removes
    // the category prefix and leaves just "Diet Order - Changes"
    // on the card.
    reportName: '{EHR_Title} Integration - Diet Order - Changes',
    description: 'no available options...press generate to continue',
    fKey_RS_Report: 118,
    apiEndpoint: 'EhrDietOrderChanges',
    fields: [],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print block (.vb:267-271). Only sends
    // PrintDate = Now; FKey_Facility added by GenericReportForm.
    buildSettings: () => ({
      PrintDate: new Date(),
    }),
  },
  // ─── EHR — MEAL PATTERN NOTES (pKey 140, fKey 140) ─────────────
  // WinForms source: ucDietech_Report_Generator_EHR_MealPattern_Notes.vb.
  // Print (.vb:217-233):
  //   .FKey_Facility = _FKey_Facility
  //   .DateStart     = ucDate.EditValue
  //   .DateEnd       = ucDeEnd.EditValue
  //   .NoteType      = radPrintAll/Complete/Uncomplete →
  //                    MealPatternNoteType enum (All=0, Complete=1,
  //                    Uncomplete=2 per MealPatternNotesSettings.vb).
  //   report class   = MealPatternNotes (server pKey 140 per
  //                    ReportDispatcher.cs:159)
  //   queue title    = "{MealPatternShortDescription} - Notes"
  //                    (.vb:231)
  //
  // Init (.vb:78-92):
  //   ucDate  (start): no label, DateMin = Now - 10 years,
  //                    DateInit = today - 7 days
  //   ucDeEnd:         DateMin = start, DateInit = today, label "To"
  //   radPrintAll:     Checked (default) — Designer .vb:314
  //
  // Validate (.vb:251-254): always returns True — no validation.
  {
    slug: 'meal-pattern-notes',
    // Banner reads e.g. "EHR Mike Integration - Meal Pattern -
    // Notes" — matches the sidebar category label
    // `{EHR_Title} Integration` so the card leaf-strip in
    // AllReports removes the category prefix and leaves just
    // "Meal Pattern - Notes" on the card (with facility
    // MealPatternShortDescription substituted).
    reportName: '{EHR_Title} Integration - {MealPatternShortDescription} - Notes',
    fKey_RS_Report: 140,
    apiEndpoint: 'MealPatternNotes',
    fields: [
      // Date Range — bidirectional invariant `DateEnd >= DateStart`.
      // Start default = today - 7 (.vb:82); End default = today
      // (.vb:86).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) {
            const d = new Date();
            d.setDate(d.getDate() - 7);
            return d;
          }
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      // Print (NoteType radio) — All default (Designer .vb:314).
      // Values map to `MealPatternNoteType` enum in
      // `MealPatternNotesSettings.vb`.
      { key: 'NoteType', label: '', type: 'radio', defaultValue: 0,
        group: 'Print',
        options: [
          { value: 0, label: 'All' },
          { value: 1, label: 'Completed' },
          { value: 2, label: 'Uncompleted' },
        ] },
    ],
    buildDateFor: (v) => formatDateRange(v.DateStart, v.DateEnd),
    // Mirrors WinForms Print settings block (.vb:220-228).
    buildSettings: (v) => ({
      DateStart: v.DateStart,
      DateEnd: v.DateEnd,
      NoteType: Number(v.NoteType),
    }),
  },

  // ─── PRODUCTION - TRAY TICKETS (pKey 4, fKey 4) ────────────────
  // WinForms source: ucDietech_Report_Generator_TrayTicket.vb.
  //   TabPageText = "Tray Tickets" (.vb:52).
  //
  // Facility-setting-driven defaults (Init .vb:112-356):
  //   TT_PrintOrder     → SortOrder + GroupOnName (.vb:310-329):
  //                       0/3 = Name,  1/4 = Room,  2/5 = TrayLine;
  //                       3/4/5 additionally set GroupOnName = true.
  //   TT_DefaultPrintOrder → Collate + StartNewPage (.vb:293-302):
  //                       1 → Collate=true, StartNewPage=false.
  //                       else → Collate=false, StartNewPage=!GroupOnName.
  //   TT_DefaultPrintType_Index → PrintType dropdown (.vb:339).
  //   TrayTicketPaperType (facility) + UseProduction → PaperType
  //                       dropdown index (.vb:261-289).
  //   UseProduction     → shows NumberOfDays field + swaps
  //                       PaperType option list to 6-entry.
  //
  // Wire payload → TrayTicketsSettings (see .vb:589-675):
  //   FKey_Facility (middleware), UseUnitMapping, StartDate,
  //   LstMealtimes (int[][] — one array per day),
  //   PrintOrder (PatientSortOrder: TrayLine=0/Room=1/Name=2),
  //   PrintWhat (ProductionGeneratorMenuType), PrintIndex,
  //   PaperType, PrintAll/SpecificSet/SpecificResident/SpecificFloorUnit,
  //   SetList/PatientList/LstPatientNames/LstFloorUnits/LstFloorUnitNames,
  //   PKey_Menu_Header/MenuStartDate/MenuDayToUse, GroupOnName,
  //   Collate, StartNewPage, StartNewPageAfterEachCart,
  //   Sort_ByName/ByRoomNumber/ByTrayLine/ByCart,
  //   PrintScheduled=false, CreateMenuComplianceReport
  //   (= PrintType idx 3), TrayTicketNoteDisplayText,
  //   PrinterName=null, ImageName, SelectiveRejectionPatientList=null.
  //
  // NOT PORTED (matching the WinForms non-batch / non-server-print
  // defaults so the wire payload still round-trips cleanly):
  //   - `SelectiveRejectionPatientList` (WinForms non-returned SM
  //     confirmation dialog `frmDietech_TT_SelectiveMenu_Notify`)
  //   - `PrinterName` (WinForms only for 1-part continuous paper)
  //   - `LstPatientNames` empty when >10 residents (WinForms cap at
  //     10 for the printed cover list; over that count sends empty)
  //   - `LstFloorUnitNames` — server can derive from LstFloorUnits.
  //
  // WinForms `reportPKey_Override = 136` for Specific Resident with
  // ≤10 rows (.vb:406-409) is applied via `preSubmit` below.
  {
    slug: 'tray-tickets',
    reportName: 'Tray Tickets',
    fKey_RS_Report: 4,
    tempMenuSupported: trayTicketsUseTempMenu,
    groupLayout: {
      Options: { density: 'compact', checkboxPad: '1px', gap: '2px 8px' },
      'Start New Page After': { density: 'compact', checkboxPad: '1px', gap: '2px 8px' },
    },
    // No side column — this report inlines all of its selectors.
    layout: { width: 'wide', mainWidth: '1020px' },
    fields: [
      // Facility tree — gates on the same PT_ShowMultiFacility + >1 user
      // facility check as every other report (TrayTicket.vb:152). Renders
      // nothing when off, and the pickers below stay single-facility.
      // `inline` because this report has NO side rail (layout has no sideWidth)
      // and inlines every selector — routing the tree to a side column it does
      // not have overflowed the fieldset.
      { key: 'LstFacilityMultiple', label: '', type: 'facilityTree', inline: true,
        defaultValue: [] as number[],
        // Hide the whole group when the facility has multi-facility reporting
        // off, so single-facility sites see no empty fieldset. The second half
        // of the WinForms gate (user has >1 facility) needs the facility list,
        // which only the field itself can see - it reports that case inline.
        visibleWhen: { check: () => Boolean(
          (useGlobalStore.getState().facilitySettings as { pT_ShowMultiFacility?: boolean } | null)?.pT_ShowMultiFacility,
        ) },
        group: '{Facility_Plural}' },

      // Date group — StartDate + NumberOfDays inline.
      // NumberOfDays is only meaningful when facility uses production
      // (.vb:202-206 hides the LayoutItem otherwise); we keep the
      // field visible but clamp max via WinForms MaxValue = 9.
      { key: 'StartDate', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275 },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 9, group: 'Date', width: 250 },

      // Menu Cycle info — auto-updates as StartDate changes.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — per-day picker. WinForms `ucMealltimesTab` is a
      // per-day tab strip with a checkbox list; the React field
      // type `mealtimesPerDay` mirrors that pattern. `watchGroup`
      // binds the tab count to `StartDate` + `NumberOfDays` so the
      // strip regenerates whenever the date range changes — same
      // wiring as Production Tally (pKey 3). `IncludeNourishments
      // = False` per .vb:230 — main meals only. Wire format:
      // `List<int[]>` with 0-based indices matching WinForms
      // `ucMealltimesTab.GetCheckedMealtimes`.
      { key: 'LstMealtimesPerDay', label: '', type: 'mealtimesPerDay',
        defaultValue: [[]] as number[][],
        group: 'Mealtimes',
        // WinForms .vb:261-263 sets IncludeNourishments = False and leaves
        // IncludeMainMealtimes True — main meals only. Built from settings so an
        // unnamed meal is dropped and multi-facility names merge.
        mealtimeOptions: { includeMain: true, includeNourishments: false },
        watchGroup: { start: 'StartDate', days: 'NumberOfDays' },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Order By radio — 4 options. Default derived from facility
      // `TT_PrintOrder` (.vb:310-329):
      //   0 → Name          GroupOnName=false
      //   1 → Room          GroupOnName=false
      //   2 → TrayLine      GroupOnName=false
      //   3 → Name          GroupOnName=true
      //   4 → Room          GroupOnName=true
      //   5 → TrayLine      GroupOnName=true
      // `radBySet` (Cart+Room) has no facility default — user picks
      // it manually.
      { key: 'SortOrder', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as { tT_PrintOrder?: number } | null)?.tT_PrintOrder ?? 2;
          if (raw === 0 || raw === 3) return 'name';
          if (raw === 1 || raw === 4) return 'room';
          if (raw === 2 || raw === 5) return 'trayLine';
          return 'trayLine';
        },
        group: 'Order By',
        options: [
          { value: 'name', label: 'Name' },
          { value: 'room', label: '{Room}' },
          { value: 'trayLine', label: 'Tray Line' },
          { value: 'cart', label: '{SetName}, Room' },
        ] },

      // Print radio — 4 options. Default 'all' (Designer
      // `radPrintAll.EditValue = True`). Screenshot label for cart
      // is just "{SetName}"; for unit uses `{FloorOrUnit}`; for
      // resident uses `{Resident}`.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: '{SetName}' },
          { value: 'unit', label: '{FloorOrUnit}' },
          { value: 'resident', label: '{Resident}' },
        ] },

      // Options group — Collate + Group on Name. Rendered on the
      // SAME row as the Start New Page After group below via a
      // shared `groupRow` key. Defaults derived from facility
      // `TT_DefaultPrintOrder` + `TT_PrintOrder`:
      //   TT_DefaultPrintOrder === 1 → Collate=true,  StartNewPage=false
      //   else                       → Collate=false, StartNewPage=!GroupOnName
      // GroupOnName default matches SortOrder default (3/4/5).
      //
      // Paper Type side-effects — WinForms `cmbPaperType_EditValueChanging`
      // (.vb:1718-1756), production facilities only (the whole handler sits
      // in `If _useProduction`; on a basic facility 0 / 1 are 3 / 4 Part):
      //   PaperType 0 (1 Part Continuous), 1 (1 Part 4×4), 5 (Raw Data)
      //     → force-uncheck + disable Collate AND StartNewPage
      //   PaperType 5 (Raw Data)
      //     → also force-uncheck + disable StartNewPageAfterEachCart AND GroupOnName
      //   Switching AWAY from 0/1/5 → reapply the facility defaults above
      // `recomputeWhen: ['PaperType']` re-invokes each field's
      // `defaultValue` on every PaperType change, and `enabledWhen`
      // renders the checkbox disabled (but still visible, matching
      // WinForms which greys out rather than hides).
      { key: 'Collate', label: 'Collate', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null, v?: Record<string, unknown>) => {
          if (isOnePartOrRawPaper(v?.PaperType, fs)) return false;
          return (fs as { tT_DefaultPrintOrder?: number } | null)?.tT_DefaultPrintOrder === 1;
        },
        recomputeWhen: ['PaperType'],
        enabledWhen: { check: (v) => !isOnePartOrRawPaper(v.PaperType) },
        disabledReason: 'Not available for this paper type. Label, Card and Raw Data tickets print one ticket per page.',
        group: 'Options', groupRow: 'opts-newpage' },
      { key: 'GroupOnName', label: 'Group on Name', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null, v?: Record<string, unknown>) => {
          if (isRawDataPaper(v?.PaperType, fs)) return false;
          const raw = (fs as { tT_PrintOrder?: number } | null)?.tT_PrintOrder ?? 2;
          return raw >= 3;
        },
        recomputeWhen: ['PaperType'],
        enabledWhen: { check: (v) => !isRawDataPaper(v.PaperType) },
        disabledReason: 'Not available for Raw Data tickets.',
        group: 'Options', groupRow: 'opts-newpage' },

      // Start New Page After column — two checkboxes, side-by-side
      // with the Options group above via the same `groupRow` key:
      //   Each Meal   → chkStartNewPage (StartNewPage)
      //   Each {Set}  → chkStartNewPageAfterCart (StartNewPageAfterEachCart)
      // WinForms .vb:141-147 sets the Cart label to `String.Format(
      // "Each {0}", _strSet)` with an optional " with Separator Page"
      // suffix when facility TT_UseSeparatorPage is on. Port uses
      // `{SetName}` token; separator-page suffix isn't rendered
      // (behavior applies only if useProduction + TT_UseSeparatorPage
      // — server still honors the flag via the wire value).
      { key: 'StartNewPage', label: 'Each Meal', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null, v?: Record<string, unknown>) => {
          if (isOnePartOrRawPaper(v?.PaperType, fs)) return false;
          const raw = (fs as { tT_DefaultPrintOrder?: number, tT_PrintOrder?: number } | null);
          if (raw?.tT_DefaultPrintOrder === 1) return false;
          const groupOnName = (raw?.tT_PrintOrder ?? 2) >= 3;
          return !groupOnName;
        },
        recomputeWhen: ['PaperType'],
        enabledWhen: { check: (v) => !isOnePartOrRawPaper(v.PaperType) },
        disabledReason: 'Not available for this paper type. Label, Card and Raw Data tickets print one ticket per page.',
        group: 'Start New Page After', groupRow: 'opts-newpage' },
      { key: 'StartNewPageAfterEachCart', label: 'Each {SetName}', type: 'checkbox',
        defaultValue: () => {
          // WinForms baseline default is unchecked; Raw Data forces
          // it off and disables the checkbox (.vb:1380-1381).
          return false;
        },
        recomputeWhen: ['PaperType'],
        enabledWhen: { check: (v) => !isRawDataPaper(v.PaperType) },
        disabledReason: 'Not available for Raw Data tickets.',
        group: 'Start New Page After', groupRow: 'opts-newpage' },

      // Print Type dropdown (cbePrint) — 4 options. Default index
      // from facility `TT_DefaultPrintType_Index` (.vb:339).
      //   Index 0 → PrintWhat=NormalDup(2), CreateMenuCompliance=false
      //   Index 1 → PrintWhat=Normal(4),    CreateMenuCompliance=false
      //   Index 2 → PrintWhat=Duplicate(3), CreateMenuCompliance=false
      //   Index 3 → PrintWhat=NormalDup(2), CreateMenuCompliance=true
      //   (WinForms .vb:625-632 + .vb:638 + .vb:652.)
      //
      // Diet-Lite gate — WinForms hides the entire `lciPrintNormalDup`
      // layout item when `PM_Allowed_Modules.Diet_Lite_Allowed = True`
      // (.vb:208-212). That flag is a legacy licensing bit set at
      // WinForms startup; it isn't surfaced to the API or facility
      // settings, so React always renders the full 4-option list.
      // Target user base for React is the non-Diet-Lite tier, which
      // matches this behavior. When Diet-Lite ever plumbs through,
      // wrap the field in `visibleWhen: { check: (v) => !isDietLite }`.
      { key: 'PrintType', label: '', type: 'dropdown',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const idx = (fs as { tT_DefaultPrintType_Index?: number } | null)?.tT_DefaultPrintType_Index;
          return typeof idx === 'number' && idx >= 0 && idx <= 3 ? idx : 0;
        },
        options: [
          { value: 0, label: 'Normal and Duplicate' },
          { value: 1, label: 'Normal Only' },
          { value: 2, label: 'Duplicate Only' },
          { value: 3, label: 'Menu Compliance' },
        ],
        group: 'Print Type', groupRow: 'print-paper', width: 250 },

      // Paper Type dropdown — options depend on facility
      // `useProduction`. Default derived from
      // `TrayTicketPaperType` (facility int) per WinForms
      // .vb:306-336. Production=6 entries, non-production=3 entries.
      { key: 'PaperType', label: '', type: 'dropdown',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as { trayTicketPaperType?: number, useProduction?: boolean } | null);
          const settingIdx = raw?.trayTicketPaperType ?? 0;
          const useProduction = Boolean(raw?.useProduction);
          if (useProduction) {
            // .vb:261-289 useProduction branch.
            switch (settingIdx) {
              case 0: return 3;      // 4 Part
              case 1: return 3;      // 6 Part → substitute 4 Part
              case 2: return 2;      // 3 Part
              case 3: return 0;      // 1 Part - Continuous
              case 4: return 1;      // 1 Part - 4x4
              case 5: return 4;      // 3 Part - Landscape
              default: return 2;
            }
          }
          // Non-production branch.
          switch (settingIdx) {
            case 0: return 1;        // 4 Part
            case 1: return 2;        // 6 Part
            case 2: return 0;        // 3 Part
            default: return 0;
          }
        },
        // The list depends on the facility (.vb:287-304): a production
        // facility's six choices, or "3 Part" / "4 Part" / "6 Part" as 0 / 1 /
        // 2 otherwise. The value sent is the list index either way, and the
        // non-production generator reads 0 / 1 / 2 as 3 / 4 / 6 tickets a page
        // (TrayTickets.vb:183-190) - a production index there prints nothing.
        options: () => (isProductionFacility() ? TRAY_TICKET_PAPER_PRODUCTION : TRAY_TICKET_PAPER_BASIC),
        group: 'Paper Type', groupRow: 'print-paper', width: 250 },

      // Note text — WinForms `ucTTNotes` is a `MRUEdit` fed by the
      // facility-scoped `tblFacility.TT_Notes` byte blob (see
      // `.vb:353`). Port uses a `SelectBox` with acceptCustomValue
      // so the user can pick a preset OR type a new note — same
      // free-text-plus-list semantics as MRUEdit. Wire value is
      // still just a string (`TrayTicketNoteDisplayText`).
      { key: 'Notes', label: 'Note:', type: 'trayTicketNote',
        defaultValue: '', group: 'Additional Options', newRow: true,
        width: 320 },

      // Image dropdown (`gluImages`) — WinForms populates from
      // embedded TT_*.png resources via `LoadImages()` (.vb:1143-1192).
      // React port serves the same file set from
      // `GET /api/TrayTicketImages/List/{FKey_Facility}`; the
      // selector groups by resource prefix and shows a thumbnail
      // column, and the wire value is the raw `ResourceName`
      // (e.g. `TT_Fruit_Apple`).
      //
      // Resident-picture auto-select — WinForms `SetResidentPicture`
      // (.vb:1388-1411) runs on Init AND on every PaperType change.
      // When `TT_ThreePart_ShowResidentImage && UseProduction`:
      //   PaperType 2 (3 Part) or 4 (3 Part Landscape)
      //     → force `ImageName = 'Resident_Image'`.
      //   Switching AWAY while `Resident_Image` is currently selected
      //     → clear (`ImageName = ''`).
      //   User-picked non-placeholder images survive any PaperType
      //   change per WinForms (only the Resident_Image sentinel
      //   round-trips through auto-clear).
      // `recomputeWhen: ['PaperType']` re-invokes on every change.
      { key: 'ImageName', label: 'Image:', type: 'trayTicketImage',
        defaultValue: (fs?: Record<string, unknown> | null, v?: Record<string, unknown>) => {
          const raw = fs as { tT_ThreePart_ShowResidentImage?: boolean, useProduction?: boolean } | null;
          const threePartResidentPhoto = Boolean(raw?.tT_ThreePart_ShowResidentImage && raw?.useProduction);
          if (!threePartResidentPhoto) return String(v?.ImageName ?? '');
          const pt = v?.PaperType;
          const current = String(v?.ImageName ?? '');
          if (pt === 2 || pt === 4) return 'Resident_Image';
          if (current === 'Resident_Image') return '';
          return current;
        },
        recomputeWhen: ['PaperType'],
        group: 'Additional Options', newRow: true,
        width: 320 },

      // ── Inline selectors — one visible per PrintMode ─────────
      // `inline: true` opts each selector into the main column
      // instead of the right-hand side rail. The patient grid is
      // too wide to sit in the side rail (up to ~900px of column
      // width when Loc columns show), so all three inline for a
      // consistent layout that flows below the Additional Options
      // group.
      // All three pickers go tabbed-per-facility in multi-facility mode -
      // ucTabbedClbSets / ucTabbedUnits / ucTabbedResGrid (TrayTicket.vb:272-280).
      { key: 'SetList', label: '', type: 'setSelect', inline: true, multiFacility: true,
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'set' } },
      // `defaultSelectAll` — Tray Tickets hosts the same
      // ucDietech_Report_Generator_ClbFloorUnit as Nourishments, and its
      // Fill_clbFloorUnits ends with `.CheckAll()` (.vb:97), so every unit
      // starts checked. Also drives eagerMountAll in multi-facility mode so an
      // unvisited facility tab is still populated.
      { key: 'UnitList', label: '', type: 'floorUnitSelect', inline: true, multiFacility: true,
        defaultValue: [], defaultSelectAll: true,
        visibleWhen: { key: 'PrintMode', value: 'unit' } },
      // Patient grid columns — WinForms Designer + `Fill_dtPatientGrid`
      // at .vb:1010-1122 produces this column set:
      //   ☑ | Name | Diet Order | TF | Unit/Floor | Room |
      //   Loc B | Loc L | Loc S    ← only when TT_ShowLocationColumns
      //
      // - `showInfoColumns: true`   → Name / Diet Order / Unit / Room
      //   (TF stays in the group per WinForms .vb:1103-1104 which
      //   forces `colTF_Tube_Feed.Visible = True`; DO NOT hide it).
      // - `showLocationColumnsFromFacility: 'tT_ShowLocationColumns'`
      //   toggles the 3 Loc columns per WinForms .vb:165-183. Column
      //   captions use the facility Breakfast / Lunch / Dinner
      //   first-char abbreviations (screenshot shows "Loc S" for a
      //   facility whose Dinner is labeled "SupperM").
      // - Unit-vs-Floor caption comes from ResidentSelector's
      //   `roomLabel` / `useUnitMapping` handling (matches WinForms
      //   `.vb:331-337`).
      { key: 'PatientList', label: '', type: 'residentSelect', inline: true, multiFacility: true,
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'resident' },
        showInfoColumns: true,
        showLocationColumnsFromFacility: 'tT_ShowLocationColumns',
        // WinForms `Fill_dtPatientGrid` (.vb:1010-1122) includes
        // guests whose recurrence intersects the report's date
        // range. Swap the data source to the range-aware endpoint
        // so guests appear in the picker and sort directly under
        // their parents.
        trayTicketEligible: {
          startDateField: 'StartDate',
          numberOfDaysField: 'NumberOfDays',
        } },
    ],
    buildDateFor: (v) => {
      const days = Number(v.NumberOfDays ?? 1);
      if (days <= 1) return formatDate(v.StartDate);
      const start = v.StartDate instanceof Date
        ? new Date(v.StartDate.getFullYear(), v.StartDate.getMonth(), v.StartDate.getDate())
        : (typeof v.StartDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v.StartDate))
          ? (() => { const m = (v.StartDate as string).match(/^(\d{4})-(\d{2})-(\d{2})/)!; return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); })()
          : new Date();
      const end = new Date(start); end.setDate(end.getDate() + days - 1);
      return formatDateRange(start, end);
    },
    // WinForms `reportPKey_Override = 136` applied when Specific
    // Resident is picked AND ≤10 rows are selected (.vb:406-409).
    // Also runs three client-side patient filters (Floor/Unit
    // resolution, Special Needs "Do Not Print TT", Empty diet
    // order) mirroring `.vb:770-882` - per facility in multi-
    // facility mode (`.vb:486-640`) - and fires the Non-Returned
    // Selective Menu confirmation dialog when facility warnings
    // are on (.vb:884-899; per checked facility, inside that loop,
    // .vb:600-636). Cancel from the dialog aborts
    // submission with a silent `{ error: '' }` (no toast).
    preSubmit: async (rawValues, ctx) => {
      const v = collapseSingleCheckedFacility(rawValues);
      const extras: Record<string, unknown> = {};

      const printMode = String(v.PrintMode ?? 'all');

      // In multi-facility mode PatientList is a Record<facilityId, number[]>,
      // not a flat array (see ReportFieldConfig.multiFacility). Flatten to the
      // union so the count-driven logic below still works; the authoritative
      // per-facility lists ride on `lstResidents_Multiple` from buildSettings.
      const isMultiFacility = ((v.LstFacilityMultiple as number[] | undefined) ?? []).length > 0;
      const rawPatientList = v.PatientList;
      const pickedPatients: number[] = isMultiFacility
        ? Object.values((rawPatientList ?? {}) as Record<number, number[]>).flat()
        : ((rawPatientList as number[] | undefined) ?? []);

      // ── Specific-Resident PKey override ────────────────────────
      if (printMode === 'resident' && pickedPatients.length > 0 && pickedPatients.length <= 10) {
        extras.reportPKey_Override = 136;
      }

      // ── Facility settings snapshot ─────────────────────────────
      const fs = ctx.facilitySettings as {
        useProduction?: boolean;
        useUnitMapping?: boolean;
        tT_SelectiveMenu_Static_Warning?: boolean;
        tT_SelectiveMenu_Normal_Warning?: boolean;
        tT_DoNotPrintEmptyDiets?: boolean;
        resident_Plural?: string;
      } | null;
      const useUnitMapping = Boolean(fs?.useUnitMapping);
      const doNotPrintEmpty = Boolean(fs?.tT_DoNotPrintEmptyDiets);
      const residentPlural = String(fs?.resident_Plural ?? 'residents').toLowerCase();

      // ── Client-side patient filters (.vb:770-882) ──────────────
      // Floor/Unit resolution, Special Needs "Do Not Print TT" and
      // empty diet orders - see trayTicketPatients.ts. Each step can
      // abort with a user-facing error when nothing is left.
      //
      // Patient pool matches the picker grid — range-aware, guest-
      // inclusive when the tray-ticket eligible endpoint is
      // available (`fetchTrayTicketPatientPool`). Falls back to the
      // guest-excluding `activePatients` cache when the fetcher
      // isn't wired.
      let startIsoForPool: string | null = null;
      const rawStart = v.StartDate;
      if (rawStart instanceof Date) {
        startIsoForPool = `${rawStart.getFullYear()}-${String(rawStart.getMonth() + 1).padStart(2, '0')}-${String(rawStart.getDate()).padStart(2, '0')}`;
      } else if (typeof rawStart === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawStart)) {
        startIsoForPool = rawStart.substring(0, 10);
      }
      const numberOfDaysForPool = Number(v.NumberOfDays ?? 1);
      let tempPatientList: number[] | null = null;

      // The Non-Returned Selective Menu check (DisplaySelectiveMenuNotifyForm)
      // takes the union of every day's checked mealtimes
      // (`ucMealltimesTab.GetUniqueCheckedMealtimes`).
      const uniqueMealtimes = Array.from(
        new Set(((v.LstMealtimesPerDay as number[][] | undefined) ?? []).flat()),
      ).sort((a, b) => a - b);

      if (!isMultiFacility) {
        const activePatients = ctx.fetchTrayTicketPatientPool
          ? await ctx.fetchTrayTicketPatientPool(startIsoForPool, numberOfDaysForPool)
          : (ctx.activePatients ?? []);
        const unitList = (v.UnitList as number[] | undefined) ?? [];
        if (printMode === 'unit' && unitList.length === 0) {
          return { error: 'Please select a floor/unit.' };
        }
        const filtered = filterTrayTicketPatients({
          printMode,
          pool: activePatients,
          unitSelection: unitList,
          pickedPatients,
          useUnitMapping,
          units: ctx.units ?? [],
          doNotPrintIds: ctx.fetchDoNotPrintTTPatientIds ? await ctx.fetchDoNotPrintTTPatientIds() : [],
          doNotPrintEmptyDiets: doNotPrintEmpty,
          residentPlural,
        });
        if (filtered.error !== undefined) return { error: filtered.error };
        tempPatientList = filtered.patients;

        // If any filter step built or trimmed a list, override the
        // wire value from buildSettings (which uses only the
        // PrintMode-picked list). Otherwise leave it alone so
        // PrintAll / SpecificSet still send `PatientList: null`
        // (server enumerates unfiltered).
        if (tempPatientList !== null) {
          extras.PatientList = tempPatientList;
          // Keep LstPatientNames in sync when SpecificResident's
          // picked list shrunk after filters — the printed
          // "residents included" cover list would otherwise show
          // names for PKeys we just dropped. Only the SpecificResident
          // ≤10-name path is affected; Unit / All modes send null names.
          if (
            printMode === 'resident'
            && tempPatientList.length > 0
            && tempPatientList.length <= 10
            && ctx.resolvePatientFullName
          ) {
            const rp = ctx.resolvePatientFullName;
            extras.LstPatientNames = tempPatientList.map((pk) => rp(pk));
          } else if (printMode !== 'resident' || tempPatientList.length > 10) {
            // If PrintAll / SpecificSet / >10 residents, WinForms
            // sends null (server prints an "N residents" summary
            // instead — .vb:200-201).
            extras.LstPatientNames = null;
          }
        }
      } else {
        // Multi-facility (.vb:486-640): the same three steps per checked
        // facility, each against ITS residents, units and Special Needs.
        // WinForms always sends one lstResidents_Multiple entry per
        // facility, Nothing where no step built a list (.vb:638, :671) -
        // the generator reads Nothing as "everyone" (TrayTickets.vb:383).
        // Unit mode also sends the per-facility unit names the queue
        // description lists (.vb:666; TrayTicketsSettings.vb:258-266
        // walks them and fails on null).
        const checkedIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
        const unitMap = (v.UnitList ?? {}) as Record<number, number[]>;
        const patientMap = (v.PatientList ?? {}) as Record<number, number[]>;
        const facilityNames = new Map<number, string>(
          (ctx.queryClient?.getQueryData<Array<{ id: number; name: string }>>(
            ['userFacilities', ctx.facilityId, ctx.userId],
          ) ?? []).map((f) => [f.id, f.name]),
        );
        const setMap = (v.SetList ?? {}) as Record<number, number[]>;
        const residentsMultiple: Array<number[] | null> = [];
        const unitNamesMultiple: string[][] = [];
        const rejectionsMultiple: Array<Array<{ PKey_Patient: number; Mealtime: number; MenuDate: string }> | null> = [];
        for (const fid of checkedIds) {
          const unitSelection = unitMap[fid] ?? [];
          const setup = printMode === 'unit'
            ? (fid === ctx.facilityId || !ctx.fetchFloorUnitSetup
              ? { useUnitMapping, units: ctx.units ?? [] }
              : await ctx.fetchFloorUnitSetup(fid))
            : { useUnitMapping: false, units: [] };
          const pool = ctx.fetchTrayTicketPatientPool
            ? await ctx.fetchTrayTicketPatientPool(startIsoForPool, numberOfDaysForPool, fid)
            : (fid === ctx.facilityId ? (ctx.activePatients ?? []) : []);
          const filtered = filterTrayTicketPatients({
            printMode,
            pool,
            unitSelection,
            pickedPatients: patientMap[fid] ?? [],
            useUnitMapping: setup.useUnitMapping,
            units: setup.units,
            doNotPrintIds: ctx.fetchDoNotPrintTTPatientIds ? await ctx.fetchDoNotPrintTTPatientIds(fid) : [],
            doNotPrintEmptyDiets: doNotPrintEmpty,
            residentPlural,
            facilityName: facilityNames.get(fid) ?? '',
          });
          if (filtered.error !== undefined) return { error: filtered.error };

          // Non-Returned Selective Menus, for THIS facility, before the next
          // facility is resolved (.vb:600-636). The check is on when this
          // facility warns on static or normal selective menus; it gets this
          // facility's sets (Specific Set) and the residents just resolved, and
          // Cancel stops the whole print. Each facility's rejections ride on
          // lstSelectiveRejectionPatientList at its index, which is how the
          // generator reads them (TrayTickets.vb:435).
          const fsFor = (fid === ctx.facilityId
            ? fs
            : (ctx.fetchFacilitySettingsFor ? await ctx.fetchFacilitySettingsFor(fid) : null)) as {
            tT_SelectiveMenu_Static_Warning?: boolean;
            tT_SelectiveMenu_Normal_Warning?: boolean;
          } | null;
          if (
            (fsFor?.tT_SelectiveMenu_Static_Warning || fsFor?.tT_SelectiveMenu_Normal_Warning)
            && startIsoForPool && uniqueMealtimes.length > 0
          ) {
            const result = await ctx.showSelectiveMenuNonReturnedDialog({
              startDate: startIsoForPool,
              numberOfDays: numberOfDaysForPool,
              pKey_Menu_Header: 0, // the server resolves each facility's own cycle
              uniqueMealtimes,
              setList: printMode === 'set' ? (setMap[fid] ?? []) : null,
              patientList: filtered.patients,
              facilityId: fid,
            });
            if (result === 'cancel') return { error: '' };
            rejectionsMultiple.push(result);
          } else {
            rejectionsMultiple.push(null);
          }

          residentsMultiple.push(filtered.patients);
          if (printMode === 'unit') {
            unitNamesMultiple.push(floorUnitNames(unitSelection, setup.useUnitMapping, setup.units));
          }
        }
        extras.lstResidents_Multiple = residentsMultiple;
        if (printMode === 'unit') extras.lstUnitNames_Multiple = unitNamesMultiple;
        extras.lstSelectiveRejectionPatientList = rejectionsMultiple;
        return extras;
      }

      // ── Selective-Menu Non-Returned dialog (single facility) ───
      const gateOn = Boolean(
        fs?.useProduction
        && (fs?.tT_SelectiveMenu_Static_Warning || fs?.tT_SelectiveMenu_Normal_Warning),
      );
      if (gateOn) {
        // Build StartDate ISO (yyyy-MM-dd) — same helper as buildSettings
        let startIso: string | null = null;
        const d = v.StartDate;
        if (d instanceof Date) {
          startIso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        } else if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
          startIso = d.substring(0, 10);
        }

        const numberOfDays = Number(v.NumberOfDays ?? 1);
        const pKeyMenuHeader = 0; // server recomputes; we can't call ctx here

        // Per-print-mode filters — mirrors WinForms passing setList
        // + tempPatientList into DisplaySelectiveMenuNotifyForm
        // (`.vb:885`). WinForms uses the POST-filter list, so if
        // Floor/Unit / SN / Empty-diet trimmed the pool we pass the
        // trimmed set — otherwise null so the server enumerates.
        const setList = printMode !== 'set'
          ? null
          : ((v.SetList as number[] | undefined) ?? null);
        const patientList = tempPatientList
          ?? (printMode === 'resident' ? pickedPatients : null);

        if (startIso && uniqueMealtimes.length > 0) {
          const result = await ctx.showSelectiveMenuNonReturnedDialog({
            startDate: startIso,
            numberOfDays,
            pKey_Menu_Header: pKeyMenuHeader,
            uniqueMealtimes,
            setList,
            patientList,
          });
          if (result === 'cancel') return { error: '' };
          if (result.length > 0) {
            extras.SelectiveRejectionPatientList = result;
          }
        }
      }

      return extras;
    },
    buildSettings: (rawValues, fs, ctx) => {
      // One checked facility (the current one) prints as a single facility,
      // as WinForms does (see collapseSingleCheckedFacility).
      const v = collapseSingleCheckedFacility(rawValues);
      // ── Date/menu-cycle resolution ─────────────────────────────
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const menuDayToUse = ctx?.resolveMenuDayToUse?.(startIso) ?? -1;
      // WinForms `.MenuStartDate = ucMenuCycleInfo.MenuCycleInUseInfo.Start_Date`
      // (.vb:634) — the cycle's own start, NOT the report StartDate.
      // Server DTO (`TrayTicketsSettings.MenuStartDate`) is a
      // non-nullable `DateTime`, so a JSON `null` fails Newtonsoft
      // deserialization at `modReportSettingsFactory.vb:35`. When the
      // resolver returns null (menu-cycle cache not yet warm), fall
      // back to the report StartDate — in the WinForms happy path
      // these dates are the same or adjacent, and the server-side
      // report engine re-resolves the cycle from
      // `PKey_Menu_Header + MenuDayToUse` regardless.
      const menuStartIsoRaw = ctx?.resolveMenuStartDate?.(startIso) ?? startIso;
      const menuStartDateIso = menuStartIsoRaw ? `${menuStartIsoRaw}T00:00:00` : null;

      // ── Per-day mealtimes (List<int[]>) ────────────────────────
      const numberOfDays = Number(v.NumberOfDays ?? 1);
      const mealtimesRaw = (v.LstMealtimesPerDay as number[][] | undefined) ?? [];
      const lstMealtimes: number[][] = Array.from(
        { length: numberOfDays },
        (_, i) => mealtimesRaw[i] ?? [],
      );

      const sortOrder = String(v.SortOrder ?? 'trayLine');
      const printMode = String(v.PrintMode ?? 'all');
      const printIndex = Number(v.PrintType ?? 0);

      // WinForms PrintOrder mapping — .vb:613-622. `radBySet` (cart)
      // does NOT assign PrintOrder, so the DTO keeps its default
      // enum value 0 (TrayLine); the Sort_ByCart flag carries the
      // cart-sort intent to the generator.
      const printOrder =
        sortOrder === 'trayLine' ? 0 :
        sortOrder === 'room' ? 1 :
        sortOrder === 'name' ? 2 :
        0;

      // cbePrint index → PrintWhat + CreateMenuComplianceReport.
      // WinForms .vb:625-638 — cases 0 AND 3 both send
      // TrayTicketNormalDuplicate; case 1 → Normal; case 2 →
      // Duplicate. CreateMenuComplianceReport is TRUE only for
      // index 3.
      const wirePrintWhat =
        printIndex === 0 || printIndex === 3 ? 2 :
        printIndex === 1 ? 4 :
        printIndex === 2 ? 3 : 2;
      const createMenuComplianceReport = printIndex === 3;

      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );

      // ── PrintMode-scoped lists ─────────────────────────────────
      // WinForms `.vb:594-608` — SetList / LstFloorUnits /
      // PatientList / LstPatientNames are assigned ONLY when their
      // corresponding radio is checked; otherwise the properties
      // stay null. Any residual value from a previous PrintMode
      // pick must NOT bleed through.
      // ── Multi-facility ─────────────────────────────────────────
      // `facilityTree` leaves this empty unless PT_ShowMultiFacility is on and
      // the user has >1 facility, so empty IS the single-facility path. In
      // multi-facility mode SetList / UnitList / PatientList hold a per-facility
      // map rather than a flat array (ReportFieldConfig.multiFacility), and the
      // positional `*_Multiple` arrays carry the selection instead.
      const checkedFacilityIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
      const isMultiFacility = checkedFacilityIds.length > 0;
      const mfArg = isMultiFacility ? checkedFacilityIds : undefined;
      const mfNull = { ids: null, names: null, selections: null };
      const mfSets = ctx?.resolveMultiFacility?.(mfArg, v.SetList as Record<string, unknown> | undefined) ?? mfNull;
      const mfUnits = ctx?.resolveMultiFacility?.(mfArg, v.UnitList as Record<string, unknown> | undefined) ?? mfNull;
      const mfResidents = ctx?.resolveMultiFacility?.(mfArg, v.PatientList as Record<string, unknown> | undefined) ?? mfNull;

      const multi = {
        lstFKey_Facility_Multiple: mfSets.ids,
        lstFacilityNames_Multiple: mfSets.names,
        // Each list is filled only under its own PrintMode — TrayTicket.vb:486-505.
        lstSets_Multiple: isMultiFacility && printMode === 'set' ? mfSets.selections : null,
        lstUnits_Multiple: isMultiFacility && printMode === 'unit' ? mfUnits.selections : null,
        // preSubmit replaces this with the per-facility lists WinForms sends
        // (one per facility, after the floor/unit, Special Needs and empty-diet
        // steps - TrayTicket.vb:486-640); this is the resident picks alone.
        lstResidents_Multiple: isMultiFacility && printMode === 'resident' ? mfResidents.selections : null,
        // Per-facility unit DISPLAY names (.vb:666). Each facility's names come
        // from its own unit rows, which preSubmit loads; it fills this in.
        lstUnitNames_Multiple: null as unknown,
      };

      const setListPicked = isMultiFacility ? [] : ((v.SetList as number[] | undefined) ?? []);
      const unitListPicked = isMultiFacility ? [] : ((v.UnitList as number[] | undefined) ?? []);
      const patientListPicked = isMultiFacility ? [] : ((v.PatientList as number[] | undefined) ?? []);

      const setList = printMode === 'set' && setListPicked.length > 0
        ? setListPicked : null;
      const lstFloorUnits = printMode === 'unit' && unitListPicked.length > 0
        ? unitListPicked : null;
      const patientList = printMode === 'resident' && patientListPicked.length > 0
        ? patientListPicked : null;

      // LstPatientNames — WinForms caps at 10 (.vb:447); above that
      // it sends an empty list (the report generator then prints
      // the "N residents selected" summary instead). Only populated
      // when SpecificResident is active.
      const lstPatientNames = printMode === 'resident'
        && patientList != null
        && patientList.length > 0
        && patientList.length <= 10
          ? patientList.map((pk) => ctx?.resolvePatientFullName?.(pk) ?? '')
          : null;

      // LstFloorUnitNames — WinForms `.vb:485-491`. Names come from
      // `dtUnitMapping.Rows.Find(pkey)("Unit")` when UseUnitMapping
      // is on, else the raw floor number as string. Only populated
      // when SpecificFloorUnit is active.
      const lstFloorUnitNames = printMode === 'unit' && lstFloorUnits != null
        ? ctx?.resolveFloorUnitNames?.(lstFloorUnits) ?? []
        : null;

      // ── StartNewPageAfterEachCart parity ───────────────────────
      // WinForms `.vb:613-622` — StartNewPageAfterEachCart is
      // assigned ONLY inside the radByTrayLine and radBySet
      // branches. For Name/Room it stays at the class default
      // (false), regardless of what the checkbox holds. Match that.
      const isCartRelevantSort = sortOrder === 'trayLine' || sortOrder === 'cart';
      const startNewPageAfterEachCart = isCartRelevantSort
        ? Boolean(v.StartNewPageAfterEachCart) : false;

      // ── ImageName wire value ───────────────────────────────────
      // WinForms `.vb:657-659` — only assigns ImageName when a value
      // was picked; otherwise `.ImageName` stays at its DTO default
      // (empty string).
      const imageName = String(v.ImageName ?? '');

      return {
        UseUnitMapping: useUnitMapping,
        StartDate: startIso ? `${startIso}T00:00:00` : null,
        LstMealtimes: lstMealtimes,
        PrintOrder: printOrder,
        PrintWhat: wirePrintWhat,
        PrintIndex: printIndex,
        PaperType: Number(v.PaperType ?? 0),
        PrintAll: printMode === 'all',
        SpecificSet: printMode === 'set',
        SetList: setList,
        SpecificResident: printMode === 'resident',
        PatientList: patientList,
        LstPatientNames: lstPatientNames,
        SpecificFloorUnit: printMode === 'unit',
        LstFloorUnits: lstFloorUnits,
        LstFloorUnitNames: lstFloorUnitNames,
        PKey_Menu_Header: pKeyMenuHeader,
        MenuStartDate: menuStartDateIso,
        MenuDayToUse: menuDayToUse,
        GroupOnName: Boolean(v.GroupOnName),
        Collate: Boolean(v.Collate),
        StartNewPage: Boolean(v.StartNewPage),
        StartNewPageAfterEachCart: startNewPageAfterEachCart,
        PrintScheduled: false,
        CreateMenuComplianceReport: createMenuComplianceReport,
        Sort_ByTrayLine: sortOrder === 'trayLine',
        Sort_ByRoomNumber: sortOrder === 'room',
        Sort_ByName: sortOrder === 'name',
        Sort_ByCart: sortOrder === 'cart',
        // `SelectiveRejectionPatientList` — preSubmit overrides
        // this with the picker rejections when the dialog fires;
        // otherwise stays null (WinForms leaves it null too).
        SelectiveRejectionPatientList: null,
        TrayTicketNoteDisplayText: String(v.Notes ?? ''),
        // `PrinterName` — WinForms only populates for 1-part
        // continuous production (.vb:421-427). We don't spawn a
        // print dialog client-side, so this stays null and the
        // server uses its default printer for that paper type.
        PrinterName: null,
        ImageName: imageName,
        ...multi,
      };
    },
    // Mirrors WinForms `ValidateForm` (.vb:1302-1345).
    validate: (v) => {
      // .vb:1315-1323 — every per-day mealtime array must be
      // non-empty.
      const numberOfDays = Number(v.NumberOfDays ?? 1);
      const mealtimes = (v.LstMealtimesPerDay as number[][] | undefined) ?? [];
      for (let i = 0; i < numberOfDays; i++) {
        if ((mealtimes[i]?.length ?? 0) === 0) return 'Please select a mealtime.';
      }
      // Multi-facility: GetEmptySelectionCount parity — every checked facility
      // needs its own selection (TrayTicket.vb mirrors ProductionTally.vb:1682).
      const checkedFacilityIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
      if (checkedFacilityIds.length > 0) {
        const emptyFor = (raw: unknown) => {
          const map = (raw ?? {}) as Record<number, number[]>;
          return checkedFacilityIds.filter((id) => (map[id] ?? []).length === 0).length;
        };
        if (v.PrintMode === 'set' && emptyFor(v.SetList) > 0) {
          return 'One or more {Facility_Plural} do not have any {SetName_Plural} selected.  Either select at least one {SetName} or remove {Facility} from selected list.';
        }
        if (v.PrintMode === 'resident' && emptyFor(v.PatientList) > 0) {
          return 'One or more {Facility_Plural} do not have any {Resident_Plural} selected.  Either select at least one {Resident} or remove {Facility} from selected list.';
        }
        if (v.PrintMode === 'unit' && emptyFor(v.UnitList) > 0) {
          return 'One or more {Facility_Plural} do not have any {FloorOrUnit} selected.  Either select at least one {FloorOrUnit} or remove {Facility} from selected list.';
        }
        return null;
      }
      if (v.PrintMode === 'set') {
        const sets = (v.SetList as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select a {SetName}.';
      }
      if (v.PrintMode === 'resident') {
        const pts = (v.PatientList as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select a {Resident}.';
      }
      if (v.PrintMode === 'unit') {
        const units = (v.UnitList as number[] | undefined) ?? [];
        if (units.length === 0) return 'Please select a {FloorOrUnit}.';
      }
      return null;
    },
  },
  // ─── PRODUCTION - TRAY TICKETS - NOTES (pKey 46) ───────────────
  // WinForms source: ucDietech_Report_Generator_TrayTicketNotes.vb.
  //   TabPageText = "Tray Ticket Notes" (.vb:57).
  //   Init (.vb:79-101):
  //     _useUnitMapping = g_facilitySettings.UseUnitMapping(FKey_Facility)
  //     radByFloor.Text = if _useUnitMapping then "By Unit" else "By Floor"
  //     radByResident.Text = "By " + Resident label
  //   Designer defaults (.Designer.vb:155):
  //     radByFloor.EditValue = True  → default checked
  //     radByResident        = False
  //     chkDietOrder         = False
  //   Print (.vb:277-291) → TrayTicketNotesSettings:
  //     .FKey_Facility     (middleware)
  //     .DateFor         = Now
  //     .OrderByFloor    = radByFloor.Checked
  //     .OrderByResident = radByResident.Checked
  //     .PrintDietOrder  = chkDietOrder.Checked
  //   ValidateForm always returns True (.vb:428-431); no client-
  //   side validation required.
  // ── Past Tray Tickets (RS 150) ───────────────────────────────────
  // Reprints a tray ticket exactly as it printed, from the snapshot saved on
  // its census row (src/docs/migrations/PastTrayTickets-migration.md).
  //
  // Deliberately far smaller than the Tray Tickets config above. Everything
  // that one asks — sets, units, sort order, collating, paper type, number of
  // days — was already decided when the ticket printed and is recorded in its
  // own snapshot. Asking again would imply a reprint could change it, and
  // PastTrayTicket ignores all of it: it groups what it finds by the paper
  // type and mealtime each ticket recorded and renders each group that way.
  //
  // Field keys match PastTrayTicketSettings, which the generator deserializes.
  {
    slug: 'past-tray-tickets',
    reportName: 'Past Tray Tickets',
    fKey_RS_Report: 150,
    // Wide, like the live Tray Tickets form (:2592). The resident grid's Name
    // column takes the leftover width, and a past report's names carry a
    // "(Deleted)" or "(Discharged)" suffix that the default column cannot hold.
    layout: { width: 'wide', mainWidth: '1140px' },
    fields: [
      // One date, not a range: a saved ticket belongs to the day it printed
      // for, and the census row is keyed on it.
      { key: 'StartDate', label: 'Date Printed For', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275,
        // Clamped to the days that actually have saved snapshots, with the note
        // under it saying so. Without this the operator can pick any day and only
        // finds out there was nothing to reprint after the report comes back empty.
        snapshotDateBounds: { kind: 'trayTicket' } },

      // The three main mealtimes only - no nourishments (Mike, 2026-10-27).
      // Nourishment passes are reprinted by Past Nourishment Labels (RS 151),
      // which reads the nourishment census rows; offering 3-8 here would point
      // at the tray ticket rows for those meals, which is a different document.
      //
      // The list still comes from the facility's own names via mealtimeOptions,
      // so a facility that renames or does not use a main meal gets the right
      // text; `options` is only the fallback before settings load.
      { key: 'Mealtimes', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        mealtimeOptions: { includeMain: true, includeNourishments: false },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // The resident grid, the same control Tray Tickets uses. Empty means
      // every resident who has a saved ticket for the date and mealtimes -
      // PastTrayTicket treats an empty list that way, so there is no All /
      // Specific radio to carry.
      { key: 'PatientList', label: '', type: 'residentSelect', inline: true,
        defaultValue: [], group: 'Residents',
        showInfoColumns: true,
        showLocationColumnsFromFacility: 'tT_ShowLocationColumns',
        // The list comes from saved census rows, so months on it still includes
        // residents who have been discharged, deleted, or purged outright.
        showResidentStatus: true,
        pruneSelectionToRoster: true,
        // Only residents who actually have a saved ticket for the chosen date
        // and mealtimes, not every resident in the facility. The selector
        // re-queries whenever either field changes, the way trayTicketEligible
        // swaps Tray Tickets' source for its date range.
        pastTrayTicketEligible: {
          dateField: 'StartDate',
          mealtimesField: 'Mealtimes',
        } },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    buildSettings: (v) => {
      // Midnight appended to the picked date, NOT `new Date(iso)`. A bare
      // "2026-10-26" parses as UTC midnight, so in any negative offset it
      // renders back as the 25th — the census lookup then asks for the wrong
      // day and finds nothing. Same form as Tray Tickets' own StartDate.
      const startIso = typeof v.StartDate === 'string' ? v.StartDate.substring(0, 10) : null;

      return {
        // Property names match PastTrayTicketSettings. Newtonsoft is
        // case-insensitive but the wire mirrors the class.
        StartDate: startIso ? `${startIso}T00:00:00` : null,
        Mealtimes: (v.Mealtimes ?? []) as number[],
        PatientList: (v.PatientList ?? []) as number[],
        StampAsReprint: true,
      };
    },
  },

  // ── Past Nourishment Labels (RS 151) ─────────────────────────────
  // Reprints nourishment labels exactly as they printed, from the snapshots
  // saved on their census rows (src/docs/migrations/PastTrayTickets-migration.md,
  // Nourishment Labels section).
  //
  // Same shape as Past Tray Tickets above, and for the same reason: the label
  // type, labels-to-skip, sort order, floor/unit filtering and multi-facility
  // selection the live Nourishments report asks for were all decided when the
  // label printed and are recorded in its own snapshot. PastNourishmentLabels
  // groups what it finds by the label type each snapshot recorded and renders
  // each group through that layout.
  //
  // Field keys match PastNourishmentLabelSettings, which the generator
  // deserializes.
  {
    slug: 'past-nourishment-labels',
    // Matches tblRS_Report RS 151, which PM_Server structure update 194 writes as
    // 'Past Nourishments' (sql/migrations/add-past-tray-ticket-snapshot.sql). The name
    // goes on the queued document, so the web queue and the desktop dashboard have to
    // agree on it. "Past Nourishment Labels" reads more precisely - this reprints labels,
    // not the Summary or List reports - but changing it means a follow-up to a shipped
    // script and to the desktop structure update.
    reportName: 'Past Nourishments',
    fKey_RS_Report: 151,
    // Wide, like the live Tray Tickets form (:2592). The resident grid's Name
    // column takes the leftover width, and a past report's names carry a
    // "(Deleted)" or "(Discharged)" suffix that the default column cannot hold.
    layout: { width: 'wide', mainWidth: '1140px' },
    fields: [
      // One date, not a range: a saved label belongs to the day it printed for,
      // and the census row is keyed on it.
      { key: 'StartDate', label: 'Date Printed For', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275,
        // Clamped to the days that actually have saved snapshots, with the note
        // under it saying so. Without this the operator can pick any day and only
        // finds out there was nothing to reprint after the report comes back empty.
        snapshotDateBounds: { kind: 'nourishmentLabel' } },

      // Nourishments ONLY - no Breakfast, Lunch or Dinner.
      // CreateSpecialRequestNourishment_DataSet has its main-meal branch
      // commented out when isNourishement is true, so mealtimes 0-2 never
      // produce a nourishment label and never have a snapshot to find. Offering
      // them would be a dead option that always reports "none found".
      //
      // The list is built from the facility's own nourishment names and
      // Nourish_*_SortOrder, so an unnamed pass is not offered either.
      { key: 'Mealtimes', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        mealtimeOptions: { includeMain: false, includeNourishments: true },
        options: [
          { value: 3, label: 'EAM' },
          { value: 4, label: 'AM' },
          { value: 5, label: 'PM' },
          { value: 6, label: 'HS' },
          { value: 7, label: 'PM2' },
          { value: 8, label: 'HS2' },
        ] },

      // The resident grid. Empty means every resident who has a saved label for
      // the date and mealtimes - PastNourishmentLabels treats an empty list that
      // way, so there is no All / Specific radio to carry.
      { key: 'PatientList', label: '', type: 'residentSelect', inline: true,
        defaultValue: [], group: 'Residents',
        showInfoColumns: true,
        showLocationColumnsFromFacility: 'tT_ShowLocationColumns',
        // The list comes from saved census rows, so months on it still includes
        // residents who have been discharged, deleted, or purged outright.
        showResidentStatus: true,
        pruneSelectionToRoster: true,
        pastNourishmentLabelEligible: {
          dateField: 'StartDate',
          mealtimesField: 'Mealtimes',
        } },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Pre-print label selector, the same dialog the live Nourishments report
    // shows - but driven by the SAVED labels, not by today's facility setting.
    //
    // The live report reads both halves from the facility:
    //   .LabelType    = Nourishment_Labels_Type
    //   .LabelsToSkip = the selector's answer, gated on
    //                   LabelReports_UseLabelSelector
    //
    // A reprint has to split those two apart, because they want opposite
    // sources:
    //
    //   LabelType    → from the SNAPSHOT. It picks the layout and the page
    //                  geometry the labels were laid out for. A facility that
    //                  has since changed stock must not reprint an old label
    //                  on the new one, so today's Nourishment_Labels_Type is
    //                  the wrong answer and the generator ignores it.
    //
    //   LabelsToSkip → from the OPERATOR, now. It describes the partly-used
    //                  sheet in the printer today; what the original print
    //                  skipped has nothing to do with it.
    //
    // Which makes the selector's VARIANT a snapshot question too: A5162 for a
    // saved label type 0, the generic 30-up otherwise. And it must not appear
    // at all when the saved labels are type 2 or 3 - single/roll labels, whose
    // layouts hardcode BlankDetailCount = 0.
    //
    // MIXED label types are refused rather than guessed at. One print job feeds
    // one stock, and the four types are not interchangeable: 0 and 1 are letter
    // sheets, 2 is a 436x183 custom label and 3 is 300x100. A single selector
    // answer cannot describe both a 14-up A5162 sheet and a 30-up generic one
    // either. It happens when someone changes Nourishment_Labels_Type between
    // two prints on the same day, so the fix is to reprint those mealtimes
    // separately - and the message says which ones go together.
    preSubmit: async (v, { facilityId, facilitySettings, showLabelSelector }) => {
      const startIso = typeof v.StartDate === 'string' ? v.StartDate.substring(0, 10) : null;
      const mealtimes = (v.Mealtimes ?? []) as number[];

      // Nothing picked yet - let the normal required-field validation and the
      // server's "no saved labels" message handle it.
      if (!startIso || mealtimes.length === 0) return {};

      const saved = await fetchPastNourishmentLabelTypes(facilityId, startIso, mealtimes);

      // Nothing saved for this date. Let the generator say so - it words it
      // with the date, and it is the one that knows about the resident filter.
      if (saved.length === 0) return { LabelsToSkip: 0 };

      const distinctTypes = [...new Set(saved.map((r) => r.labelType))].sort((a, b) => a - b);

      if (distinctTypes.length > 1) {
        const mealtimeName = (m: number) => {
          const own = facilitySettingsToMealtimeFacility(
            facilitySettings as Record<string, unknown> | null, facilityId,
          );
          const opts = own
            ? buildMealtimeOptions([own], { includeMain: true, includeNourishments: true })
            : [];
          return opts.find((o) => o.value === m)?.label ?? `Mealtime ${m}`;
        };

        const groups = distinctTypes.map((t) => {
          const names = saved
            .filter((r) => r.labelType === t)
            .map((r) => r.mealtime)
            .sort((a, b) => a - b)
            .map(mealtimeName);
          return `${names.join(', ')} (label type ${t})`;
        });

        return {
          error: 'These mealtimes were printed on more than one kind of label, '
            + 'and one print run can only feed one kind: '
            + `${groups.join(' and ')}. Reprint them separately.`,
        };
      }

      const labelType = distinctTypes[0];

      // Only a sheet label has positions to leave blank.
      if (!SHEET_LABEL_TYPES.includes(labelType)) return { LabelsToSkip: 0 };

      // The facility's own on/off switch still applies, exactly as it does for
      // the live report: a facility that does not use the selector never sees it.
      const fs = facilitySettings as { labelReports_UseLabelSelector?: boolean } | null;
      if (!fs?.labelReports_UseLabelSelector) return { LabelsToSkip: 0 };

      // Variant from the SAVED label type, not `nourishment_Labels_Type`.
      const variant = labelType === 0 ? 'A5162' : 'generic30';

      const result = await showLabelSelector(variant);

      // Close/Escape → silent cancel sentinel, as the live report does.
      if (result === 'cancel') return { error: '' };

      return { LabelsToSkip: result };
    },
    buildSettings: (v) => {
      // Midnight appended to the picked date, NOT `new Date(iso)`. A bare
      // "2026-10-26" parses as UTC midnight, so in any negative offset it
      // renders back as the 25th - the census lookup then asks for the wrong
      // day and finds nothing.
      const startIso = typeof v.StartDate === 'string' ? v.StartDate.substring(0, 10) : null;

      return {
        // Property names match PastNourishmentLabelSettings.
        StartDate: startIso ? `${startIso}T00:00:00` : null,
        Mealtimes: (v.Mealtimes ?? []) as number[],
        PatientList: (v.PatientList ?? []) as number[],
        StampAsReprint: true,
        // From the selector in preSubmit above. The generator spends it on the
        // first sheet-label group only - one physical sheet is fed once.
        LabelsToSkip: Number(v.LabelsToSkip ?? 0),
      };
    },
  },

  {
    slug: 'tray-ticket-notes',
    reportName: 'Tray Ticket Notes',
    fKey_RS_Report: 46,
    fields: [
      // ── Order group ────────────────────────────────────────────
      // Single-select radio, By Floor/Unit default (.Designer.vb:155
      // `radByFloor.EditValue = True`). The "By Unit"/"By Floor"
      // label swap is driven by facility `UseUnitMapping` — token
      // `{FloorOrUnit}` resolves to the correct label globally.
      // "By {Resident}" mirrors WinForms `.vb:96 String.Format("By {0}", ...)`.
      { key: 'Order', label: '', type: 'radio', defaultValue: 'floor',
        group: 'Order',
        options: [
          { value: 'floor', label: 'By {FloorOrUnit}' },
          { value: 'resident', label: 'By {Resident}' },
        ] },

      // ── Print group ────────────────────────────────────────────
      // Single Diet Order checkbox — WinForms Designer default false.
      { key: 'PrintDietOrder', label: 'Diet Order', type: 'checkbox',
        defaultValue: false, group: 'Print' },
    ],
    // No user-visible date. Use today for the sidebar date column;
    // WinForms sends `.DateFor = Now` (server timestamps the run).
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => {
      const order = String(v.Order ?? 'floor');
      return {
        // Match WinForms property casing exactly — Newtonsoft is
        // case-insensitive but keep the wire mirroring the DTO.
        OrderByFloor: order === 'floor',
        OrderByResident: order === 'resident',
        PrintDietOrder: Boolean(v.PrintDietOrder),
        // WinForms sends `Now` — server re-stamps anyway, but keep
        // ISO so Newtonsoft can deserialize a `DateTime` slot cleanly.
        DateFor: toLocalWireDateTime(new Date()),
      };
    },
  },
  {
    slug: 'tray-ticket-visualizer',
    reportName: 'Tray Ticket Visualizer',
    fKey_RS_Report: 84,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'Meal', group: 'Mealtimes', label: 'Meal', type: 'dropdown', defaultValue: 'Breakfast', options: MEAL_OPTIONS },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },
  // ─── FACILITY - TRAY LINE - UNASSIGNED (pKey 74, fKey 74) ──────
  // WinForms source: ucDietech_Report_Generator_TrayLine_Unassigned.vb.
  //   TabPageText  = "Tray Line - Unassigned" (.vb:47).
  //   The ONLY input is `ucRadMealtimes` — a single-select radio
  //   over the facility's main mealtimes. `.Init()` (.vb:88-89)
  //   selects Breakfast (index 0) by default; the screenshot
  //   confirms.
  //   Validation always returns True (.vb:310-313).
  //   Print (.vb:267-280) → TrayLineUnassignedSettings:
  //     .FKey_Facility = _FKey_Facility  (middleware)
  //     .DateFor       = Now             (current time at print)
  //     .Mealtime      = ucradMealtimes.GetSelectedMealtime  (int)
  {
    slug: 'tray-line-unassigned',
    reportName: 'Tray Line - Unassigned',
    fKey_RS_Report: 74,
    fields: [
      // Single-select mealtime radio — default Breakfast (0).
      // Labels use facility-scoped tokens.
      { key: 'Mealtime', label: '', type: 'radio', defaultValue: 0,
        group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },
    ],
    // No user-visible date. Use today for the sidebar date column
    // (WinForms doesn't display DateFor either).
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => ({
      // WinForms .vb:273 `.DateFor = Now` — always the current
      // wall-clock time at print, not a user-picked date.
      DateFor: new Date(),
      Mealtime: Number(v.Mealtime ?? 0),
    }),
    // No validate — WinForms `ValidateForm() Return True`.
  },
  // ─── FACILITY - TRAY LINE - ASSIGNED (pKey 76, fKey 76) ────────
  // WinForms source: ucDietech_Report_Generator_TrayLine_Assigned.vb.
  //   TabPageText  = "Tray Line - Assigned" (.vb:59).
  //   Date         = today (.vb:98).
  //   Mealtimes    = ucClbMealtime, main mealtimes only
  //                  (`IncludeNourishments = False` .vb:106),
  //                  empty default (`_lstMealtimesInit` empty).
  //                  Validation requires ≥1 (.vb:376-378).
  //   Cart         = ucClbSet with `SetItemChecks(Checked)` on Init
  //                  (.vb:113) — ALL sets checked by default.
  //                  Validation requires ≥1 (.vb:381-384).
  //   Group by     = single checkbox, default UNchecked; label
  //                  swaps "Floor" → "Unit" based on facility
  //                  `useUnitMapping` (.vb:116-122).
  //
  // Wire payload → TrayLineAssignedSettings:
  //   DateFor, FKey_Facility (middleware), LstMealtime (int[]),
  //   LstSet (int[]), GroupByFloor (bool).
  {
    slug: 'tray-line-assigned',
    reportName: 'Tray Line - Assigned',
    fKey_RS_Report: 76,
    fields: [
      // Date group.
      { key: 'DateFor', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275 },

      // Mealtimes — main only, empty default. Column layout to
      // match the vertical WinForms checkbox list.
      { key: 'LstMealtime', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Cart / Sets picker — WinForms `SetItemChecks(Checked)` on
      // Init selects all sets by default. `defaultSelectAll: true`
      // reproduces that.
      { key: 'LstSet', label: '', type: 'setSelect', inline: true,
        defaultValue: [], defaultSelectAll: true, group: 'Cart' },

      // Options — Group by Floor / Unit. Label swaps via
      // `{FloorOrUnit}` token (facility `useUnitMapping`).
      { key: 'GroupByFloor', label: 'Group by {FloorOrUnit}', type: 'checkbox',
        defaultValue: false, group: 'Options' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      LstMealtime: (v.LstMealtime as number[] | undefined) ?? [],
      LstSet: (v.LstSet as number[] | undefined) ?? [],
      GroupByFloor: Boolean(v.GroupByFloor),
    }),
    // Mirrors WinForms `ValidateForm` (.vb:374-388).
    validate: (v) => {
      const meals = (v.LstMealtime as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const sets = (v.LstSet as number[] | undefined) ?? [];
      if (sets.length === 0) return 'Please select at least one {SetName}.';
      return null;
    },
  },
  // ─── FACILITY - TRAY LINE - LOCATOR (pKey 77, fKey 77) ─────────
  // WinForms source: ucDietech_Report_Generator_TrayLineLocator.vb.
  //   TabPageText  = "Tray Line - Locator" (.vb:57).
  //   Order        = 2-way radio (By Floor/Unit or By {Resident}).
  //                  Default By Floor/Unit — Designer
  //                  `radByFloor.EditValue = True` (.Designer.vb:111).
  //                  radByFloor label swaps "Floor" → "Unit" per
  //                  facility `useUnitMapping` (.vb:83-88).
  //                  radByResident label = "By {Resident}"
  //                  (.vb:80).
  //   No date picker — WinForms hard-codes `DateFor = Now` at
  //   print time (.vb:274).
  //   No mealtime / set / any other input.
  // Validation: always True (.vb:300-303).
  //
  // Wire payload → TrayLineLocatorSettings:
  //   DateFor (always current time — mirrors WinForms `= Now`),
  //   FKey_Facility (middleware),
  //   OrderByFloor (bool),
  //   OrderByResident (bool)
  {
    slug: 'tray-line-locator',
    reportName: 'Tray Line - Locator',
    fKey_RS_Report: 77,
    fields: [
      // Order radio — default 'floor' (matches WinForms Designer
      // `radByFloor.EditValue = True`). Labels use `{FloorOrUnit}`
      // and `{Resident}` tokens for facility label swap.
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'floor',
        group: 'Order',
        options: [
          { value: 'floor', label: 'By {FloorOrUnit}' },
          { value: 'resident', label: 'By {Resident}' },
        ] },
    ],
    // No user-visible date; use today for the sidebar date column
    // (WinForms doesn't display DateFor anywhere either).
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => {
      const orderBy = String(v.OrderBy ?? 'floor');
      return {
        // WinForms .vb:274 `.DateFor = Now` — always the current
        // wall-clock time at print, NOT a user-picked date. Mirror
        // that by using `new Date()` at buildSettings time.
        DateFor: new Date(),
        OrderByFloor: orderBy === 'floor',
        OrderByResident: orderBy === 'resident',
      };
    },
    // WinForms `ValidateForm` always returns True — no validation.
  },
  {
    slug: 'tray-line-order',
    reportName: 'Tray Line - Order',
    fKey_RS_Report: 78,
    // TrayLineOrderSettings.DateFor. (The generator body itself is empty,
    // TrayLine/TrayLineOrder.vb; that is shared VB, not fixed here.)
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
  },
  // ─── FACILITY - TRAY LINE - OVERRIDES (pKey 79, fKey 79) ──────
  // WinForms source: ucDietech_Report_Generator_TrayLineOverride.vb.
  //   TabPageText = "Tray Line - Overrides" (.vb:47).
  //   No form inputs — Init only sets the title and shows the
  //   `ResdientDGMisc.NO_OPTIONS_MESSAGE` label (.vb:86-88).
  //   Validation always returns True (.vb:307-310).
  //   Print (.vb:266-277) → TrayLineOverrideSettings:
  //     .FKey_Facility = _FKey_Facility  (middleware)
  //     .DateFor       = Now             (current time at print)
  //   That's the entire wire payload.
  {
    slug: 'tray-line-overrides',
    reportName: 'Tray Line - Overrides',
    fKey_RS_Report: 79,
    description: 'no available options...press generate to continue',
    fields: [],
    buildDateFor: () => formatDate(today()),
    buildSettings: () => ({
      // WinForms .vb:271 `.DateFor = Now` — always the current
      // wall-clock time at print, not a user-picked date.
      DateFor: new Date(),
    }),
    // No validate — WinForms `ValidateForm() Return True`.
  },
  // ─── DISH UP LABELS (pKey 117, fKey 117) ───────────────────────
  // WinForms source: ucDietech_Report_Generator_DishUpLabels.vb.
  // Print (.vb:263-274):
  //   .StartDate         = ucDeStart.EditValue
  //   .EndDate           = ucDeEnd.EditValue
  //   .MealtimesInt      = ucClbMealtimeTab.GetCheckMealtimes
  //                        (int[] — 0-based PKeys from
  //                        `Get_MealNames`, Breakfast=0, Lunch=1,
  //                        Dinner=2; NOT the Mealtime enum bit
  //                        flags. Per
  //                        `ucDietech_Report_Generator_ClbMealtime.vb:114-124`
  //                        the value is `.ValueMember="PKey"` off
  //                        the datatable populated by
  //                        `Get_MealNames` at
  //                        `FacilitySettings.vb:6213-6236`.)
  //   .UseUnitMapping    = g_facilitySettings.UseUnitMapping (bool)
  //   .PKey_Menu_Header  = ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header
  //   .LabelsToSkip      = from label-selector dialog when
  //                        DishUpLabels_LabelType == 0. React port
  //                        sends 0 (no label-skip UI ported yet).
  //   .lstFloorUnit      = ucClb_FloorUnit.GetCheckedFloorUnits
  //                        (PKey_Unit_Mapping[] when
  //                        UseUnitMapping, else Floor int[])
  //   report class       = DishUpLabels (server pKey 117)
  //
  // Init (.vb:87-144):
  //   Date Range: Start = today, End = today, End.DateMin = Start
  //   Menu Cycle: shown when UseProduction (facility setting)
  //   Mealtimes tab: unchecked default (`_lstMealtimesInit` empty)
  //   Floor/Units tab: all checked by default (`.CheckAll()` at
  //                    `ucDietech_Report_Generator_ClbFloorUnit.vb:97`)
  //
  // Validate (.vb:449-462):
  //   1. `IsMenuCategoryOrderEmtpy(15)` — skipped (facility
  //      settings check that requires a settings-side data prep).
  //   2. ≥1 mealtime selected.
  {
    slug: 'dish-up-labels',
    reportName: 'Dish Up Labels',
    fKey_RS_Report: 117,
    apiEndpoint: 'DishUpLabels',
    fields: [
      // Date Range — bidirectional invariant `EndDate >= StartDate`.
      // Both dates default to today (WinForms `_dateTimeInitialized
      // = Now` at .vb:31 / .vb:38).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      // Menu Cycle info — read-only display, driven by DateStart.
      // Populates the React Query cache the `resolvePkeyMenuHeader`
      // ctx reader taps at submit time.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'DateStart' },

      // Selection tabs — Mealtimes (unchecked default) / Floor-Units
      // (all-checked default). Mealtimes uses `layout: 'column'`
      // so all three items render on their own line vertically —
      // matches the WinForms tab layout in the supplied screenshot.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },
      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.DateStart, v.DateEnd),
    // Pre-print label-selector dialog — WinForms
    // `ucDietech_Report_Generator_DishUpLabels.vb:252-261`.
    // Unlike the other three label reports Dish-Up does NOT
    // gate on `labelReports_UseLabelSelector`; the ONLY switch
    // is `dishUpLabels_LabelType`:
    //   0   → show the OL125 dialog (10-label OnlineLabels sheet)
    //   ≠0  → skip the selector, LabelsToSkip stays 0
    preSubmit: async (v, { facilitySettings, showLabelSelector }) => {
      void v;
      const fs = facilitySettings as { dishUpLabels_LabelType?: number } | null;
      if ((fs?.dishUpLabels_LabelType ?? 0) !== 0) return {};
      const result = await showLabelSelector('OL125');
      // Close/Escape → silent cancel; abort submit without a
      // toast. Matches WinForms's early-return after
      // `frmLabelSelector_OL125.ShowDialog() = DialogResult.Cancel`.
      if (result === 'cancel') return { error: '' };
      return { LabelsToSkip: result };
    },
    // Mirrors WinForms Print settings block (.vb:265-274).
    //   - `PKey_Menu_Header` resolved from the React Query cache
    //     populated by the sibling `menuCycleInfo` field.
    //   - `UseUnitMapping` read from facility settings — server
    //     needs this to interpret `lstFloorUnit` values (PKey vs
    //     Floor number, DishUpLabels.vb:180-201). The floor/unit
    //     checklist gives unit keys on a unit-mapping facility and
    //     floor numbers otherwise (FloorUnitSelector), as WinForms'
    //     GetCheckedFloorUnits does (ClbFloorUnit.vb:213-229).
    //   - `LabelsToSkip` defaults to 0; the preSubmit hook above
    //     overrides via `preSubmitExtras` when the OL125 dialog
    //     runs and the user picks a non-first starting label.
    buildSettings: (v, fs, ctx) => {
      // ISO yyyy-MM-dd string from the DateStart form value —
      // matches the queryKey format the useMenuCycleInfo hook uses.
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.DateStart);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );
      return {
        StartDate: v.DateStart,
        EndDate: v.DateEnd,
        MealtimesInt: (v.MealtimesInt as number[] | undefined) ?? [],
        UseUnitMapping: useUnitMapping,
        PKey_Menu_Header: pKeyMenuHeader,
        LabelsToSkip: 0,
        lstFloorUnit: (v.lstFloorUnit as number[] | undefined) ?? [],
      };
    },
    // Mirrors ValidateForm (.vb:449-462). WinForms arm 1
    // (`IsMenuCategoryOrderEmtpy(15)`) requires a facility-settings
    // -side data prep check — skipped in the React port; if empty
    // the report generates blank labels instead of blocking.
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      return null;
    },
  },

  // ─── PRODUCTION - TALLY (pKey 3, fKey 3) ───────────────────────
  // WinForms source: ucDietech_Report_Generator_ProductionTally.vb.
  //
  // Init (.vb:131-259) — facility-setting-driven throughout:
  //   TabPageText  = "Production Tally"
  //   ucDeStart.DateInit = today (LabelVisible=False)
  //   ucNumberOfDays.Min=1, Max=9
  //   ucMenuCycleInfo watches StartDate
  //   Report Type default from `pT_ReportType`
  //     (0=Extended, 1=Summary, 2=ByFloor/ByUnit, 3=Combined, 4=BySet)
  //   Notes default = `pT_PrecheckNotesAdvancedPrep`
  //   Extended-mode Utensils = `pT_Extended_ShowUntensil`
  //   Mealtimes.IncludeNourishments = `pT_ShowNourishments`
  //   "By Floor" caption becomes "By Unit" when `useUnitMapping` true
  //   ProductionArea grid = SelectAll on load
  //
  // Print (.vb:394-469) — 30-field `ProductionTallySettings` DTO.
  //   See buildSettings below for the exact mapping.
  //
  // Validate (.vb:1599-1652) — 7 checks, see validate below.
  //
  // Deferred (documented per project defer patterns):
  //   • Predefined Filters combo + save/rename/reload/delete
  //     (same defer as Selective Menu Posting pKey 12)
  //   • Multi-facility corporate tree
  //     (same defer as Grocery List pKey 125)
  //   • Data-cache freshness indicator
  {
    slug: 'production-tally',
    reportName: 'Production Tally',
    fKey_RS_Report: 3,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    apiEndpoint: 'ProductionTally',
    groupLayout: {
      // Three fixed columns; the middle one is wider so longer
      // labels ('Notes / Advanced Prep') stay on one line.
      Options: {
        density: 'compact', checkboxPad: '1px', flushLabels: true,
        cols: '150px 200px 150px', gap: '1px 12px',
      },
    },
    layout: { mainWidth: '560px', sideWidth: '260px' },
    fields: [
      // Facility tree — the multi-facility entry point. Renders only when
      // `PT_ShowMultiFacility` is on AND the user has more than one facility
      // (ProductionTally.vb:166-167); otherwise `facilityTree` returns null and
      // every `multiFacility` selector below falls back to its single-facility
      // form. Checking a facility here creates its tab in the {SetName} and
      // {Resident} pickers — WinForms does that imperatively from
      // ucFacTree_AfterCheckNode (.vb:1884).
      { key: 'LstFacilityMultiple', label: '', type: 'facilityTree',
        defaultValue: [] as number[],
        // Hide the whole group when the facility has multi-facility reporting
        // off, so single-facility sites see no empty fieldset. The second half
        // of the WinForms gate (user has >1 facility) needs the facility list,
        // which only the field itself can see - it reports that case inline.
        visibleWhen: { check: () => Boolean(
          (useGlobalStore.getState().facilitySettings as { pT_ShowMultiFacility?: boolean } | null)?.pT_ShowMultiFacility,
        ) },
        group: '{Facility_Plural}' },

      // Date row — StartDate + NumberOfDays side-by-side.
      { key: 'StartDate', label: '', type: 'date',
        defaultValue: () => today(),
        required: true, group: 'Date', width: 220 },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 9, width: 240, group: 'Date' },

      // Menu Cycle info — display-only, driven by StartDate.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Predefined Filters combo — mirrors WinForms `chkCmbFilter`
      // (CheckedComboBoxEdit) with 5 popup buttons New / Save /
      // Delete / Reload / Rename. Multi-select: each picked filter
      // becomes its own report section server-side (already
      // implemented in ProductionTally.vb:210 which iterates
      // settings.CMBFilter as a comma-list). Single-select: loads
      // the filter into every mapped form field. Multi-select:
      // clears + locks Mealtimes / Food Groups / Production Areas /
      // Sets on the form (WinForms .vb:764-780).
      { key: 'LstFilters', label: '', type: 'filterSelect',
        defaultValue: [] as number[],
        group: 'Filter',
        filterFieldMap: {
          mealtimes: 'LstMealtimesPerDay',
          selectedSets: 'SetList',
          printRecipes: 'Options_Recipe',
          reportType: 'ReportType',
          days: 'NumberOfDays',
          foodGroupIds: 'FoodGroupsSelected',
          productionAreaIds: 'ProductionAreasSelected',
          printMode: 'PrintMode',
        },
        // WinForms .vb:764-780 disables Mealtimes tab, food-group
        // tree, production-area grid, AND the entire Print group
        // (`lcgPrint.Enabled = False` @ .vb:775 covers the PrintMode
        // radio + its child pickers) when 2+ filters are picked.
        filterMultiLockKeys: [
          'LstMealtimesPerDay',
          'FoodGroupsSelected',
          'ProductionAreasSelected',
          'PrintMode',
          'SetList',
          'PatientList',
        ],
        // Filter row's `reportType` int ↔ this config's radio value:
        //   0=Extended → 'extended', 1=Summary → 'summary',
        //   2=Combined → 'combined', 3=ByFloor → 'byfloor'.
        filterReportTypeDecode: (n) => {
          const map = ['extended', 'summary', 'combined', 'byfloor'];
          return map[n] ?? 'summary';
        },
        filterReportTypeEncode: (v) => {
          const map: Record<string, number> = { extended: 0, summary: 1, combined: 2, byfloor: 3, byset: 3 };
          return typeof v === 'string' ? (map[v] ?? 1) : (typeof v === 'number' ? v : 1);
        },
      },

      // Mealtimes — per-day tabs. Reuses `mealtimesPerDay` field
      // type (built for Employee Production Tally pKey 130). Wire
      // format: `List<int[]>` with 0-based indices matching
      // WinForms `ucMealltimesTab.GetCheckedMealtimes`.
      { key: 'LstMealtimesPerDay', label: '', type: 'mealtimesPerDay',
        defaultValue: [[]] as number[][],
        group: 'Mealtimes',
        // Checklist built from facility settings (Fill_clbMeals). WinForms
        // .vb:211-213 leaves IncludeMainMealtimes at its True default and sets
        // IncludeNourishments = PT_ShowNourishments(facility). The static
        // `options` below are the fallback only.
        mealtimeOptions: { includeMain: true, includeNourishments: 'pT_ShowNourishments' },
        // 3-column CSS multi-column so 7 mealtimes flow vertically
        // top-to-bottom (Breakfast/Lunch/Dinner col 1, nourishments
        // col 2, HS col 3) instead of stretching the fieldset tall.
        layout: 'threeCol',
        watchGroup: { start: 'StartDate', days: 'NumberOfDays' },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Print mode — three-radio group. Merges WinForms
      // radPrintAll / radSpecificSet / radPrintResident.
      { key: 'PrintMode', label: '', type: 'radio',
        defaultValue: 'all', group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },

      // Selective Menu filter (cmbSMFilter: Include / Do Not Include / Only).
      // WinForms starts it at the facility's PT_DefaultSelectiveMenuFilter and
      // shows the combo only when PT_DisplaySelectiveMenuFilter is on
      // (ucDietech_Report_Generator_ProductionTally.vb:240-245); hidden, the
      // default still goes out (.vb:475). ProductionTally.vb:196-218 drops the
      // selective-menu residents' meals (1) or keeps only theirs (2).
      { key: 'smFilterType', label: 'Selective Menu:', type: 'dropdown',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as { pT_DefaultSelectiveMenuFilter?: number } | null)?.pT_DefaultSelectiveMenuFilter;
          return typeof raw === 'number' ? raw : 0;
        },
        visibleWhen: { check: () => Boolean(
          (useGlobalStore.getState().facilitySettings as { pT_DisplaySelectiveMenuFilter?: boolean } | null)?.pT_DisplaySelectiveMenuFilter,
        ) },
        options: [
          { value: 0, label: 'Include' },
          { value: 1, label: 'Do Not Include' },
          { value: 2, label: 'Only' },
        ],
        group: 'Print', width: 220 },

      // Report Type — five-radio group. Default from facility
      // `pT_ReportType` (0-4). "By Floor" text swaps to "By Unit"
      // when `useUnitMapping` is on.
      { key: 'ReportType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const map = ['extended', 'summary', 'byfloor', 'combined', 'byset'];
          const raw = (fs as { pT_ReportType?: number } | null)?.pT_ReportType;
          return typeof raw === 'number' && raw >= 0 && raw < map.length ? map[raw] : 'summary';
        },
        group: 'Report Type',
        // "By Floor" swaps to "By Unit" per facility `useUnitMapping`
        // — mirrors WinForms .vb:179-181.
        options: () => {
          const fs = useGlobalStore.getState().facilitySettings as { useUnitMapping?: boolean } | null;
          const useUnitMapping = Boolean(fs?.useUnitMapping);
          return [
            { value: 'extended', label: 'Extended' },
            { value: 'summary', label: 'Summary' },
            { value: 'combined', label: 'Combined' },
            { value: 'byfloor', label: useUnitMapping ? 'By Unit' : 'By Floor' },
            { value: 'byset', label: 'By {SetName}' },
          ];
        } },

      // Options — 9 checkboxes. Default checked state is driven by
      // the current ReportType per WinForms `radReportType_CheckedChanged`
      // (.vb:1077-1139), which Init triggers when it sets the
      // initial radio from `PT_ReportType` (.vb:219-231):
      //   Extended : Tally + FoodStorage + ServiceItem + SpecialDevices
      //              + Garnish all TRUE; Utensils = `PT_Extended_ShowUntensil`;
      //              Notes = `_preCheckNotes`; SortBySet + Recipe FALSE.
      //   Summary  : Tally + Garnish TRUE; FoodStorage + ServiceItem
      //              + SpecialDevices + Utensils FALSE;
      //              Notes = `_preCheckNotes`; SortBySet + Recipe FALSE.
      //   Combined : ALL options FALSE (radio interlock unchecks
      //              every one, including Notes / Garnish / Recipes).
      //   ByFloor / ByCart : radio has NO interlock in WinForms, so
      //              Designer defaults apply — Tally / FoodStorage /
      //              ServiceItem / SpecialDevices / Utensils / Garnish
      //              TRUE; SortBySet FALSE; Notes = preCheckNotes;
      //              Recipes FALSE.
      // `recomputeWhen: ['ReportType']` re-fires each field's
      // defaultValue when the ReportType radio changes — matches
      // WinForms's fire-on-radio-change behavior. Trade-off: any
      // user tweaks on Options get overwritten on ReportType
      // switch. That's what WinForms does too.
      // Enabled state per WinForms `radReportType_CheckedChanged`
      // (.vb:1077-1139):
      //   Extended       : all options ENABLED
      //   Summary        : all ENABLED except Utensils (DISABLED)
      //   Combined       : ALL options DISABLED
      //   ByFloor / ByCart : radio has NO enabled-state interlock,
      //                      so options stay ENABLED
      // WinForms `radReportType_CheckedChanged` (.vb:1077-1139)
      // Handles ONLY radReportTypeExtended, radReportTypeSummary,
      // radReportCombined. ByFloor / ByCart do NOT trigger the
      // handler — options preserve their previous state (both
      // .Checked AND .Enabled) when those two radios are picked.
      //
      // Also: chkOptions_Recipes is NEVER touched in any branch —
      // it's not in this handler at all. It stays whatever it was.
      // chkOptions_SortBySet has .Enabled set but .Checked is only
      // touched in the Combined branch. Others follow the
      // per-branch matrix below.

      { key: 'Options_ProductionTally', label: 'Tally',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'extended' || rt === 'summary') return true;
          if (rt === 'combined') return false;
          return values?.Options_ProductionTally; // byfloor/byset preserve
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_SpecialDevices', label: '{SpecialDevice}',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'extended') return true;
          if (rt === 'summary' || rt === 'combined') return false;
          return values?.Options_SpecialDevices; // byfloor/byset preserve
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_FoodStorage', label: 'Food Storage',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'extended') return true;
          if (rt === 'summary' || rt === 'combined') return false;
          return values?.Options_FoodStorage;
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_Garnish', label: 'Garnish',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'extended' || rt === 'summary') return true;
          if (rt === 'combined') return false;
          return values?.Options_Garnish;
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_PrintNotes', label: 'Notes / Advanced Prep',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'combined') return false;
          if (rt === 'extended' || rt === 'summary') {
            const preCheck = (fs as { pT_PrecheckNotesAdvancedPrep?: boolean } | null)?.pT_PrecheckNotesAdvancedPrep;
            return Boolean(preCheck);
          }
          return values?.Options_PrintNotes; // byfloor/byset preserve
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_ServiceItems', label: 'Service Item',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'extended') return true;
          if (rt === 'summary' || rt === 'combined') return false;
          return values?.Options_ServiceItems;
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_SortBySet', label: 'Group By {SetName}',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          // WinForms Extended and Summary don't touch .Checked on
          // SortBySet (only .Enabled). Combined unchecks it. Others
          // preserve.
          if (values?.ReportType === 'combined') return false;
          return values?.Options_SortBySet;
        },
        enabledWhen: { check: (v) => v.ReportType !== 'combined' },
        disabledReason: 'Not available for the Combined report type.',
      },
      { key: 'Options_Utensils', label: 'Utensils',
        type: 'checkbox', group: 'Options',
        recomputeWhen: ['ReportType'],
        defaultValue: (fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const rt = String(values?.ReportType ?? 'summary');
          if (rt === 'extended') {
            const raw = (fs as { pT_Extended_ShowUntensil?: boolean } | null)?.pT_Extended_ShowUntensil;
            return raw === undefined ? true : Boolean(raw);
          }
          if (rt === 'summary' || rt === 'combined') return false;
          return values?.Options_Utensils; // byfloor/byset preserve
        },
        // Utensils is DISABLED in Summary AND Combined; enabled
        // otherwise.
        enabledWhen: { check: (v) => v.ReportType !== 'combined' && v.ReportType !== 'summary' },
        disabledReason: 'Not available for the Summary or Combined report types.',
      },
      // Recipes — WinForms never touches this checkbox in any
      // branch of `radReportType_CheckedChanged`. Static default,
      // always enabled, no ReportType interlock.
      { key: 'Options_Recipe', label: 'Recipes',
        type: 'checkbox', group: 'Options',
        defaultValue: false },

      // Set + Resident pickers — rendered INLINE in the main column
      // beneath the Options group, gated by PrintMode. `inline:
      // true` opts these selectors out of the auto side-column
      // routing so they sit directly below Options.
      { key: 'SetList', label: 'Select {SetName_Plural}',
        type: 'setSelect', defaultValue: [],
        visibleWhen: { key: 'PrintMode', value: 'set' },
        // Multi-facility: one {SetName} list per checked facility, tabbed.
        // Ports ucTabbedClbSets (ProductionTally.vb:171-172 swaps lciSets for
        // lciTabbedSets). Value becomes Record<facilityId, number[]>.
        multiFacility: true,
        // Residents per {SetName} on the start date, so empty ones are easy to leave out.
        showResidentCountsFor: 'StartDate',
        inline: true },
      // One report, one section per picked {SetName}, menus built once per day
      // (ProductionTallySettings.EachSetOnOwnPages). Website only.
      { key: 'EachSetOnOwnPages', label: 'Each {SetName} on its own pages',
        // Unnamed fieldset: it sits under the {SetName} list it applies to.
        type: 'checkbox', group: '', defaultValue: false,
        visibleWhen: { check: (v) => productionTallyEachSetOnOwnPagesAvailable(v) },
        inline: true },
      // Patient picker — column set matches the WinForms screenshot:
      // Name | Diet Order | Unit | Room | TF. `showInfoColumns`
      // adds Diet Order / Unit / Room / TF alongside Name.
      //
      // `postingSpecificResidentEligible` wires the picker's data
      // source to `usePostingSpecificResidentEligiblePatients` — the
      // same endpoint that powers the Posting - Specific Resident
      // report. It returns TblPatient records INCLUDING GUESTS
      // filtered by StartDate day-of-week + guest recurrence,
      // matching WinForms `Fill_dtPatient` at
      // `ucDietech_Report_Generator_ProductionTally.vb:845-903`.
      // Guests appear indented under their parent with a bi-people
      // icon; parents whose eligibility doesn't match the current
      // filter render as silver "dummy" rows so the guest still
      // shows its parent context. Mealtimes source is left at the
      // default (`MealtimesInt` — nonexistent in this config) so
      // the endpoint's attendance clause defaults to no-op and the
      // grid shows every active resident. Downstream `buildSettings`
      // sends `tempPatientList` only when PrintMode === 'resident'.
      // Patient picker — EXACT structural + column parity with
      // Posting Menu → Specific Resident (pKey 11). Same props,
      // same rendering path, same fieldset wrapper.
      { key: 'PatientList', label: '', type: 'residentSelect',
        defaultValue: [],
        visibleWhen: { key: 'PrintMode', value: 'resident' },
        showInfoColumns: true,
        postingSpecificResidentEligible: {},
        predefinedListType: 1,
        // Multi-facility: one resident grid per checked facility, tabbed.
        // Ports ucTabbedResGrid (ProductionTally.vb:174-175).
        multiFacility: true,
        group: '{Resident_Plural}', inline: true },

      // Production Areas grid + Food Groups tree — side selectors.
      // Both auto-route to `.report-form-grouped-side` because
      // their types are in SELECTOR_TYPES. Production Areas rendered
      // FIRST (above Food Groups) per user preference.
      // `group:` MUST be set — even though these are side selectors
      // (which don't use the main-column group layout), the value
      // renders as the `data-group="…"` attribute on the fieldset
      // wrapper. Per-slug CSS targets that attribute, so without
      // it every scoped rule for these two selectors silently
      // no-ops.
      { key: 'ProductionAreasSelected', label: '',
        type: 'productionAreaGrid', defaultValue: [] as number[],
        group: 'Production Areas' },
      // `getGroupHeader` injects the selected-count into the fieldset
      // header — matches WinForms `SetFoodGroupLabel` (.vb:1798).
      { key: 'FoodGroupsSelected', label: '', type: 'foodGroupTree',
        defaultValue: [] as number[],
        group: 'Food Groups',
        getGroupHeader: (values) => {
          const sel = (values?.FoodGroupsSelected as number[] | undefined) ?? [];
          return sel.length > 0 ? `Food Groups (${sel.length})` : 'Food Groups';
        } },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // `PKey_UserPrintedBy` — WinForms .vb:434 sends
    // `g_ConnectionUserManager.CU_UserID`. The middleware appends
    // its own `FKey_User: userId` field, but the WinForms DTO
    // property is `PKey_UserPrintedBy` (different name), so the
    // middleware value doesn't land where WinForms puts it. Use
    // preSubmit to add the exact wire field name (PascalCase
    // matches every other DTO field on the wire).
    preSubmit: async (v, { userId, facilityId, queryClient }) => {
      const filterIds = (v.LstFilters as number[] | undefined) ?? [];
      // Build CMBFilter (comma-joined PKeys) + TallyFilterString
      // (comma-joined names) exactly like WinForms .vb:430-431. The
      // server generator iterates CMBFilter as a comma-list —
      // ProductionTally.vb:210.
      let cmbFilter: string | null = null;
      let tallyFilterString = '[Select Predefined Filters]';
      if (filterIds.length > 0) {
        cmbFilter = filterIds.join(',');
        const cached = queryClient?.getQueryData<
          Array<{ pKey_Prd_Tally_Filter_Header: number; name: string }>
        >(['tallyFilters', facilityId]) ?? [];
        const nameByPKey = new Map(cached.map((f) => [f.pKey_Prd_Tally_Filter_Header, f.name]));
        tallyFilterString = filterIds.map((id) => nameByPKey.get(id) ?? String(id)).join(', ');
      }
      return {
        PKey_UserPrintedBy: userId,
        CMBFilter: cmbFilter,
        TallyFilterString: tallyFilterString,
      };
    },
    // Full 30-field payload matching WinForms Print (.vb:394-469).
    // Property names use camelCase (System.Text.Json default on
    // the API). Wire semantics per the audit:
    //   • sets / setsSelectedString ONLY populated when FilterBySet
    //   • tempPatientList ONLY populated when FilterByResident
    //   • lstMealtimes: List<int[]>, 0-based indices; when
    //     multiple filters selected, force {0,1,2} × NumberOfDays
    //   • productionAreasSelected: (number|null)[]; [None]
    //     synthetic row (pKey===0) emits as null
    //   • options_SummaryTotal duplicates reportTypeSummary
    //     (WinForms .vb:425 — intentional)
    buildSettings: (v, fs, ctx) => {
      const printMode = String(v.PrintMode ?? 'all');
      const filterBySet = printMode === 'set';
      // Selective Menu filter: the user's pick when the combo shows, else the
      // facility default (ProductionTally.vb:240-245, :475).
      const smFs = fs as { pT_DefaultSelectiveMenuFilter?: number; pT_DisplaySelectiveMenuFilter?: boolean } | null;
      const smDefault = typeof smFs?.pT_DefaultSelectiveMenuFilter === 'number' ? smFs.pT_DefaultSelectiveMenuFilter : 0;
      const smFilterTypeWire = smFs?.pT_DisplaySelectiveMenuFilter && typeof v.smFilterType === 'number'
        ? v.smFilterType
        : smDefault;
      const filterByResident = printMode === 'resident';
      const reportType = String(v.ReportType ?? 'summary');
      const numberOfDays = Number(v.NumberOfDays ?? 1);

      // Mealtimes: internal state uses 0-based indices already
      // (mealtimesPerDay field emits raw option values). WinForms
      // wire format is also 0-based, so pass through unchanged.
      const mealtimesRaw = (v.LstMealtimesPerDay as number[][] | undefined) ?? [];
      // Multi-filter-mode override: when 2+ filters are picked the
      // form's Mealtimes has been cleared/locked; wire back
      // `{0,1,2}` per day so the server's iterator has valid values
      // for filters that don't override (mirrors WinForms .vb:456-464
      // — .LstMealtimes = List<int()>{{0,1,2},{0,1,2},…}).
      const activeFilterIds = (v.LstFilters as number[] | undefined) ?? [];
      const multiFilter = activeFilterIds.length >= 2;
      const lstMealtimes: number[][] = multiFilter
        ? Array.from({ length: numberOfDays }, () => [0, 1, 2])
        : Array.from({ length: numberOfDays }, (_, i) => mealtimesRaw[i] ?? []);

      // Production areas: [None] synthetic row pKey===0 → null
      const rawAreas = (v.ProductionAreasSelected as number[] | undefined) ?? [];
      const productionAreasSelected: (number | null)[] = rawAreas.map((id) => (id > 0 ? id : null));

      // Report-type interlock — WinForms .vb:1077-1139. Extended
      // and Summary let the user tweak options; Combined blanks
      // everything. ByFloor/ByCart preserve user selections.
      const rawOptions = {
        productionTally: Boolean(v.Options_ProductionTally),
        foodStorage: Boolean(v.Options_FoodStorage),
        serviceItems: Boolean(v.Options_ServiceItems),
        utensils: Boolean(v.Options_Utensils),
        specialDevices: Boolean(v.Options_SpecialDevices),
        garnish: Boolean(v.Options_Garnish),
        sortBySet: Boolean(v.Options_SortBySet),
        printNotes: Boolean(v.Options_PrintNotes),
        recipe: Boolean(v.Options_Recipe),
      };
      const options = reportType === 'combined'
        ? { productionTally: false, foodStorage: false, serviceItems: false, utensils: false,
            specialDevices: false, garnish: false, sortBySet: false, printNotes: false, recipe: false }
        : rawOptions;

      // Menu header — resolved from StartDate via ctx helper.
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;

      // WinForms sends `StartDate` and `MenuCycleInfo_MenuDatePassedIn`
      // as full LOCAL ISO datetime strings (no `Z` suffix), e.g.
      // `"2026-07-17T13:11:32.5701083"`. React's date picker gives us
      // just the date part — append the current local time so
      // Newtonsoft's `DateTimeZoneHandling.Unspecified` deserializes
      // it as a local `DateTime` (matches WinForms `ucDeStart.EditValue`).
      const startLocalIso: string = (() => {
        if (!startIso) return '';
        const n = new Date();
        const pad = (x: number, w = 2) => String(x).padStart(w, '0');
        const fff = pad(n.getMilliseconds(), 3);
        return `${startIso}T${pad(n.getHours())}:${pad(n.getMinutes())}:${pad(n.getSeconds())}.${fff}0000`;
      })();

      // Wire field names use PascalCase matching the WinForms
      // `ProductionTallySettings` DTO property names EXACTLY.
      // Every other config-driven report in this file uses the same
      // convention (Grocery List, Prep/Pull, Selective Menu, …).
      // Newtonsoft.Json IS case-insensitive by default, but mixing
      // camelCase here with the middleware's PascalCase
      // `FKey_Facility` produces duplicate keys in the JSON — the
      // resulting deserialization is order-dependent and has caused
      // generator errors. Matching the DTO exactly avoids the
      // duplicate-key hazard entirely.
      // Key wire-level distinctions from the WinForms JSON blob
      // (audited byte-by-byte against a WinForms trace):
      //   • `Sets` / `SetsSelectedString`: WinForms leaves these
      //     null unless `radSpecificSet.Checked` (.vb:410-413).
      //   • `tempPatientList`: WinForms leaves this null unless
      //     `FilterByResident` (.vb:440-454).
      //   • `lstFKey_Facility_Multiple` / `lstFacilityNames_Multiple`:
      //     WinForms only allocates the lists in multi-facility mode
      //     (.vb:396-404); otherwise both remain null.
      //   • `CMBFilter`: WinForms sends the raw combo `EditValue`
      //     which is `Nothing` when nothing picked (.vb:430).
      //   • `TallyFilterString`: WinForms sends `chkCmbFilter.Text`
      //     which is the ComboBox display text — for the default
      //     empty state that is the literal "[Select Predefined
      //     Filters]" placeholder (.vb:431).
      //   • `StartDate` / `MenuCycleInfo_MenuDatePassedIn`: full
      //     local ISO datetime, not a date-only string.
      // ── Multi-facility ────────────────────────────────────────────────
      // `facilityTree` renders nothing (and leaves this empty) unless
      // PT_ShowMultiFacility is on and the user has >1 facility, so an empty
      // list here IS the single-facility path.
      const checkedFacilityIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
      const isMultiFacility = checkedFacilityIds.length > 0;

      // In multi-facility mode SetList / PatientList hold a per-facility map
      // rather than a flat array (see ReportFieldConfig.multiFacility).
      const mfSets = ctx?.resolveMultiFacility?.(
        isMultiFacility ? checkedFacilityIds : undefined,
        v.SetList as Record<string, unknown> | undefined,
      ) ?? { ids: null, names: null, selections: null };
      const mfResidents = ctx?.resolveMultiFacility?.(
        isMultiFacility ? checkedFacilityIds : undefined,
        v.PatientList as Record<string, unknown> | undefined,
      ) ?? { ids: null, names: null, selections: null };

      const multi = {
        lstFKey_Facility_Multiple: mfSets.ids,
        lstFacilityNames_Multiple: mfSets.names,
        // WinForms only fills these under the matching PrintMode
        // (.vb:443-446 / .vb:480-482); the other stays null.
        lstSets_Multiple: isMultiFacility && filterBySet ? mfSets.selections : null,
        lstResidents_Multiple: isMultiFacility && filterByResident ? mfResidents.selections : null,
        // Mealtime names are NOT per-facility — a flat (name, ordinal) union the
        // generator filters per facility via GetAllMealNames (.vb:507-509).
        // Same builder the checklist uses, so names match what the user ticked.
        lstMealtime_MultipleNames: isMultiFacility
          ? ctx?.resolveMealtimeNames?.(checkedFacilityIds, { includeMain: true, includeNourishments: 'pT_ShowNourishments' }) ?? null
          : null,
      };

      // Single-facility wire fields. Blank in multi-facility mode, where the
      // per-facility lists above carry the selection instead.
      const setListRaw = isMultiFacility ? undefined : (v.SetList as number[] | undefined);
      const setListWire: number[] | null = filterBySet && !isMultiFacility
        ? (setListRaw ?? [])
        : null;
      const setsSelectedStringWire: string | null = filterBySet && !isMultiFacility
        ? (ctx?.resolveSetsSelectedString?.(setListRaw ?? []) ?? '')
        : null;
      const tempPatientListWire: number[] | null = filterByResident && !isMultiFacility
        ? ((v.PatientList as number[] | undefined) ?? [])
        : null;

      return {
        FKey_Facility: 0, // server fills from URL
        FilterByResident: filterByResident,
        FilterBySet: filterBySet,
        ReportTypeByFloor: reportType === 'byfloor',
        ReportTypeBySet: reportType === 'byset',
        ReportsCombined: reportType === 'combined',
        ReportTypeExtended: reportType === 'extended',
        ReportTypeSummary: reportType === 'summary',
        MealTimesEnabled: !multiFilter,
        Sets: setListWire,
        SetsSelectedString: setsSelectedStringWire,
        // Only when on: every other print sends exactly the WinForms payload.
        ...(v.EachSetOnOwnPages && productionTallyEachSetOnOwnPagesAvailable(v) ? { EachSetOnOwnPages: true } : {}),
        LstMealtimes: lstMealtimes,
        // WinForms `_filterOverride` (.vb:109/416/804/1539): reset
        // to false on filter-select change, set true when the user
        // modifies the form after a filter is loaded. Tells the
        // server generator whether to use FORM data (true) or each
        // picked filter's own DB rows (false — the iterate branch
        // at ProductionTally.vb:180). Scoped to single-filter mode
        // — multi-filter always uses each filter's own data.
        FilterOverride: !multiFilter && (ctx?.hasUserEdited ?? false),
        NumberOfDays: numberOfDays,
        StartDate: startLocalIso,
        CMBFilter: null,
        Options_ProductionTally: options.productionTally,
        Options_FoodStorage: options.foodStorage,
        Options_ServiceItems: options.serviceItems,
        Options_Utensils: options.utensils,
        Options_SpecialDevices: options.specialDevices,
        Options_SummaryTotal: reportType === 'summary',
        Options_SortBySet: options.sortBySet,
        Options_PrintNotes: options.printNotes,
        Options_Garnish: options.garnish,
        Options_Recipe: options.recipe,
        MenuCycleInfo_PKey_Menu_Header: pKeyMenuHeader,
        MenuCycleInfo_MenuDatePassedIn: startLocalIso,
        // The form value (the facility default until the user picks), or the
        // facility default itself when the combo is hidden (.vb:240, :475).
        smFilterType: smFilterTypeWire,
        tempPatientList: tempPatientListWire,
        foodGroupsSelected: (v.FoodGroupsSelected as number[] | undefined) ?? [],
        ProductionAreasSelected: productionAreasSelected,
        TallyFilterString: '[Select Predefined Filters]',
        // PKey_UserPrintedBy — added in preSubmit above (WinForms
        // .vb:434 uses `CU_UserID`).
        //
        // Multi-facility (WinForms .vb:426-446 / 480-482). Every `*_Multiple`
        // list is INDEX-ALIGNED to lstFKey_Facility_Multiple — the child lists
        // carry no facility id, so position is the key. Null when the report is
        // not in multi-facility mode, which is what makes the server generator
        // take its single-facility path.
        ...multi,
      };
    },
    // Validation mirrors WinForms .vb:1599-1652 (7 checks).
    validate: (v) => {
      const numberOfDays = Number(v.NumberOfDays ?? 1);
      const mealtimes = (v.LstMealtimesPerDay as number[][] | undefined) ?? [];
      const filterIds = (v.LstFilters as number[] | undefined) ?? [];
      const multiFilter = filterIds.length >= 2;
      // Combined report type + multi-filter → block. WinForms .vb:1625.
      if (multiFilter && String(v.ReportType) === 'combined') {
        return 'Only one filter may be used when printing the Combined Summary.';
      }
      // Per-day mealtime check — skipped in multi-filter mode since
      // each filter carries its own Mealtimes (.vb:1612-1622).
      if (!multiFilter) {
        for (let i = 0; i < numberOfDays; i++) {
          if ((mealtimes[i]?.length ?? 0) === 0) return 'Please select a mealtime.';
        }
      }
      // ≥1 production area.
      const areas = (v.ProductionAreasSelected as number[] | undefined) ?? [];
      if (!multiFilter && areas.length === 0) return 'Please select at least one production area.';
      // Multi-facility: every checked facility must have its own selection.
      // Ports GetEmptySelectionCount (WinForms .vb:1682-1703), which reports
      // the shortfall per facility rather than globally — picking sets for one
      // facility and none for another must still block.
      const checkedFacilityIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
      if (checkedFacilityIds.length > 0) {
        const emptyFor = (raw: unknown) => {
          const map = (raw ?? {}) as Record<number, number[]>;
          return checkedFacilityIds.filter((id) => (map[id] ?? []).length === 0).length;
        };
        if (v.PrintMode === 'set' && emptyFor(v.SetList) > 0) {
          return 'One or more {Facility_Plural} do not have any {SetName_Plural} selected.  Either select at least one {SetName} or remove {Facility} from selected list.';
        }
        if (v.PrintMode === 'resident' && emptyFor(v.PatientList) > 0) {
          return 'One or more {Facility_Plural} do not have any {Resident_Plural} selected.  Either select at least one {Resident} or remove {Facility} from selected list.';
        }
        return null;
      }
      // Specific Set requires ≥1 set. Uses `{SetName}` token so
      // the facility label ("Cart" / "Set" / etc.) substitutes at
      // display time.
      if (v.PrintMode === 'set') {
        const sets = (v.SetList as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select a {SetName}.';
      }
      // Specific Resident requires ≥1 resident. Uses `{Resident}`
      // token so the facility label ("Patient" / "Resident")
      // substitutes at display time.
      if (v.PrintMode === 'resident') {
        const pts = (v.PatientList as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select a {Resident}.';
      }
      return null;
    },
  },
  {
    slug: 'production-generation',
    reportName: 'Production Generation',
    fKey_RS_Report: 0,
    fields: [
      { key: 'StartDate', group: 'Date', label: 'Start Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'NumberOfDays', group: 'Date', label: 'Number of Days', type: 'number', defaultValue: 1, min: 1, max: 7 },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
  },
  // ─── PRODUCTION - PREP/PULL (pKey 124, fKey 124) ───────────────
  // WinForms source: ucDietech_Report_Generator_Production_PrepPull.vb.
  // Minimal port — same Date + Menu Cycle layout as Production -
  // Grocery List (pKey 125) but WITHOUT the Print Exceptions
  // checkbox and WITHOUT the corporate branch (WinForms doesn't
  // switch on `IsCorporate` here at all).
  //
  // Init (.vb:83-113):
  //   TabPageText  = "Production - Prep/Pull"
  //   ucDeStart.DateInit = today (LabelVisible=False)
  //   ucDeEnd.DateInit   = today, DateMin = today,
  //                       DateMax = today + 9 days (10-day span cap)
  //                       LabelText = "To"
  //   ucMenuCycleInfo: `IsTrayTicket = True`, driven by StartDate
  //
  // Print (.vb:195-214) — 5-field `ProductionPrePullSettings`:
  //   .StartDate                = ucDeStart.EditValue
  //   .FKey_Menu_Header_Override = g_FKey_Menu_Hdr_Override
  //   .FKey_Facility            = FKey_Facility
  //   .NumberOfDays             = (End − Start).Days + 1
  //   .MenuName                 = nz(RowMenuHeader("Menu_Name"))
  //
  // Validate (.vb:331-337): `Return True` — WinForms performs NO
  // client-side checks at all.
  {
    slug: 'production-prep-pull',
    reportName: 'Production - Prep/Pull',
    fKey_RS_Report: 124,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    apiEndpoint: 'ProductionPrepPull',
    fields: [
      // Date row — no visible StartDate label (WinForms
      // LabelVisible=False .vb:94).
      { key: 'StartDate', label: '', type: 'date',
        defaultValue: () => today(),
        required: true, group: 'Date', width: 220 },

      // Container is 250 (vs Start's 220) so the actual DateBox
      // width matches Start after subtracting the inline "To"
      // label (~26px including padding). Max clamped to
      // Start + 9 days (10-day span cap, .vb:311). On StartDate
      // change: keep existing EndDate if it still lies inside
      // [Start, Start+9], otherwise snap to StartDate.
      { key: 'EndDate', label: 'To', type: 'date',
        required: true, group: 'Date', width: 250,
        recomputeWhen: ['StartDate'],
        maxOffsetDaysFrom: { fromField: 'StartDate', days: 9 },
        minOffsetDaysFrom: { fromField: 'StartDate', days: 0 },
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd) {
            const maxEnd = new Date(start);
            maxEnd.setDate(maxEnd.getDate() + 9);
            if (existingEnd >= start && existingEnd <= maxEnd) return existingEnd;
          }
          return start;
        } },

      // Trailing "(N Day)" / "(N Days)" indicator — mirrors WinForms
      // `SetDaysLabel` at .vb:321-329.
      { key: '_DaysBetween', label: '', type: 'daysBetween',
        defaultValue: null, group: 'Date',
        startField: 'StartDate', endField: 'EndDate' },

      // Menu Cycle info — display-only, driven by StartDate. WinForms
      // passes `IsTrayTicket = True` at .vb:106; the React display
      // hook doesn't yet distinguish tray-ticket vs default rendering
      // (menu name + week/day + MC day fields are the same).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },
    ],
    buildDateFor: (v) => formatDateOnlyRange(v.StartDate, v.EndDate, { collapseSameDay: true }),
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const parseLocal = (val: unknown): Date | null => {
        if (val instanceof Date) return new Date(val.getFullYear(), val.getMonth(), val.getDate());
        if (typeof val === 'string' && val) {
          const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        }
        return null;
      };
      const start = parseLocal(v.StartDate);
      const end = parseLocal(v.EndDate) ?? start;
      // `NumberOfDays = (End − Start).Days + 1` — WinForms .vb:206.
      const numberOfDays = start && end
        ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1)
        : 1;
      const startIso = parseIso(v.StartDate);
      const menuName = ctx?.resolveMenuName?.(startIso) ?? '';
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfDays: numberOfDays,
        MenuName: menuName,
        // WinForms reads `g_FKey_Menu_Hdr_Override` global; the DTO
        // default sentinel is -999999 — matches the typical case
        // where the global isn't set.
        FKey_Menu_Header_Override: -999999,
      };
    },
    validate: (v) => {
      // WinForms `ValidateForm` (.vb:331-337) always returns True.
      // Only client-side guard here is that both dates parse and
      // the range doesn't exceed the 10-day cap (which the picker
      // already enforces via `maxOffsetDaysFrom`, but a validate
      // catch guards against manually-typed values that slip past
      // the picker mask on some browsers).
      const parseLocal = (val: unknown): Date | null => {
        if (val instanceof Date) return new Date(val.getFullYear(), val.getMonth(), val.getDate());
        if (typeof val === 'string' && val) {
          const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        }
        return null;
      };
      const start = parseLocal(v.StartDate);
      const end = parseLocal(v.EndDate);
      if (!start || !end) return 'Please select a start and end date.';
      if (end < start) return 'End date must be on or after the start date.';
      const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
      if (days > 10) return 'Please select a range of 10 days or fewer.';
      return null;
    },
  },
  // ─── PRODUCTION - GROCERY LIST (pKey 125, fKey 125) ────────────
  // WinForms source: ucDietech_Report_Generator_Production_GroceryList.vb.
  //
  // Init (.vb:88-137):
  //   TabPageText  = "Production - Grocery List"
  //   ucDeStart.DateInit = today (no label; label hidden via
  //                       LabelVisible=False at .vb:101)
  //   ucDeEnd.DateInit   = today, DateMin = today,
  //                       DateMax = today + 9 days  (10-day span cap)
  //                       LabelText = "To"
  //   ucMenuCycleInfo: `IsTrayTicket = True`, driven by StartDate
  //   ucFacilityTree: only surfaced for corporate facilities
  //                   (`g_facilitySettings.IsCorporate` .vb:122).
  //   chkIncludeMP: only surfaced for corporate; caption formatted
  //                 as "Include {MP-short-label} and {Adj-short-label}"
  //                 from `MealPatternShortDescription` /
  //                 `AdjustmentsShortDescription` facility strings
  //                 (.vb:124-125).
  //   chkRaw / chkUseV1: Designer-hidden by default; only visible
  //                     after user clicks the arrow on ddpRaw
  //                     (.vb:412-415). Both default False.
  //   chkPrintExceptions: default False. Tooltip:
  //                     "Print a list of items and ingredients that
  //                     do not have sizes associated to them."
  //                     Interlock: disabled + force-unchecked when
  //                     chkRaw OR chkUseV1 is checked (.vb:436-448).
  //
  // Print (.vb:222-280) — settings shape `ProductionGroceryListSettings`:
  //   .StartDate                = ucDeStart.EditValue
  //   .FKey_Facility            = FKey_Facility
  //   .NumberOfDays             = (End - Start).Days + 1
  //   .PrintRaw                 = chkRaw.Checked
  //   .PrintExceptions          = chkPrintExceptions.Checked
  //   .UseV1                    = chkUseV1.Checked
  //   .FKey_Menu_Header_Override = g_FKey_Menu_Hdr_Override
  //   Corporate branch adds .IsCorporate, .IncludeMP,
  //     .LstFKey_Facility, .StrCorporateInclusion.
  //   Non-corporate branch sets .MenuName = current MC row's
  //     `Menu_Name` (.vb:273).
  //
  // Validate (.vb:387-402):
  //   1. Menu cycle row must exist → "No Menu Cycle Found." toast.
  //   2. Corporate: at least one facility node checked.
  //
  // React port scope:
  //   • Corporate mode (live bug 2026-10-22: a corporate facility sent
  //     IsCorporate false and printed its own, usually empty, data):
  //     the facility tree (`facilityTreeGate: 'corporate'`, every user
  //     facility, all checked), Include MP, and the corporate wire
  //     shape. The Menu Cycle line hides, as lciMenu does (.vb:127).
  //     StrCorporateInclusion is the checked names joined with ", "
  //     (String_Comma_Separated); the UC's `_useDivisions` is never set,
  //     so the division wording branch (.vb:250-266) never runs.
  //     NOTE the generator: with Include MP off it takes
  //     PrintQuickCorporate (ProductionGroceryList.vb:41), which never
  //     returns its report, so that path prints "no data" on the desktop
  //     too. Include MP on runs the per-facility Print().
  //   • Advanced Raw / UseV1 toggles are hidden (Designer-hidden in
  //     WinForms too). Both send as `False` at wire time.
  //   • 10-day span cap enforced client-side by `EndDate` recompute
  //     + a `validate()` guard.
  {
    slug: 'production-grocery-list',
    reportName: 'Production - Grocery List',
    fKey_RS_Report: 125,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    apiEndpoint: 'ProductionGroceryList',
    fields: [
      // Corporate facility tree (.vb:119-120, lcgCorporate .vb:127-128).
      { key: 'LstFacilityMultiple', label: '', type: 'facilityTree',
        facilityTreeGate: 'corporate',
        defaultValue: [] as number[],
        visibleWhen: { check: () => isCorporateFacility() },
        group: '{Facility_Plural}' },

      // Date row — no visible label on StartDate (WinForms hides it
      // with LabelVisible=False .vb:101).
      { key: 'StartDate', label: '', type: 'date',
        defaultValue: () => today(),
        required: true, group: 'Date', width: 220 },

      // End date defaults to today, and to the same day as StartDate
      // whenever StartDate changes. Max span is StartDate + 9 days
      // (10-day cap) — WinForms .vb:373-376 sets
      // `ucDeEnd.DateMax = editValue.AddDays(9)`. If the user has
      // an EndDate within the new [Start, Start+9] window, keep it;
      // otherwise clamp back to StartDate. `maxOffsetDaysFrom`
      // restricts the DateBox picker itself so the user can't type
      // or click past Start + 9 (matches WinForms's `DateMax`).
      // Container is 250 (vs Start's 220) so the actual DateBox
      // width matches Start after subtracting the inline "To" label
      // (~26px including padding).
      { key: 'EndDate', label: 'To', type: 'date',
        required: true, group: 'Date', width: 250,
        recomputeWhen: ['StartDate'],
        maxOffsetDaysFrom: { fromField: 'StartDate', days: 9 },
        minOffsetDaysFrom: { fromField: 'StartDate', days: 0 },
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd) {
            const maxEnd = new Date(start);
            maxEnd.setDate(maxEnd.getDate() + 9);
            if (existingEnd >= start && existingEnd <= maxEnd) return existingEnd;
          }
          return start;
        } },

      // Trailing "(N Day)" / "(N Days)" indicator — mirrors WinForms
      // `SetDaysLabel` (.vb:417-425). Read-only; recomputes when
      // either date changes.
      { key: '_DaysBetween', label: '', type: 'daysBetween',
        defaultValue: null, group: 'Date',
        startField: 'StartDate', endField: 'EndDate' },

      // Menu Cycle info — display-only, driven by StartDate. WinForms
      // passes `IsTrayTicket = True` at .vb:113; the React display
      // hook doesn't yet distinguish tray-ticket vs default rendering
      // (menu name + week/day + MC day fields are the same).
      // Hidden for a corporate facility (lciMenu, .vb:127).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate',
        visibleWhen: { check: () => !isCorporateFacility() } },

      // Include MP - corporate only, unchecked by default (Designer.vb:180).
      // Caption and tooltip from .vb:124-125.
      { key: 'IncludeMP', group: 'Options',
        label: 'Include {MealPatternShortDescription} and {AdjustmentsShortDescription}',
        type: 'checkbox', defaultValue: false,
        visibleWhen: { check: () => isCorporateFacility() },
        tooltip: 'If checked, when generating this grocery simulate running full tray tickets that take into consideration {MealPatternShortDescription} and {AdjustmentsShortDescription}. If unchecked, the system will use the diet census along with buffers to determine the appropriate amount. This is a faster calculation.' },

      // Print Exceptions checkbox. Tooltip verbatim from Designer.vb:150.
      // WinForms disables + force-unchecks this when either Raw or
      // UseV1 is checked — since neither of those are surfaced in
      // the React UI yet, the interlock is a no-op here.
      { key: 'PrintExceptions', group: 'Options', label: 'Print Exceptions', type: 'checkbox',
        defaultValue: false,
        tooltip: 'Print a list of items and ingredients that do not have sizes associated to them.' },
    ],
    buildDateFor: (v) => formatDateOnlyRange(v.StartDate, v.EndDate, { collapseSameDay: true }),
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const parseLocal = (v: unknown): Date | null => {
        if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
        if (typeof v === 'string' && v) {
          const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        }
        return null;
      };
      const start = parseLocal(v.StartDate);
      const end = parseLocal(v.EndDate) ?? start;
      // `NumberOfDays = (End - Start).Days + 1` — WinForms .vb:232.
      const numberOfDays = start && end
        ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1)
        : 1;
      const startIso = parseIso(v.StartDate);
      const common = {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfDays: numberOfDays,
        // Advanced toggles — hidden in the React UI, both false.
        PrintRaw: false,
        UseV1: false,
        PrintExceptions: Boolean(v.PrintExceptions),
        // Menu Header override — WinForms uses a global
        // `g_FKey_Menu_Hdr_Override` that's rarely set in normal
        // flow; the DTO defaults to -999999 (see the .vb settings
        // class) so we forward that sentinel.
        FKey_Menu_Header_Override: -999999,
      };
      if (isCorporateFacility(_fs)) {
        // Corporate branch - WinForms .vb:236-271. The checked facilities, in
        // tree order; the root (this facility) can't be unchecked, so the list
        // is never empty. A corporate user with no other facility gets no tree
        // (it has nothing to offer) and sends this facility alone, as the
        // desktop's root-only tree does.
        const own = useGlobalStore.getState();
        const checked = (v.LstFacilityMultiple as number[] | undefined) ?? [];
        const ids = checked.length > 0 ? checked : [own.facilityId];
        const names = ctx?.resolveMultiFacility?.(ids, undefined).names ?? [];
        return {
          ...common,
          IsCorporate: true,
          IncludeMP: Boolean(v.IncludeMP),
          LstFKey_Facility: ids,
          StrCorporateInclusion: ids
            .map((id, i) => names[i] || (id === own.facilityId ? own.facilityName ?? '' : ''))
            .filter(Boolean)
            .join(', '),
        };
      }
      return {
        ...common,
        // Non-corporate branch - WinForms .vb:272-274.
        MenuName: ctx?.resolveMenuName?.(startIso) ?? '',
        IsCorporate: false,
        IncludeMP: false,
        LstFKey_Facility: [] as number[],
        StrCorporateInclusion: '',
      };
    },
    validate: (v) => {
      const parseLocal = (val: unknown): Date | null => {
        if (val instanceof Date) return new Date(val.getFullYear(), val.getMonth(), val.getDate());
        if (typeof val === 'string' && val) {
          const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        }
        return null;
      };
      const start = parseLocal(v.StartDate);
      const end = parseLocal(v.EndDate);
      if (!start || !end) return 'Please select a start and end date.';
      if (end < start) return 'End date must be on or after the start date.';
      const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
      // 10-day cap matches WinForms `ucDeEnd.DateMax = start + 9`
      // (.vb:376). Anything past 10 days is a client-side error.
      if (days > 10) return 'Please select a range of 10 days or fewer.';
      return null;
    },
  },
  // ─── EMPLOYEE — PRODUCTION TALLY (pKey 130, fKey 130) ──────────
  // WinForms source: ucDietech_Report_Generator_Employee_ProductionTally.vb.
  // Print (.vb:351-374):
  //   .FKey_Facility     = FKey_Facility
  //   .NumberOfDays      = ucNumberOfDays.EditValue
  //   .StartDate         = ucDeStart.EditValue
  //   .LstMealtimes      = ucMealltimesTab.GetCheckedMealtimes
  //                        (List<int[]> — ONE int[] per day; each
  //                        entry is the mealtime selection for that
  //                        day. Sub-control `TabbedClbMealtime` at
  //                        `ucDietech_Report_Generator_TabbedClbMealtime.vb:146-154`
  //                        iterates its tabs and pulls each tab's
  //                        checked mealtime PKeys.)
  //   .PKey_Menu_Header  = ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header
  //                        (overridden by facility
  //                        `FKey_Menu_Header_Employee` when set —
  //                        WinForms .vb:124-126)
  //   report class       = EmployeeProductionTally (server pKey 130)
  //   detailedReportName = "Employee Production Tally"
  //
  // Init (.vb:100-140):
  //   ucDeStart: no label, DateInit = today
  //   ucNumberOfDays: "Number of Days" label, min 1, max 9,
  //                   default 1 (Designer)
  //   ucMenuCycleInfo: date-driven, overridden by
  //                    `FKey_Menu_Header_Employee` facility setting
  //                    (Employee Menu picker at .vb:124-126)
  //   ucMealltimesTab: tabbed clb — one tab per day. React port
  //                    below simplifies to a SINGLE mealtime
  //                    selection replicated across every day.
  //                    Per-day mealtime override isn't ported;
  //                    revisit if any facility needs it.
  //   `IncludeNourishments = False` — only main mealtimes
  //                    (Breakfast/Lunch/Dinner).
  //
  // Validate (.vb:441-454):
  //   1. `UseProduction` + invalid menu day → "select valid menu
  //      cycle" toast (skipped in React — the menuCycleInfo
  //      display shows the invalid-day state instead).
  //   2. `Prd_DTCollection.IsMenuCategoryOrderEmtpy(1)` — skipped
  //      (facility-settings-side data prep check).
  {
    slug: 'employee-production-tally',
    reportName: 'Employee Production Tally',
    fKey_RS_Report: 130,
    apiEndpoint: 'EmployeeProductionTally',
    fields: [
      // Date — single date, defaults to today (.vb:110).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => today() },

      // Number of Days — flanking chevron spin. Range 1..9,
      // default 1 (.vb:114-119). Same width pair as Employee -
      // Charge Summary.
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 9, required: true,
        group: 'Date', width: 220 },

      // Menu Cycle info — read-only, driven by StartDate. The
      // Employee report scopes the lookup to the facility's
      // `FKey_Menu_Header_Employee` (Employee Menu) instead of
      // the default active resident cycle — matches WinForms
      // `.FKey_Menu_Header_Override = ...` at .vb:124-126.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate',
        overrideFacilitySettingKey: 'fKey_Menu_Header_Employee' },

      // Mealtimes — per-day tabbed selector (WinForms
      // `ucMealltimesTab.GetCheckedMealtimes` returns
      // `List<int[]>`, one array per day). Tab count follows
      // NumberOfDays. When NumberOfDays=1 the tab strip hides
      // and it reads as a plain vertical checkbox list, matching
      // the screenshot layout. Values are 0-based Get_MealNames
      // PKeys (Breakfast=0, Lunch=1, Dinner=2) — same
      // `IncludeNourishments = False` ClbMealtime encoding Dish
      // Up Labels uses.
      { key: 'LstMealtimesPerDay', label: '', type: 'mealtimesPerDay',
        defaultValue: [[]] as number[][],
        group: 'Selection', tab: 'Mealtimes',
        watchGroup: { start: 'StartDate', days: 'NumberOfDays' },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Mirrors WinForms Print block (.vb:354-362). The Employee
    // Menu override (facility `FKey_Menu_Header_Employee`) is
    // passed to `resolvePkeyMenuHeader` so it taps the same
    // cache entry the sibling `menuCycleInfo` display populated.
    buildSettings: (v, fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const rawOverride = (fs as { fKey_Menu_Header_Employee?: number | null } | null)?.fKey_Menu_Header_Employee;
      const employeeMenuOverride = typeof rawOverride === 'number' && rawOverride > 0
        ? rawOverride
        : null;
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso, employeeMenuOverride) ?? 0;
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      // `LstMealtimesPerDay` is the reshaped per-day tabbed
      // selection — value shape matches WinForms `List<int[]>`.
      const perDay = ((v.LstMealtimesPerDay as number[][] | undefined) ?? []);
      // Pad / truncate to match the current NumberOfDays. Empty
      // days (out of range) send an empty selection for that day.
      const lstMealtimes: number[][] = Array.from({ length: numDays }, (_, i) => (
        Array.isArray(perDay[i]) ? [...perDay[i]] : []
      ));
      return {
        StartDate: v.StartDate,
        NumberOfDays: numDays,
        LstMealtimes: lstMealtimes,
        PKey_Menu_Header: pKeyMenuHeader,
      };
    },
    // WinForms ValidateForm arms 1 + 2 are skipped in the React
    // port (see block comment above). Enforce ≥1 mealtime PER
    // DAY so no day would print without a meal — matches the
    // per-day tabbed WinForms UX intent.
    validate: (v) => {
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      const perDay = ((v.LstMealtimesPerDay as number[][] | undefined) ?? []);
      for (let i = 0; i < numDays; i++) {
        const day = perDay[i];
        if (!Array.isArray(day) || day.length === 0) {
          return numDays === 1
            ? 'Please select at least one mealtime.'
            : `Please select at least one mealtime for Day ${i + 1}.`;
        }
      }
      return null;
    },
  },
  // ─── EMPLOYEE — TRAY TICKETS (pKey 132, fKey 132) ──────────────
  // WinForms source: ucDietech_Report_Generator_Employee_TrayTickets.vb.
  // Print (.vb:325-362):
  //   .FKey_Facility     = FKey_Facility
  //   .NumberOfDays      = ucNumberOfDays.EditValue
  //   .StartDate         = ucDeStart.EditValue
  //   .LstMealtimes      = ucMealltimesTab.GetCheckedMealtimes
  //                        (List<int[]> — one array per day)
  //   .PKey_Menu_Header  = ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header
  //                        (Employee Menu override — .vb:115-117)
  //   .LstFKey_Employee  = grid selection → PKey_Employee list
  //                        (only when radPrintEmployee.Checked;
  //                        radPrintAll leaves the property unset,
  //                        server treats null as "all")
  //   report class       = EmployeeTrayTickets (server pKey 132)
  //   detailedReportName = "Employee Tray Tickets"
  //
  // Init (.vb:91-133):
  //   ucDeStart: no label, DateInit = today
  //   ucNumberOfDays: "Number of Days", min 1, max 9, default 1
  //   ucMenuCycleInfo: date-driven, Employee Menu override
  //   ucMealltimesTab: tabbed per-day mealtime picker
  //                    (IncludeNourishments = False → main mealtimes)
  //   Employee grid populated up-front via `Fill_dt()` at .vb:447
  //   radPrintAll default checked (Designer); Employees pane
  //   hidden until radPrintEmployee is picked.
  //
  // Validate (.vb:428-442): same 2 arms as pKey 130 — both skipped
  // in React (facility-settings-side data checks).
  {
    slug: 'employee-tray-tickets',
    reportName: 'Employee Tray Tickets',
    fKey_RS_Report: 132,
    apiEndpoint: 'EmployeeTrayTickets',
    fields: [
      // Date — single date, defaults to today (.vb:101).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => today() },

      // Number of Days — flanking chevron spin. Range 1..9,
      // default 1 (.vb:105-110). Same width pair as pKey 130.
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 9, required: true,
        group: 'Date', width: 220 },

      // Menu Cycle info — Employee Menu override, driven by
      // StartDate. Same wiring as pKey 130.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate',
        overrideFacilitySettingKey: 'fKey_Menu_Header_Employee' },

      // Print scope radio — All / Employee. Default 'all' per
      // WinForms radPrintAll.EditValue = True (Designer).
      // WinForms label is just "Employee" (screenshot).
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'specific', label: 'Employee' },
        ] },

      // Per-day mealtime picker — tabs follow NumberOfDays.
      { key: 'LstMealtimesPerDay', label: '', type: 'mealtimesPerDay',
        defaultValue: [[]] as number[][],
        group: 'Selection', tab: 'Mealtimes',
        watchGroup: { start: 'StartDate', days: 'NumberOfDays' },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Employees side grid — visible only when Employee radio
      // is picked. `showIdColumn` adds the ID column per the
      // WinForms grid layout (`SQL_tblEmployee_Select` at .vb:447-458).
      { key: 'LstFKey_Employee', label: '', type: 'employeeSelect',
        defaultValue: [],
        group: 'Employees',
        visibleWhen: { key: 'PrintMode', value: 'specific' },
        showIdColumn: true },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Mirrors WinForms Print block (.vb:332-350).
    buildSettings: (v, fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const rawOverride = (fs as { fKey_Menu_Header_Employee?: number | null } | null)?.fKey_Menu_Header_Employee;
      const employeeMenuOverride = typeof rawOverride === 'number' && rawOverride > 0 ? rawOverride : null;
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso, employeeMenuOverride) ?? 0;
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      const perDay = ((v.LstMealtimesPerDay as number[][] | undefined) ?? []);
      const lstMealtimes: number[][] = Array.from({ length: numDays }, (_, i) => (
        Array.isArray(perDay[i]) ? [...perDay[i]] : []
      ));
      return {
        StartDate: v.StartDate,
        NumberOfDays: numDays,
        LstMealtimes: lstMealtimes,
        PKey_Menu_Header: pKeyMenuHeader,
        LstFKey_Employee: v.PrintMode === 'specific' ? (v.LstFKey_Employee ?? []) : null,
      };
    },
    // Mirrors ValidateForm intent. ≥1 mealtime per day, plus ≥1
    // employee when the Employee radio is picked (WinForms
    // .vb:327-329 inlines this in Print but treats it as a
    // pre-print warning).
    validate: (v) => {
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      const perDay = ((v.LstMealtimesPerDay as number[][] | undefined) ?? []);
      for (let i = 0; i < numDays; i++) {
        const day = perDay[i];
        if (!Array.isArray(day) || day.length === 0) {
          return numDays === 1
            ? 'Please select at least one mealtime.'
            : `Please select at least one mealtime for Day ${i + 1}.`;
        }
      }
      if (v.PrintMode === 'specific'
          && (!Array.isArray(v.LstFKey_Employee) || v.LstFKey_Employee.length === 0)) {
        return 'Please select at least one employee.';
      }
      return null;
    },
  },
  // ─── EMPLOYEE — CHARGE SUMMARY (pKey 138, fKey 138) ────────────
  // WinForms source: ucDietech_Report_Generator_EmployeeCharges.vb.
  // Print (.vb:216-242):
  //   .FKey_Facility     = _FKey_Facility
  //   .DateFor           = ucDeStart.EditValue.Date
  //   .NumberOfDays      = ucNumberOfDays.EditValue
  //   .LstFKey_Employee  = grid.GetSelectedRows → PKey_Employee list
  //                        (only when radPrintEmployee is checked;
  //                        when radPrintAll is checked, the property
  //                        is NOT SET and the server treats it as
  //                        "all employees")
  //   .PrintDetails      = chkPrintDetails.Checked
  //   report class       = EmployeeChargeSummary (server pKey 138)
  //   detailedReportName = "Employee - Charge Summary"
  //
  // Init (.vb:81-107):
  //   ucDeStart: no label, DateInit = Now - 13 days,
  //              DateMin = Now - 1 year
  //   ucNumberOfDays: "Number of Days" label, min 1, max 31,
  //                   default 14
  //   radPrintAll default checked (Designer)
  //   chkPrintDetails unchecked default — label "Print Item Details"
  //   Employee grid populated only when Specific Employee radio
  //   is picked (lazy `Fill_dtEmployee` at .vb:272)
  //
  // Validate (.vb:294-308): if radPrintEmployee.Checked and grid
  // selection empty → `dmSelect("employee")`.
  {
    slug: 'employee-charge-summary',
    reportName: 'Employee - Charge Summary',
    fKey_RS_Report: 138,
    apiEndpoint: 'EmployeeChargeSummary',
    fields: [
      // Date — single date, defaults to today - 13 (.vb:91).
      // `width: 250` clamps the DateBox spin so NumberOfDays can
      // sit inline to its right in the same Date group. Keep it
      // >= 250 so the MM/dd/yyyy display doesn't get cramped
      // between the chevron spin buttons.
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => {
          const d = new Date();
          d.setDate(d.getDate() - 13);
          return toLocalDateString(d);
        } },

      // Number of Days — inline NumberBox with spin buttons to
      // the right of the Date field. Range 1..31, default 14
      // (.vb:96-102). `width: 220` accommodates the "Number of
      // Days" label + a 3-digit spin box + the spin chevrons.
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 14, min: 1, max: 31, required: true,
        group: 'Date', width: 220 },

      // Print scope radio — All / Specific Employee. Default 'all'
      // per WinForms radPrintAll.EditValue = True (Designer).
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'specific', label: 'Specific Employee' },
        ] },

      // Options — "Print Item Details" (.Designer label for
      // chkPrintDetails). Unchecked default.
      { key: 'PrintDetails', label: 'Print Item Details', type: 'checkbox',
        defaultValue: false, group: 'Options' },

      // Employees grid — visible only when Specific Employee is
      // picked. WinForms lazy-populates on the radio toggle
      // (.vb:272); React always loads via useRefDataList and just
      // hides the grid.
      { key: 'LstFKey_Employee', label: '', type: 'employeeSelect',
        defaultValue: [],
        group: 'Employees',
        inline: true,
        visibleWhen: { key: 'PrintMode', value: 'specific' } },
    ],
    buildDateFor: (v) => formatDayCountRange(v.DateFor, v.NumberOfDays),
    // Mirrors WinForms Print settings block (.vb:220-236).
    //   - `LstFKey_Employee` sent as `null` when All is picked so
    //     the server hits its "print every employee" branch —
    //     matches WinForms which doesn't set the property in All
    //     mode.
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      NumberOfDays: v.NumberOfDays,
      LstFKey_Employee: v.PrintMode === 'specific' ? (v.LstFKey_Employee ?? []) : null,
      PrintDetails: v.PrintDetails,
    }),
    // Mirrors ValidateForm (.vb:294-308).
    validate: (v) => {
      if (v.PrintMode === 'specific'
          && (!Array.isArray(v.LstFKey_Employee) || v.LstFKey_Employee.length === 0)) {
        return 'Please select at least one employee.';
      }
      return null;
    },
  },

  // ─── POSTING - DAILY MENU - FULL DAY (pKey 8, fKey 8) ──────────
  // WinForms source: ucDietech_Report_Generator_Posting_DailyMenuFullDay.vb.
  // Very close to the Posting - Daily config above; differences:
  //   - Alternates is a 2-option radio (Do Not Print / Print),
  //     wire field is `PrintAlternates: boolean`. No AlternateType
  //     enum.
  //   - Adds a Paper Size radio (Letter / Legal), wire field
  //     `IsLegal: boolean`. Default from facility
  //     `PostingMenu_PaperSize` (.vb:95-100: 0=Letter, 1=Legal).
  //   - NumberOfDays MaxValue is `MenuCycleInUseInfo.NumberOfDaysInCycle`
  //     at Init time (.vb:125); React port caps at 14 as a
  //     safeguard — the server rejects anything past the cycle.
  //
  // Init (.vb:82-138) — same StartDate default as Daily via
  // Start_Day_Posting_Menu / Date_getFirstDayOfWeek. Mealtimes
  // list picks up nourishments when facility
  // MenuCycle_ShowNourishments is true.
  //
  // Print (.vb:267-296) — settings shape
  // `PostingMenuDailyFullDaySettings`:
  //   FKey_Facility, StartDate, NumberOfDays,
  //   Mealtimes (bit-flag Mealtime enum via GetMealTimeValues),
  //   MealtimesInt (int[]),
  //   PKey_Menu_Header, IsLegal, IncludeSMOnlyItems,
  //   PrintAlternates, PrintAS, ASCount (999999 when cmb index=0),
  //   ShowDates
  //
  // Validate (.vb:342-361): ≥1 mealtime + valid menu cycle day +
  // menu category order defined for pMenu 8. React catches the
  // trivial mealtime check.
  {
    slug: 'posting-menu-daily-full-day',
    reportName: 'Posting - Daily Menu - Full Day',
    fKey_RS_Report: 8,
    apiEndpoint: 'PostingMenuDailyFullDay',
    groupLayout: POSTING_MENU_GROUP_LAYOUT,
    fields: [
      // Date + Number of Days on one row. StartDate default logic
      // is identical to Posting - Daily (facility
      // Start_Day_Posting_Menu → today or next occurrence).
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 14, width: 240, group: 'Date' },

      // Menu Cycle info — display-only, tied to StartDate; feeds
      // PKey_Menu_Header via resolvePkeyMenuHeader at submit time.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — 7 items (B/L/D + 4 nourishments) flowing
      // column-major (vertical then horizontal) per the mockup.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes', layout: 'twoColumn',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — two-option radio (differs from Posting -
      // Daily which has three). Wire field is `PrintAlternates:
      // boolean`, false when Do Not Print / true when Print.
      // Default = Print (matches supplied UI mockup).
      { key: 'PrintAlternates', label: '', type: 'radio',
        defaultValue: true, group: 'Alternates',
        options: [
          { value: false, label: 'Do Not Print' },
          { value: true, label: 'Print' },
        ] },

      // Print section — mirrors Posting - Daily layout: AS + count
      // inline, then Include SM Only + Show Dates on line 2.
      { key: 'PrintAS', label: 'AS', type: 'checkbox',
        defaultValue: false, group: 'Print' },
      { key: 'ASCount', label: '', type: 'number',
        defaultValue: 2, min: 0, max: 99, width: 110, group: 'Print',
        enabledWhen: { key: 'PrintAS', value: true } },
      { key: 'IncludeSMOnlyItems', label: 'Include Selective Menu Only',
        type: 'checkbox', defaultValue: false, group: 'Print', newRow: true },
      { key: 'ShowDates', label: 'Show Dates', type: 'checkbox',
        defaultValue: true, group: 'Print', enabledWhen: notPosterDesign,
        disabledReason: "Today's menu poster always prints the date." },

      // Paper Size — two-option radio. Wire field `IsLegal:
      // boolean`. Default reads from facility
      // `postingMenu_PaperSize` (0=Letter → false, 1=Legal → true)
      // per .vb:95-100.
      { key: 'IsLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1;
        },
        group: 'Paper Size', enabledWhen: notPosterDesign,
        disabledReason: "Today's menu poster prints on letter paper.",
        options: [
          { value: false, label: 'Letter (8.5x11)' },
          { value: true, label: 'Legal (8.5x14)' },
        ] },
      ...POSTING_DESIGN_FIELDS,
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Mirrors WinForms Print settings block (.vb:267-291).
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum — same mapping as Posting - Daily
      // (Diet_Reports.vb:3006-3029): 0→1, 1→2, 2→4, 3→8, 4→16,
      // 5→32, 6→64.
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );
      const printAS = Boolean(v.PrintAS);
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfDays: Number(v.NumberOfDays ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,
        IsLegal: Boolean(v.IsLegal),
        IncludeSMOnlyItems: Boolean(v.IncludeSMOnlyItems),
        PrintAlternates: Boolean(v.PrintAlternates),
        PrintAS: printAS,
        // WinForms sets ASCount to 999999 when cmbAlwaysServedNumber
        // SelectedIndex = 0 (.vb:282-285). We forward the raw value
        // when PrintAS is on, else 0.
        ASCount: printAS ? Number(v.ASCount ?? 0) : 0,
        ShowDates: Boolean(v.ShowDates),
        ...postingDesignSettings(v),
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      return null;
    },
  },
  // ─── POSTING - DAILY MENU (pKey 13, fKey 13) ───────────────────
  // WinForms source: ucDietech_Report_Generator_Posting_DailyMenu.vb.
  //
  // Init (.vb:83-141):
  //   TabPageText  = "Posting - Daily Menu"
  //   StartDate    = today when today.DayOfWeek matches
  //     `g_facilitySettings.Start_Day_Posting_Menu(FKey_Facility)`
  //     else next week's start day (Date_getFirstDayOfWeek(today+7)).
  //     React port keeps it simple — defaults to today.
  //   NumberOfDays: MinValue = 1, MaxValue = 7 (default 1).
  //   MenuCycleInfo: display-only, driven by StartDate — resolves
  //     PKey_Menu_Header for the wire payload.
  //   Mealtimes: `ucClbMealtimes` shows B/L/D + EAM/AM/PM/HS when
  //     facility MenuCycle_ShowNourishments = true. Values 0..6
  //     match WinForms `GetMealTimeValues` at Diet_Reports.vb:3006-3029
  //     (0→Breakfast, 1→Lunch, 2→Dinner, 3→EAM, 4→AM, 5→PM, 6→HS).
  //     Default (per _lstMealtimesInit): empty (nothing pre-checked).
  //   AlternateType: default from facility `Posting_Alternates_Default`
  //     (0=NotIncluded, 1=Italicized, 2=SeparateGroup). React port
  //     defaults to 2 (Separate Group) — matches supplied UI mockup.
  //   Print section:
  //     PrintAS (chkOptions_AlwaysServed) — default unchecked.
  //     ASCount (cmbAlwaysServedNumber) — combo default 2. Only
  //       enabled when PrintAS is on.
  //     IncludeSMOnlyItems (chkIncludeSelectiveMenuOnly) — default off.
  //     ShowDates (chkShowDates) — default checked per mockup.
  //
  // Print (.vb:271-308) — settings shape `PostingMenuDailySettings`:
  //   FKey_Facility, StartDate, NumberOfDays,
  //   Mealtimes (bit-flag enum via GetMealTimeValues),
  //   MealtimesInt (int[]),
  //   PKey_Menu_Header, IncludeSMOnlyItems,
  //   AlternateType (enum 0/1/2),
  //   PrintAS, ASCount, ShowDates
  //
  // Validate (.vb:361-385): ≥1 mealtime; valid menu cycle day;
  // house diet type defined (server-side). React validates the
  // client-side checks; server enforces the rest.
  {
    slug: 'posting-menu-daily',
    reportName: 'Posting - Daily Menu',
    fKey_RS_Report: 13,
    apiEndpoint: 'PostingMenuDaily',
    groupLayout: POSTING_MENU_GROUP_LAYOUT,
    fields: [
      // Date + Number of Days on one row (matches WinForms
      // designer layout at .vb:98-121). Number-of-days spin is
      // capped at 7 per ucNumberOfDays.MaxValue.
      //
      // StartDate default — mirrors WinForms Init at .vb:100-105.
      // Uses today when today's DayOfWeek matches the facility's
      // `Start_Day_Posting_Menu` setting; otherwise rolls forward
      // to the next occurrence of that day-of-week, matching
      // `Date_getFirstDayOfWeek(today + 7, dayOfWeek)` at
      // modUtilsToRename.vb:28-31 (the "most recent occurrence of
      // dayOfWeek on or before today+7", which equals the next
      // occurrence after today when today ≠ dayOfWeek).
      { key: 'StartDate', label: '', type: 'date',
        // `width` clamps the date-spin container so NumberOfDays
        // can render inline to its right in the same group row —
        // same pattern as Employee - Charge Summary's Date + Number
        // of Days pair.
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 7, width: 240, group: 'Date' },

      // Menu Cycle info — display-only, updates when StartDate
      // changes. Populates the React Query cache read by
      // `resolvePkeyMenuHeader` at submit time (.vb:280
      // `.PKey_Menu_Header = ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header`).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — B/L/D + EAM/AM/PM/HS. Server maps ints via
      // Diet_Reports.GetMealTimeValues (.vb:3006-3029) to the
      // Mealtime bit-flag enum. Facility meal-name tokens
      // ({Breakfast}/{Lunch}/{Dinner}) resolve to the facility's
      // configured meal labels. Nourishment names come from
      // {NourishEAM}/{NourishAM}/{NourishPM}/{NourishHS} tokens.
      // `layout: 'twoColumn'` flows the 7 items column-major —
      // left col: Breakfast/Lunch/Dinner/EAM (indexes 0-3), right
      // col: AM/PM/HS (indexes 4-6). Matches the supplied UI mockup
      // which stacks the four main meals on the left and the three
      // nourishment times on the right.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes', layout: 'twoColumn',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — one-of-three radio. Values match
      // `AlternateType` enum at EnumerationsConstantsCollections.vb:331
      // (NotIncluded=0, Italicized=1, SeparateGroup=2).
      { key: 'AlternateType', label: '', type: 'radio',
        defaultValue: 2, group: 'Alternates',
        options: [
          { value: 0, label: 'Do Not Print' },
          { value: 1, label: 'Italicized' },
          { value: 2, label: 'Separate Group' },
        ] },

      // Print section.
      { key: 'PrintAS', label: 'AS', type: 'checkbox',
        defaultValue: false, group: 'Print' },
      // ASCount is only meaningful when PrintAS is on. WinForms
      // shows a combo dropdown; React port uses a number spin
      // (default 2 matches the supplied UI). `enabledWhen` mirrors
      // `chkOptions_AlwaysServed_CheckedChanged` at .vb:315-318.
      // `width` clamps the number-inline wrapper so the AS checkbox
      // to the left of it and the other Print-group checkboxes to
      // the right stay on the same flex row.
      { key: 'ASCount', label: '', type: 'number',
        defaultValue: 2, min: 0, max: 99, width: 110, group: 'Print',
        enabledWhen: { key: 'PrintAS', value: true } },
      // `newRow: true` breaks the Print group's flex row here so
      // IncludeSMOnlyItems + ShowDates sit on line 2, below the
      // AS checkbox + count spin pair on line 1.
      { key: 'IncludeSMOnlyItems', label: 'Include Selective Menu Only',
        type: 'checkbox', defaultValue: false, group: 'Print', newRow: true },
      { key: 'ShowDates', label: 'Show Dates', type: 'checkbox',
        defaultValue: true, group: 'Print', enabledWhen: notPosterDesign,
        disabledReason: "Today's menu poster always prints the date." },
      ...POSTING_DESIGN_FIELDS,
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Mirrors WinForms Print settings block (.vb:271-302).
    buildSettings: (v, _fs, ctx) => {
      // Resolve PKey_Menu_Header via the React Query cache the
      // menuCycleInfo field populated when StartDate changed.
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum from int[] — mirrors
      // Diet_Reports.GetMealTimeValues (Diet_Reports.vb:3006-3029).
      //   0→Breakfast(1), 1→Lunch(2), 2→Dinner(4),
      //   3→EAM(8), 4→AM(16), 5→PM(32), 6→HS(64)
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );
      const printAS = Boolean(v.PrintAS);
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfDays: Number(v.NumberOfDays ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,
        IncludeSMOnlyItems: Boolean(v.IncludeSMOnlyItems),
        AlternateType: Number(v.AlternateType ?? 2),
        PrintAS: printAS,
        // WinForms sets ASCount=0 when PrintAS is off (.vb:297-301).
        ASCount: printAS ? Number(v.ASCount ?? 0) : 0,
        ShowDates: Boolean(v.ShowDates),
        ...postingDesignSettings(v),
      };
    },
    // Mirrors ValidateForm (.vb:361-385). Server enforces menu-day
    // and house-diet-type validity; React catches the trivial
    // "must select a mealtime" check up front.
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      return null;
    },
  },
  // ─── POSTING - EXTENSION SHEET (pKey 9, fKey 9) ────────────────
  // WinForms source: ucDietech_Report_Generator_Posting_ExtensionSheets.vb.
  //
  // Init (.vb:102-201):
  //   TabPageText  = "Posting - Extension Sheet"
  //   StartDate    = same facility Start_Day_Posting_Menu roll-forward
  //     logic as Posting - Daily / Diet Specific — today when today's
  //     DayOfWeek matches, else next occurrence of that day-of-week.
  //   NumberOfDays: MinValue = 1, MaxValue = 7 (default 1).
  //   MenuCycleInfo: display-only, driven by StartDate; resolves
  //     PKey_Menu_Header for the wire payload.
  //   Mealtimes: 7-item list (B/L/D + EAM/AM/PM/HS) when facility
  //     `MenuCycle_ShowNourishments` = true. Default: empty.
  //   PaperSize: default from facility `PostingMenu_PaperSize`
  //     (0=Letter, 1=Legal).
  //   AlternateType: default from `Posting_Alternates_Default`
  //     (0=NotIncluded, 1=Italicized, 2=Separate → radAlternates_Italicized
  //     falls through to Italicized). WinForms treats 0 as Do Not Print
  //     and 1|2 as Italicized. React port exposes a 3-option radio
  //     (Do Not Print / Italicized / Only) per the mockup — Only
  //     drives `PrintAlternatesOnly` in the wire payload.
  //   PrintGarnish default from `ExtensionSheet_PrintGarnish`.
  //   PrintNotes default from `ExtensionSheet_PrintNotes`.
  //   Signature block hidden unless `ExtensionSheet_ShowSignature`.
  //   Diet Types + Consistencies pulled from menu-cycle-scoped SQL
  //     (`SQL_Diet_Type_Select_MenuCycle` / `SQL_Consist_Select_MenuCycle`,
  //     Fill_dtDietTypes / Fill_dtConsist at .vb:460-523) ordered by
  //     Priority; both `CheckAll` on load. Only the cycle's rows are
  //     listed: the generator prints a column only for a pick the cycle
  //     has (Diet_Reports.vb:6013-6049). WinForms keeps the lists it
  //     loaded when the date moves (.vb:651-655); the website lists the
  //     new date's cycle.
  //   Select Active (.vb:1002-1017) checks the listed rows residents'
  //     current diet orders use (DT_ResidentDietFlucs) and unchecks
  //     the rest.
  //
  // Print (.vb:324-455) — settings shape `PostingMenuExtensionSheetSettings`:
  //   FKey_Facility, StartDate, NumberOfDays,
  //   Mealtimes (bit-flag enum), MealtimesInt (int[]),
  //   PKey_Menu_Header,
  //   PrintAlternates, PrintAlternatesOnly,
  //   PrintAS, IncludeSMOnly, PrintGarnish,
  //   PrintNotes, Notes,
  //   LstFKey_DietType, LstFKey_Consist, IsLegal
  //
  // Deferred vs. WinForms (future work — mocked mockup shows them
  // but they're not on the C# `PostingMenuExtensionSheetSettings`
  // yet, and each requires additional plumbing):
  //   • Add Custom Diet dialog (frmMenuCycle_StickyDiet_AddMod)
  //     drives .AryListFKey_Diet_Custom / .AryListFKey_Consist_Custom /
  //     .backColorCustom / .foreColorCustom / .StrCustomDiet
  //   • Excel export radio + mixed diet/consist DGR grid
  //     (radExcel_CheckedChanged, gvDTConsist_Extension / gvExcel).
  //   • Signature block (chkSignExtensionSheets + cmbSignatureType +
  //     txtSignatureName / Title + picSignature).
  //   • Menu Categories dropdown (cmbMenuCategories) — not on the
  //     C# settings DTO.
  //   • PrintModifiers (chkPrintModifiers) — not on the C# settings
  //     DTO; UI checkbox retained per mockup, wire value dropped.
  //   • Always Served day picker (cmbChkAS → LstASDays) — sends
  //     empty list; server treats as "Combined" (all days) per
  //     .vb:434-443.
  {
    slug: 'posting-menu-extension-sheet',
    reportName: 'Posting - Extension Sheet',
    fKey_RS_Report: 9,
    tempMenuSupported: postingMenuUsesTempMenu,
    apiEndpoint: 'PostingMenuExtensionSheet',
    // Narrow label column + the tightest rows in the family, so
    // Options and Custom Diet can stack beside Print.
    groupLayout: {
      ...POSTING_MENU_GROUP_LAYOUT_WITH_OPTIONS,
      Print: {
        ...POSTING_MENU_FIELDSET,
        cols: 'minmax(0, 110px) 1fr', gap: '2px 8px', checkboxPad: '0',
      },
    },
    fields: [
      // Date + Number of Days on one row (same as Posting - Daily).
      // Number-of-days spin is capped at 7 per ucNumberOfDays.MaxValue
      // at .vb:151. StartDate default mirrors WinForms Init at
      // .vb:132-137 — facility `Start_Day_Posting_Menu` day-of-week.
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 7, width: 240, group: 'Date' },

      // Menu Cycle info — display-only, updates when StartDate
      // changes. Populates the React Query cache read by
      // `resolvePkeyMenuHeader` at submit time.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — 7 items column-major (matches Diet Specific).
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes',
        layout: 'twoColumn', firstColumnMaxWidth: 250,
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — 3-option radio per mockup. Values encode
      // both `PrintAlternates` and `PrintAlternatesOnly` on the
      // wire (`buildSettings` splits them):
      //   0 → PrintAlternates=false, PrintAlternatesOnly=false
      //   1 → PrintAlternates=true,  PrintAlternatesOnly=false (Italicized)
      //   2 → PrintAlternates=true,  PrintAlternatesOnly=true  (Only)
      // Default from facility `Posting_Alternates_Default` — WinForms
      // maps 0 to Do Not Print and 1|2 to Italicized (.vb:171-178);
      // React port keeps 2 as "Only" to align with the mockup radio.
      // Alternates + Paper Size — paired side-by-side via
      // `groupRow: 'alt-paper'`. Both are compact 3-option radios,
      // so the row stays short.
      { key: 'AlternateType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { posting_Alternates_Default?: number } | null)?.posting_Alternates_Default;
          if (typeof raw !== 'number') return 1;
          return raw === 0 ? 0 : 1;
        },
        group: 'Alternates', groupRow: 'alt-paper',
        options: [
          { value: 0, label: 'Do Not Print' },
          { value: 1, label: 'Italicized' },
          { value: 2, label: 'Only' },
        ] },

      // Paper Size — 3-option radio (Letter / Legal / Excel).
      //   0 → Letter (IsLegal=false, ExportToExcel=false)
      //   1 → Legal  (IsLegal=true,  ExportToExcel=false)
      //   2 → Excel  (IsLegal=false, ExportToExcel=true)
      // Letter/Legal default from facility `postingMenu_PaperSize`
      // (.vb:115-120). Excel radio flips the WinForms UI into the
      // DGR mixed-diet mode (.vb:1068-1097 `radExcel_CheckedChanged`);
      // the React port sends `ExportToExcel: true` on the wire but
      // the DGR/column-config flow is deferred — the API server
      // treats an Excel request as "export same content in xlsx"
      // for now.
      { key: 'PaperSize', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1 ? 1 : 0;
        },
        group: 'Paper Size', groupRow: 'alt-paper',
        options: [
          { value: 0, label: 'Letter (8.5x11)' },
          { value: 1, label: 'Legal (8.5x14)' },
          { value: 2, label: 'Excel' },
        ] },

      // Print + Options + Custom Diet — paired side-by-side via
      // `groupRow: 'print-opts'`. Print takes the LEFT column;
      // Options + Custom Diet STACK on the right (Options top,
      // Custom Diet bottom). Report-scoped CSS in `Reports.css`
      // sizes Print's row so its height matches the combined
      // Options + Custom Diet stack — killing the dead space Print
      // would otherwise leave when paired against a single short
      // group.
      //
      // Print section — 2 columns matching the mockup:
      //   Row 1: AS                    · Include Selective Menu Only
      //   Row 2: Garnish               · Modifiers
      //   Row 3: Notes (parent)        · —
      //   Row 4: Notes text edit (spans both cols)
      // `newRow: true` breaks the flex row on Garnish and Notes so
      // pairs sit side-by-side; the text edit ships on its own line
      // via the report-scoped CSS grid override.
      { key: 'PrintAS', label: 'AS', type: 'checkbox',
        defaultValue: false,
        group: 'Print', groupRow: 'print-opts' },
      { key: 'IncludeSMOnly', label: 'Include Selective Menu Only',
        type: 'checkbox', defaultValue: false, group: 'Print' },
      // Garnish default from `ExtensionSheet_PrintGarnish` (.vb:195).
      { key: 'PrintGarnish', label: 'Garnish', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { extensionSheet_PrintGarnish?: boolean } | null)?.extensionSheet_PrintGarnish;
          return typeof raw === 'boolean' ? raw : false;
        },
        group: 'Print', newRow: true },
      // PrintModifiers — UI-only per mockup. Not on the C# DTO yet;
      // `buildSettings` drops the value from the wire payload.
      { key: 'PrintModifiers', label: 'Modifiers', type: 'checkbox',
        defaultValue: false, group: 'Print' },
      // Notes default from `ExtensionSheet_PrintNotes` (.vb:194).
      // Notes text edit is always visible but disabled until the
      // checkbox is on (mirrors chkOptions_Notes_CheckedChanged at
      // .vb:945-947 which toggles txtNote.Enabled).
      { key: 'PrintNotes', label: 'Notes', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { extensionSheet_PrintNotes?: boolean } | null)?.extensionSheet_PrintNotes;
          return typeof raw === 'boolean' ? raw : false;
        },
        group: 'Print', newRow: true },
      { key: 'Notes', label: '', type: 'text',
        defaultValue: '', group: 'Print',
        enabledWhen: { key: 'PrintNotes', value: true } },

      // Menu Categories combo. WinForms `Fill_cmbMenuCategories`
      // at .vb:707-733 populates from
      // `Sql_Prd_Menu_Category_PrintingOrder_Select` filtered by
      // Type=4 (Menu Categories A) and Type=17 (Menu Categories B).
      // Falls back to the literal strings when no rows exist. This
      // port uses the static fallback labels; server-side report
      // generator only reads `MenuCategoryIndex` (0/1) — no name
      // needed (PostingMenuExtensionSheet.vb:102).
      { key: 'MenuCategoryIndex', label: 'Menu Categories', type: 'dropdown',
        defaultValue: 0,
        group: 'Options', groupRow: 'print-opts',
        options: [
          { value: 0, label: 'Menu Categories A' },
          { value: 1, label: 'Menu Categories B' },
        ] },

      // Custom Diet button — opens `CustomDietDialog` which mirrors
      // WinForms `frmMenuCycle_StickyDiet_AddMod` (5 Diet Type
      // dropdowns + 5 Consistency dropdowns + Background/Font color
      // pickers). Value shape when saved:
      //   { aryDiet, aryConsist, backColor, foreColor, displayName }
      // On the wire this splits into WinForms-compatible fields
      // (see `buildSettings`).
      { key: 'CustomDiet', label: '', type: 'customDietButton',
        defaultValue: null,
        group: 'Custom Diet', groupRow: 'print-opts',
        watchField: 'StartDate',
        // Fieldset header reflects the current selection —
        // "Custom Diet - Regular, Puree" once saved, plain
        // "Custom Diet" when unset (matches WinForms
        // `lblCustomDiet.Text = String.Format("Custom Diet - {0}", strVal)`
        // at .vb:1054).
        getGroupHeader: (values) => {
          const cd = values.CustomDiet as { displayName?: string } | null | undefined;
          return cd?.displayName ? `Custom Diet - ${cd.displayName}` : 'Custom Diet';
        } },

      // Diet Types + Consistencies — menu-cycle-scoped selectors
      // matching Diet Specific. Both default to all checked on load
      // via `defaultCheckAll: true` per WinForms .vb:165-166
      // (`clbDietTypes.CheckAll()` / `clbConsist.CheckAll()`).
      // Rendered inline in the main column below Paper Size (same
      // placement as Diet Specific).
      { key: 'LstFKey_DietType', label: 'Diet Types', type: 'dietTypeSelect',
        defaultValue: [], defaultCheckAll: true, showSelectActive: true,
        group: 'Diet Types / Consistencies', inline: true,
        watchField: 'StartDate', allCheckedKey: 'AllDietTypes' },
      { key: 'LstFKey_Consist', label: 'Consistencies', type: 'consistencySelect',
        defaultValue: [], defaultCheckAll: true, showSelectActive: true,
        group: 'Diet Types / Consistencies', inline: true,
        watchField: 'StartDate', allCheckedKey: 'AllConsists' },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Mirrors WinForms Print settings block (.vb:329-450).
    // Full-field WinForms parity — every field
    // `ucDietech_Report_Generator_Posting_ExtensionSheets.Print`
    // sets on the settings object at .vb:329-450 is emitted here,
    // including the UI-side interlocks (Modifiers cascade at
    // .vb:1106-1128, Always Served cascade at .vb:835-846, Excel
    // radAlternatesSeparate auto-check at .vb:1080). VB DTO members
    // that map from the JSON are case-insensitive under Newtonsoft
    // — Pascal casing on the wire lines up with VB fields whose
    // casing differs (`backColorCustom`, `foreColorCustom`).
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum from int[] — Diet_Reports.vb:3006-3029.
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );

      // Paper Size 3-way: 0=Letter, 1=Legal, 2=Excel.
      const paperSize = Number(v.PaperSize ?? 0);
      const isExcel = paperSize === 2;

      // Modifiers cascade (.vb:1106-1128) — when PrintModifiers is
      // on, WinForms forces AlternateType→DoNotPrint, PrintAS→off,
      // IncludeSMOnly→off, and clears the custom diet. Reflect
      // those clamps in the wire payload so the server sees the
      // same state WinForms would have serialised.
      const printModifiers = Boolean(v.PrintModifiers);

      // Alternates — WinForms encodes THREE booleans on the wire:
      //   PrintAlternates          = Italicized | Only | Separate
      //   PrintAlternatesOnly      = Only
      //   PrintAlernatesSeparate   = Separate    (VB typo preserved!)
      // Excel mode auto-checks Separate (.vb:1080). Modifiers on
      // clamps everything to Do Not Print.
      const altRaw = Number(v.AlternateType ?? 1);
      const alt = printModifiers ? 0 : altRaw;
      const separate = isExcel;
      const italicized = !isExcel && alt === 1;
      const only = !isExcel && alt === 2;
      const printAlternates = italicized || only || separate;
      const printAlternatesOnly = only;

      // Always Served cascade (.vb:835-846) — PrintAS on forces
      // NumberOfDays→1 and PrintGarnish→false. Modifiers on
      // forces PrintAS→false first.
      const printAS = !printModifiers && Boolean(v.PrintAS);
      const numberOfDays = printAS ? 1 : Number(v.NumberOfDays ?? 1);
      const printGarnish = !printAS && Boolean(v.PrintGarnish);

      // Modifiers on also clears IncludeSMOnly (.vb:1123-1124).
      const includeSMOnly = !printModifiers && Boolean(v.IncludeSMOnly);

      const printNotes = Boolean(v.PrintNotes);

      // Menu Categories — WinForms sends both index + text label
      // (.vb:346-347). `MenuCategoryIndex` drives the sort choice
      // (Type=4 / Type=17). `MenuCategoryName` is displayed by the
      // VB DTO's ToString() (info-popup / queue title).
      const menuCategoryIndex = Number(v.MenuCategoryIndex ?? 0);
      const menuCategoryName = menuCategoryIndex === 1
        ? 'Menu Categories B' : 'Menu Categories A';

      // Custom Diet — mirrors WinForms .vb:445-449 (VB field names
      // `backColorCustom` / `foreColorCustom` are case-insensitively
      // matched by Newtonsoft). Modifiers on clears it (.vb:1126).
      // Excel mode hides the Custom Diet box (.vb:1085) and its generator
      // never reads it.
      const cd = printModifiers || isExcel ? null : (v.CustomDiet as {
        aryDiet?: number[];
        aryConsist?: number[];
        backColor?: number;
        foreColor?: number;
        displayName?: string;
      } | null | undefined);
      const customDiet = cd && ((cd.aryDiet?.length ?? 0) + (cd.aryConsist?.length ?? 0) > 0) ? cd : null;

      const dietTypeIds = (v.LstFKey_DietType as number[] | undefined) ?? [];
      const consistIds = (v.LstFKey_Consist as number[] | undefined) ?? [];
      // Excel columns. WinForms fills them from the `gvExcel` grid
      // (.vb:395-416). With no saved column table that grid is one row per
      // diet type and one per consistency (.vb:556-606), and a row with no
      // diet type gets the house diet in slot 0 (.vb:406-408). The website
      // has no column editor, so the checked diet types and consistencies
      // are those rows. Each entry is [diet 1..5, consistency 1..5].
      const houseDiet = Number((_fs as { fKey_Diet_Type_House?: number } | null)?.fKey_Diet_Type_House ?? 0);
      const excelDf: Array<Array<number | null>> = [];
      const excelBack: number[] = [];
      const excelFore: number[] = [];
      for (const id of dietTypeIds) {
        excelDf.push([id, null, null, null, null, null, null, null, null, null]);
        const c = ctx?.resolveDietTypeColors?.(id);
        excelBack.push(c?.back ?? 0);
        excelFore.push(c?.fore ?? 0);
      }
      for (const id of consistIds) {
        excelDf.push([houseDiet > 0 ? houseDiet : null, null, null, null, null, id, null, null, null, null]);
        const c = ctx?.resolveConsistencyColors?.(id);
        excelBack.push(c?.back ?? 0);
        excelFore.push(c?.fore ?? 0);
      }
      const excel = { LstDF_Excel: excelDf, LstBack_Excel: excelBack, LstFore_Excel: excelFore };
      // The menu rows are fetched for the listed diet types
      // (PostingMenuExtensionSheet.vb:421-428): a consistency column
      // prints under the house diet, so the house diet has to be loaded.
      const excelDietTypes = consistIds.length > 0 && houseDiet > 0 && !dietTypeIds.includes(houseDiet)
        ? [...dietTypeIds, houseDiet]
        : dietTypeIds;

      return {
        // ─── Core report scope ───────────────────────────────
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfDays: numberOfDays,
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,

        // ─── Paper Size (.vb:338 + .vb:389) ──────────────────
        IsLegal: paperSize === 1,
        ExportToExcel: isExcel,

        // ─── Alternates (.vb:341-343) ────────────────────────
        PrintAlternates: printAlternates,
        PrintAlternatesOnly: printAlternatesOnly,
        // VB typo preserved — matches `.PrintAlernatesSeparate` field
        // on the VB DTO (PostingMenuExtensionSheetSettings.vb:15).
        PrintAlernatesSeparate: separate,

        // ─── Print options (.vb:344, 349-352) ────────────────
        PrintAS: printAS,
        IncludeSMOnly: includeSMOnly,
        PrintGarnish: printGarnish,
        PrintModifiers: printModifiers,
        PrintNotes: printNotes,
        // WinForms sends `txtNote.Text` UNCONDITIONALLY (.vb:350).
        Notes: String(v.Notes ?? ''),

        // ─── Menu Categories (.vb:346-347) ───────────────────
        MenuCategoryIndex: menuCategoryIndex,
        MenuCategoryName: menuCategoryName,

        // ─── Diet Types / Consistencies (.vb:377-378) ────────
        LstFKey_DietType: isExcel ? excelDietTypes : dietTypeIds,
        LstFKey_Consist: consistIds,

        // ─── Signature block (.vb:354-357) ───────────────────
        // Signature UI is deferred — send defaults matching what
        // WinForms would emit when `_showSignature` is false and
        // `chkSignExtensionSheets.Checked` remains false.
        SignExtensionSheets: false,
        SignatureType: 0,
        SignatureName: '',
        SignatureTitle: '',

        // ─── Always Served day picker (.vb:436-443) ──────────
        // `cmbChkAS` day-multi-select is deferred. WinForms only
        // populates LstASDays when PrintAS on AND the combo is
        // visible AND ≥1 day checked; otherwise LstASDays stays
        // Nothing. Send null so Newtonsoft materialises Nothing
        // on the VB DTO.
        LstASDays: null,

        // ─── DGR mixed-diet grid (.vb:362-374) ───────────────
        // `lcgDGR` mixed-diet-and-consist grid is deferred.
        // WinForms only populates these when the DGR panel is
        // visible; otherwise Nothing.
        LstFKey_Mixed: null,
        LstMixedType: null,

        // ─── Excel columns (.vb:380-416) ─────────────────────
        // Excel mode builds one spreadsheet column per entry, and the
        // generator walks all three lists with `For Each`
        // (PostingMenuExtensionSheet.vb:486, 549, 678): null throws.
        ...(isExcel ? excel : { LstDF_Excel: null, LstBack_Excel: null, LstFore_Excel: null }),

        // ─── Custom Diet block (.vb:445-449) ─────────────────
        // No custom diet = Nothing. rptDietech_Prd_Extension_Sheet.vb:795,
        // 920, 1447 add the custom column whenever either list
        // `IsNot Nothing`, so [] printed an extra blank column.
        AryListFKey_Diet_Custom: customDiet ? (customDiet.aryDiet ?? []) : null,
        AryListFKey_Consist_Custom: customDiet ? (customDiet.aryConsist ?? []) : null,
        BackColorCustom: customDiet?.backColor ?? 0,
        ForeColorCustom: customDiet?.foreColor ?? 0,
        StrCustomDiet: customDiet?.displayName ?? '',
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const dt = (v.LstFKey_DietType as number[] | undefined) ?? [];
      // Excel: one column per checked diet type or consistency (.vb:148-152).
      if (Number(v.PaperSize ?? 0) === 2) {
        const consist = (v.LstFKey_Consist as number[] | undefined) ?? [];
        if (dt.length + consist.length === 0) return 'At least one diet type/consistency must be selected.';
        return null;
      }
      if (dt.length === 0) return 'Please select at least one diet type.';
      return null;
    },
  },
  // ─── POSTING - DIET SPECIFIC (pKey 10, fKey 10) ────────────────
  // WinForms source: ucDietech_Report_Generator_Posting_DietSpecific.vb.
  //
  // Init (.vb:85-155):
  //   TabPageText  = "Posting - Diet Specific"
  //   StartDate    = same day-of-week logic as Posting - Daily
  //     (facility Start_Day_Posting_Menu → today or next occurrence).
  //   NumberOfWeeks: MinValue 1, MaxValue = MenuCycleInUseInfo.NumberOfWeeksIncycle.
  //     Screenshot shows default 1.
  //   Mealtimes: 7-item list with facility meal names + nourishments
  //     when MenuCycle_ShowNourishments = true.
  //   AlternateType: default from `Posting_Alternates_Default`
  //     (0=NotIncluded, 1=Italicized, 2=SeparateGroup). React port
  //     defaults to 2 per mockup.
  //   ShowSizes: default from facility
  //     `PostingMenu_DietSpecific_ShowSizes` (checked per mockup).
  //   PaperSize: default from facility `PostingMenu_PaperSize`
  //     (0=Letter, 1=Legal).
  //   Diet Types + Consistencies: menu-cycle-scoped lists sorted
  //     by Priority. Multi-select capped at 5 each server-side.
  //
  // Print (.vb:284-333) — settings shape `PostingMenuDietSpecificSettings`:
  //   FKey_Facility, StartDate, NumberOfWeeks,
  //   Mealtimes (bit-flag Mealtime), MealtimesInt (int[]),
  //   PKey_Menu_Header,
  //   AlternateType (enum 0/1/2),
  //   PrintGarnish, IncludeSMOnly (NOTE: singular vs Daily's
  //     IncludeSMOnlyItems), PrintNotes, ShowDates, ShowSizes,
  //   Notes (string — only meaningful when PrintNotes on),
  //   IsLegal (bool),
  //   LstFKey_DietType (int[]), LstFKey_Consist (int[]),
  //   LstDietType (string[] — display names, comma-joined for
  //     server-side title), LstConsist (string[])
  //
  // Validate (.vb:514-543): ≥1 mealtime + valid menu cycle day +
  // house diet type defined + ≥1 diet type. React port catches
  // the trivial mealtime + diet-type checks up front.
  {
    slug: 'posting-menu-diet-specific',
    reportName: 'Posting - Diet Specific',
    fKey_RS_Report: 10,
    tempMenuSupported: postingMenuUsesTempMenu,
    apiEndpoint: 'PostingMenuDietSpecific',
    // Diet Specific uses a wider label column and looser rows.
    groupLayout: {
      ...POSTING_MENU_GROUP_LAYOUT,
      Print: { ...POSTING_MENU_FIELDSET, cols: 'minmax(0, 250px) 1fr', gap: '8px 8px' },
    },
    fields: [
      // Date + Number of Weeks on one row. StartDate uses the
      // same facility Start_Day_Posting_Menu roll-forward logic as
      // Posting - Daily / Full Day.
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      // NumberOfWeeks (not Days). Runtime max tracks the current
      // menu cycle's `NumberOfWeeksIncycle` (mirrors WinForms
      // `ucNumberOfWeeks.MaxValue = MenuCycleInUseInfo.NumberOfWeeksIncycle`
      // at .vb:130). `max: 12` is a hard ceiling for pathological
      // cycles (typical run 4-8 weeks).
      { key: 'NumberOfWeeks', label: 'Number of Weeks', type: 'number',
        defaultValue: 1, min: 1, max: 12, width: 240, group: 'Date',
        dynamicMaxFromMenuCycle: 'StartDate' },

      // Menu Cycle info — display-only, tied to StartDate.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — 7 items in column-major flow. Left column
      // clamped to 250px so the meal labels stay tight and the
      // nourishment times on the right column line up cleanly
      // (matches supplied UI mockup).
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes',
        layout: 'twoColumn', firstColumnMaxWidth: 250,
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — three-option radio (matches Posting - Daily's
      // AlternateType enum). Default from facility
      // `Posting_Alternates_Default`; falls back to 2 (Separate
      // Group) per the supplied mockup.
      { key: 'AlternateType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { posting_Alternates_Default?: number } | null)?.posting_Alternates_Default;
          return typeof raw === 'number' && raw >= 0 && raw <= 2 ? raw : 2;
        },
        group: 'Alternates',
        options: [
          { value: 0, label: 'Do Not Print' },
          { value: 1, label: 'Italicized' },
          { value: 2, label: 'Separate Group' },
        ] },

      // Print section — 3 tight rows (2 controls per row) instead
      // of 6 separate rows. `newRow: true` fires on every other
      // control so pairs sit side-by-side within the fieldset.
      //   Row 1: Garnish              · Include Selective Menu Only
      //   Row 2: Show Dates           · Show Sizes
      //   Row 3: Notes (parent)       · Notes text
      { key: 'PrintGarnish', label: 'Garnish', type: 'checkbox',
        defaultValue: false, group: 'Print' },
      { key: 'IncludeSMOnly', label: 'Include Selective Menu Only',
        type: 'checkbox', defaultValue: false, group: 'Print' },
      { key: 'ShowDates', label: 'Show Dates', type: 'checkbox',
        defaultValue: false, group: 'Print', newRow: true },
      // ShowSizes default from facility
      // `PostingMenu_DietSpecific_ShowSizes` (.vb:98). Falls back
      // to true per mockup.
      { key: 'ShowSizes', label: 'Show Sizes', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_DietSpecific_ShowSizes?: boolean } | null)?.postingMenu_DietSpecific_ShowSizes;
          return typeof raw === 'boolean' ? raw : true;
        },
        group: 'Print' },
      // rptDietech_Prd_PostingMenu_Cross.NewLayout / IndentSubItems (web only; both on when a print lacks them,
      // so the desktop and schedules print the new layout, indented). Jeff, 2026-11-03: new by default, the old
      // one still printable; sub-items a little in from their item, as before #2804.
      { key: 'NewLayout', label: 'New layout', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true,
        tooltip: 'Clear to print the posting menu as it looked before November 2026.' },
      { key: 'IndentSubItems', label: 'Indent sub-items', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true,
        enabledWhen: { key: 'NewLayout', value: true },
        tooltip: 'Sub-items, such as the syrup under French Toast, start a little in from the item they go with.' },
      // PrintNotes parent + Notes text child. Notes is always
      // visible but disabled until PrintNotes is checked
      // (mirrors chkOptions_Notes_CheckedChanged at .vb:549-551).
      { key: 'PrintNotes', label: 'Notes', type: 'checkbox',
        defaultValue: false, group: 'Print', newRow: true },
      { key: 'Notes', label: '', type: 'text',
        defaultValue: '', group: 'Print',
        enabledWhen: { key: 'PrintNotes', value: true } },

      // Paper Size — Letter/Legal radio. Default from facility
      // `postingMenu_PaperSize` (0=Letter → false, 1=Legal → true).
      { key: 'IsLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1;
        },
        group: 'Paper Size',
        options: [
          { value: false, label: 'Letter (8.5x11)' },
          { value: true, label: 'Legal (8.5x14)' },
        ] },

      // Diet Types + Consistencies — menu-cycle-scoped selectors
      // (WinForms Fill_dtDietTypes / Fill_dtConsist at .vb:417-475
      // pull from SQL_Diet_Type_Select_MenuCycle /
      // SQL_Consist_Select_MenuCycle sorted by Priority).
      // `watchField: 'StartDate'` drives the menu-cycle lookup.
      //
      // `group: 'Diet Types / Consistencies'` + `inline: true`
      // pull them OUT of the right-hand side column and place them
      // side-by-side in the main column BELOW the Paper Size band
      // (last two fields render as the last main-column group).
      { key: 'LstFKey_DietType', label: 'Diet Types', type: 'dietTypeSelect',
        defaultValue: [],
        group: 'Diet Types / Consistencies', inline: true,
        watchField: 'StartDate' },
      { key: 'LstFKey_Consist', label: 'Consistencies', type: 'consistencySelect',
        defaultValue: [],
        group: 'Diet Types / Consistencies', inline: true,
        watchField: 'StartDate' },
    ],
    buildDateFor: (v) => formatWeeksRange(v.StartDate, v.NumberOfWeeks, 0),
    // `.StrNotesHTML = GetFacilityMenuNotes(...)` (.vb:308), printed in the
    // menu notes box (PostingMenuDietSpecific.vb:249-252). Never sent before.
    preSubmit: async (v, { facilityId, useTempMenu }) => fetchFacilityMenuNotes(facilityId, v.StartDate, useTempMenu),
    // Mirrors WinForms Print settings block (.vb:284-329).
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );

      // Diet Types + Consistencies — send both the PKey lists AND
      // the display-name lists (WinForms iterates the checkedlist
      // to populate both at .vb:315-327). Names resolve via ctx
      // resolvers populated by React Query for the menu cycle.
      const dietTypeIds = (v.LstFKey_DietType as number[] | undefined) ?? [];
      const consistIds = (v.LstFKey_Consist as number[] | undefined) ?? [];
      const dietTypeNames = dietTypeIds
        .map((id) => ctx?.resolveDietTypeName?.(id) ?? '')
        .filter((n) => n);
      const consistNames = consistIds
        .map((id) => ctx?.resolveConsistencyName?.(id) ?? '')
        .filter((n) => n);

      const printNotes = Boolean(v.PrintNotes);
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfWeeks: Number(v.NumberOfWeeks ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,
        AlternateType: Number(v.AlternateType ?? 2),
        PrintGarnish: Boolean(v.PrintGarnish),
        IncludeSMOnly: Boolean(v.IncludeSMOnly),
        PrintNotes: printNotes,
        // Notes forwarded verbatim when PrintNotes on; empty
        // string when off (matches WinForms which sends txtNote
        // regardless — server-side ToString gates on PrintNotes).
        Notes: printNotes ? String(v.Notes ?? '') : '',
        ShowDates: Boolean(v.ShowDates),
        ShowSizes: Boolean(v.ShowSizes),
        NewLayout: v.NewLayout !== false,
        // The HTML layout (no DevExpress; docs/posting-menus/html-engine.md) where the renderer has it: website only,
        // so the desktop keeps its layout. Off with New layout: the layout from before November 2026.
        HtmlLayout: v.NewLayout !== false,
        IndentSubItems: v.IndentSubItems !== false,
        IsLegal: Boolean(v.IsLegal),
        LstFKey_DietType: dietTypeIds,
        LstFKey_Consist: consistIds,
        LstDietType: dietTypeNames,
        LstConsist: consistNames,
        // Filled by preSubmit (merged over this); '' = no notes.
        StrNotesHTML: '',
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const dt = (v.LstFKey_DietType as number[] | undefined) ?? [];
      if (dt.length === 0) return 'Please select at least one diet type.';
      return null;
    },
  },
  // NOTE: an earlier, superseded config for pKey 11 lived here
  // (slug `posting-menu-specific-resident`, 13 ungrouped fields,
  // `NumberOfDays`/`OrderBy*` checkboxes, no `buildSettings`). It was a
  // duplicate of the WinForms-faithful config further down and was
  // removed 2026-08-23. It had been dead-ish but not harmless: the
  // slug Map keeps the LAST entry, so the runner rendered the newer
  // config, while REPORT_CONFIG_BY_PKEY keeps the FIRST, so the catalog
  // card read its stale title ("Posting Menu - Specific Resident") over
  // a form that was actually "Posting - Specific {Resident}".
  // ─── POSTING - SELECTIVE MENU (pKey 12, fKey 12) ───────────────
  // WinForms source: ucDietech_Report_Generator_Posting_SelectiveMenu.vb.
  //
  // Init (.vb:98-191):
  //   TabPageText  = "Posting - Selective Menu"
  //   StartDate    = facility Start_Day_Posting_Menu roll-forward.
  //   NumberOfWeeks: MinValue 1, MaxValue = NumberOfWeeksIncycle.
  //   Mealtimes: 3-item list ONLY (Breakfast/Lunch/Dinner) —
  //     `ucClbMealtimes.IncludeNourishments = False` at .vb:156
  //     so nourishment times are NOT shown.
  //   PaperSize: default from facility `PostingMenu_PaperSize`
  //     (0=Letter, 1=Legal).
  //   AlternateType: default from `Posting_Alternates_Default`
  //     (0=NotIncluded, 1=Italicized, 2=SeparateGroup). Designer
  //     also defaults radAlternates_Italicized to True, but Init
  //     overrides. Mockup shows Separate Group per facility.
  //   Designer defaults (Designer.vb):
  //     radPrintAll.EditValue = True             → default "All"
  //     radOrderBy_TrayLine.EditValue = True     → default Tray Line
  //     chkOptions_Notes.EditValue = True        → Notes on
  //     radAlternates_Italicized.EditValue = True (overridden by Init)
  //     radPaperLetter default from facility
  //
  // Print (.vb:375-434) — settings shape `PostingMenuSelectiveMenuSettings`:
  //   .Mealtimes (bit-flag)
  //   .MealtimesInt (int[])
  //   .NumberOfWeeks
  //   .StartDate
  //   .PKey_Menu_Header
  //   .FKey_Facility
  //   .IsLegal
  //   .ShowDates
  //   .ShowSizes
  //   .ListPatient (List<int>)
  //   .LstNames (List<string> — display names when ≤15 patients)
  //   .ListSets (List<int>)
  //   .AlternateType (enum 0/1/2)
  //   .OrderByTrayline, .OrderByRoomNo, .OrderByName (three
  //     booleans, only one true — WinForms uses 3 radios)
  //   .PrintNotes, .Notes
  //
  // Print-mode branching (.vb:396-417):
  //   All / Specific Set → ListPatient = all rows from the
  //     residents grid (which is `GetSelectiveMenuResidents(...)` —
  //     SM-eligible patients matching mealtimes + date range,
  //     filtered by StaticSelectiveMenu unless facility setting
  //     `PostingMenu_SelectiveMenu_ShowStaticResidents` = true).
  //     LstNames populated when ≤15 patients.
  //   Specific Set → also sends ListSets = checked sets.
  //   Specific Patient → ListPatient = selected patients from grid.
  //     LstNames when ≤15 selected.
  //
  // React port matches WinForms client-side enrichment:
  //   All / Specific Set → `preSubmit` fetches SM-eligible
  //     patients from `POST /api/Patient/SelectiveMenuEligible/{fac}`,
  //     applies the `StaticSelectiveMenu = False` filter when
  //     facility setting `PostingMenu_SelectiveMenu_ShowStaticResidents`
  //     is false, and populates `ListPatient`. `LstNames` is
  //     populated when the resulting list has ≤15 rows.
  //   Specific Set → `ListSets` = user's checked sets.
  //   Specific Patient → `ListPatient` = selected residents;
  //     `preSubmit` still fires so `LstNames` gets populated when
  //     selection is ≤15 rows (WinForms iterates gvResidents.GetSelectedRows).
  //   Deferred: predefined resident-list save/load infrastructure
  //     (luResidents + tblPrd_ResidentSelect_Header/Detail).
  //
  // Validate (.vb:932-961): menu categories type=7 defined +
  // ≥1 mealtime + valid menu day + (Specific Patient mode → ≥1
  // resident) + (Specific Set mode → ≥1 set) + NumberOfWeeks valid.
  {
    slug: 'posting-menu-selective-menu',
    reportName: 'Posting - Selective Menu',
    fKey_RS_Report: 12,
    apiEndpoint: 'PostingMenuSelectiveMenu',
    groupLayout: POSTING_MENU_GROUP_LAYOUT_WITH_OPTIONS,
    fields: [
      // Date + Number of Weeks on one row.
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfWeeks', label: 'Number of Weeks', type: 'number',
        defaultValue: 1, min: 1, max: 12, width: 240, group: 'Date',
        dynamicMaxFromMenuCycle: 'StartDate' },

      // Menu Cycle info — display-only, driven by StartDate.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — ONLY 3 items (Breakfast/Lunch/Dinner). WinForms
      // sets `IncludeNourishments = False` at .vb:156, so
      // nourishment times are hidden for this report. Vertical
      // stack — never split 3 mealtime items across a 2-col grid
      // (see the `feedback_multicheck_mealtime_layout` memory).
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        defaultValue: [], group: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Alternates — 3-option radio (Do Not Print / Italicized /
      // Separate Group). Default from facility
      // `Posting_Alternates_Default` (.vb:169-176).
      { key: 'AlternateType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { posting_Alternates_Default?: number } | null)?.posting_Alternates_Default;
          return typeof raw === 'number' && raw >= 0 && raw <= 2 ? raw : 2;
        },
        group: 'Alternates',
        options: [
          { value: 0, label: 'Do Not Print' },
          { value: 1, label: 'Italicized' },
          { value: 2, label: 'Separate Group' },
        ] },

      // Order By — 3-option radio. Wire fields
      // `OrderByTrayline` / `OrderByRoomNo` / `OrderByName` are
      // split in `buildSettings`. Default Tray Line per Designer
      // (`radOrderBy_TrayLine.EditValue = True`).
      // The Room label reads "{Room} No" — WinForms uses facility
      // Room label (`.vb:115`).
      { key: 'OrderBy', label: '', type: 'radio',
        defaultValue: 0, group: 'Order By',
        options: [
          { value: 0, label: 'Tray Line' },
          { value: 1, label: '{Room} No' },
          { value: 2, label: 'Name' },
        ] },

      // Print mode — 3-option radio (All / Specific {SetName} /
      // Specific {Resident}). Default 'all' per Designer
      // `radPrintAll.EditValue = True`. Wire encodes via
      // `ListPatient` / `ListSets` values in `buildSettings`.
      { key: 'PrintMode', label: '', type: 'radio',
        defaultValue: 'all', group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },

      // Options — Show Dates + Show Sizes checkboxes.
      { key: 'ShowDates', label: 'Show Dates', type: 'checkbox',
        defaultValue: false, group: 'Options' },
      { key: 'ShowSizes', label: 'Show Sizes', type: 'checkbox',
        defaultValue: false, group: 'Options' },
      // rptDietech_Prd_PostingMenu_Cross.NewLayout / IndentSubItems (web only; both on when a print lacks them,
      // so the desktop and schedules print the new layout, indented). Jeff, 2026-11-03: new by default, the old
      // one still printable; sub-items a little in from their item, as before #2804.
      { key: 'NewLayout', label: 'New layout', type: 'checkbox',
        defaultValue: true, group: 'Options', newRow: true,
        tooltip: 'Clear to print the posting menu as it looked before November 2026.' },
      { key: 'IndentSubItems', label: 'Indent sub-items', type: 'checkbox',
        defaultValue: true, group: 'Options', newRow: true,
        enabledWhen: { key: 'NewLayout', value: true },
        tooltip: 'Sub-items, such as the syrup under French Toast, start a little in from the item they go with.' },
      // Notes — Designer default `chkOptions_Notes.EditValue = True`.
      // Notes text always visible, disabled until PrintNotes on
      // (mirrors chkOptions_Notes_CheckedChanged at .vb:998-1000).
      { key: 'PrintNotes', label: 'Notes', type: 'checkbox',
        defaultValue: true, group: 'Options', newRow: true },
      { key: 'Notes', label: '', type: 'text',
        defaultValue: '', group: 'Options',
        enabledWhen: { key: 'PrintNotes', value: true } },

      // Paper Size — Letter/Legal radio. Default from facility
      // `postingMenu_PaperSize` (0=Letter → false, 1=Legal → true).
      { key: 'IsLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1;
        },
        group: 'Paper Size',
        options: [
          { value: false, label: 'Letter (8.5x11)' },
          { value: true, label: 'Legal (8.5x14)' },
        ] },

      // Set picker — visible only in Specific Set mode. Rendered
      // as its own fieldset at the BOTTOM of the main column
      // (below Paper Size) so it doesn't shrink the tight
      // Options group above.
      { key: 'ListSets', label: 'Select {SetName_Plural}', type: 'setSelect',
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'set' },
        defaultSelectAll: true,
        group: '{SetName_Plural}', inline: true },

      // Resident picker — visible only in Specific Patient mode.
      //
      // `showInfoColumns` renders Diet Order / Unit / Room / TF;
      // `showStaticColumn` inserts the Static (SelectiveMenu)
      // boolean between Diet Order and Unit — mirrors the WinForms
      // column order at Designer.vb:452-534:
      //   Selection | Name | Diet Order | Static | Floor/Unit | Room | TF
      //
      // `selectiveMenuEligible` filters the roster down to just
      // Selective-Menu-eligible patients — matches WinForms
      // `Fill_dtResidents` at .vb:508 which calls
      // `GetSelectiveMenuResidents(cnn, fac, startDate, endDate,
      // meals, false)`. Powered by a new C# endpoint
      // `POST /api/Patient/SelectiveMenuEligibleIds/{fac}` that
      // wraps `Diet_Utils.GetSelectiveMenuResidents`; the React
      // hook `useSelectiveMenuEligiblePatientIds` intersects the
      // returned PKey list with the standard active-residents
      // roster from `useResidents(fac)`. Sibling field names for
      // the request default to `StartDate` / `NumberOfWeeks` /
      // `MealtimesInt` — which match this config.
      { key: 'ListPatient', label: '', type: 'residentSelect',
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'resident' },
        showInfoColumns: true, showStaticColumn: true,
        selectiveMenuEligible: {},
        // Toolbar dropdown mirrors WinForms luResidents on this
        // report — saved lists stored in tblPrd_ResidentSelect_*
        // with Type = 0 (SelectiveMenuPosting).
        predefinedListType: 0,
        group: '{Resident_Plural}', inline: true },
    ],
    buildDateFor: (v) => formatWeeksRange(v.StartDate, v.NumberOfWeeks, 0),
    // Enrichment for All / Specific Set modes — mirrors WinForms
    // Print (.vb:396-406) which iterates a DataView over the SM
    // resident grid and populates `ListPatient` (plus `LstNames`
    // when ≤15). The client-side static filter applies:
    //   If Not showStaticSelectiveMenuResidents
    //     dv.RowFilter = "StaticSelectiveMenu = False"
    // where `showStaticSelectiveMenuResidents` =
    // facility `PostingMenu_SelectiveMenu_ShowStaticResidents`.
    //
    // For Specific Patient mode, the user's selection lives in
    // `values.ListPatient` — `buildSettings` forwards that directly
    // and we return `{}` here so preSubmit doesn't overwrite it.
    //
    // A fresh POST to `/api/Patient/SelectiveMenuEligible/{fac}`
    // is fired every submit — the resident selector's React Query
    // cache is only populated when the selector is visible
    // (Specific Patient mode), so it isn't reachable from All /
    // Set modes. One request at Print-time is acceptable.
    preSubmit: async (v, { facilityId, facilitySettings }) => {
      const printMode = String(v.PrintMode ?? 'all');
      const startDate = v.StartDate;
      const iso = ((): string | null => {
        if (startDate instanceof Date) {
          return `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
        }
        if (typeof startDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(startDate)) return startDate.substring(0, 10);
        return null;
      })();
      if (!iso) return {};
      const numberOfWeeks = Number(v.NumberOfWeeks ?? 1);
      const endIso = addDaysDateOnly(iso, numberOfWeeks * 7);
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce((acc, i) => acc | (bitMap[i] ?? 0), 0);
      if (mealtimesFlag === 0) return {};
      try {
        const { default: apiClient } = await import('../../api/client');
        const { data } = await apiClient.post<Array<{
          pKey_Patient: number;
          firstName?: string | null;
          lastName?: string | null;
          fullName?: string | null;
          staticSelectiveMenu?: boolean | null;
        }>>(
          `/api/Patient/SelectiveMenuEligible/${facilityId}`,
          { StartDate: iso, EndDate: endIso, Mealtimes: mealtimesFlag },
        );
        const rows = data ?? [];
        const nameOf = (p: { fullName?: string | null; lastName?: string | null; firstName?: string | null }): string =>
          (p.fullName ?? `${p.lastName ?? ''}, ${p.firstName ?? ''}`).trim().replace(/^,\s*/, '');

        // Specific Patient mode — mirrors WinForms
        // `radPrintResident.Checked` branch at .vb:410-417 which
        // iterates the SELECTED rows and populates LstNames when
        // SelectedRowsCount ≤ 15. `ListPatient` stays as the user's
        // selection (buildSettings passes it through); only LstNames
        // is enriched here from the SM roster.
        if (printMode === 'resident') {
          const selected = (v.ListPatient as number[] | undefined) ?? [];
          if (selected.length === 0 || selected.length > 15) return {};
          const byPKey = new Map(rows.map((p) => [p.pKey_Patient, p] as const));
          const lstNames = selected.map((id) => nameOf(byPKey.get(id) ?? { pKey_Patient: id })).filter(Boolean);
          return { LstNames: lstNames };
        }

        // All / Specific Set — mirrors .vb:396-406 which iterates
        // the DataView (SM-eligible residents grid) and populates
        // ListPatient for EVERY visible row. The client-side
        // filter is applied when facility setting is false:
        //   dv.RowFilter = "StaticSelectiveMenu = False"
        const fs = facilitySettings as Record<string, unknown> | null;
        const rawShowStatic = fs?.postingMenu_SelectiveMenu_ShowStaticResidents
          ?? fs?.PostingMenu_SelectiveMenu_ShowStaticResidents;
        const showStatic = Boolean(rawShowStatic);
        const filtered = showStatic ? rows : rows.filter((p) => !p.staticSelectiveMenu);
        const listPatient = filtered.map((p) => p.pKey_Patient);
        const lstNames = filtered.length <= 15 ? filtered.map(nameOf) : [];
        return { ListPatient: listPatient, LstNames: lstNames };
      } catch {
        return {};
      }
    },
    // Mirrors WinForms Print settings block (.vb:377-429).
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum from int[] — Diet_Reports.vb:3006-3029.
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );

      // Order By — split UI radio into three wire booleans.
      const orderBy = Number(v.OrderBy ?? 0);

      // Print mode → ListPatient / ListSets shape:
      //   'all'      → empty ListPatient here; `preSubmit` above
      //                populates it from the SM-eligible endpoint
      //                (respecting the static-selective-menu filter),
      //                matching WinForms .vb:396-406.
      //   'set'      → empty ListPatient here (same preSubmit path)
      //                + user's ListSets.
      //   'resident' → user's ListPatient (preSubmit only adds
      //                LstNames when the selection is ≤15,
      //                matching WinForms .vb:411-416).
      const printMode = String(v.PrintMode ?? 'all');
      const listPatient = printMode === 'resident'
        ? ((v.ListPatient as number[] | undefined) ?? [])
        : [];
      const listSets = printMode === 'set'
        ? ((v.ListSets as number[] | undefined) ?? [])
        : [];

      const printNotes = Boolean(v.PrintNotes);
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfWeeks: Number(v.NumberOfWeeks ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,

        // Print-mode payload
        ListPatient: listPatient,
        LstNames: [], // server derives from ListPatient (≤15 rows)
        ListSets: listSets,

        // Alternates
        AlternateType: Number(v.AlternateType ?? 2),

        // Order By (three-boolean radio group)
        OrderByTrayline: orderBy === 0,
        OrderByRoomNo: orderBy === 1,
        OrderByName: orderBy === 2,

        // Notes — WinForms sends `.Notes = txtNote.Text`
        // UNCONDITIONALLY at .vb:428, regardless of PrintNotes.
        PrintNotes: printNotes,
        Notes: String(v.Notes ?? ''),

        // Options
        ShowDates: Boolean(v.ShowDates),
        ShowSizes: Boolean(v.ShowSizes),
        NewLayout: v.NewLayout !== false,
        // The HTML layout (no DevExpress; docs/posting-menus/html-engine.md) where the renderer has it: website only,
        // so the desktop keeps its layout. Off with New layout: the layout from before November 2026.
        HtmlLayout: v.NewLayout !== false,
        IndentSubItems: v.IndentSubItems !== false,

        IsLegal: Boolean(v.IsLegal),
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const mode = String(v.PrintMode ?? 'all');
      if (mode === 'resident') {
        const pts = (v.ListPatient as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select at least one resident.';
      }
      if (mode === 'set') {
        const sets = (v.ListSets as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select at least one set.';
      }
      return null;
    },
  },
  // ─── POSTING - SPECIFIC RESIDENT (pKey 11, fKey 11) ────────────
  // WinForms source: ucDietech_Report_Generator_Posting_SpecificResident.vb.
  //
  // Init (.vb:92-182):
  //   TabPageText  = String.Format("Posting - Specific {0}", _strResident)
  //   StartDate    = facility Start_Day_Posting_Menu roll-forward
  //     (today when today's DayOfWeek matches; else next
  //     occurrence). Same helper other Posting reports use.
  //   NumberOfWeeks: MinValue 1, MaxValue = NumberOfWeeksIncycle.
  //   Mealtimes: 7-item list with facility meal names +
  //     nourishments when `MenuCycle_ShowNourishments` = true
  //     (.vb:146). No mealtime-change handler → grid is not
  //     re-filtered when mealtimes change (see React note below).
  //   ShowSizes: default from facility
  //     `PostingMenu_ResidentSpecific_ShowSizes` (.vb:106).
  //   PaperSize: default from facility `PostingMenu_PaperSize`
  //     (0=Letter, 1=Legal).
  //   AlternateType: default from `Posting_Alternates_Default`
  //     (0=NotIncluded, 1=Italicized, 2=SeparateGroup). Designer
  //     also defaults radAlternates_Italicized to True; Init
  //     overrides.
  //   Designer defaults (Designer.vb):
  //     radOrderBy_TrayLine.EditValue = True  → default Tray Line
  //     chkOptions_Notes.EditValue = True     → Notes on
  //     radAlternates_Italicized.EditValue = True (Init overrides)
  //     radPaperLetter.EditValue = True (Init overrides)
  //
  // Print (.vb:313-346) — settings shape `PostingMenuSpecificResidentSettings`:
  //   .Mealtimes (bit-flag)
  //   .MealtimesInt (int[])
  //   .NumberOfWeeks
  //   .StartDate
  //   .PKey_Menu_Header
  //   .FKey_Facility
  //   .IsLegal
  //   .ShowDates
  //   .ShowSizes
  //   .ListPatient (List<int> — selected rows from resident grid)
  //   .OrderByTrayline, .OrderByRoomNo, .OrderByName (three bools)
  //   .AlternateType (enum 0/1/2 — SeparateGroup covers both radios)
  //   .PrintBlankAlternateSpace (True when radAlternatesBlankArea
  //     is checked — distinguishes "Separate Group" from "w/ Blank
  //     Area" downstream)
  //   .PrintNotes, .Notes
  //
  // Validate (.vb:920-944): ≥1 mealtime + valid menu day + ≥1
  // resident selected + menu categories type=3 defined
  // (server-side).
  //
  // Grid data source: WinForms `Fill_dtResidents` (.vb:490-583)
  // runs raw SQL against tblPatient with SetMealtimeAttendance
  // filter for StartDate.DayOfWeek, then post-filters guests via
  // ValidMealtimesForGuestRecurrence. React port uses the new
  // `postingSpecificResidentEligible` field option which powers
  // `POST /api/Patient/PostingSpecificResidentEligible/{fac}` +
  // `usePostingSpecificResidentEligiblePatients`.
  {
    slug: 'posting-menu-specific-resident',
    reportName: 'Posting - Specific {Resident}',
    fKey_RS_Report: 11,
    tempMenuSupported: postingMenuUsesTempMenu,
    apiEndpoint: 'PostingMenuSpecificResident',
    groupLayout: POSTING_MENU_GROUP_LAYOUT,
    fields: [
      // Date + Number of Weeks on one row.
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfWeeks', label: 'Number of Weeks', type: 'number',
        defaultValue: 1, min: 1, max: 12, width: 240, group: 'Date',
        dynamicMaxFromMenuCycle: 'StartDate' },

      // Menu Cycle info — display-only, driven by StartDate.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — 7-item column-major list (includes nourishments
      // when facility `MenuCycle_ShowNourishments` = true; WinForms
      // `.vb:146` sets `IncludeNourishments` from that flag). The
      // 250px cap on the left column keeps nourishment times tight.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes',
        layout: 'twoColumn', firstColumnMaxWidth: 250,
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — 4-option radio (Do Not Print / Italicized /
      // Separate Group / Separate Group w/ Blank Area). Default
      // from facility `Posting_Alternates_Default` (.vb:168-175);
      // "Separate Group" is 2 in the facility setting, which is
      // shared with "w/ Blank Area" (both map to AlternateType.
      // SeparateGroup — see .vb:340). We default to the plain
      // "Separate Group" (index 2) when facility setting is 2.
      { key: 'AlternateType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { posting_Alternates_Default?: number } | null)?.posting_Alternates_Default;
          return typeof raw === 'number' && raw >= 0 && raw <= 2 ? raw : 2;
        },
        group: 'Alternates',
        options: [
          { value: 0, label: 'Do Not Print' },
          { value: 1, label: 'Italicized' },
          { value: 2, label: 'Separate Group' },
          { value: 3, label: 'Separate Group w/ Blank Area' },
        ] },

      // Order By — 3-option radio. `radOrderBy_TrayLine.EditValue
      // = True` is the Designer default (.vb:388). The Room label
      // reads "{Room} No" — WinForms .vb:104 uses facility Room
      // label.
      { key: 'OrderBy', label: '', type: 'radio',
        defaultValue: 0, group: 'Order By',
        options: [
          { value: 0, label: 'Tray Line' },
          { value: 1, label: '{Room} No' },
          { value: 2, label: 'Name' },
        ] },

      // Print — Show Dates + Show Sizes + Notes.
      // ShowSizes default from facility
      // `PostingMenu_ResidentSpecific_ShowSizes` (.vb:106).
      { key: 'ShowDates', label: 'Show Dates', type: 'checkbox',
        defaultValue: false, group: 'Print' },
      { key: 'ShowSizes', label: 'Show Sizes', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_ResidentSpecific_ShowSizes?: boolean } | null)
            ?.postingMenu_ResidentSpecific_ShowSizes;
          return Boolean(raw);
        },
        group: 'Print' },
      // rptDietech_Prd_PostingMenu_Cross.NewLayout / IndentSubItems (web only; both on when a print lacks them,
      // so the desktop and schedules print the new layout, indented). Jeff, 2026-11-03: new by default, the old
      // one still printable; sub-items a little in from their item, as before #2804.
      { key: 'NewLayout', label: 'New layout', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true,
        tooltip: 'Clear to print the posting menu as it looked before November 2026.' },
      { key: 'IndentSubItems', label: 'Indent sub-items', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true,
        enabledWhen: { key: 'NewLayout', value: true },
        tooltip: 'Sub-items, such as the syrup under French Toast, start a little in from the item they go with.' },
      // Notes — Designer default `chkOptions_Notes.EditValue = True`.
      // Notes textbox is enabled only when the checkbox is on
      // (mirrors chkOptions_Notes_CheckedChanged behavior).
      { key: 'PrintNotes', label: 'Notes', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true },
      { key: 'Notes', label: '', type: 'text',
        defaultValue: '', group: 'Print',
        enabledWhen: { key: 'PrintNotes', value: true } },

      // Paper Size — default from facility `postingMenu_PaperSize`
      // (0=Letter → false, 1=Legal → true).
      { key: 'IsLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1;
        },
        group: 'Paper Size',
        options: [
          { value: false, label: 'Letter (8.5x11)' },
          { value: true, label: 'Legal (8.5x14)' },
        ] },

      // Resident picker — takes the right column of the report
      // form. `postingSpecificResidentEligible` powers the new
      // endpoint that mirrors WinForms Fill_dtResidents SQL
      // exactly (mealtime attendance + guest recurrence filter),
      // returns regulars + guests, and the ResidentSelector then
      // applies silver-dummy-parent grouping + guest icon + sort
      // by SortName. Column order matches Designer.vb:452-536:
      //   Selection | Name | Diet Order | Static | Unit(Floor) | Room | TF
      { key: 'ListPatient', label: '', type: 'residentSelect',
        defaultValue: [], showInfoColumns: true, showStaticColumn: true,
        postingSpecificResidentEligible: {},
        // Toolbar dropdown mirrors WinForms luResidents — saved lists
        // stored in tblPrd_ResidentSelect_Header/Detail with
        // Type = 1 (SpecificResidentPosting).
        predefinedListType: 1,
        group: '{Resident_Plural}', inline: true },
    ],
    buildDateFor: (v) => formatWeeksRange(v.StartDate, v.NumberOfWeeks, 0),
    // Mirrors WinForms Print settings block (.vb:315-346).
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum from int[] — Diet_Reports.vb:3006-3029.
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );

      // Order By — split UI radio into three wire booleans.
      const orderBy = Number(v.OrderBy ?? 0);

      // Alternates — 4-radio → 3-enum + PrintBlankAlternateSpace.
      // Index 3 (Separate Group w/ Blank Area) collapses to
      // AlternateType.SeparateGroup with PrintBlankAlternateSpace=true
      // — matches WinForms .vb:338-342.
      const altIdx = Number(v.AlternateType ?? 2);
      const alternateType = altIdx === 3 ? 2 : altIdx;
      const printBlankAlternateSpace = altIdx === 3;

      const printNotes = Boolean(v.PrintNotes);
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfWeeks: Number(v.NumberOfWeeks ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,

        // User's resident selection
        ListPatient: (v.ListPatient as number[] | undefined) ?? [],

        // Alternates
        AlternateType: alternateType,
        PrintBlankAlternateSpace: printBlankAlternateSpace,

        // Order By (three-boolean radio group)
        OrderByTrayline: orderBy === 0,
        OrderByRoomNo: orderBy === 1,
        OrderByName: orderBy === 2,

        // Notes — WinForms sends `.Notes = txtNote.Text`
        // UNCONDITIONALLY at .vb:345, regardless of PrintNotes.
        PrintNotes: printNotes,
        Notes: String(v.Notes ?? ''),

        // Options
        ShowDates: Boolean(v.ShowDates),
        ShowSizes: Boolean(v.ShowSizes),
        NewLayout: v.NewLayout !== false,
        // The HTML layout (no DevExpress; docs/posting-menus/html-engine.md) where the renderer has it: website only,
        // so the desktop keeps its layout. Off with New layout: the layout from before November 2026.
        HtmlLayout: v.NewLayout !== false,
        IndentSubItems: v.IndentSubItems !== false,

        IsLegal: Boolean(v.IsLegal),
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const pts = (v.ListPatient as number[] | undefined) ?? [];
      if (pts.length === 0) return 'Please select at least one resident.';
      return null;
    },
  },
  // ─── POSTING - GENERAL MENU (pKey 14, fKey 14) ─────────────────
  // WinForms source: ucDietech_Report_Generator_Posting_GeneralMenu.vb.
  //
  // Init (.vb:85-183):
  //   TabPageText  = "Posting - General Menu"
  //   StartDate    = facility Start_Day_Posting_Menu roll-forward
  //     (today when today's DayOfWeek matches; else next occurrence
  //     of that day). Same helper other Posting reports use.
  //   NumberOfWeeks: MinValue 1, MaxValue = MenuCycleInUseInfo.NumberOfWeeksIncycle.
  //   Mealtimes: 7-item list with facility meal names + nourishments
  //     when `MenuCycle_ShowNourishments` = true.
  //   ShowSizes: default from facility `PostingMenu_General_ShowSize`.
  //   PaperSize: default from facility `PostingMenu_PaperSize`
  //     (0=Letter, 1=Legal).
  //   AlternateType: default from `Posting_Alternates_Default`
  //     (0=NotIncluded, 1=Italicized, 2=SeparateGroup). Mockup
  //     shows Separate Group selected.
  //   Production-only fields (AS/Garnish/IncludeSMOnly): visible
  //     only when facility `UseProduction` = true (.vb:102-110).
  //   AS + Garnish interlock: chkOptions_AlwaysServed.CheckedChanged
  //     disables NumberOfWeeks + Garnish and force-clears Garnish
  //     (.vb:408-412).
  //
  // Print (.vb:308-401) — settings shape `PostingMenuGeneralSettings`:
  //   Two paths — PrintProduction (all fields) or PrintBasic (subset)
  //   chosen from facility `UseProduction`. Both routes serialise
  //   the same VB DTO; basic mode leaves the production-only bools
  //   as their `False` defaults.
  //
  //   .Mealtimes (bit-flag)
  //   .MealtimesInt (int[])
  //   .AlternateType (enum 0/1/2)
  //   .FKey_Facility
  //   .StartDate
  //   .NumberOfWeeks
  //   .PKey_Menu_Header
  //   .PrintAS               (production only — false in basic mode)
  //   .PrintGarnish          (production only)
  //   .IncludeSMOnlyItems    (production only)
  //   .PrintNotes
  //   .ShowNumbersNextToDays
  //   .ShowDates
  //   .StrNotes              (only when PrintNotes)
  //   .StrNotesHTML          (server-derived facility notes — send '')
  //   .IsLegal
  //   .ShowWeekStartDate     (production only)
  //   .IsProduction          (from facility.useProduction)
  //   .ShowSizes             (production only)
  //
  // Validate (.vb:456-475): ≥1 mealtime + valid menu day + menu
  // categories defined (server-side). React port catches the
  // trivial mealtime check up front.
  {
    slug: 'posting-menu-general',
    reportName: 'Posting - General Menu',
    fKey_RS_Report: 14,
    // Both paths take the days, weeks and menu name from the menu sent (PostingMenuGeneral.vb), so the
    // Temp Menu prints whole, Production or not.
    tempMenuSupported: true,
    apiEndpoint: 'PostingMenuGeneral',
    // Slightly wider label column — this report's Print labels run
    // longer than the 150px the family default allows before wrapping.
    groupLayout: {
      ...POSTING_MENU_GROUP_LAYOUT,
      Print: { ...POSTING_MENU_FIELDSET, cols: 'minmax(0, 170px) 1fr' },
    },
    fields: [
      // Date + Number of Weeks on one row (.vb:119-143). NumberOfWeeks
      // is capped in WinForms at `MenuCycleInUseInfo.NumberOfWeeksIncycle`;
      // 12 is a safe React-side upper bound (typical cycles are 1-8).
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfWeeks', label: 'Number of Weeks', type: 'number',
        defaultValue: 1, min: 1, max: 12, width: 240, group: 'Date',
        // Runtime max tracks the current menu cycle's
        // `NumberOfWeeksIncycle` (mirrors WinForms
        // `ucNumberOfWeeks.MaxValue = ucMenuCycleInfo.MenuCycleInUseInfo.NumberOfWeeksIncycle`
        // at .vb:141). `field.max: 12` remains a hard ceiling for
        // the pathological case where the menu cycle reports an
        // unusually large weeks count.
        dynamicMaxFromMenuCycle: 'StartDate',
        // AS interlock (.vb:409): disable NumberOfWeeks when AS on.
        enabledWhen: { key: 'PrintAS', value: false },
        disabledReason: 'Off while AS (Always Served) is ticked under Print: Always Served prints one week. Untick AS to choose the number of weeks.' },

      // Menu Cycle info — display-only, tied to StartDate.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — 7 items column-major (twoColumn) with a 250px
      // left cap so nourishment times sit tight on the right.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes',
        layout: 'twoColumn', firstColumnMaxWidth: 250,
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — 3-option radio matching WinForms enum
      // `AlternateType` (NotIncluded=0, Italicized=1, SeparateGroup=2).
      // Default from facility `Posting_Alternates_Default`; falls
      // back to 2 (Separate Group) per the supplied mockup.
      { key: 'AlternateType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { posting_Alternates_Default?: number } | null)?.posting_Alternates_Default;
          return typeof raw === 'number' && raw >= 0 && raw <= 2 ? raw : 2;
        },
        group: 'Alternates',
        options: [
          { value: 0, label: 'Do Not Print' },
          { value: 1, label: 'Italicized' },
          { value: 2, label: 'Separate Group' },
        ] },

      // Print section — 2-column layout matching the mockup:
      //   Row 1: AS                    · Include Selective Menu Only
      //   Row 2: Date below DOW        · MC Day Next to DOW
      //   Row 3: Garnish               · MC Start Week Date
      //   Row 4: Notes                 · Show Sizes
      //   Row 5: Notes text (spans both cols when PrintNotes on)
      // Production-only fields (AS / IncludeSMOnly / Garnish /
      // ShowSizes / ShowWeekStartDate) still show in the React port
      // regardless of facility.UseProduction — WinForms hides them
      // (.vb:102-110); React sends them regardless and the server
      // gates on `IsProduction`.
      // Tooltip strings copied verbatim from the WinForms Designer
      // (`ucDietech_Report_Generator_Posting_GeneralMenu.Designer.vb`).
      // Only the four Print controls that WinForms decorates with a
      // ToolTip / SuperTip get one — Garnish / IncludeSMOnly / Notes /
      // Show Sizes ship without tooltips in WinForms, so we leave
      // them plain too.
      { key: 'PrintAS', label: 'AS', type: 'checkbox',
        defaultValue: false, group: 'Print',
        // SuperTip at Designer.vb:284-286 = "Always Served".
        tooltip: 'Always Served' },
      { key: 'IncludeSMOnlyItems', label: 'Include Selective Menu Only',
        type: 'checkbox', defaultValue: false, group: 'Print' },
      { key: 'ShowDates', label: 'Date below DOW', type: 'checkbox',
        defaultValue: false, group: 'Print', newRow: true,
        // Designer.vb:249.
        tooltip: 'When checked, the date will appear below the day of the week.' },
      { key: 'ShowNumbersNextToDays', label: 'MC Day Next to DOW',
        type: 'checkbox', defaultValue: false, group: 'Print',
        // Designer.vb:314-315.
        tooltip: 'When checked, the menu cycle day will appear to the right of the day of the week.' },
      // Garnish is disabled + force-cleared when AS is on (.vb:410-411).
      // `enabledWhen` handles the disable; `buildSettings` clamps
      // the wire value so the server never sees Garnish=true with
      // AS=true.
      { key: 'PrintGarnish', label: 'Garnish', type: 'checkbox',
        defaultValue: false, group: 'Print', newRow: true,
        enabledWhen: { key: 'PrintAS', value: false },
        disabledReason: 'Off while AS (Always Served) is ticked.' },
      { key: 'ShowWeekStartDate', label: 'MC Start Week Date',
        type: 'checkbox', defaultValue: true, group: 'Print',
        // Designer.vb:219-220.
        tooltip: 'When checked, the first date of the menu cycle week will appear in the upper left corner of the report.' },
      // rptDietech_Prd_PostingMenu_Cross.NewLayout / IndentSubItems (web only; both on when a print lacks them,
      // so the desktop and schedules print the new layout, indented). Jeff, 2026-11-03: new by default, the old
      // one still printable; sub-items a little in from their item, as before #2804.
      { key: 'NewLayout', label: 'New layout', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true,
        tooltip: 'Clear to print the posting menu as it looked before November 2026.' },
      { key: 'IndentSubItems', label: 'Indent sub-items', type: 'checkbox',
        defaultValue: true, group: 'Print', newRow: true,
        enabledWhen: { key: 'NewLayout', value: true },
        tooltip: 'Sub-items, such as the syrup under French Toast, start a little in from the item they go with.' },
      { key: 'PrintNotes', label: 'Notes', type: 'checkbox',
        defaultValue: false, group: 'Print', newRow: true },
      // ShowSizes default from facility `PostingMenu_General_ShowSize`
      // (.vb:100).
      { key: 'ShowSizes', label: 'Show Sizes', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_General_ShowSize?: boolean } | null)?.postingMenu_General_ShowSize;
          return typeof raw === 'boolean' ? raw : false;
        },
        group: 'Print' },
      // Notes text — always visible per mockup, disabled until
      // PrintNotes is on (mirrors chkOptions_Notes_CheckedChanged
      // at .vb:489-491 which toggles txtNote.Enabled).
      { key: 'StrNotes', label: '', type: 'text',
        defaultValue: '', group: 'Print',
        enabledWhen: { key: 'PrintNotes', value: true } },

      // Paper Size — Letter/Legal radio. Default from facility
      // `postingMenu_PaperSize` (0=Letter → false, 1=Legal → true).
      { key: 'IsLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1;
        },
        group: 'Paper Size',
        options: [
          { value: false, label: 'Letter (8.5x11)' },
          { value: true, label: 'Legal (8.5x14)' },
        ] },
      ...LARGE_PRINT_DESIGN_FIELDS,
    ],
    buildDateFor: (v) => formatWeeksRange(v.StartDate, v.NumberOfWeeks, -1),
    // Fetch facility notes and inject them into the wire payload
    // as `StrNotesHTML`. Mirrors WinForms `PrintProduction` at
    // .vb:350 which calls `GetFacilityMenuNotes(FKey_Facility,
    // PKey_Menu_Header)` right before submitting.
    //
    // Route: `GET /api/MenuFacility/Notes/{fac}/{menuHeader}` →
    // `MenuFacilityDAL.GetNotes`. The DAL's SQL was fixed to use
    // the actual schema columns (`FKey_Facility` +
    // `FKey_Menu_Header`, no `Note_Date`) to match WinForms'
    // `SELECT Note FROM tblMenu_Facility_Notes` query.
    preSubmit: async (v, { facilityId, useTempMenu }) => fetchFacilityMenuNotes(facilityId, v.StartDate, useTempMenu),
    // Mirrors WinForms Print settings block — PrintProduction at
    // .vb:310-367 and PrintBasic at .vb:369-401.
    buildSettings: (v, fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum from int[] — Diet_Reports.vb:3006-3029.
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );
      // AS interlock (.vb:410-411): Garnish force-cleared when AS on.
      const printAS = Boolean(v.PrintAS);
      const printGarnish = !printAS && Boolean(v.PrintGarnish);
      const printNotes = Boolean(v.PrintNotes);
      // IsProduction — from facility `UseProduction`. WinForms
      // Print_Handler at .vb:222 branches on this; we send the
      // flag so the server picks the right code path.
      const useProduction = Boolean(
        (fs as unknown as { useProduction?: boolean } | null)?.useProduction,
      );
      // Basic vs Production mode (.vb:222-226 routes via facility
      // `UseProduction`). PrintBasic at .vb:369-401 leaves the
      // production-only DTO members at their VB defaults — so we
      // must force those defaults on the wire when the facility
      // isn't in production mode. Otherwise the React UI (which
      // shows every checkbox regardless of facility mode) would
      // send user-toggled values that WinForms never sent.
      //   IncludeSMOnlyItems / PrintAS / PrintGarnish / ShowSizes → False
      //   ShowWeekStartDate → True (VB DTO default at .vb:22)
      //   StrNotesHTML → '' (not fetched in Basic mode)
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfWeeks: Number(v.NumberOfWeeks ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,
        AlternateType: Number(v.AlternateType ?? 2),
        // ─── Production-only booleans (.vb:333, 341, 335-346) ─
        PrintAS: useProduction ? printAS : false,
        PrintGarnish: useProduction ? printGarnish : false,
        IncludeSMOnlyItems: useProduction ? Boolean(v.IncludeSMOnlyItems) : false,
        ShowSizes: useProduction ? Boolean(v.ShowSizes) : false,
        NewLayout: v.NewLayout !== false,
        // The HTML layout (no DevExpress; docs/posting-menus/html-engine.md) where the renderer has it: website only,
        // so the desktop keeps its layout. Off with New layout: the layout from before November 2026.
        HtmlLayout: v.NewLayout !== false,
        IndentSubItems: v.IndentSubItems !== false,
        ShowWeekStartDate: useProduction ? Boolean(v.ShowWeekStartDate) : true,
        // ─── Shared booleans (both modes send these) ──────────
        PrintNotes: printNotes,
        ShowNumbersNextToDays: Boolean(v.ShowNumbersNextToDays),
        ShowDates: Boolean(v.ShowDates),
        // Notes text — WinForms .vb:348 / .vb:386 only sets
        // StrNotes when PrintNotes on. Both modes.
        StrNotes: printNotes ? String(v.StrNotes ?? '') : '',
        // StrNotesHTML — Production-only. `preSubmit` fetches the
        // facility notes and merges them into `v.StrNotesHTML`
        // before we run. Basic mode doesn't set the field in
        // WinForms (.vb:369-401), so send '' there.
        StrNotesHTML: useProduction ? String(v.StrNotesHTML ?? '') : '',
        IsLegal: Boolean(v.IsLegal),
        IsProduction: useProduction,
        ...postingLargePrintSettings(v),
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      return null;
    },
  },
  // ─── POSTING - GENERAL MENU 2 (pKey 15, fKey 15) ───────────────
  // WinForms source: ucDietech_Report_Generator_Posting_GeneralMenu2.vb.
  //
  // Init (.vb:83-139):
  //   TabPageText  = "Posting - General Menu 2"
  //   StartDate    = facility Start_Day_Posting_Menu roll-forward
  //     (today when today's DayOfWeek matches, else next occurrence).
  //   NumberOfWeeks: MinValue 1, MaxValue = NumberOfWeeksIncycle.
  //   Mealtimes: 7-item list with facility meal names + nourishments
  //     when `MenuCycle_ShowNourishments` = true.
  //   PaperSize: default from facility `PostingMenu_PaperSize`
  //     (0=Letter, 1=Legal).
  //   Alternates: 2-radio group (Do Not Print / Print). The
  //     Designer doesn't set an explicit default — WinForms lands
  //     on whichever radio has `EditValue=True` in Designer.vb;
  //     the supplied mockup shows Print selected, so default true.
  //
  // Print (.vb:268-286) — settings shape `PostingMenuGeneral2Settings`:
  //   .Mealtimes (bit-flag)
  //   .MealtimesInt (int[])
  //   .NumberOfWeeks
  //   .StartDate
  //   .PKey_Menu_Header
  //   .FKey_Facility
  //   .IncludeSMOnlyItems (chkIncludeSelectiveMenuOnly)
  //   .PrintAlternates (radAlternates_Print.Checked)
  //   .IsLegal (radPaperLegal.Checked)
  // Simpler than General Menu — no Production/Basic branch, no
  // AS/Garnish/Notes/Show*.
  //
  // Validate (.vb:393-412): ≥1 mealtime + valid menu day + menu
  // categories defined (type=8, server-side) + NumberOfWeeks valid.
  {
    slug: 'posting-menu-general-2',
    reportName: 'Posting - General Menu 2',
    fKey_RS_Report: 15,
    apiEndpoint: 'PostingMenuGeneral2',
    groupLayout: POSTING_MENU_GROUP_LAYOUT,
    fields: [
      // Date + Number of Weeks on one row. NumberOfWeeks capped at
      // 12 (typical menu cycles run 1-8 weeks; WinForms uses
      // MenuCycleInUseInfo.NumberOfWeeksIncycle).
      { key: 'StartDate', label: '', type: 'date',
        width: 220,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const rawDay = (fs as unknown as { start_Day_Posting_Menu?: number } | null)?.start_Day_Posting_Menu;
          const startDay = typeof rawDay === 'number' ? rawDay : 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (today.getDay() === startDay) return today;
          const target = new Date(today);
          target.setDate(target.getDate() + 7);
          while (target.getDay() !== startDay) {
            target.setDate(target.getDate() - 1);
          }
          return target;
        },
        required: true, group: 'Date' },
      { key: 'NumberOfWeeks', label: 'Number of Weeks', type: 'number',
        defaultValue: 1, min: 1, max: 12, width: 240, group: 'Date',
        // Runtime max tracks the current menu cycle's
        // `NumberOfWeeksIncycle` (mirrors WinForms
        // `ucNumberOfWeeks.MaxValue = ucMenuCycleInfo.MenuCycleInUseInfo.NumberOfWeeksIncycle`
        // at .vb:126). `field.max: 12` remains a hard ceiling.
        dynamicMaxFromMenuCycle: 'StartDate' },

      // Menu Cycle info — display-only, tied to StartDate.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — 7 items column-major (twoColumn) with a 250px
      // left cap so nourishment times sit tight on the right.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox',
        // WinForms: IncludeNourishments = MenuCycle_ShowNourishments (#2003).
        nourishmentsSetting: 'menuCycle_ShowNourishments',
        defaultValue: [], group: 'Mealtimes',
        layout: 'twoColumn', firstColumnMaxWidth: 250,
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Alternates — 2-option radio matching WinForms
      // radAlternates_DoNotPrint / radAlternates_Print. Wire field
      // `PrintAlternates: boolean` (false = Do Not Print,
      // true = Print). Default true per the supplied mockup.
      { key: 'PrintAlternates', label: '', type: 'radio',
        defaultValue: true, group: 'Alternates',
        options: [
          { value: false, label: 'Do Not Print' },
          { value: true, label: 'Print' },
        ] },

      // Print section — single checkbox matching the mockup.
      { key: 'IncludeSMOnlyItems', label: 'Include Selective Menu Only',
        type: 'checkbox', defaultValue: false, group: 'Print' },

      // Paper Size — Letter/Legal radio. Default from facility
      // `postingMenu_PaperSize` (0=Letter → false, 1=Legal → true).
      { key: 'IsLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as unknown as { postingMenu_PaperSize?: number } | null)?.postingMenu_PaperSize;
          return typeof raw === 'number' && raw === 1;
        },
        group: 'Paper Size',
        options: [
          { value: false, label: 'Letter (8.5x11)' },
          { value: true, label: 'Legal (8.5x14)' },
        ] },
    ],
    buildDateFor: (v) => formatWeeksRange(v.StartDate, v.NumberOfWeeks, 0),
    // Mirrors WinForms Print settings block (.vb:270-282).
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const mealtimesInt = (v.MealtimesInt as number[] | undefined) ?? [];
      // Bit-flag Mealtime enum from int[] — Diet_Reports.vb:3006-3029.
      const bitMap = [1, 2, 4, 8, 16, 32, 64];
      const mealtimesFlag = mealtimesInt.reduce(
        (acc, i) => acc | (bitMap[i] ?? 0),
        0,
      );
      return {
        FKey_Facility: 0, // server fills from URL
        StartDate: v.StartDate,
        NumberOfWeeks: Number(v.NumberOfWeeks ?? 1),
        Mealtimes: mealtimesFlag,
        MealtimesInt: mealtimesInt,
        PKey_Menu_Header: pKeyMenuHeader,
        PrintAlternates: Boolean(v.PrintAlternates),
        IncludeSMOnlyItems: Boolean(v.IncludeSMOnlyItems),
        IsLegal: Boolean(v.IsLegal),
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      return null;
    },
  },

  // ─── NOURISHMENTS (combined pKey 5/6/7) ────────────────────────
  // WinForms source: ucDietech_Report_Generator_Nourishment_New.vb.
  // Combines the three separate WinForms reports (Summary / List /
  // Labels — one report class per pKey in ReportDispatcher.cs:27-29)
  // AND the ByFloor variants into a SINGLE React card driven by
  // a Report Type radio + a "By {FloorOrUnit}" Options checkbox.
  //
  // Print — one of six branches based on
  //   `UseProduction × ReportType`:
  //   .FKey_Facility       = _FKey_Facility
  //   .StartDate           = ucDeStart.EditValue
  //   .EndDate             = StartDate + (NumberOfDays - 1)
  //   .NumberOfDays        = ucNumberOfDays.EditValue
  //   .LstMealtimes        = ucMealltimesTab.GetCheckedMealtimes
  //                          (List<int[]>, one entry per day)
  //   .UseProduction       = g_facilitySettings.UseProduction
  //   .UseUnitMapping      = g_facilitySettings.UseUnitMapping
  //   .NourishmentReportType = ReportType enum (Summary=0/List=1/Labels=2)
  //   .SpecificFloorUnit   = chkSpcificUnit.Checked (radio arm "Unit")
  //   .LstFloorUnits       = ucClb_FloorUnit.GetCheckedFloorUnits
  //                          (when SpecificFloorUnit)
  //   .SpecificResident    = radPrintResident.Checked (radio arm "Resident")
  //   .PatientList         = selected residents in gvTrayTickets
  //                          (when SpecificResident)
  //   .ByFloor             = _byFloor  (constructor arg on the
  //                          WinForms class; exposed here as the
  //                          Options → "By {FloorOrUnit}" checkbox)
  //   .LabelsToSkip / .LabelType — Labels branch only; wired to
  //                          the WinForms label selector. React
  //                          port sends 0 for LabelsToSkip.
  //   report class         = NourishmentSummary(5) / NourishmentList(6)
  //                          / NourishmentLabels(7). resolveFkeyReport
  //                          routes each ReportType radio choice.
  //   queue title          = "Nourishment Summary" / "Nourishment List"
  //                          / "Nourishment Labels" (.vb:413/443/474)
  //
  // Init (.vb:116-190):
  //   radLabels default checked (Designer .vb:178 EditValue=True).
  //   radPrintAll default checked (Designer .vb:450 EditValue=True).
  //   ucDeStart.DateInit = _dateTimeInitialized (today).
  //   ucNumberOfDays: Min=1, Max=9, default 1 (.vb:153-158).
  //   ucMenuCycleInfo bound to StartDate (IsTrayTicket=True).
  //   ucMealltimesTab.IncludeNourishments=True + IncludeMainMealtimes=False
  //     → nourishment mealtimes only (EAM/AM/PM/HS values 3..6).
  //   Print/Unit/Resident is a single radio group
  //     (radPrintAll/chkSpcificUnit/radPrintResident all share
  //     RadioGroupIndex=1 in the Designer — chkSpcificUnit is
  //     styled as a checkbox but participates in the radio group).
  //   Facility Resident label swaps the "Resident" radio caption
  //     (per .vb:141 `radPrintResident.Text = _strResident`).
  //
  // Validate (.vb:1142-1178):
  //   - production menu-cycle must be valid when UseProduction.
  //   - each per-day mealtimes array must be non-empty.
  //   - Resident mode: at least one resident selected.
  {
    slug: 'nourishments',
    reportName: 'Nourishments',
    // Static default — resolveFkeyReport swaps at submit based on
    // the Report Type radio. Value used by the sidebar card entry
    // and as a fallback for the queue endpoint.
    fKey_RS_Report: 7,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    resolveFkeyReport: (v) => {
      // ReportType radio → NourishmentSettings.ReportType enum:
      //   Summary = 0 → server pKey 5
      //   List    = 1 → server pKey 6
      //   Labels  = 2 → server pKey 7
      const t = Number(v.ReportType);
      if (t === 0) return 5;
      if (t === 1) return 6;
      return 7;
    },
    apiEndpoint: 'Nourishments',
    fields: [
      // Facility tree — same PT_ShowMultiFacility + >1 user facility gate as the
      // other reports (Nourishment_New.vb:140), but LABELS ONLY. The Summary /
      // Detail / Acceptance nourishment reports are single-facility; only the
      // Labels output combines facilities.
      //
      // `recomputeWhen: ['ReportType']` clears the checked facilities when the
      // user leaves Labels, which is what unttabs the pickers below and returns
      // the payload to its single-facility shape. Switching back re-seeds the
      // root. Without the clear, a hidden field would keep its stale value and
      // the selectors would stay tabbed.
      { key: 'LstFacilityMultiple', label: '', type: 'facilityTree',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => (
          Number(values?.ReportType) === 2
            ? ((values?.LstFacilityMultiple as number[] | undefined) ?? [])
            : []
        ),
        recomputeWhen: ['ReportType'],
        // Hide the whole group when the facility has multi-facility reporting
        // off, so single-facility sites see no empty fieldset. The second half
        // of the WinForms gate (user has >1 facility) needs the facility list,
        // which only the field itself can see - it reports that case inline.
        visibleWhen: { check: (v) => Number(v.ReportType) === 2 && Boolean(
          (useGlobalStore.getState().facilitySettings as { pT_ShowMultiFacility?: boolean } | null)?.pT_ShowMultiFacility,
        ) },
        group: '{Facility_Plural}' },

      // Report Type radio — Labels default (Designer .vb:178
      // radLabels EditValue=True). Enum values match
      // NourishmentSettings.ReportType.
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 2,
        group: 'Report Type',
        options: [
          { value: 2, label: 'Labels' },
          { value: 1, label: 'List' },
          { value: 0, label: 'Summary' },
        ] },

      // By {FloorOrUnit} checkbox — exposes the WinForms
      // `_byFloor` constructor arg. Sits inline to the right of
      // the Report Type radios (shares the group's flex row).
      { key: 'ByFloor', label: 'By {FloorOrUnit}', type: 'checkbox',
        defaultValue: false, group: 'Report Type' },

      // Date — StartDate defaults to today. NumberOfDays sits
      // inline (width 265 fits the "Number of Days:" label + spin
      // arrow buttons).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },
      { key: 'NumberOfDays', label: 'Number of Days:', type: 'number',
        defaultValue: 1, min: 1, max: 9, group: 'Date', width: 265 },

      // Menu Cycle info display — bound to StartDate (.vb:160-165).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — per-day tabbed list of nourishment mealtimes
      // (`IncludeNourishments=True`, `IncludeMainMealtimes=False`
      // at .vb:169-170). Values 3..6 match the WinForms Mealtime
      // enum for nourishment slots.
      // `layout: 'row'` renders the 4 nourishment options in a
      // 2-column grid (matches the WinForms `ClbMealtime`
      // `ColumnWidth = 90` output for wider option lists — see
      // feedback memory `feedback_multicheck_mealtime_layout` for
      // when to prefer 'column').
      { key: 'LstMealtimesPerDay', label: '', type: 'mealtimesPerDay',
        defaultValue: [[]] as number[][],
        group: 'Mealtimes', layout: 'row',
        // WinForms .vb:192-195 sets IncludeNourishments = True and
        // IncludeMainMealtimes = False — nourishments only. Built from settings so
        // PM2 / HS2 appear, the facility's nourishment order is honoured, EAM
        // follows the UseProduction rule, and unnamed meals are dropped.
        mealtimeOptions: { includeMain: false, includeNourishments: true },
        watchGroup: { start: 'StartDate', days: 'NumberOfDays' },
        options: [
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Print — 3-way radio (All / Unit / Patient). Mirrors the
      // WinForms RadioGroupIndex=1 group of radPrintAll +
      // chkSpcificUnit + radPrintResident. Values map to the
      // wire fields: All=0 sends neither SpecificFloorUnit nor
      // SpecificResident; Unit=1 sends SpecificFloorUnit=true;
      // Patient=2 sends SpecificResident=true.
      // Resident label uses `{Resident}` token so a facility that
      // relabels resident → "Patient" gets the right text (matches
      // .vb:141 `radPrintResident.Text = _strResident`).
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 0,
        group: 'Print',
        options: [
          { value: 0, label: 'All' },
          { value: 1, label: 'Unit' },
          { value: 2, label: '{Resident}' },
        ] },

      // Floor/Unit picker — visible only when PrintMode === 1
      // (Unit). Matches WinForms lcgUnits visibility toggle at
      // .vb:1048-1054 (chkSpcificUnit_CheckedChanged).
      // `defaultSelectAll` matches WinForms
      // `ucClb_FloorUnit.Fill_clbFloorUnits → .CheckAll()` — all
      // units start checked when the panel opens.
      { key: 'LstFloorUnits', label: '', type: 'floorUnitSelect',
        defaultValue: [], group: 'Floor/Unit', inline: true,
        defaultSelectAll: true, multiFacility: true,
        visibleWhen: { key: 'PrintMode', value: 1 } },

      // Resident picker — visible only when PrintMode === 2.
      // Matches WinForms lcgResident visibility toggle at
      // .vb:1035-1045 (radPrintResident_CheckedChanged).
      { key: 'PatientList', label: '', type: 'residentSelect',
        defaultValue: [], group: '{Resident_Plural}', inline: true, multiFacility: true,
        visibleWhen: { key: 'PrintMode', value: 2 } },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Pre-print label-selector dialog — WinForms
    // `ucDietech_Report_Generator_Nourishment_New.vb:366-386`:
    //   • Gate: `LabelReports_UseLabelSelector` (facility bool).
    //     Off → skip the dialog entirely, LabelsToSkip stays 0.
    //   • Variant pick: `Nourishment_Labels_Type`.
    //       0 → frmLabelSelector_A5162 (variant "A5162")
    //       else → generic 30-up dialog (variant "generic30")
    // Only fires for the Labels report (ReportType === 2); the
    // Summary/List reports don't print labels.
    preSubmit: async (v, { facilitySettings, showLabelSelector }) => {
      if (Number(v.ReportType) !== 2) return {};
      const fs = facilitySettings as {
        labelReports_UseLabelSelector?: boolean;
        nourishment_Labels_Type?: number;
      } | null;
      if (!fs?.labelReports_UseLabelSelector) return {};
      const variant = (fs.nourishment_Labels_Type ?? 0) === 0
        ? 'A5162'
        : 'generic30';
      const result = await showLabelSelector(variant);
      // Close/Escape → silent cancel sentinel: abort the report
      // submit without a toast. Matches WinForms's early-return
      // after `frmLabelSelector.ShowDialog() = DialogResult.Cancel`.
      if (result === 'cancel') return { error: '' };
      return { LabelsToSkip: result };
    },
    // Mirrors WinForms Print settings blocks (.vb:389-476 basic
    // branches / .vb:481-596 production branches — same wire
    // fields, the server picks the concrete report by pKey +
    // UseProduction facility setting).
    buildSettings: (v, fs, ctx) => {
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      // Local calendar day: `new Date('yyyy-MM-dd')` is UTC midnight,
      // the evening before in US zones.
      const start = parseDateOnly(v.StartDate) ?? parseDateOnly(new Date())!;
      const end = new Date(start);
      end.setDate(end.getDate() + numDays - 1);
      const printMode = Number(v.PrintMode ?? 0);
      const perDay = ((v.LstMealtimesPerDay as number[][] | undefined) ?? []);
      // Pad / truncate to match NumberOfDays — WinForms
      // `List<int[]>` has one entry per day.
      const lstMealtimes: number[][] = Array.from({ length: numDays }, (_, i) => (
        Array.isArray(perDay[i]) ? [...perDay[i]] : []
      ));
      // Labels-only field — matches WinForms
      // `.LabelType = g_facilitySettings.Nourishment_Labels_Type`
      // (.vb:397). Server uses this to switch the print template.
      // 0 for non-Labels reports so the payload stays clean.
      const isLabels = Number(v.ReportType) === 2;
      const labelType = isLabels
        ? Number((fs as { nourishment_Labels_Type?: number } | null)?.nourishment_Labels_Type ?? 0)
        : 0;

      // ── Multi-facility ────────────────────────────────────────
      // NOTE: WinForms Nourishment gates on `GetCheckedNodes.Count > 1`
      // (.vb:548) where ProductionTally / TrayTickets gate on
      // `_showMultiFacility` alone — so WinForms sends a one-element list
      // from those two and nothing from this one. Normalised here to the
      // ProductionTally behavior: a single checked facility still takes the
      // multi path, which the generator handles identically (its loop runs
      // once). Keeps all three reports on one wire contract.
      const checkedFacilityIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
      const isMultiFacility = checkedFacilityIds.length > 0;
      const mfArg = isMultiFacility ? checkedFacilityIds : undefined;
      const mfNull = { ids: null, names: null, selections: null };
      const mfUnits = ctx?.resolveMultiFacility?.(mfArg, v.LstFloorUnits as Record<string, unknown> | undefined) ?? mfNull;
      const mfResidents = ctx?.resolveMultiFacility?.(mfArg, v.PatientList as Record<string, unknown> | undefined) ?? mfNull;

      const multi = {
        lstFKey_Facility_Multiple: mfUnits.ids,
        lstFacilityNames_Multiple: mfUnits.names,
        lstUnits_Multiple: isMultiFacility && printMode === 1 ? mfUnits.selections : null,
        lstResidents_Multiple: isMultiFacility && printMode === 2 ? mfResidents.selections : null,
        // Flat (name, ordinal) union, not per-facility — the generator filters
        // it per facility via GetAllMealNames (NourishmentLabels.vb:47-53).
        lstMealtime_MultipleNames: isMultiFacility
          ? ctx?.resolveMealtimeNames?.(checkedFacilityIds, { includeMain: false, includeNourishments: true }) ?? null
          : null,
      };

      return {
        NourishmentReportType: Number(v.ReportType),
        StartDate: start,
        EndDate: end,
        LstMealtimes: lstMealtimes,
        UseProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
        UseUnitMapping: Boolean(fs?.useUnitMapping ?? fs?.UseUnitMapping ?? false),
        SpecificFloorUnit: printMode === 1,
        LstFloorUnits: printMode === 1 && !isMultiFacility ? ((v.LstFloorUnits as number[] | undefined) ?? []) : [],
        SpecificResident: printMode === 2,
        PatientList: printMode === 2 && !isMultiFacility ? ((v.PatientList as number[] | undefined) ?? []) : [],
        ByFloor: Boolean(v.ByFloor),
        ...multi,
        // Default 0 — overridden by the preSubmit hook above when
        // the label-selector dialog runs and the user picks a
        // non-first starting label. `preSubmitExtras` spreads on
        // top of the wireSettings in GenericReportForm.handleSubmit
        // so the wire value wins.
        LabelsToSkip: 0,
        LabelType: labelType,
      };
    },
    // Mirrors ValidateForm (.vb:1142-1178).
    validate: (v) => {
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      const perDay = ((v.LstMealtimesPerDay as number[][] | undefined) ?? []);
      for (let i = 0; i < numDays; i++) {
        const day = perDay[i];
        if (!Array.isArray(day) || day.length === 0) {
          return numDays === 1
            ? 'Please select at least one mealtime.'
            : `Please select at least one mealtime for Day ${i + 1}.`;
        }
      }
      const printMode = Number(v.PrintMode ?? 0);
      // Multi-facility: GetEmptySelectionCount parity (Nourishment_New.vb:1231).
      const checkedFacilityIds = (v.LstFacilityMultiple as number[] | undefined) ?? [];
      if (checkedFacilityIds.length > 0) {
        const emptyFor = (raw: unknown) => {
          const map = (raw ?? {}) as Record<number, number[]>;
          return checkedFacilityIds.filter((id) => (map[id] ?? []).length === 0).length;
        };
        if (printMode === 1 && emptyFor(v.LstFloorUnits) > 0) {
          return 'One or more {Facility_Plural} do not have any {FloorOrUnit} selected.  Either select at least one {FloorOrUnit} or remove {Facility} from selected list.';
        }
        if (printMode === 2 && emptyFor(v.PatientList) > 0) {
          return 'One or more {Facility_Plural} do not have any {Resident_Plural} selected.  Either select at least one {Resident} or remove {Facility} from selected list.';
        }
        return null;
      }
      if (printMode === 1) {
        const units = (v.LstFloorUnits as number[] | undefined) ?? [];
        if (units.length === 0) return 'Please select at least one {FloorOrUnit}.';
      }
      if (printMode === 2) {
        const residents = (v.PatientList as number[] | undefined) ?? [];
        if (residents.length === 0) return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },

  // ─── BEVERAGE ───
  {
    slug: 'beverage',
    reportName: 'Beverage',
    fKey_RS_Report: 0,
    fields: [
      { key: 'StartDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'MealtimesInt', group: 'Mealtimes', label: 'Mealtimes', type: 'multiCheckbox', defaultValue: [0, 1, 2], options: [{ value: 0, label: 'Breakfast' }, { value: 1, label: 'Lunch' }, { value: 2, label: 'Dinner' }] },
      { key: 'PKey_Menu_Header', group: 'Menu Cycle', label: 'Menu Cycle', type: 'menuCycleSelect', defaultValue: 0 },
      { key: 'IsProduction', group: 'Options', label: 'Use Production Data', type: 'checkbox', defaultValue: false },
      { key: 'AllSetsSelected', group: 'Selection', label: 'All Sets', type: 'checkbox', defaultValue: true },
      { key: 'SetList', group: 'Selection', label: 'Select Sets', type: 'setSelect', defaultValue: [], visibleWhen: { key: 'AllSetsSelected', value: false } },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
  },
  // ─── BEVERAGE — BY COLD/HOT (pKey 22, fKey 22) ─────────────────
  // WinForms source: ucDietech_Report_Generator_Beverages.vb hosts
  // FIVE beverage report variants selected by a radio (Cold/Hot,
  // Meal, Resident, Set, Total). Each variant instantiates a
  // different report class (BeverageByHotCold / BeverageByMeal /
  // BeverageByResident / BeverageBySet / BeverageTotal) with a
  // different `BeverageSettings.ReportTypeSelected` enum value.
  // We split them into separate React configs so each has its own
  // card + fields tailored to that variant's Print_Handler branch.
  //
  // Print_Handler ColdHot branch (.vb:254-269):
  //   .IsProduction       = g_facilitySettings.UseProduction(FKey_Facility)
  //   .PKey_Menu_Header   = mci.PKey_Menu_Header  (server derives — we send -999999)
  //   .FKey_Facility      = FKey_Facility
  //   .StartDate          = ucDate.EditValue
  //   .SetList            = ucClbSetsTab.GetCheckedSetsList
  //   .MealtimesInt       = ucClbMealtimesTab.GetCheckMealtimes
  //   .ReportTypeSelected = BeverageSettings.ReportType.ColdHot (0)
  //   detailedReportName  = "Beverage - By Cold/Hot"
  //
  // Init (.vb:85-146):
  //   ucDate.DateInit          = Now                    → today default
  //   ucDate.LabelVisible      = False                  → no inline label
  //   .IncludeNourishments     = True on mealtimes tab  → all 7 mealtimes
  //   .SetItemChecks(empty)                             → no mealtime checks
  //   ucClbSetsTab.Init                                 → sets tab
  //
  // Validate (.vb:528-545): ≥1 mealtime, ≥1 set, ≤19 sets.
  {
    slug: 'beverage-by-cold-hot',
    reportName: 'Beverage - By Cold/Hot',
    fKey_RS_Report: 22,
    apiEndpoint: 'BeverageByHotCold',
    fields: [
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Tabbed selection — Mealtimes (all 7, empty by default) +
      // Cart ({SetName_Plural}, all checked). Matches WinForms
      // tabbed layout (screenshot) with mealtimes tab + sets tab.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
      { key: 'SetList', label: '', type: 'setSelect', defaultValue: [],
        group: 'Selection', tab: '{SetName_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler ColdHot branch (.vb:254-269).
    //   - `IsProduction` comes from `g_facilitySettings.UseProduction`
    //   - `PKey_Menu_Header = -999999` tells the server-side
    //     `GetMenuCycleInUseInfo` to skip the override branch and
    //     derive the menu header from facility + date (matches
    //     `_FKey_Menu_Header_Override` class default at GetMenuCycleInUseInfo.vb:17).
    //     Without the sentinel Newtonsoft would deserialize a
    //     missing field as `0`, causing the server to look for
    //     `PKey_Menu_Hdr = 0`, which doesn't exist.
    //   - `ReportTypeSelected = 0` matches `BeverageSettings.ReportType.ColdHot`.
    buildSettings: (v, fs) => ({
      IsProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
      PKey_Menu_Header: -999999,
      StartDate: v.StartDate,
      SetList: v.SetList ?? [],
      MealtimesInt: v.MealtimesInt,
      ReportTypeSelected: 0,
    }),
    // Mirrors ValidateForm ColdHot arm (.vb:528-545).
    validate: (v) => {
      if (!Array.isArray(v.MealtimesInt) || v.MealtimesInt.length === 0) {
        return 'Please select at least one mealtime.';
      }
      if (!Array.isArray(v.SetList) || v.SetList.length === 0) {
        return 'Please select at least one {SetName}.';
      }
      if (Array.isArray(v.SetList) && v.SetList.length > 19) {
        return 'Sorry, but you cannot select more than 19 {SetName_Plural}.';
      }
      return null;
    },
  },
  // ─── BEVERAGE — BY MEAL (pKey 23, fKey 23) ─────────────────────
  // WinForms source: ucDietech_Report_Generator_Beverages.vb hosts
  // the ByMeal variant in the same generator as ByCold/Hot. Same
  // UI (Date group + Selection tabs Mealtimes / Cart), same init
  // logic (.vb:85-146), same validate rules (.vb:528-545). Only
  // the Print_Handler branch (.vb:270-286) differs:
  //   .ReportTypeSelected = BeverageSettings.ReportType.Meal (1)
  //   report class         = BeverageByMeal (server pKey 23)
  //   detailedReportName   = "Beverage - By Meal"
  {
    slug: 'beverage-by-meal',
    reportName: 'Beverage - By Meal',
    fKey_RS_Report: 23,
    apiEndpoint: 'BeverageByMeal',
    fields: [
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Tabbed selection — Mealtimes (all 7, empty by default) +
      // Cart ({SetName_Plural}, all checked). Identical to ColdHot
      // per WinForms shared `radColdHot_CheckedChanged` handler
      // (.vb:454-460) which also handles `radMeal.CheckedChanged`
      // and shows the same tab strip.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
      { key: 'SetList', label: '', type: 'setSelect', defaultValue: [],
        group: 'Selection', tab: '{SetName_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler Meal branch (.vb:270-286). Only
    // `ReportTypeSelected` differs from ColdHot — both variants
    // send the same six other wire fields, matching the shared
    // `With settings` block.
    buildSettings: (v, fs) => ({
      IsProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
      PKey_Menu_Header: -999999,
      StartDate: v.StartDate,
      SetList: v.SetList ?? [],
      MealtimesInt: v.MealtimesInt,
      ReportTypeSelected: 1,
    }),
    // Mirrors ValidateForm Meal arm (.vb:528-545) — same as
    // ColdHot: ≥1 mealtime, ≥1 set, ≤19 sets.
    validate: (v) => {
      if (!Array.isArray(v.MealtimesInt) || v.MealtimesInt.length === 0) {
        return 'Please select at least one mealtime.';
      }
      if (!Array.isArray(v.SetList) || v.SetList.length === 0) {
        return 'Please select at least one {SetName}.';
      }
      if (Array.isArray(v.SetList) && v.SetList.length > 19) {
        return 'Sorry, but you cannot select more than 19 {SetName_Plural}.';
      }
      return null;
    },
  },
  // ─── BEVERAGE — BY RESIDENT (pKey 24, fKey 24) ─────────────────
  // WinForms source: ucDietech_Report_Generator_Beverages.vb hosts
  // the ByResident variant in the same generator. Print_Handler
  // branch at .vb:288-313:
  //   .IsProduction       = g_facilitySettings.UseProduction(FKey_Facility)
  //   .PKey_Menu_Header   = mci.PKey_Menu_Header  (server derives via -999999 sentinel)
  //   .FKey_Facility      = FKey_Facility
  //   .StartDate          = ucDate.EditValue
  //   .SetList            = ucClbSet.GetCheckedSetsList  (side-column selector, defaults all-checked; NOT read by BeverageByResident.vb — sent for parity)
  //   .ReportTypeSelected = BeverageSettings.ReportType.Resident (2)
  //   .PrintOrder         = Name/Room/TrayLine (from chkSortName/RoomNumber/Trayline radio group)
  //   .MealtimeForSort    = cmbTrayLineOrder.SelectedIndex (main-meal-name first-char dropdown)
  //   report class        = BeverageByResident (server pKey 24)
  //   detailedReportName  = String.Format("Beverage - By {0}", g_facilitySettings.Resident(FKey_Facility))
  //
  // Init defaults:
  //   .Designer.vb:210 chkSortName.EditValue = True     → default sort by Name
  //   .vb:137 cmbTrayLineOrder.SelectedIndex = 0        → first main meal (B by default)
  //
  // UI differs from Cold/Hot / Meal:
  //   .vb:463-471 radResident_CheckedChanged hides both the mealtimes tab strip
  //   and the sets tab strip, showing only the "Order By" group. Mealtimes and
  //   Set/Cart selection are irrelevant to this report.
  //
  // Validate (.vb:527-553): no radResident branch → no per-field validation.
  {
    slug: 'beverage-by-resident',
    reportName: 'Beverage - By Resident',
    fKey_RS_Report: 24,
    apiEndpoint: 'BeverageByResident',
    fields: [
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Order By radio: values match PatientSortOrder enum
      // (TrayLine=0, Room=1, Name=2) so buildSettings sends the
      // raw value with no re-mapping. Default = Name (chkSortName
      // is the WinForms default per Designer.vb:210).
      { key: 'PrintOrder', label: '', type: 'radio', defaultValue: 2, group: 'Order By',
        options: [
          { value: 2, label: 'Name' },
          { value: 1, label: 'Room Number' },
          { value: 0, label: 'Tray Line' },
        ] },
      // Tray-Line meal-of-day dropdown — appears only when Tray
      // Line sort is picked (matches WinForms
      // `chkSortTrayline_CheckedChanged` at .vb:600-604 which
      // enables/disables `cmbTrayLineOrder`). Labels show the
      // first character of each main meal name, mirroring
      // `cmbTrayLineOrder` init (.vb:129-135) which pushes
      // `strMeals(i)(0)` — the first char of Breakfast / Lunch /
      // Dinner. Wire value = selected index (0-based).
      { key: 'MealtimeForSort', label: '', type: 'dropdown', defaultValue: 0,
        group: 'Order By', width: 65,
        options: [
          { value: 0, label: 'B' },
          { value: 1, label: 'L' },
          { value: 2, label: 'D' },
        ],
        enabledWhen: { key: 'PrintOrder', value: 0 } },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler radResident branch (.vb:288-309).
    //   - `SetList = []` because WinForms sends
    //     `ucClbSet.GetCheckedSetsList` (all-checked default) but
    //     `BeverageByResident.vb` NEVER reads `settings.SetList`.
    //     Sending an empty array is safe and avoids the extra
    //     roundtrip of fetching all cart numbers just to send them
    //     back for a field the server ignores.
    //   - `ReportTypeSelected = 2` matches
    //     `BeverageSettings.ReportType.Resident`.
    //   - `PrintOrder` is the PatientSortOrder enum value (0/1/2).
    //   - `MealtimeForSort` is the raw dropdown index; server
    //     reads it in the TrayLine sort branch.
    buildSettings: (v, fs) => ({
      IsProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
      PKey_Menu_Header: -999999,
      StartDate: v.StartDate,
      SetList: [],
      ReportTypeSelected: 2,
      PrintOrder: v.PrintOrder,
      MealtimeForSort: v.MealtimeForSort ?? 0,
    }),
    // WinForms ValidateForm has no radResident branch (.vb:527-553).
    validate: () => null,
  },
  // ─── BEVERAGE — BY SET (pKey 25, fKey 25) ──────────────────────
  // WinForms source: ucDietech_Report_Generator_Beverages.vb hosts
  // the BySet variant. Print_Handler branch at .vb:314-328:
  //   .IsProduction       = g_facilitySettings.UseProduction(FKey_Facility)
  //   .PKey_Menu_Header   = mci.PKey_Menu_Header  (server derives via -999999 sentinel)
  //   .FKey_Facility      = FKey_Facility
  //   .StartDate          = ucDate.EditValue
  //   .SetList            = ucClbSet.GetCheckedSetsList  (side-column selector — no default check-all)
  //   .ReportTypeSelected = BeverageSettings.ReportType.BySet (3)
  //   report class        = BeverageBySet (server pKey 25)
  //   detailedReportName  = String.Format("Beverage - By {0}", g_facilitySettings.SetName(FKey_Facility))
  //
  // UI (.vb:475-482 radTruck_CheckedChanged, handles radSets):
  //   lciTab.Visibility        = Never   → mealtime + cart tabs hidden
  //   lcgByResident.Visibility = Never   → Order By group hidden
  //   lcgSets.Visibility       = Always  → ucClbSet side selector shown
  //
  // ucClbSet Init has NO CheckAll call (see .vb:114-117 + ClbSet.vb:85-148)
  // so the cart list starts fully UNCHECKED (matches screenshot).
  //
  // Validate (.vb:546-551): ≥1 set required.
  {
    slug: 'beverage-by-set',
    reportName: 'Beverage - By {SetName}',
    fKey_RS_Report: 25,
    apiEndpoint: 'BeverageBySet',
    fields: [
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Cart selector — WinForms `ucClbSet` doesn't call CheckAll
      // on init, so the list defaults to nothing checked. The user
      // MUST pick at least one cart before Generate proceeds
      // (validated below).
      // `inline: true` keeps the selector in the MAIN column below
      // the Date group (matches WinForms `lcgSets` at .vb:475-482
      // showing the Sets group as the primary form content, not a
      // side panel). Group left blank on purpose — the fieldset
      // border stays for visual separation, but no legend text
      // renders (avoids duplicating the SetSelector's own header).
      // `width: 500` caps the selector at 500px so it doesn't
      // stretch the whole main column on wide viewports.
      { key: 'SetList', label: '', type: 'setSelect', defaultValue: [],
        group: '', inline: true, width: 500 },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler radSets branch (.vb:314-324).
    //   - `ReportTypeSelected = 3` matches
    //     `BeverageSettings.ReportType.BySet`.
    //   - `MealtimesInt` / `PrintOrder` / `MealtimeForSort` are NOT
    //     set by the radSets branch and NOT read by BeverageBySet.vb;
    //     omitted from the wire (Newtonsoft defaults them to null/0
    //     on deserialization, safe for this generator).
    buildSettings: (v, fs) => ({
      IsProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
      PKey_Menu_Header: -999999,
      StartDate: v.StartDate,
      SetList: v.SetList ?? [],
      ReportTypeSelected: 3,
    }),
    // Mirrors ValidateForm BySet arm (.vb:546-551). Only cart
    // check required; mealtime/count-cap constraints apply to
    // ColdHot/Meal, not this variant. `{SetName}` resolves to the
    // facility label via `substituteTokens` in the parent so the
    // toast reads "Please select at least one Cart." (etc.).
    validate: (v) => {
      if (!Array.isArray(v.SetList) || v.SetList.length === 0) {
        return 'Please select at least one {SetName}.';
      }
      return null;
    },
  },
  // ─── BEVERAGE — TOTAL (pKey 26, fKey 26) ───────────────────────
  // WinForms source: ucDietech_Report_Generator_Beverages.vb hosts
  // the Total variant. Print_Handler branch at .vb:330-346:
  //   .IsProduction       = g_facilitySettings.UseProduction(FKey_Facility)
  //   .PKey_Menu_Header   = mci.PKey_Menu_Header  (server derives via -999999 sentinel)
  //   .FKey_Facility      = FKey_Facility
  //   .StartDate          = ucDate.EditValue
  //   .SetList            = ucClbSet.GetCheckedSetsList
  //   .AllSetsSelected    = .SetList.Count = ucClbSet.clb.ItemCount
  //   .ReportTypeSelected = BeverageSettings.ReportType.Total (4)
  //   report class        = BeverageTotal (server pKey 26)
  //   detailedReportName  = "Beverage - Total"   (fixed, no facility label)
  //
  // Differences from By Set (pKey 25):
  //   1. `ReportTypeSelected = 4` (Total, not BySet).
  //   2. `AllSetsSelected` is populated — BeverageTotal.vb:195 / 364
  //      reads it to choose "Beverage Total for All Carts" vs
  //      "Beverage Total for Cart(s) 1,2,3". Computed here by
  //      comparing `SetList.length` to `ctx.facilitySetCount` (the
  //      distinct cart-number count from `useFacilitySets`).
  //   3. detailedReportName is fixed "Beverage - Total" (no
  //      facility SetName lookup).
  //
  // UI is identical to By Set — same Date group + cart list.
  //
  // Validate (.vb:546-551): ≥1 set required (shared arm with By Set).
  {
    slug: 'beverage-total',
    reportName: 'Beverage - Total',
    fKey_RS_Report: 26,
    apiEndpoint: 'BeverageTotal',
    fields: [
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      // Cart selector — matches By Set layout. Empty by default
      // (WinForms `ucClbSet` doesn't call CheckAll on init). Inline
      // in the main column below Date, capped at 500px, no group
      // header text (fieldset border only, matches the visual
      // separation used by By Set).
      { key: 'SetList', label: '', type: 'setSelect', defaultValue: [],
        group: '', inline: true, width: 500 },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler radTotal branch (.vb:330-342).
    //   - `AllSetsSelected = SetList.length === facilitySetCount`
    //     matches WinForms `SetList.Count = ucClbSet.clb.ItemCount`.
    //     When `facilitySetCount` is 0 (cache still loading), we
    //     default to `false` so the server renders the explicit
    //     list — safer than falsely claiming "All".
    //   - `ReportTypeSelected = 4` matches
    //     `BeverageSettings.ReportType.Total`.
    buildSettings: (v, fs, ctx) => {
      const setList = Array.isArray(v.SetList) ? (v.SetList as number[]) : [];
      const total = ctx?.facilitySetCount ?? 0;
      return {
        IsProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
        PKey_Menu_Header: -999999,
        StartDate: v.StartDate,
        SetList: setList,
        AllSetsSelected: total > 0 && setList.length === total,
        ReportTypeSelected: 4,
      };
    },
    // Mirrors ValidateForm Total arm (.vb:546-551) — same as By Set:
    // ≥1 cart required. Placeholder resolves per facility.
    validate: (v) => {
      if (!Array.isArray(v.SetList) || v.SetList.length === 0) {
        return 'Please select at least one {SetName}.';
      }
      return null;
    },
  },

  // ─── ACCEPTANCE ───
  // Layout mirrors the WinForms Meal Acceptance form:
  // Report Type | Date | Mealtimes | Order | Print | Options.
  // `PrintMode` merges the WinForms All / Specific Cart / Specific
  // Patient radio group; SetList and PatientList are the same
  // selectors as before, gated by the new PrintMode value.
  {
    slug: 'acceptance-meal',
    reportName: 'Meal - Acceptance',
    fKey_RS_Report: 17,
    apiEndpoint: 'MealAcceptance',
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 'weekly', group: 'Report Type',
        options: [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }] },

      // Initial + on ReportType change (WinForms handlers):
      //   Weekly (Meal_Acceptance.vb:518-527): today if DOW matches,
      //     else Date_getFirstDayOfWeek(today+7, dow) — walks BACKWARD
      //     from anchor to that dow. For today=7/9 (Thu) + dow=Mon
      //     this lands on 7/13, not 7/20.
      //   Daily  (Meal_Acceptance.vb:529-535): today.
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['ReportType'],
        defaultValue: (fs?: Record<string, unknown>, values?: Record<string, unknown>) => {
          if (values?.ReportType === 'daily') return new Date();
          const dow = Number((fs?.start_Day_Meal_Accept ?? fs?.Start_Day_Meal_Accept) ?? 0);
          const d = new Date();
          if (d.getDay() === dow) return d;
          const anchor = new Date(d); anchor.setDate(d.getDate() + 7);
          while (anchor.getDay() !== dow) anchor.setDate(anchor.getDate() - 1);
          return anchor;
        } },

      { key: 'Mealtime', label: '', type: 'radio', defaultValue: 0, group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'trayline', group: 'Order',
        options: [
          { value: 'trayline', label: 'Tray Line' },
          { value: 'room', label: '{Room} Number' },
          { value: 'name', label: 'Name' },
        ] },

      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all', group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'cart', label: 'Specific {SetName}' },
          { value: 'patient', label: 'Specific {Resident}' },
        ] },
      { key: 'SetList', label: 'Select {SetName_Plural}', type: 'setSelect', defaultValue: [],
        visibleWhen: { key: 'PrintMode', value: 'cart' }, defaultSelectAll: true },
      { key: 'PatientList', label: 'Select {Resident_Plural}', type: 'residentSelect', defaultValue: [],
        visibleWhen: { key: 'PrintMode', value: 'patient' }, defaultSelectAll: true },

      { key: 'StartNewPageAfterEachCart', label: 'Start New Page After Each {SetName}', type: 'checkbox', defaultValue: true, group: 'Options' },
      { key: 'IncludeMealNameInTitle', label: 'Include Meal Name In Report Title', type: 'checkbox', defaultValue: true, group: 'Options' },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Translate React form values → WinForms MealAcceptanceSettings
    // shape. Mirror `Print_Handler` in
    // ucDietech_Report_Generator_Meal_Acceptance.vb:275-321 exactly:
    // one-of-three booleans for Order, one-of-two for Print +
    // Set/PatientList only populated in the corresponding mode.
    buildSettings: (v) => ({
      Mealtime: v.Mealtime,
      StartDate: v.StartDate,
      IsDaily: v.ReportType === 'daily',
      OrderByTrayline: v.OrderBy === 'trayline',
      OrderByRoom: v.OrderBy === 'room',
      OrderByName: v.OrderBy === 'name',
      PrintSpecificSet: v.PrintMode === 'cart',
      SetList: v.PrintMode === 'cart' ? (v.SetList ?? []) : [],
      PrintSpecificResident: v.PrintMode === 'patient',
      PatientList: v.PrintMode === 'patient' ? (v.PatientList ?? []) : [],
      StartNewPageAfterEachSet: v.StartNewPageAfterEachCart,
      IncludeMealNameInTitle: v.IncludeMealNameInTitle,
    }),
    // Mirrors WinForms `ValidateForm`
    // (ucDietech_Report_Generator_Meal_Acceptance.vb:497-509): the
    // Specific Cart / Specific Patient modes require at least one
    // corresponding row to be checked before Generate proceeds.
    validate: (v) => {
      if (v.PrintMode === 'cart' && (!Array.isArray(v.SetList) || v.SetList.length === 0)) {
        return 'Select at least one cart.';
      }
      if (v.PrintMode === 'patient' && (!Array.isArray(v.PatientList) || v.PatientList.length === 0)) {
        return 'Select at least one patient.';
      }
      return null;
    },
  },
  // ─── MED PASS ACCEPTANCE (pKey 18, fKey 18) ────────────────────
  // WinForms source: ucDietech_Report_Generator_MedPassAcceptance.vb
  //   .Designer.vb:121 → `radPrintDaily.EditValue = True`      → Daily default
  //   .vb:87            → `_dayOfWeek = Start_Day_Meal_Accept`  → same DOW as Meal Acceptance
  //   .vb:92            → `ucDate.DateInit = Now`               → today on init (Weekly-shift is commented out)
  //   .vb:100-101       → `ucClbMealtime.IncludeNourishments = UseProduction` → nourishments visible only when
  //                       production is on; we show all 7 options regardless and let user select
  //   .vb:216-228       → Print branches on `UseProduction`; both branches build same
  //                       MedPassAcceptanceSettings with `UseProduction` set from facility
  //   .vb:369-376       → Validate: at least one mealtime checked
  //   .vb:1062-1077     → Weekly click → next facility-DOW; Daily click → today
  {
    slug: 'acceptance-med-pass',
    reportName: 'Med Pass - Acceptance',
    fKey_RS_Report: 18,
    apiEndpoint: 'MedPassAcceptance',
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 'daily', group: 'Report Type',
        options: [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }] },

      // Same DOW-snap-on-Weekly logic as Meal Acceptance (both use
      // `Start_Day_Meal_Accept` for the anchor). Daily just picks
      // today. Recompute when ReportType toggles.
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['ReportType'],
        defaultValue: (fs?: Record<string, unknown>, values?: Record<string, unknown>) => {
          if (values?.ReportType === 'weekly') {
            const dow = Number((fs?.start_Day_Meal_Accept ?? fs?.Start_Day_Meal_Accept) ?? 0);
            const d = new Date();
            if (d.getDay() === dow) return d;
            const anchor = new Date(d); anchor.setDate(d.getDate() + 7);
            while (anchor.getDay() !== dow) anchor.setDate(anchor.getDate() - 1);
            return anchor;
          }
          return new Date();
        } },

      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [], group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler → PrintProduction_* / PrintBasic_*
    // `With settings` blocks (.vb:381-899). `UseProduction` is set
    // from `g_facilitySettings.UseProduction(FKey_Facility)` here,
    // not by user — same as WinForms.
    buildSettings: (v, fs) => ({
      MealtimesInt: v.MealtimesInt,
      StartDate: v.StartDate,
      IsDaily: v.ReportType === 'daily',
      UseProduction: Boolean(fs?.useProduction ?? fs?.UseProduction ?? false),
    }),
    // Mirrors ValidateForm (.vb:369-376).
    validate: (v) => {
      if (!Array.isArray(v.MealtimesInt) || v.MealtimesInt.length === 0) {
        return 'Please select at least one mealtime.';
      }
      return null;
    },
  },
  // ─── MONTHLY ACCEPTANCE (pKey 19, fKey 19) ─────────────────────
  // WinForms source: ucDietech_Report_Generator_MonthlyAcceptance.vb
  //   .vb:97   → ucDeStart.DateMin = Now.AddYears(-10) — informational, we don't enforce
  //   .vb:98   → StartDate default = today
  //   .vb:103-104 → EndDate default = today + 1 month - 1 day
  //   .vb:117  → CheckAllMealtimes at init (all 7 mealtimes checked)
  //   .Designer.vb:158/170 → InitialGrid, Notes default checked
  //   .Designer.vb:206/216/228 → Thickener, Protein, Fiber default checked
  //   .vb:567-571 → chkByFloor gates Floor/Units tab visibility
  //   .vb:670-678 → StartDate change re-snaps EndDate to (start + 1mo - 1d)
  //   .vb:682-706 → Validate: ≥1 mealtime, ≥1 floor/unit (when ByFloor), ≥1 resident
  {
    slug: 'acceptance-monthly',
    reportName: 'Monthly - Acceptance',
    fKey_RS_Report: 19,
    apiEndpoint: 'MonthlyAcceptance',
    fields: [
      // StartDate <-> EndDate must always satisfy StartDate < EndDate.
      // Bidirectional recomputes enforce the invariant with a minimal
      // 1-day shift when the user pushes one field past the other.
      // Local `parseLocal` avoids the `new Date("yyyy-MM-dd")` UTC
      // parsing trap that would corrupt the compare in negative-UTC
      // timezones (see the date-spin fix in GenericReportForm).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['EndDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.StartDate);
          const currentEnd = parseLocal(values?.EndDate);
          // Init path (no StartDate yet) → today.
          if (!currentStart) return new Date();
          // EndDate just moved to or past StartDate → shift StartDate
          // back to EndDate - 1 day so start < end holds.
          if (currentEnd && currentEnd <= currentStart) {
            const next = new Date(currentEnd);
            next.setDate(next.getDate() - 1);
            return next;
          }
          return currentStart;
        } },

      // End default = start + 1 month - 1 day (WinForms
      // ucDeStart_EditValueChanged). Recomputed whenever StartDate
      // changes: if the existing EndDate is still strictly after
      // the new StartDate, keep it (preserves the user's tweaks);
      // otherwise snap to StartDate + 1 month - 1 day.
      { key: 'EndDate', label: 'To', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['StartDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd && existingEnd > start) return existingEnd;
          const end = new Date(start);
          end.setMonth(end.getMonth() + 1);
          end.setDate(end.getDate() - 1);
          return end;
        } },

      { key: 'ByFloor', label: 'By Floor', type: 'checkbox', defaultValue: false, group: 'Date' },

      { key: 'Fiber', label: 'Fiber', type: 'checkbox', defaultValue: true, group: 'Include' },
      { key: 'Protein', label: 'Protein', type: 'checkbox', defaultValue: true, group: 'Include' },
      { key: 'Thickener', label: 'Thickener', type: 'checkbox', defaultValue: true, group: 'Include' },

      { key: 'InitialGrid', label: 'Initial Grid', type: 'checkbox', defaultValue: true, group: 'Print' },
      { key: 'Notes', label: 'Notes', type: 'checkbox', defaultValue: true, group: 'Print' },

      // Tabbed selection block (WinForms XtraTabControl). Every
      // field with `tab` shares one MUI Tabs strip inside the
      // "Selection" group. All three tabs are ALWAYS visible —
      // WinForms had a `chkByFloor_CheckedChanged` handler intended
      // to hide the Floor/Units tab, but the handler has no `Handles`
      // clause and never actually fires (.vb:567). ByFloor is only
      // a sort flag on the settings; it does NOT gate this tab.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [0, 1, 2, 3, 4, 5, 6],
        group: 'Selection', tab: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
      { key: 'UnitFloorList', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },
      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
    // Mirrors Print() `With settings` block (.vb:385-404).
    //   - `Mealtimes` is the WinForms `<Flags> Enum Mealtime` bit
    //     field (Breakfast=1, Lunch=2, Dinner=4, EAM=8, AM=16, PM=32,
    //     HS=64). Derived from `MealtimesInt` via `1 << idx` — same
    //     rule as `Diet_Reports.GetMealTimeValues`. The report
    //     generator itself uses `MealtimesInt` for filtering, but
    //     `MonthlyAcceptanceSettings.ToString` (info popup) reads
    //     `Mealtimes`, so both must be populated.
    //   - `UseUnitMapping` comes from the facility, not the user.
    //   - `FKey_Facility` / `FKey_User` are added by
    //     `GenericReportForm.handleSubmit` — don't duplicate here.
    buildSettings: (v, fs) => {
      const mealtimesInt = Array.isArray(v.MealtimesInt) ? (v.MealtimesInt as number[]) : [];
      const mealtimesFlags = mealtimesInt.reduce((acc, m) => acc | (1 << m), 0);
      return {
        Mealtimes: mealtimesFlags,
        MealtimesInt: mealtimesInt,
        StartDate: v.StartDate,
        EndDate: v.EndDate,
        ByFloor: v.ByFloor,
        Fiber: v.Fiber,
        Protein: v.Protein,
        Thickener: v.Thickener,
        InitialGrid: v.InitialGrid,
        Notes: v.Notes,
        UseUnitMapping: Boolean(fs?.useUnitMapping ?? fs?.UseUnitMapping ?? false),
        UnitFloorList: v.UnitFloorList ?? [],
        PatientList: v.PatientList ?? [],
      };
    },
    // Mirrors ValidateForm (.vb:682-706) — Floor/Unit is always
    // required regardless of the ByFloor sort flag.
    validate: (v) => {
      if (!Array.isArray(v.MealtimesInt) || v.MealtimesInt.length === 0) {
        return 'Please select at least one mealtime.';
      }
      if (!Array.isArray(v.UnitFloorList) || v.UnitFloorList.length === 0) {
        return 'Please select at least one floor/unit.';
      }
      if (!Array.isArray(v.PatientList) || v.PatientList.length === 0) {
        return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },
  // ─── NOURISHMENT ACCEPTANCE (pKey 20, fKey 20) ─────────────────
  // WinForms source: ucDietech_Report_Generator_NourishmentAcceptance.vb
  //   .Designer.vb:163 → `radWeekly.EditValue = True`         → Weekly default
  //   .Designer.vb:372 → `radPartialList.EditValue = True`    → Partial List default
  //   .vb:99          → `_dayOfWeek = Start_Day_Nourish_Accept` (facility DOW)
  //   .vb:111-115     → Weekly-shift date to facility DOW on init
  //   .vb:123         → `IncludeMainMealtimes = False` — only nourishment
  //                     mealtimes (EAM/AM/PM/HS), NOT Breakfast/Lunch/Dinner
  //   .vb:125         → `SetItemChecks(_lstMealtimesInit)` with empty list
  //                     → mealtimes start UNCHECKED (user must pick)
  //   .vb:130,135     → FloorUnit + Residents get `.Init()` only; the
  //                     underlying checked-listbox fills call `.CheckAll()`
  //                     → both default to ALL CHECKED
  //   .vb:520-537     → Validate: ≥1 mealtime, ≥1 resident (Floor/Unit is
  //                     NOT required — differs from Monthly Acceptance)
  //   .vb:543-557     → Daily radio → today; Weekly radio → same DOW-snap
  //                     logic as init
  {
    slug: 'acceptance-nourishments',
    reportName: 'Nourishment - Acceptance',
    fKey_RS_Report: 20,
    apiEndpoint: 'NourishmentAcceptance',
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 'weekly', group: 'Report Type',
        options: [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }] },

      // Weekly DOW-snap uses `Start_Day_Nourish_Accept` (distinct
      // from Meal / Med Pass which use `Start_Day_Meal_Accept`).
      // Walks BACKWARD from `today + 7 days` to the facility DOW
      // (same helper as WinForms `Date_getFirstDayOfWeek`).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['ReportType'],
        defaultValue: (fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          if (values?.ReportType === 'daily') return new Date();
          const dow = Number((fs?.start_Day_Nourish_Accept ?? fs?.Start_Day_Nourish_Accept) ?? 0);
          const d = new Date();
          if (d.getDay() === dow) return d;
          const anchor = new Date(d); anchor.setDate(d.getDate() + 7);
          while (anchor.getDay() !== dow) anchor.setDate(anchor.getDate() - 1);
          return anchor;
        } },

      { key: 'FilterType', label: '', type: 'radio', defaultValue: 'partial', group: 'Filter',
        options: [
          { value: 'partial', label: 'Partial List' },
          { value: 'complete', label: 'Complete List' },
          { value: 'unassigned', label: 'Unassigned' },
        ] },

      // Tabbed selection — Mealtimes limited to nourishments only
      // (`IncludeMainMealtimes = False` in the WinForms clbMealtimeTab
      // init). Values 3/4/5/6 match the `Mealtime` enum bit-flag
      // indices (EAM=8, AM=16, PM=32, HS=64 via `1 << idx`).
      // WinForms starts with nothing checked (empty `_lstMealtimesInit`)
      // but the React port defaults to all four checked on load
      // per user request — saves the extra "check all" click for
      // the common case.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [3, 4, 5, 6],
        group: 'Selection', tab: 'Mealtimes',
        options: [
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
      { key: 'UnitFloorList', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },
      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
    // Mirrors Print_Handler settings block (.vb:246-266). Both
    // `Mealtimes` (bit-flag) and `MealtimesInt` are populated —
    // report generator uses the array for filtering, ToString
    // (info popup) reads the bit-flag.
    buildSettings: (v, fs) => {
      const mealtimesInt = Array.isArray(v.MealtimesInt) ? (v.MealtimesInt as number[]) : [];
      const mealtimesFlags = mealtimesInt.reduce((acc, m) => acc | (1 << m), 0);
      return {
        Mealtimes: mealtimesFlags,
        MealtimesInt: mealtimesInt,
        StartDate: v.StartDate,
        IsDaily: v.ReportType === 'daily',
        PartialList: v.FilterType === 'partial',
        CompleteList: v.FilterType === 'complete',
        Unassigned: v.FilterType === 'unassigned',
        UseUnitMapping: Boolean(fs?.useUnitMapping ?? fs?.UseUnitMapping ?? false),
        UnitFloorList: v.UnitFloorList ?? [],
        PatientList: v.PatientList ?? [],
      };
    },
    // Mirrors ValidateForm (.vb:520-537) — mealtime + resident are
    // required, floor/unit is NOT (differs from Monthly Acceptance).
    validate: (v) => {
      if (!Array.isArray(v.MealtimesInt) || v.MealtimesInt.length === 0) {
        return 'Please select at least one mealtime.';
      }
      if (!Array.isArray(v.PatientList) || v.PatientList.length === 0) {
        return 'Please select at least one resident.';
      }
      return null;
    },
  },

  // ─── PRODUCTION - SELECTIVE MENU (pKey 2, fKey 2) ────────────────
  // WinForms source: ucDietech_Report_Generator_SelectiveMenu.vb.
  //   TabPageText  = "Selective Menu" (.vb:52).
  //   Report Type  = radio (Standard / Restaurant Style),
  //                  default from `sM_ReportType` (0/1) at .vb:125-130.
  //   StartDate    = today, min today (.vb:143-146).
  //   NumberOfDays = 1..7, default 1 (.vb:149-154).
  //   Menu Cycle info = display, driven by StartDate (.vb:156-160).
  //   Mealtimes    = ucClbMealtimes, no default checks. Validation
  //                  requires ≥1 (.vb:760-763).
  //   Order By     = radio (Name / Room Number / Tray Line +
  //                  meal dropdown). Default from
  //                  `selectiveMenu_DefaultPrintOrder` (0/1/2).
  //                  Meal dropdown auto-tracks the single-checked
  //                  mealtime (.vb:921-925).
  //   Print        = radio (All / Specific Set / Specific Resident).
  //                  Default All.
  //   Paper Size   = radio (Letter / Legal), default from
  //                  `selectiveMenu_PaperSize` (0/1) at .vb:132-137.
  //   Options:
  //     Collate            = default from `selectiveMenu_DefaultPageOrder == 1`
  //     Skip Blank Meals   = default from `skipBlankMeals`. When
  //                          Collate=true it's forced to true and
  //                          disabled (.vb:939-946).
  //     Group by {Resident}= default from `selectiveMenuSortByResident`
  //     Note               = text
  //     Menu Categories    = dropdown, 2 options; default 0.
  //                          Names come from
  //                          `Sql_Prd_Menu_Category_PrintingOrder`;
  //                          the React port hard-codes "Menu Categories A"
  //                          / "Menu Categories B" as the WinForms
  //                          fallback labels.
  //   ImageName    = deferred — WinForms embeds TT_ image resources
  //                  from a resx enumeration that isn't ported.
  //   PatientFilterName = deferred — WinForms "predefined resident
  //                  lists" (`tblPrd_ResidentSelect_Header`) not
  //                  ported.
  //
  // Validation (.vb:757-806):
  //   - ≥1 mealtime
  //   - If SpecificSet: ≥1 set checked
  //   - MenuCycleInUseInfo.IsValidMenuDay (not checked here; the
  //     server will short-circuit if invalid)
  //   - If SpecificResident: eligible grid populated AND ≥1 selected
  //   - Menu categories set up for printing for the chosen list
  //     (.vb:789-795) and, for Restaurant Style, a paper layout or a
  //     Selective Touch layout (.vb:797): server reads, in `preSubmit`
  //
  // Wire payload (.vb:592-648):
  //   ReportTypeSelected, NumberOfDays, StartDate, FKey_Facility,
  //   PKey_Menu_Header (from useMenuCycleInfo cache),
  //   MealTimes (bit flag), CheckedMeals (int[]), Sets, tempPatientList,
  //   LstPatientNames, PrintAll, SpecificSet, SpecificResident,
  //   Collate, SortByName / SortByRoomNumber / SortByTrayLine,
  //   TrayLineOrder, GroupByResident, SkipBlankMeals, PaperLegal,
  //   MenuCategoryTypeIndex, MenuCycle_MenuDayToUse, Notes,
  //   PatientFilterName, ImageName.
  {
    slug: 'selective-menu',
    reportName: 'Selective Menu',
    fKey_RS_Report: 2,
    layout: { width: 'wide', mainWidth: '600px', sideWidth: '800px' },
    fields: [
      // Report Type radio — facility-driven default.
      { key: 'ReportType', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as { sM_ReportType?: number } | null)?.sM_ReportType;
          return raw === 1 ? 'restaurantStyle' : 'standard';
        },
        group: 'Report Type',
        options: [
          { value: 'standard', label: 'Standard' },
          { value: 'restaurantStyle', label: 'Restaurant Style' },
        ] },

      // Date group — StartDate + NumberOfDays inline on the same
      // row. Both fields carry explicit `width` so the group-body
      // flex-wrap keeps them side-by-side instead of stacking.
      { key: 'StartDate', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275 },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 7, group: 'Date', width: 250 },

      // Menu Cycle info — auto-updates as StartDate changes.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — starts empty; validation requires ≥1.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Order By radio — default from facility.
      // `selectiveMenu_DefaultPrintOrder` maps 0/1/2 →
      // 'name' / 'room' / 'trayLine' (WinForms .vb:171-178).
      // Labels are static — WinForms Designer captions are literal
      // "Name" / "Room Number" / "Tray Line" (no facility swap).
      { key: 'SortBy', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as { selectiveMenu_DefaultPrintOrder?: number } | null)?.selectiveMenu_DefaultPrintOrder;
          if (raw === 1) return 'room';
          if (raw === 2) return 'trayLine';
          return 'name';
        },
        group: 'Order By',
        options: [
          { value: 'name', label: 'Name' },
          { value: 'room', label: 'Room Number' },
          { value: 'trayLine', label: 'Tray Line' },
        ] },
      // Tray line mealtime dropdown — enabled only when SortBy =
      // 'trayLine'. WinForms auto-syncs this to the single-checked
      // mealtime (.vb:921-925) — port as `recomputeWhen:
      // ['MealtimesInt']` so it tracks. Default 0 (Breakfast).
      { key: 'TrayLineOrder', label: '', type: 'dropdown',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const meals = (values?.MealtimesInt as number[] | undefined) ?? [];
          if (meals.length === 1) return meals[0];
          return 0;
        },
        recomputeWhen: ['MealtimesInt'],
        options: [
          { value: 0, label: 'B' },
          { value: 1, label: 'L' },
          { value: 2, label: 'D' },
        ],
        enabledWhen: { key: 'SortBy', value: 'trayLine' },
        group: 'Order By', width: 75 },

      // Print radio — All / Specific Set / Specific Resident.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },

      // Paper Size radio — facility-driven default.
      { key: 'PaperLegal', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const raw = (fs as { selectiveMenu_PaperSize?: number } | null)?.selectiveMenu_PaperSize;
          return raw === 1 ? 'legal' : 'letter';
        },
        group: 'Paper Size',
        options: [
          { value: 'letter', label: 'Letter (8.5x11)' },
          { value: 'legal', label: 'Legal (8.5x14)' },
        ] },

      // Options — Collate + SkipBlankMeals + Group by Resident.
      { key: 'Collate', label: 'Collate', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          return (fs as { selectiveMenu_DefaultPageOrder?: number } | null)?.selectiveMenu_DefaultPageOrder === 1;
        },
        group: 'Options' },
      // Skip Blank Meals — when Collate is checked, force this to
      // true AND disable it (.vb:939-946). Uses `enabledWhen: { check }`
      // to gate on Collate=false. `recomputeWhen: ['Collate']`
      // re-derives the value whenever Collate toggles.
      { key: 'SkipBlankMeals', label: 'Skip Blank Meals', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          if (values?.Collate === true) return true;
          return Boolean((fs as { skipBlankMeals?: boolean } | null)?.skipBlankMeals);
        },
        recomputeWhen: ['Collate'],
        enabledWhen: { check: (v) => v.Collate !== true },
        disabledReason: 'Always on while Collate is checked.',
        group: 'Options' },
      { key: 'GroupByResident', label: 'Group by {Resident}', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          return Boolean((fs as { selectiveMenuSortByResident?: boolean } | null)?.selectiveMenuSortByResident);
        },
        group: 'Options' },

      // Note text field.
      { key: 'Notes', label: 'Note:', type: 'text', defaultValue: '',
        group: 'Options', newRow: true },

      // Menu Categories dropdown.
      { key: 'MenuCategoryTypeIndex', label: 'Menu Categories:', type: 'dropdown',
        defaultValue: 0,
        options: [
          { value: 0, label: 'Menu Categories A' },
          { value: 1, label: 'Menu Categories B' },
        ],
        group: 'Options', newRow: true, width: 300 },

      // ── Side selectors ───────────────────────────────────────
      // Specific Set picker — visible when PrintMode='set'.
      { key: 'SetList', label: '', type: 'setSelect',
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'set' } },
      // Specific Resident picker — visible when PrintMode='resident'.
      // Filtered to selective-menu-eligible residents for the
      // selected date range + mealtimes (mirrors WinForms
      // `GetSelectiveMenuResidents` at .vb:820).
      // Columns match WinForms Designer grid at
      // `ucDietech_Report_Generator_SelectiveMenu.Designer.vb`:
      //   ☑ | Name | Diet Order | Static | Unit | Room
      // (no TF column — hidden via `hideTFColumn: true`).
      { key: 'PatientList', label: '', type: 'residentSelect',
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'resident' },
        showInfoColumns: true,
        showStaticColumn: true,
        hideTFColumn: true,
        selectiveMenuEligible: {
          startDateField: 'StartDate',
          numberOfDaysField: 'NumberOfDays',
          mealtimesField: 'MealtimesInt',
        } },
    ],
    buildDateFor: (v) => {
      const days = Number(v.NumberOfDays ?? 1);
      if (days <= 1) return formatDate(v.StartDate);
      const start = v.StartDate instanceof Date
        ? new Date(v.StartDate.getFullYear(), v.StartDate.getMonth(), v.StartDate.getDate())
        : (typeof v.StartDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v.StartDate))
          ? (() => { const m = (v.StartDate as string).match(/^(\d{4})-(\d{2})-(\d{2})/)!; return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); })()
          : new Date();
      const end = new Date(start); end.setDate(end.getDate() + days - 1);
      return formatDateRange(start, end);
    },
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const menuDayToUse = ctx?.resolveMenuDayToUse?.(startIso) ?? -1;

      const printMode = String(v.PrintMode ?? 'all');
      const sortBy = String(v.SortBy ?? 'name');
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      // Mealtime bit flag — same map as ProductionTally / Recipes -
      // Menu Cycle (Breakfast=1, Lunch=2, Dinner=4, EAM=8, AM=16,
      // PM=32, HS=64, PM2=128, HS2=256).
      const mealtimesFlag = meals.reduce((acc, i) => acc | (1 << i), 0);

      // Resolve LstPatientNames from the selected PatientList
      // PKeys using the residents cache. Mirrors WinForms
      // .vb:430-435 which pushes `nz(row("FullName"))` for EACH
      // selected row — the two lists MUST stay index-aligned so
      // the generator can zip them together. Do NOT filter empties
      // (nz-of-null is ""; WinForms keeps the empty entry).
      const patientList = printMode === 'resident'
        ? ((v.PatientList as number[] | undefined) ?? [])
        : [];
      const patientNames = patientList.map((pk) =>
        ctx?.resolvePatientFullName?.(pk) ?? '',
      );

      return {
        ReportTypeSelected: v.ReportType === 'restaurantStyle' ? 1 : 0,
        NumberOfDays: Number(v.NumberOfDays ?? 1),
        StartDate: v.StartDate,
        PKey_Menu_Header: pKeyMenuHeader,
        MenuCycle_MenuDayToUse: menuDayToUse,
        MealTimes: mealtimesFlag,
        CheckedMeals: meals,
        Sets: printMode === 'set'
          ? ((v.SetList as number[] | undefined) ?? [])
          : [],
        tempPatientList: patientList,
        LstPatientNames: patientNames,
        PrintAll: printMode === 'all',
        SpecificSet: printMode === 'set',
        SpecificResident: printMode === 'resident',
        Collate: Boolean(v.Collate),
        SortByName: sortBy === 'name',
        SortByRoomNumber: sortBy === 'room',
        SortByTrayLine: sortBy === 'trayLine',
        TrayLineOrder: Number(v.TrayLineOrder ?? 0),
        GroupByResident: Boolean(v.GroupByResident),
        SkipBlankMeals: Boolean(v.SkipBlankMeals),
        PaperLegal: v.PaperLegal === 'legal',
        MenuCategoryTypeIndex: Number(v.MenuCategoryTypeIndex ?? 0),
        Notes: String(v.Notes ?? ''),
        // WinForms "predefined resident lists" filter — not ported;
        // WinForms sends "" (DTO default) when the picker is empty.
        PatientFilterName: '',
        // WinForms TT_ image dropdown — not ported; WinForms sends
        // "" (DTO default) when no image is selected.
        ImageName: '',
        // Batch-split fields — WinForms Print() splits large jobs
        // into pieces and calls GenerateReport once per piece with
        // `SplitTitle` = "1 of N" + `IsSplitReport` = true (.vb:528-533).
        // Not ported; single-shot generation only. Include the DTO
        // defaults explicitly so the wire payload byte-matches
        // WinForms' non-split path.
        SplitTitle: '',
        IsSplitReport: false,
      };
    },
    // Two server reads WinForms ValidateForm makes, in its order. Both are
    // server reads, so they run here rather than in the synchronous
    // `validate`. They add nothing to the settings, so a schedule replays the
    // same report (scheduleSupport).
    //   1. Every print, Standard or Restaurant Style: the facility needs menu
    //      categories set up for printing (tblPrd_Menu_Category_PrintingOrder)
    //      for the chosen Menu Categories list, A = Type 0, B = Type 14
    //      (.vb:789-795, Prd_DTCollection.IsMenuCategoryOrderEmtpy).
    //   2. Restaurant Style: the facility needs the paper layout
    //      (tblPrd_Menu_Category_SMReport_Layout) or, since 2026-10-25, a
    //      Selective Touch layout the report prints meals without one from
    //      (.vb:797, SMReportLayoutFromTouch.HasPrintableLayout); queued with
    //      neither, the report prints blank.
    // An API without an endpoint yet (404) answers as before that check, so a
    // website deployed ahead of the API does not block every print. Any other
    // failure refuses, as WinForms treats a failed read as "none" (both
    // helpers return True).
    preSubmit: async (v, { facilityId }) => {
      const apiClient = (await import('../../api/client')).default;
      const { API } = await import('../../api/endpoints');
      const notFound = (e: unknown) => (e as { response?: { status?: number } })?.response?.status === 404;

      const menuCategoryTypeIndex = Number(v.MenuCategoryTypeIndex ?? 0) === 1 ? 1 : 0;
      try {
        const { data } = await apiClient.get<{ hasCategories?: boolean }>(
          API.reportSelectiveMenuCategoryOrder(facilityId, menuCategoryTypeIndex),
        );
        if (data?.hasCategories !== true) return { error: SELECTIVE_MENU_NO_MENU_CATEGORIES };
      } catch (e) {
        if (!notFound(e)) return { error: 'Could not check the selective menu categories. Please try again.' };
      }

      if (v.ReportType !== 'restaurantStyle') return {};
      try {
        const { data } = await apiClient.get<{ hasLayout?: boolean }>(
          API.reportSelectiveMenuRestaurantLayout(facilityId),
        );
        if (data?.hasLayout !== true) return { error: SELECTIVE_MENU_NO_RESTAURANT_LAYOUT };
        return {};
      } catch (e) {
        if (notFound(e)) return {};
        return { error: 'Could not check the Restaurant Style layout. Please try again.' };
      }
    },
    // Mirrors WinForms `ValidateForm` (.vb:757-806).
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select a mealtime.';
      if (v.PrintMode === 'set') {
        const sets = (v.SetList as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select a {SetName}.';
      }
      if (v.PrintMode === 'resident') {
        const pts = (v.PatientList as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select a {Resident}.';
      }
      return null;
    },
  },
  // ─── PRODUCTION - SELECTIVE MENU - NON-RETURNED MENUS
  //     (pKey 58, fKey 58) ────────────────────────────────────────
  // WinForms source: ucDietech_Report_Generator_SelectiveMenu_NonReturned.vb.
  //   TabPageText   = "Selective Menu - Non-Returned Menus" (.vb:61).
  //   DateFor       = today (.vb:83).
  //   NumberOfDays  = 1..9, default 1 (.vb:86-91).
  //   Menu Cycle    = ucMenuCycleInfo driven by DateFor (.vb:93-97).
  //   Mealtimes     = ucClbMealtimes, empty by default (`_lstMealtimesInit`
  //                   defaults to empty list).
  //   Order         = radio pair (By Room / By Name), default By Room
  //                   (Designer `radByRoom.EditValue = True` at
  //                   Designer.vb:180).
  //   Start New Page= chkStartNewPage checked by default (Designer.vb:145),
  //                   label swaps "Floor" → "Unit" when facility has
  //                   `useUnitMapping` (.vb:105). Enabled only when
  //                   Order=Room (.vb:401-403).
  // Validation: none (.vb:379-382 always returns True).
  // Wire payload (.vb:322-340) → SelectiveMenuNonReturnedSettings:
  //   DateFor, NumberOfDays, FKey_Facility (middleware),
  //   PKey_Menu_Header, OrderByName, OrderByRoom,
  //   StartNewPageAfterEachFloor, LstMealtimes (int[])
  {
    slug: 'selective-menu-non-returned',
    reportName: 'Selective Menu - Non-Returned Menus',
    fKey_RS_Report: 58,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    fields: [
      // Date group — StartDate + NumberOfDays inline. WinForms
      // caps NumberOfDays at 9, not 7 like other Selective Menu
      // reports (.vb:88).
      { key: 'DateFor', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275 },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 1, min: 1, max: 9, group: 'Date', width: 250 },

      // Menu Cycle info — auto-updates as DateFor changes.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'DateFor' },

      // Mealtimes — starts empty (WinForms `_lstMealtimesInit`
      // default). No validation, matching WinForms
      // `ValidateForm() Return True`.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Order radio — default By Room (Designer `radByRoom.EditValue = True`).
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'room',
        group: 'Order',
        options: [
          { value: 'room', label: 'By Room' },
          { value: 'name', label: 'By Name' },
        ] },

      // Start New Page checkbox — default checked (Designer). Label
      // swaps "Floor" → "Unit" via the `{FloorOrUnit}` token which
      // resolves to `floorOrUnitLabel` from the global store —
      // matches WinForms `.vb:105` which sets the caption to "Unit"
      // when the facility has `useUnitMapping`. Enabled only when
      // OrderBy='room' (.vb:401-403). NEW ROW so it sits below
      // the radio pair.
      { key: 'StartNewPageAfterEachFloor', group: 'Options', type: 'checkbox',
        label: 'Start New Page After Each {FloorOrUnit}',
        defaultValue: true,
        enabledWhen: { key: 'OrderBy', value: 'room' },
        newRow: true },
    ],
    buildDateFor: (v) => {
      const days = Number(v.NumberOfDays ?? 1);
      if (days <= 1) return formatDate(v.DateFor);
      const start = v.DateFor instanceof Date
        ? new Date(v.DateFor.getFullYear(), v.DateFor.getMonth(), v.DateFor.getDate())
        : (typeof v.DateFor === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v.DateFor))
          ? (() => { const m = (v.DateFor as string).match(/^(\d{4})-(\d{2})-(\d{2})/)!; return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); })()
          : new Date();
      const end = new Date(start); end.setDate(end.getDate() + days - 1);
      return formatDateRange(start, end);
    },
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.DateFor);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      const orderBy = String(v.OrderBy ?? 'room');
      return {
        DateFor: v.DateFor,
        NumberOfDays: Number(v.NumberOfDays ?? 1),
        PKey_Menu_Header: pKeyMenuHeader,
        OrderByName: orderBy === 'name',
        OrderByRoom: orderBy === 'room',
        StartNewPageAfterEachFloor: Boolean(v.StartNewPageAfterEachFloor),
        LstMealtimes: meals,
      };
    },
    // WinForms `ValidateForm` always returns True — no validation.
  },
  // ─── PRODUCTION - SELECTIVE MENU - RESIDENT LIST
  //     (pKey 59, fKey 59) ────────────────────────────────────────
  // WinForms source: ucDietech_Report_Generator_SelectiveMenu_ResidentList.vb.
  //   TabPageText   = "Selective Menu - {Resident} List" (.vb:107).
  //   DateFor       = today (.vb:121).
  //   Menu Cycle    = ucMenuCycleInfo driven by DateFor (.vb:125-129).
  //   Order By      = 3-way radio (By Name / By Room / By Tray Line),
  //                   default By Name (Designer `radByName.EditValue = True`).
  //                   Tray Line meal dropdown enabled only when
  //                   By Tray Line is picked (.vb:549-550).
  //   Start New Page = checkbox. Label + enabled state depend on
  //                   Order By selection (.vb:383-422):
  //                     Name      → "…After Each Floor", DISABLED
  //                     Room      → "…After Each Floor", enabled
  //                     Tray Line → "…After Each {SetName}", enabled
  //   Print         = 3-way radio (All / Specific {SetName} /
  //                   Specific {Resident}), default All (Designer
  //                   `radPrintAll.EditValue = True`). When Order=
  //                   Tray Line, Specific {SetName} is DISABLED and
  //                   forced to All if it was selected (.vb:549-558).
  //   Specific Set meal dropdown = default 0 (B). Enabled only when
  //                   Print=Specific Set (.vb:401-413). Drives the
  //                   set list's `MealtimeOverride`.
  //
  // Validation (.vb:458-470):
  //   - If Print=Specific Set: ≥1 set checked
  //   - If Print=Specific Resident: ≥1 patient checked
  //
  // Wire payload (.vb:322-358) → SelectiveMenuResidentListSettings:
  //   DateFor, FKey_Facility, LstMealtimes = [0,1,2] (hardcoded),
  //   OrderByTrayLine, OrderByName, OrderByRoom,
  //   PrintTypeSelected (0=All / 1=SpecificSet / 2=SpecificResident),
  //   LstSpecificSets, SetsMealtime (cmbSpecificSetMeal.SelectedIndex),
  //   LstSpecificResident, PKey_Menu_Header,
  //   MenuDay (MenuCycleInUseInfo.MenuDayToUse), TrayLineOrder
  //   (cmbTrayLineOrder.SelectedIndex), StartNewPageAfterEachSetRoom
  {
    slug: 'selective-menu-resident-list',
    reportName: 'Selective Menu - {Resident} List',
    fKey_RS_Report: 59,
    fields: [
      // Date group — no NumberOfDays; single-day report.
      { key: 'DateFor', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275 },

      // Menu Cycle info — auto-updates as DateFor changes.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'DateFor' },

      // Order By radio — WinForms default `radByName.EditValue = True`.
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'name',
        group: 'Order By',
        options: [
          { value: 'name', label: 'Name' },
          { value: 'room', label: 'Room Number' },
          { value: 'trayLine', label: 'Tray Line' },
        ] },
      // Tray Line meal dropdown — enabled only when Order=Tray Line
      // (.vb:549-550). Default 0 (B). Static B/L/D options — matches
      // the hardcoded LstMealtimes = [0,1,2] at .vb:332 which never
      // touches EAM/AM/PM/HS.
      { key: 'TrayLineOrder', label: '', type: 'dropdown', defaultValue: 0,
        options: [
          { value: 0, label: 'B' },
          { value: 1, label: 'L' },
          { value: 2, label: 'D' },
        ],
        enabledWhen: { key: 'OrderBy', value: 'trayLine' },
        group: 'Order By', width: 75 },

      // Start New Page checkbox — WinForms .vb:132/385/394/419:
      //   Name      → "…After Each Floor", DISABLED
      //   Room      → "…After Each Floor", enabled
      //   Tray Line → "…After Each {SetName}", enabled
      // React port keeps the label static as "…After Each Floor"
      // (matches the default state shown in the WinForms screenshot);
      // enable-state matches WinForms — enabled when OrderBy is
      // 'room' or 'trayLine', disabled when 'name'. The wire field
      // is `StartNewPageAfterEachSetRoom` regardless of the label.
      { key: 'StartNewPageAfterEachSetRoom', type: 'checkbox',
        label: 'Start New Page After Each Floor',
        defaultValue: false,
        enabledWhen: { check: (v) => v.OrderBy !== 'name' },
        disabledReason: 'Available when Order By is Room or Cart.',
        group: 'Options' },

      // Print radio — default All (Designer `radPrintAll.EditValue = True`).
      // The Specific {SetName} option is DISABLED when Order=Tray Line
      // (.vb:552-557); switching to Tray Line while Specific Set is
      // picked auto-flips to All. Handled at the buildSettings /
      // interlock level — a `recomputeWhen` on Print does the flip.
      { key: 'PrintMode', label: '', type: 'radio',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          // Force All when Order=Tray Line and current pick is 'set'.
          if (values?.OrderBy === 'trayLine' && values?.PrintMode === 'set') return 'all';
          return (values?.PrintMode as string | undefined) ?? 'all';
        },
        recomputeWhen: ['OrderBy'],
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },
      // Specific Set meal dropdown — enabled only when Print='set'.
      // Default 0 (B). Static B/L/D options.
      { key: 'SetsMealtime', label: '', type: 'dropdown', defaultValue: 0,
        options: [
          { value: 0, label: 'B' },
          { value: 1, label: 'L' },
          { value: 2, label: 'D' },
        ],
        enabledWhen: { key: 'PrintMode', value: 'set' },
        group: 'Print', width: 75 },

      // ── Inline selectors below the Print group ──────────────
      // `inline: true` keeps them in the main column instead of
      // the right-hand side rail. Columns: Name + Diet Order +
      // Static + Unit + Room — matches WinForms Designer grid,
      // no TF column.
      { key: 'SetList', label: '', type: 'setSelect', inline: true,
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'set' } },
      { key: 'PatientList', label: '', type: 'residentSelect', inline: true,
        defaultValue: [], visibleWhen: { key: 'PrintMode', value: 'resident' },
        showInfoColumns: true,
        showStaticColumn: true,
        hideTFColumn: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v, _fs, ctx) => {
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.DateFor);
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const menuDay = ctx?.resolveMenuDayToUse?.(startIso) ?? -1;
      const orderBy = String(v.OrderBy ?? 'name');
      const printMode = String(v.PrintMode ?? 'all');
      const printTypeSelected =
        printMode === 'set' ? 1 :
        printMode === 'resident' ? 2 : 0;
      return {
        DateFor: v.DateFor,
        // WinForms hardcodes LstMealtimes = [0, 1, 2] at .vb:332.
        LstMealtimes: [0, 1, 2],
        OrderByName: orderBy === 'name',
        OrderByRoom: orderBy === 'room',
        OrderByTrayLine: orderBy === 'trayLine',
        PrintTypeSelected: printTypeSelected,
        LstSpecificSets: printMode === 'set'
          ? ((v.SetList as number[] | undefined) ?? [])
          : [],
        SetsMealtime: Number(v.SetsMealtime ?? 0),
        LstSpecificResident: printMode === 'resident'
          ? ((v.PatientList as number[] | undefined) ?? [])
          : [],
        PKey_Menu_Header: pKeyMenuHeader,
        MenuDay: menuDay,
        TrayLineOrder: Number(v.TrayLineOrder ?? 0),
        StartNewPageAfterEachSetRoom: Boolean(v.StartNewPageAfterEachSetRoom),
      };
    },
    // Mirrors WinForms `ValidateForm` (.vb:458-470).
    validate: (v) => {
      if (v.PrintMode === 'set') {
        const sets = (v.SetList as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select a {SetName}.';
      }
      if (v.PrintMode === 'resident') {
        const pts = (v.PatientList as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select a {Resident}.';
      }
      return null;
    },
  },
  {
    slug: 'selective-menu-touch',
    reportName: 'Selective Menu - Touch',
    fKey_RS_Report: 85,
    fields: [
      { key: 'StartDate', group: 'Date', label: 'Start Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'NumberOfDays', group: 'Date', label: 'Number of Days', type: 'number', defaultValue: 1, min: 1, max: 7 },
    ],
    buildDateFor: (v) => formatDate(v.StartDate),
  },

  // ─── PATIENT - WEIGHT - SIGNIFICANT CHANGE (pKey 70) ───────────
  // WinForms source: ucDietech_Report_Generator_WeightSignificantChange.vb.
  //   TabPageText = "Weight - Significant Change" (.vb:57).
  //   Init (.vb:68-81): sets lblTitle + favorite image only —
  //     no facility-driven UI state.
  //   Designer default:
  //     radGain.EditValue = True  (.Designer.vb:109)
  //   Print (.vb:257-269) → WeightSignificantChangeSettings:
  //     .FKey_Facility (middleware)
  //     .DateFor     = Now
  //     .IsGain      = radGain.Checked   (radLoss.Checked implies IsGain = false)
  //   ValidateForm always returns True (.vb:287-290).
  {
    slug: 'weight-significant-change',
    reportName: 'Weight - Significant Change',
    fKey_RS_Report: 70,
    fields: [
      // ── Report Type radio — Gain (default) / Loss ─────────────
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 'gain',
        group: 'Report Type',
        options: [
          { value: 'gain', label: 'Gain' },
          { value: 'loss', label: 'Loss' },
        ] },
    ],
    // WinForms sends `.DateFor = Now` — the form has no date
    // picker. Sidebar uses today for the date column.
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => ({
      // ISO-now so the non-nullable DateTime slot deserializes
      // cleanly. Matches WinForms `.DateFor = Now`.
      DateFor: toLocalWireDateTime(new Date()),
      IsGain: String(v.ReportType ?? 'gain') === 'gain',
    }),
    // WinForms `ValidateForm` always returns True.
    validate: () => null,
  },
  // pKey 72 (Weight - Matrix) canonical config is defined earlier
  // in this file next to the other Patient/Weight entries — an
  // older stub previously lived here but was removed to prevent
  // `REPORT_CONFIG_BY_PKEY` first-wins from silently shadowing the
  // full port with an older 5-field placeholder.
  // ─── PATIENT - WEIGHT - TRIGGER (pKey 73) ──────────────────────
  // WinForms source: ucDietech_Report_Generator_WeightTrigger.vb.
  //   TabPageText = "Weight - Trigger" (.vb:46).
  //   Init (.vb:80-98):
  //     radByRoom.Text = "By {Room}"
  //     Grid data comes from `Fill_dtWeight_Trigger` at .vb:327-414 —
  //     SQL loads every active/non-template/non-zero-weight patient,
  //     then per-patient calls `Diet_Utils.WeightCheckForTriggers`
  //     (`Diet_Utils.vb:496`); only patients with ≥1 trigger row are
  //     kept. Displayed as a DevExpress master-detail grid
  //     (`dgrResWeightTrigger`) where each master row shows Name +
  //     Current Weight and the detail band shows one row per
  //     trigger window (Days / Weight Gain / Weight Loss).
  //   Designer default:
  //     radByName.EditValue = True (.Designer.vb:230)
  //   Print (.vb:200-223) → WeightTriggerSettings:
  //     .FKey_Facility (middleware)
  //     .DateFor       = Now
  //     .LstPatient    = every PKey_Patient in `tblResidents` above
  //     .ProgramName   = g_PRIME_Program_Name  (server-side default)
  //     .ByName        = radByName.Checked
  //     .ByRoom        = radByRoom.Checked
  //   ValidateForm (.vb:314-318): returns False when the residents
  //   table is empty — no triggers → nothing to print.
  {
    slug: 'weight-trigger',
    reportName: 'Weight - Trigger',
    fKey_RS_Report: 73,
    fields: [
      // ── Order ──────────────────────────────────────────────────
      // Default By Name (.Designer.vb:230).
      { key: 'Order', label: '', type: 'radio', defaultValue: 'name',
        group: 'Order',
        options: [
          { value: 'name', label: 'By Name' },
          { value: 'room', label: 'By {Room}' },
        ] },

      // ── Patient master-detail grid ─────────────────────────────
      // Server-computed via `GET /api/Patient/WeightTriggerCandidates/{fac}`.
      // The component populates the parent form's `LstPatient`
      // value with every returned PKey — the wire payload sends
      // that list as-is on submit.
      // `inline: true` keeps the grid in the main column directly
      // under the Order group instead of routing it to the side
      // rail (default for selector-type fields).
      { key: 'LstPatient', label: '', type: 'weightTriggerGrid',
        defaultValue: [] as number[], inline: true },
    ],
    // WinForms sends `.DateFor = Now` — sidebar column shows today.
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => {
      const order = String(v.Order ?? 'name');
      const patientList = (v.LstPatient as number[] | undefined) ?? [];
      return {
        // ISO-now so the non-nullable DateTime slot deserializes
        // cleanly. Matches WinForms `.DateFor = Now` at `.vb:209`.
        DateFor: toLocalWireDateTime(new Date()),
        // Full patient list the grid produced. WinForms iterates
        // its `tblResidents` DataTable at `.vb:212-214` and adds
        // every row's PKey; we mirror that by trusting the grid's
        // computed list.
        LstPatient: patientList,
        // Server-side default in WinForms
        // (`.ProgramName = g_PRIME_Program_Name`). Sending "Dietech"
        // matches the common global; the server can override it
        // during report rendering.
        ProgramName: 'Dietech',
        ByName: order === 'name',
        ByRoom: order === 'room',
      };
    },
    // Mirrors WinForms `ValidateForm` (`.vb:314-318`): empty
    // residents table means no triggers found — nothing to print.
    validate: (v) => {
      const patients = (v.LstPatient as number[] | undefined) ?? [];
      if (patients.length === 0) return 'No {Resident_Plural} have triggered weight.';
      return null;
    },
  },
  // ─── WEIGHTS (Weight Entry, fKey 80) ────────────────────────────
  // WinForms prints this from the Weight Entry screen (Print_Handler,
  // frmDietech_WeightEntry .vb:901) with a WeightEntrySettings; the website
  // prints the same report from Weight Entry and from this card. Server-side
  // the WeightEntryWeb generator (WeightEntryWebSettings) reads only:
  //   DateFor, FKey_Facility, ByWeeks, StrStreamGrid,
  //   WeightFilterSelected (All 0, Weekly 1, Monthly 2, Tube Feed 3),
  //   WeightDisplayOrderSelected (1st weight 0, last weight 1).
  // How far back it prints is a facility setting (Wgt_Entry_AmtToPrintW /
  // AmtToPrintM), so there is no date range here. StrStreamGrid is the
  // desktop's saved grid layout; the website has none, so it sends ''.
  {
    slug: 'weights',
    reportName: 'Weights',
    fKey_RS_Report: 80,
    fields: [
      { key: 'ByWeeks', group: 'Options', label: 'Weekly weights', type: 'checkbox', defaultValue: false },
      { key: 'WeightFilterSelected', label: '', type: 'radio', defaultValue: 0,
        group: '{Resident_Plural}',
        options: [
          { value: 0, label: 'All {Resident_Plural}' },
          { value: 1, label: 'Weekly weight {Resident_Plural}' },
          { value: 2, label: 'Monthly weight {Resident_Plural}' },
          { value: 3, label: 'Tube feed {Resident_Plural}' },
        ] },
      // Desktop default: last weight of the month.
      { key: 'WeightDisplayOrderSelected', label: '', type: 'radio', defaultValue: 1,
        group: 'Monthly weights',
        visibleWhen: { key: 'ByWeeks', value: false },
        options: [
          { value: 0, label: '1st weight of the month' },
          { value: 1, label: 'Last weight of the month' },
        ] },
    ],
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => ({
      DateFor: new Date(),
      ByWeeks: v.ByWeeks === true,
      WeightFilterSelected: Number(v.WeightFilterSelected ?? 0),
      WeightDisplayOrderSelected: Number(v.WeightDisplayOrderSelected ?? 1),
      StrStreamGrid: '',
    }),
  },
  {
    slug: 'weight-history',
    reportName: 'Weight - History',
    fKey_RS_Report: 81,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'From Date', type: 'date', defaultValue: () => threeMonthsAgo(), required: true },
      { key: 'EndDate', group: 'Date Range', label: 'To Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'PrintList', group: 'Print', label: 'Print List', type: 'checkbox', defaultValue: true },
      { key: 'PrintChart', group: 'Print', label: 'Print Chart', type: 'checkbox', defaultValue: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  // ─── PATIENT - WEIGHT - MATRIX (pKey 72) ────────────────────────
  // WinForms source: ucDietech_Report_Generator_WeightMatrix.vb.
  //   TabPageText = "Weight - Matrix" (.vb:54).
  //   Init (.vb:90-132):
  //     ucMeStart.YearMonthOnly = True    ← month/year-only picker
  //     ucMeStart.DateInit      = Now.AddMonths(-11)  ← 11 months back
  //     lblDateUntil = " - {MonthName(start-1)} {start.Year+1}"
  //       Effectively the END month of the 12-month window inclusive
  //       (start + 11 months). WinForms recomputes on picker change.
  //     radByRoom.Text = "By {Room}"
  //     Two tabs: `tpResidents` ("{Resident}") + `tpFloorUnits`
  //       (floor/unit picker) — both start UNCHECKED per WinForms
  //       Fill_dtPatient / Fill_Weight_Triggers. React port defaults
  //       both to `defaultSelectAll` so users only deselect
  //       exceptions (matches the standard picker pattern used
  //       across the other Weight reports).
  //   Designer default:
  //     radByRoom.EditValue = True (.Designer.vb:383)
  //   Print (.vb:338-355) → WeightMatrixSettings:
  //     .DateFor       = ucMeStart.EditValue  (start month; server
  //                                              rounds to first-of-month
  //                                              via DateSerial at .vb:681)
  //     .LstPatient    = ucClbResidents.GetCheckResidents
  //     .LstFloorWing  = ucClb_FloorUnit.GetCheckedFloorWing_CommaSep
  //                        (List<String>; "Floor,Wing" for unit-mapped
  //                         facilities, "Floor,*" otherwise)
  //     .ByName        = radByName.Checked
  //     .ByRoom        = radByRoom.Checked
  //   ValidateForm (.vb:554-567): ≥1 resident AND ≥1 floor/unit.
  //   Preset "Select" ribbon options (mbtnAll/mbtnWith/mbtnWithout/
  //   mbtnWithWeekly) — filter presets keyed off weight-trigger and
  //   Flag_Weight_Interval. NOT ported here; users can manually
  //   deselect.
  {
    slug: 'weight-matrix',
    reportName: 'Weight - Matrix',
    fKey_RS_Report: 72,
    fields: [
      // ── Date ───────────────────────────────────────────────────
      // Year-month-only picker mirroring WinForms `ucMeStart`
      // (`MonthEdit.YearMonthOnly = True` at .vb:104). Spin buttons
      // shift by one month. `endMonthOffset: 11` renders the
      // computed " - {endMonth} {endYear}" static text after the
      // picker (matches WinForms `lblDateUntil` at .vb:110/807):
      // the 12-month matrix window ENDS 11 months after the picked
      // month. Default = today - 11 months snapped to the 1st
      // (matches WinForms `Now.AddMonths(-11)` at .vb:105).
      { key: 'DateFor', label: '', type: 'date', required: true,
        group: 'Date',
        yearMonthOnly: true,
        endMonthOffset: 11,
        // Widen the date-spin container so the chevrons + editor +
        // computed "- July 2026" suffix don't wrap.
        width: 400,
        // Explicit pixel width for the inner DateBox — the actual
        // input the user sees. Default DateBox natural width sits
        // around 160px; +30 puts it at ~190px so "August 2025" has
        // room without truncation.
        dateBoxWidth: 190,
        defaultValue: () => {
          const d = new Date();
          d.setDate(1);
          d.setMonth(d.getMonth() - 11);
          return d;
        } },

      // ── Order ──────────────────────────────────────────────────
      // Default By Room (.Designer.vb:383).
      { key: 'Order', label: '', type: 'radio', defaultValue: 'room',
        group: 'Order',
        options: [
          { value: 'room', label: 'By {Room}' },
          { value: 'name', label: 'By Name' },
        ] },

      // ── Selection (tabbed): Patient + Floor/Units ──────────────
      // Two tabs mirror WinForms `tpResidents` + `tpFloorUnits`.
      // Both auto-select-all on mount so users only deselect
      // exceptions — matches the screenshot's initial state.
      { key: 'LstPatient', label: '', type: 'residentSelect',
        defaultValue: [], defaultSelectAll: true,
        group: 'Selection', tab: 'Patient' },
      { key: 'LstFloorWing', label: '', type: 'floorUnitSelect',
        defaultValue: [], defaultSelectAll: true,
        group: 'Selection', tab: '{FloorOrUnit}' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v, _fs, ctx) => {
      const order = String(v.Order ?? 'room');
      const patientList = (v.LstPatient as number[] | undefined) ?? [];
      const floorUnitIds = (v.LstFloorWing as number[] | undefined) ?? [];
      // `LstFloorWing` on the wire is List<String> — "Floor,Wing"
      // or "Floor,*" strings via `ctx.resolveFloorWing`. Matches
      // WinForms `ucClb_FloorUnit.GetCheckedFloorWing_CommaSep`.
      const lstFloorWing = ctx?.resolveFloorWing?.(floorUnitIds) ?? [];
      return {
        DateFor: v.DateFor,
        LstPatient: patientList,
        LstFloorWing: lstFloorWing,
        ByRoom: order === 'room',
        ByName: order === 'name',
      };
    },
    validate: (v) => {
      const patients = (v.LstPatient as number[] | undefined) ?? [];
      if (patients.length === 0) return 'Please select at least one {Resident}.';
      const floorUnits = (v.LstFloorWing as number[] | undefined) ?? [];
      if (floorUnits.length === 0) return 'Please select at least one floor/unit.';
      return null;
    },
  },

  // ─── PATIENT - WEIGHT - ENTRY WORKSHEET (pKey 101) ─────────────
  // WinForms source: ucDietech_Report_Generator_WeightEntryWorksheet.vb.
  //   TabPageText = "Weight - Entry Worksheet" (.vb:54).
  //   Init (.vb:93-131):
  //     radByRoom.Text = "By {Room}"
  //     chkStartNewPage.Text switches on UseUnitMapping:
  //       true  → "Start New Page After Each Unit"
  //       false → "Start New Page After Each Floor"
  //     Fill_Residents loads all active/non-discharged residents;
  //     each starts UNCHECKED (`row("Selected") = False` at .vb:483).
  //   Designer defaults:
  //     radByRoom.EditValue        = True  (.Designer.vb:322)
  //     chkStartNewPage.EditValue  = True  (.Designer.vb:208)
  //   Ribbon "Select" dropdown offers preset selections:
  //     mbtnAll     → "All {ResidentPlural}"
  //     mbtnMonthly → "{ResidentPlural} on Monthly Weights..."
  //     mbtnWeekly  → "{ResidentPlural} on Weekly Weights..."
  //     mbtnDaily   → "{ResidentPlural} on Daily Weights..."
  //     Each preset sets `_filter` and checks the matching residents
  //     (`Flag_Weight_Interval` 7 / 30 / 31 / 1-3). The React port
  //     defaults to `defaultSelectAll` so the picker starts with
  //     every resident checked — matches the screenshot the user
  //     supplied — and drops the preset-picker UI (users can
  //     manually deselect exceptions).
  //   Print (.vb:378-442) → WeightEntryWorksheetSettings:
  //     .FKey_Facility     (middleware)
  //     .DateFor         = Now
  //     .LstFKey_Patient = ucClbResidents.GetCheckResidents
  //     .ByRoom          = radByRoom.Checked
  //     .ShowCurrentWeight       = chkShowCurentWeight.Checked
  //     .StartNewPageAfterEachFloor = chkStartNewPage.Checked
  //     .WeightFilterSelected — enum (.vb:390-431):
  //       -1 None   → partial subset (mixed pick)
  //        0 All    → every resident checked
  //        1 Daily  → filter mode = Daily AND all daily patients checked
  //        2 Weekly → filter mode = Weekly AND all weekly patients checked
  //        3 Monthly→ filter mode = Monthly AND all monthly patients checked
  //     Report ONLY reads this to build the printed report name
  //     (`ReportNameOverride` at .vb:50-70). React sends `All(0)`
  //     when every row is checked, `None(-1)` otherwise —
  //     the presets that would set 1/2/3 aren't ported.
  //   ValidateForm (.vb:515-523): ≥1 resident required.
  {
    slug: 'weight-entry-worksheet',
    reportName: 'Weight - Entry Worksheet',
    fKey_RS_Report: 101,
    fields: [
      // ── Order ──────────────────────────────────────────────────
      // Default By Room (.Designer.vb:322).
      { key: 'Order', label: '', type: 'radio', defaultValue: 'room',
        group: 'Order',
        options: [
          { value: 'room', label: 'By {Room}' },
          { value: 'name', label: 'By Name' },
        ] },

      // ── Options ────────────────────────────────────────────────
      // "Start New Page After Each Unit/Floor" default checked
      // (.Designer.vb:208). Label switches on facility UseUnitMapping
      // via `{FloorOrUnit}` token.
      // "Show Current Weight" defaults unchecked.
      { key: 'StartNewPageAfterEachFloor', label: 'Start New Page After Each {FloorOrUnit}',
        type: 'checkbox', defaultValue: true, group: 'Options' },
      { key: 'ShowCurrentWeight', label: 'Show Current Weight',
        type: 'checkbox', defaultValue: false, group: 'Options' },

      // ── Patient selector ───────────────────────────────────────
      // WinForms `ucClbResidents` is a large multi-column checkbox
      // list of every active/non-discharged/non-template resident.
      // Starts unchecked per WinForms, but the React port defaults
      // to `defaultSelectAll` so users only have to DE-select
      // exceptions — matches the screenshot's initial state.
      { key: 'LstFKey_Patient', label: 'Patient', type: 'residentSelect',
        defaultValue: [], defaultSelectAll: true, inline: true },
    ],
    // WinForms sends `.DateFor = Now` — sidebar date column uses
    // today.
    buildDateFor: () => formatDate(today()),
    buildSettings: (v, _fs, ctx) => {
      const order = String(v.Order ?? 'room');
      const patientList = (v.LstFKey_Patient as number[] | undefined) ?? [];
      const activeAll = ctx?.resolveAllResidentPKeys?.() ?? [];
      // `WeightFilterSelected` enum mapping — matches WinForms
      // `.vb:390-431` for the `FilterType.None/All` branches; the
      // preset-driven Daily/Weekly/Monthly branches aren't ported.
      // All rows checked → All(0). Otherwise → None(-1).
      const allChecked = activeAll.length > 0 && patientList.length === activeAll.length;
      const weightFilterSelected = allChecked ? 0 : -1;
      return {
        DateFor: toLocalWireDateTime(new Date()),
        LstFKey_Patient: patientList,
        ByRoom: order === 'room',
        ShowCurrentWeight: Boolean(v.ShowCurrentWeight),
        StartNewPageAfterEachFloor: Boolean(v.StartNewPageAfterEachFloor),
        WeightFilterSelected: weightFilterSelected,
      };
    },
    validate: (v) => {
      const patients = (v.LstFKey_Patient as number[] | undefined) ?? [];
      if (patients.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },

  // ─── DRUG ALLERGIES (pKey 60, fKey 60) ─────────────────────────
  // WinForms source: ucDietech_Report_Generator_DrugAllergies.vb.
  // Print (.vb:298-310):
  //   .FKey_Facility = _FKey_Facility
  //   .DateFor       = Now  (WinForms hard-codes current time —
  //                     .vb:303; form has no date picker)
  //   .Assinged      = radWithAllergies.Checked  (Boolean — note
  //                     the WinForms typo "Assinged", server class
  //                     has the same misspelled property so we
  //                     MUST send the same name on the wire —
  //                     see `DrugAllergiesSettings.vb:6`)
  //   report class   = DrugAllergies (server pKey 60)
  //   detailedReportName = "Drug Allergies"
  //
  // Init (.vb:78-91): no field initialization beyond the title —
  // just the two Report Type radios in the Designer.
  //
  // Validate (.vb:289-293): always returns True — no validation.
  {
    slug: 'drug-allergies',
    reportName: 'Drug Allergies',
    fKey_RS_Report: 60,
    apiEndpoint: 'DrugAllergies',
    fields: [
      // Report Type radio — "With Drug Allergies" (default checked
      // per Designer, matches screenshot) vs "Without Drug
      // Allergies". Values 1/0 map to the `.Assinged` boolean
      // on the settings class (FieldOption.value is `string |
      // number`; buildSettings coerces to boolean at submit).
      { key: 'Assinged', label: '', type: 'radio', defaultValue: 1,
        group: 'Report Type',
        options: [
          { value: 1, label: 'With Drug Allergies' },
          { value: 0, label: 'Without Drug Allergies' },
        ] },
    ],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print block (.vb:301-305). Preserves the
    // "Assinged" typo on the wire so the VB Newtonsoft.Json
    // deserializer binds the property correctly.
    buildSettings: (v) => ({
      DateFor: new Date(),
      Assinged: Number(v.Assinged) === 1,
    }),
  },
  // ─── FOOD ALLERGIES (pKey 63, fKey 63) ─────────────────────────
  // WinForms source: ucDietech_Report_Generator_FoodAllergies.vb.
  // Print (.vb:273-286):
  //   .FKey_Facility = _FKey_Facility
  //   .DateFor       = Now  (hard-coded — form has no date picker)
  //   .ByFloor       = radByFloor.Checked
  //   .ByName        = NOT radByFloor.Checked  (radio pair)
  //   report class   = FoodAllergies (server pKey 63 per
  //                    ReportDispatcher.cs:85)
  //   queue title    = "Food Allergies" (.vb:284 — literal)
  //
  // Init (.vb:80-100):
  //   radByFloor default checked (Designer .vb:106 EditValue=True).
  //   radByFloor.Text swaps at runtime via UseUnitMapping:
  //     UseUnitMapping = true  → "By Unit"
  //     UseUnitMapping = false → "By Floor"
  //   radByResident (caption "By Name") — the paired radio.
  //
  // Validate (.vb:316-319): always returns True — no validation.
  {
    slug: 'food-allergies',
    reportName: 'Food Allergies',
    fKey_RS_Report: 63,
    apiEndpoint: 'FoodAllergies',
    fields: [
      // Order radio — mirrors radByFloor / radByResident pair.
      // Second label uses `{FloorOrUnit}` which
      // GenericReportForm's substituteTokens resolves to "Unit"
      // or "Floor" depending on the facility's UseUnitMapping
      // setting (matches WinForms `.vb:91-95`).
      { key: 'Order', label: '', type: 'radio', defaultValue: 'floor',
        group: 'Order',
        options: [
          { value: 'floor', label: 'By {FloorOrUnit}' },
          { value: 'name', label: 'By Name' },
        ] },
    ],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print settings block (.vb:276-281).
    buildSettings: (v) => ({
      DateFor: new Date(),
      ByFloor: v.Order === 'floor',
      ByName: v.Order === 'name',
    }),
  },
  {
    slug: 'allergy-group',
    reportName: 'Allergy Group',
    fKey_RS_Report: 110,
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'ReportTypeSelected', group: 'Report Type', label: 'Report Type', type: 'dropdown', defaultValue: 0, options: ALLERGY_GROUP_TYPE_OPTIONS, required: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
  },

  // ─── FEEDING STATUS — UNASSIGNED (pKey 48, fKey 48) ────────────
  // WinForms source: ucDietech_Report_Generator_FeedingStatus_Unassigned.vb.
  // Same layout + wiring as Feeding Status - Assigned (pKey 49);
  // only differences:
  //   - `.ReportSelected = MealSettings.Report.FeedingStatusUnassigned` (=1)
  //     — vs 0 for Assigned
  //   - report class = MealFeedingStatusUnassigned (pKey 48)
  //   - TabPageText = "Feeding Status - Unassigned"
  {
    slug: 'meal-feeding-status-unassigned',
    reportName: 'Feeding Status - Unassigned',
    fKey_RS_Report: 48,
    apiEndpoint: 'MealFeedingStatusUnassigned',
    fields: [
      { key: 'ReportTypeSelected', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const facType = Number((fs as { fac_Type?: number } | null)?.fac_Type ?? 1);
          const idx = Math.max(0, Math.min(3, facType - 1));
          return idx + 1;
        },
        group: 'Report Type',
        options: [
          { value: 1, label: 'MDS' },
          { value: 2, label: 'PRI' },
          { value: 3, label: '{Facility}' },
          { value: 4, label: 'Free Text' },
        ] },

      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => today() },

      { key: 'GroupByFloor', label: 'Group By {FloorOrUnit}', type: 'checkbox',
        defaultValue: false, group: 'Options' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print block (.vb:307-323, Unassigned variant).
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      ReportSelected: 1, // Report.FeedingStatusUnassigned
      ReportTypeSelected: Number(v.ReportTypeSelected),
      GroupByFloor: v.GroupByFloor,
    }),
  },
  // ─── FEEDING STATUS — ASSIGNED (pKey 49, fKey 49) ──────────────
  // WinForms source: ucDietech_Report_Generator_FeedingStatus_Assigned.vb.
  // Print (.vb:303-329):
  //   .FKey_Facility      = _FKey_Facility
  //   .DateFor            = ucDate.EditValue
  //   .ReportSelected     = MealSettings.Report.FeedingStatusAssigned (=0)
  //   .ReportTypeSelected = MDS=1 / PRI=2 / Facility=3 / FreeText=4
  //                         (MealSettings.ReportType enum at
  //                         MealSettings.vb:11-16)
  //   .GroupByFloor       = chkGroupByFloor.Checked
  //   report class        = MealFeedingStatusAssigned (pKey 49)
  //   detailedReportName  = "Meal - Feeding Status - Assigned"
  //
  // Init (.vb:70-106):
  //   ucDate: no label, DateInit = today
  //   Radio default from `Fac_Type - 1` facility setting:
  //     0 → MDS, 1 → PRI, 2 → Facility, 3 → FreeText (.vb:84-93)
  //   `radFacility.Text = facility.Facility` — facility-labeled
  //     third radio (screenshot shows "Hospital" for this facility)
  //   `chkGroupByFloor.Text = "Group By Unit"` when UseUnitMapping,
  //     else "Group By Floor" (.vb:97-101)
  //
  // Validate (.vb:359-362): always returns True — no validation.
  {
    slug: 'meal-feeding-status-assigned',
    reportName: 'Feeding Status - Assigned',
    fKey_RS_Report: 49,
    apiEndpoint: 'MealFeedingStatusAssigned',
    fields: [
      // Report Type radio — MDS/PRI/{Facility}/Free Text. Values
      // map to MealSettings.ReportType enum (1/2/3/4). Default
      // pulled from facility `fac_Type - 1` at render time
      // (.vb:84-93). The third label uses the {Facility} token
      // which resolves to the facility-configured `facility`
      // string (e.g. "Hospital" per the screenshot).
      { key: 'ReportTypeSelected', label: '', type: 'radio',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const facType = Number((fs as { fac_Type?: number } | null)?.fac_Type ?? 1);
          // fac_Type - 1 → 0=MDS, 1=PRI, 2=Facility, 3=FreeText;
          // MealSettings.ReportType enum values are shifted +1
          // (None=0, MDS=1, PRI=2, Facility=3, FreeText=4).
          const idx = Math.max(0, Math.min(3, facType - 1));
          return idx + 1; // 1..4
        },
        group: 'Report Type',
        options: [
          { value: 1, label: 'MDS' },
          { value: 2, label: 'PRI' },
          { value: 3, label: '{Facility}' },
          { value: 4, label: 'Free Text' },
        ] },

      // Date — single date, defaults to today (.vb:81).
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => today() },

      // Options — Group By Floor/Unit checkbox, unchecked default.
      // Label swaps via facility UseUnitMapping (.vb:97-101) using
      // the `{FloorOrUnit}` token — resolves to "Unit" when
      // `useUnitMapping`, else "Floor".
      { key: 'GroupByFloor', label: 'Group By {FloorOrUnit}', type: 'checkbox',
        defaultValue: false, group: 'Options' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print block (.vb:307-323).
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      ReportSelected: 0, // Report.FeedingStatusAssigned
      ReportTypeSelected: Number(v.ReportTypeSelected),
      GroupByFloor: v.GroupByFloor,
    }),
  },
  // ─── MEAL — PORTION (pKey 50, fKey 50) ─────────────────────────
  // WinForms source: ucDietech_Report_Generator_PortionSummary.vb.
  // Print (.vb:283-297):
  //   .FKey_Facility        = _FKey_Facility
  //   .DateFor              = ucDate.EditValue
  //   .ReportSelected       = MealSettings.Report.Portion (=2)
  //   .ReportTypeSelected   = MealSettings.ReportType.None (=0)
  //   report class          = MealPortion (server pKey 50)
  //   detailedReportName    = "Meal - Portion"
  //
  // Init (.vb:71-88):
  //   ucDate: no label, DateInit = today
  //
  // Validate (.vb:325-328): always returns True — no validation.
  {
    slug: 'meal-portion',
    reportName: 'Portion',
    fKey_RS_Report: 50,
    apiEndpoint: 'MealPortion',
    fields: [
      // Date — single date, defaults to today (.vb:82).
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => today() },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print block (.vb:287-292). ReportSelected /
    // ReportTypeSelected are fixed (Report.Portion, ReportType.None)
    // per the enum values at MealSettings.vb:7,11.
    buildSettings: (v) => ({
      DateFor: v.DateFor,
      ReportSelected: 2, // Report.Portion
      ReportTypeSelected: 0, // ReportType.None
    }),
  },
  {
    slug: 'meal-summary',
    reportName: 'Meal - Summary',
    fKey_RS_Report: 86,
    // WinForms ucDietech_Report_Generator_MealSummary.vb. By Cold/Hot (the
    // Designer default) and By Meal send the checked mealtimes and sets
    // (.vb:499-513), By Set only the sets (.vb:531-538), Total neither
    // (.vb:553-559). MealSummarySettings.ToString counts LstSets.
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'ReportTypeSelected', group: 'Report Type', label: 'Report Type', type: 'dropdown', defaultValue: 0, options: MEAL_SUMMARY_TYPE_OPTIONS },
      { key: 'LstMealtimes', group: 'Mealtimes', label: 'Mealtimes', type: 'multiCheckbox', defaultValue: [0, 1, 2],
        options: [{ value: 0, label: '{Breakfast}' }, { value: 1, label: '{Lunch}' }, { value: 2, label: '{Dinner}' }],
        visibleWhen: { check: (v) => Number(v.ReportTypeSelected ?? 0) <= 1 } },
      { key: 'LstSets', group: 'Selection', label: 'Select {SetName_Plural}', type: 'setSelect', defaultValue: [],
        visibleWhen: { check: (v) => Number(v.ReportTypeSelected ?? 0) <= 2 } },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v) => {
      const type = Number(v.ReportTypeSelected ?? 0);
      const meals = (v.LstMealtimes as number[] | undefined) ?? [];
      const sets = (v.LstSets as number[] | undefined) ?? [];
      return {
        DateFor: v.DateFor,
        ReportTypeSelected: type,
        ...(type <= 1 ? { LstMealtimes: meals } : {}),
        ...(type <= 2 ? { LstSets: sets } : {}),
      };
    },
    // ValidateForm (.vb:462-482).
    validate: (v) => {
      const type = Number(v.ReportTypeSelected ?? 0);
      const meals = (v.LstMealtimes as number[] | undefined) ?? [];
      const sets = (v.LstSets as number[] | undefined) ?? [];
      if (type <= 1) {
        if (meals.length === 0) return 'Please select at least one mealtime.';
        if (sets.length === 0) return 'Please select at least one {SetName}.';
        if (sets.length > 19) return 'Sorry, but you cannot select more than 19 sets.';
      } else if (type === 2 && sets.length === 0) {
        return 'Please select at least one {SetName}.';
      }
      return null;
    },
  },
  {
    slug: 'meal-dislike',
    reportName: 'Meal - Dislike',
    fKey_RS_Report: 87,
    // WinForms ucDietech_Report_Generator_Dislike.vb. One Type radio (By
    // Consistency for Menu Cycle is the Designer default, .Designer.vb:316),
    // each print path sending its own lists (MealDislikesSettings):
    //   By Consistency / By Menu Cycle: the checked mealtimes (.vb:342-356,
    //     387-401), all checked on open (.vb:117 CheckAllMealtimes)
    //   By Set: the one radio mealtime + the checked sets (.vb:404-418)
    //   By Item: the ticked items' PKey_Item + Itm_Descr (.vb:361-380);
    //     MealDislikes.vb:472-483 reads them, Nothing threw
    // and always DateFor + the menu cycle in use on that date.
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo', defaultValue: null, group: 'Menu Cycle', watchField: 'DateFor',
        visibleWhen: { check: (v) => Number(v.ReportTypeSelected ?? 0) !== 1 } },
      { key: 'ReportTypeSelected', group: 'Type', label: '', type: 'radio', defaultValue: 0, layout: 'column',
        options: [
          { value: 0, label: 'By Consistency for Menu Cycle' },
          { value: 1, label: 'By Item' },
          { value: 2, label: 'By Menu Cycle' },
          { value: 3, label: 'By {SetName}' },
        ] },
      { key: 'LstMealtimes', group: 'Mealtimes', label: '', type: 'multiCheckbox', defaultValue: [0, 1, 2],
        options: [{ value: 0, label: '{Breakfast}' }, { value: 1, label: '{Lunch}' }, { value: 2, label: '{Dinner}' }],
        visibleWhen: { check: (v) => [0, 2].includes(Number(v.ReportTypeSelected ?? 0)) } },
      { key: 'Mealtime', group: 'Mealtimes', label: '', type: 'radio', defaultValue: 0,
        options: [{ value: 0, label: '{Breakfast}' }, { value: 1, label: '{Lunch}' }, { value: 2, label: '{Dinner}' }],
        visibleWhen: { key: 'ReportTypeSelected', value: 3 } },
      { key: 'LstSets', group: '{SetName_Plural}', label: '', type: 'setSelect', defaultValue: [],
        visibleWhen: { key: 'ReportTypeSelected', value: 3 } },
      { key: 'Items', group: 'Items', label: '', type: 'itemSelect', defaultValue: [], inline: true,
        visibleWhen: { key: 'ReportTypeSelected', value: 1 } },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v, _fs, ctx) => {
      const type = Number(v.ReportTypeSelected ?? 0);
      const dateIso = typeof v.DateFor === 'string' ? v.DateFor.substring(0, 10) : null;
      const base = {
        DateFor: v.DateFor,
        FKey_Menu_Header: ctx?.resolvePkeyMenuHeader?.(dateIso) ?? 0,
        ReportTypeSelected: type,
      };
      if (type === 1) {
        const items = (v.Items as Array<{ pKey_Item: number; itm_Descr: string }> | undefined) ?? [];
        return {
          ...base,
          LstFKey_Items: items.map((i) => i.pKey_Item),
          LstItm_Descr: items.map((i) => i.itm_Descr),
        };
      }
      if (type === 3) {
        return {
          ...base,
          LstMealtimes: [Number(v.Mealtime ?? 0)],
          LstSets: (v.LstSets as number[] | undefined) ?? [],
        };
      }
      return { ...base, LstMealtimes: (v.LstMealtimes as number[] | undefined) ?? [] };
    },
    validate: (v) => {
      const type = Number(v.ReportTypeSelected ?? 0);
      if (type === 1 && ((v.Items as unknown[] | undefined) ?? []).length === 0) return 'Please select at least one item.';
      if (type === 3 && ((v.LstSets as number[] | undefined) ?? []).length === 0) return 'Please select at least one {SetName}.';
      if ((type === 0 || type === 2) && ((v.LstMealtimes as number[] | undefined) ?? []).length === 0) {
        return 'Please select at least one mealtime.';
      }
      return null;
    },
  },

  // ─── FACILITY - SUPPLEMENT (pKey 68, fKey 68) ─────────────────
  // WinForms source: ucDietech_Report_Generator_Supplement.vb.
  //   TabPageText = "Supplement" (.vb:75).
  //   Print (radio, 5 options)  — default By Cold/Hot (Designer
  //     `radByColdHot.EditValue = True`):
  //       0=HotCold / 1=Meal / 2=Resident / 3=BySet / 4=Total
  //   Date = today (.vb:100-102).
  //   Cart picker (setSelect) — visible when Print ∈ {HotCold,
  //     Meal, BySet} (.vb:504-508).
  //   Tabs (Mealtimes / Floor-Units / {Resident}s):
  //     • Mealtimes: all 7 slots (main + Nourish EAM/AM/PM/HS).
  //       Empty default (`_lstMealtimesInit` empty).
  //     • Floor/Units: floorUnitSelect, default all-checked.
  //     • Residents: residentSelect, default empty.
  //   Options group — visible ONLY when Print=Resident (.vb:498-502):
  //     • Sort Alpha (default checked, Designer
  //       `chkSortAlpha.EditValue = True`).
  //     • Group on Floor / Unit (label swaps for
  //       `useUnitMapping`; default unchecked).
  //
  // Validation (.vb:546-563):
  //   - HotCold/Meal/BySet require ≥1 set
  //   - HotCold/Meal require ≤19 sets
  //   - ≥1 mealtime
  //
  // Wire payload → SupplementSettings:
  //   DateFor, ReportTypeSelected, FKey_Facility (middleware),
  //   UseProduction (facility setting), SortByName, GroupByFloorUnit,
  //   LstResident (int[]), LstSet (int[]), LstMealtimes (int[]),
  //   LstFloorWing (string[] "Floor,Wing" — resolved via ctx).
  {
    slug: 'supplement',
    reportName: 'Supplement',
    fKey_RS_Report: 68,
    fields: [
      // Print radio — 5 options, default HotCold (index 0).
      // Column layout so all five stack vertically like the
      // WinForms Print groupbox.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'hotCold',
        group: 'Print',
        options: [
          { value: 'hotCold', label: 'By Cold/Hot' },
          { value: 'meal', label: 'By Meal' },
          { value: 'resident', label: 'By {Resident}' },
          { value: 'set', label: 'By {SetName}' },
          { value: 'total', label: 'Total' },
        ] },

      // Date group — single date, today default.
      { key: 'DateFor', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date', width: 275 },

      // Cart (Sets) picker — visible for HotCold, Meal, BySet.
      { key: 'LstSet', label: '', type: 'setSelect', inline: true,
        defaultValue: [], group: 'Cart',
        visibleWhen: { check: (v) =>
          v.PrintMode === 'hotCold' || v.PrintMode === 'meal' || v.PrintMode === 'set' } },

      // Options group — only visible when Print=Resident.
      { key: 'SortByName', label: 'Sort Alpha', type: 'checkbox',
        defaultValue: true, group: 'Options',
        visibleWhen: { key: 'PrintMode', value: 'resident' } },
      { key: 'GroupByFloorUnit', label: 'Group on {FloorOrUnit}', type: 'checkbox',
        defaultValue: false, group: 'Options',
        visibleWhen: { key: 'PrintMode', value: 'resident' } },

      // Tabs — Mealtimes / Floor-Units / Residents. WinForms tab
      // caption for Residents uses `Resident_Plural`; port uses
      // `{Resident_Plural}` token so it swaps to "Patients" for
      // facilities that rename Resident.
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },
      { key: 'LstResident', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    buildSettings: (v, fs, ctx) => {
      const printMode = String(v.PrintMode ?? 'hotCold');
      const reportTypeSelected =
        printMode === 'hotCold' ? 0 :
        printMode === 'meal' ? 1 :
        printMode === 'resident' ? 2 :
        printMode === 'set' ? 3 :
        printMode === 'total' ? 4 : 0;
      const useProduction = Boolean(
        (fs as { useProduction?: boolean } | null)?.useProduction,
      );
      const setsShown = printMode === 'hotCold' || printMode === 'meal' || printMode === 'set';
      // LstFloorWing wire format is List<string> "Floor,Wing"
      // pairs — mirror WinForms `GetCheckedFloorWing_CommaSep` via
      // the ctx resolver populated from `pKey_Unit_Mapping` ints.
      const floorUnitIds = (v.lstFloorUnit as number[] | undefined) ?? [];
      const lstFloorWing = ctx?.resolveFloorWing?.(floorUnitIds) ?? [];
      return {
        DateFor: v.DateFor,
        ReportTypeSelected: reportTypeSelected,
        UseProduction: useProduction,
        SortByName: Boolean(v.SortByName),
        GroupByFloorUnit: Boolean(v.GroupByFloorUnit),
        LstResident: (v.LstResident as number[] | undefined) ?? [],
        LstSet: setsShown ? ((v.LstSet as number[] | undefined) ?? []) : [],
        LstMealtimes: (v.LstMealtimes as number[] | undefined) ?? [],
        LstFloorWing: lstFloorWing,
      };
    },
    // Mirrors WinForms `ValidateForm` (.vb:546-563).
    validate: (v) => {
      const printMode = String(v.PrintMode ?? 'hotCold');
      const sets = (v.LstSet as number[] | undefined) ?? [];
      const meals = (v.LstMealtimes as number[] | undefined) ?? [];
      const setsRequired = printMode === 'hotCold' || printMode === 'meal' || printMode === 'set';
      if (setsRequired && sets.length === 0) return 'Please select a {SetName}.';
      const setsMax19 = printMode === 'hotCold' || printMode === 'meal';
      if (setsMax19 && sets.length > 19) return 'Sorry, but you cannot select more than 19 sets.';
      if (meals.length === 0) return 'Please select at least one mealtime.';
      return null;
    },
  },
  // The generic "Tube Feed" card that sat here (raw form values, LstSet /
  // LstPatient always [], which TubeFeed.vb:461-475 reads as "nobody") is
  // gone. It shared pKey 69 with `tube-feed-labels` and, coming first, won
  // the pKey lookup, so the catalog showed it instead of the WinForms port.
  // Old links to /reports/run/tube-feed land on Tube Feed - Labels
  // (LEGACY_SLUGS below).
  {
    slug: 'tube-feed-form',
    reportName: 'Tube Feed Form',
    fKey_RS_Report: 83,
    fields: [
      { key: 'DateStart', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'DateEnd', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'OrderByRoom', group: 'Order By', label: 'Order by Room', type: 'checkbox', defaultValue: true },
      { key: 'OrderByName', group: 'Order By', label: 'Order by Name', type: 'checkbox', defaultValue: false },
      { key: 'PrintAll', group: 'Options', label: 'Print All', type: 'checkbox', defaultValue: true },
    ],
    buildDateFor: (v) => formatDateRange(v.DateStart, v.DateEnd),
  },
  // ─── FACILITY - TUBE FEED - LABELS (pKey 69) ───────────────────
  // WinForms source: ucDietech_Report_Generator_TubeFeed.vb.
  //   TabPageText = "Tube Feed - Labels" (.vb:61).
  //   Init (.vb:84-146):
  //     radPrintSpecificSet.Text = "Specific {SetName}"
  //     radSpecificResident.Text = "Specific {Resident}"
  //     radByRoomNumber.Text     = "{Room} Number"
  //     ucDeStart / ucDeEnd both init to _dateTimeInitialized (now)
  //     ucClbMealtimes:
  //       .IncludeCustom       = (tblTubeFeed_Label has any DateTimeCustom row)
  //       .IncludeNourishments = True
  //       .ShowAllNourishmentInLite = True
  //       .SetItemChecks(empty list)  → user picks
  //     ucClbResident.TubeFeedOnly = True
  //   Designer defaults:
  //     radLabels.EditValue           = True  → default Labels
  //     radPrintAll.EditValue         = True  → default All
  //     radOrderByTrayLine.EditValue  = True  → default Tray Line
  //   SetControlVisibilities (.vb:430-465):
  //     Summary   → hide Order By group (radios stay at TrayLine)
  //     TrayLine  → hide Sets, show Mealtimes
  //     Room/Name + Labels  → show Mealtimes
  //     Room/Name + Summary → hide Mealtimes  ← the ONE case
  //     Print=Specific Set    → show Sets
  //     Print=Specific Patient → show Residents
  //   Print (.vb:330-396) → TubeFeedSettings:
  //     Labels branch:
  //       .ReporTypeSelected = Labels(0)
  //       .DateStart / .DateEnd
  //       .LstMealtimes
  //       .OrderByTrayLine / .OrderByName / .OrderByRoom
  //       .PrintAll / .LstSet / .LstPatient  (conditional)
  //       .LabelsToSkip  (label-selector modal — React sends -1)
  //     Summary branch:
  //       .ReporTypeSelected = Summary(1)
  //       .DateStart / .DateEnd / .LstMealtimes
  //       .PrintAll / .LstSet / .LstPatient  (conditional)
  //   ValidateForm (.vb:525-546):
  //     - Mealtimes visible + 0 checked → error
  //     - Sets visible + 0 checked      → error "select at least one {SetName}"
  //     - Residents visible + 0 checked → error "select at least one {Resident}"
  {
    slug: 'tube-feed-labels',
    reportName: 'Tube Feed - Labels',
    fKey_RS_Report: 69,
    fields: [
      // ── Report Type ────────────────────────────────────────────
      // Default Labels (.Designer.vb:215).
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 'labels',
        group: 'Report Type',
        options: [
          { value: 'labels', label: 'Labels' },
          { value: 'summary', label: 'Summary' },
        ] },

      // ── Date range ─────────────────────────────────────────────
      // WinForms shows Start with no label, End with "To:" prefix.
      // Both default to today. ucDeEnd's DateMin is dynamic (tracks
      // Start) — not modelled here; if we later need it, add a
      // recomputeWhen tie-in.
      { key: 'DateStart', label: '', type: 'date', defaultValue: () => today(),
        required: true, group: 'Date' },
      { key: 'DateEnd', label: 'To:', type: 'date', defaultValue: () => today(),
        required: true, group: 'Date' },

      // ── Mealtimes ──────────────────────────────────────────────
      // WinForms `ucClbMealtimes` with `IncludeNourishments = True`
      // + `ShowAllNourishmentInLite = True` + conditional `Custom`.
      // Values are the enum-index the wire uses (0-6 for main +
      // nourishments; 9 = Custom). Custom is 9, NOT 7: TubeFeed.vb
      // (:130, :389-404) reads 7 as PM2 and only treats `> 8` as Custom,
      // and WinForms ucDietech_Report_Generator_ClbMealtime.vb:122 adds
      // Custom as 9. Sending 7 printed PM2 labels instead of custom ones.
      // Order matches the WinForms `PSICheckedListBoxControl`
      // 2-column grid: chronological pairing per row
      // (Breakfast|AM / Lunch|PM / Dinner|HS / EAM|Custom). Default
      // 2-column grid (`1fr 1fr` row-major) drops each option into
      // the correct slot with no custom CSS.
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox',
        defaultValue: [] as number[], group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 4, label: '{NourishAM}' },
          { value: 1, label: '{Lunch}' },
          { value: 5, label: '{NourishPM}' },
          { value: 2, label: '{Dinner}' },
          { value: 6, label: '{NourishHS}' },
          { value: 3, label: '{NourishEAM}' },
          // "Custom" — WinForms `.IncludeCustom` gates on any row in
          // tblTubeFeed_Label having a non-null `DateTimeCustom`. We
          // don't gate client-side; with no custom rows the server's
          // DateTimeCustom filter simply matches nothing.
          { value: 9, label: 'Custom' },
        ] },

      // ── Order By (hidden when Summary) ────────────────────────
      // Default Tray Line (.Designer.vb:321). Hidden but not
      // re-defaulted when Summary — WinForms leaves the underlying
      // radio state alone, which is why Summary-branch buildSettings
      // still respects a stale OrderBy pick.
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'trayLine',
        group: 'Order By',
        visibleWhen: { key: 'ReportType', value: 'labels' },
        options: [
          { value: 'trayLine', label: 'Tray Line' },
          { value: 'room', label: '{Room} Number' },
          { value: 'name', label: 'Name' },
        ] },

      // ── Print scope ────────────────────────────────────────────
      // Default All (.Designer.vb:175).
      { key: 'Print', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },
      { key: 'LstSet', label: '', type: 'setSelect', inline: true,
        defaultValue: [], visibleWhen: { key: 'Print', value: 'set' } },
      { key: 'LstPatient', label: '', type: 'residentSelect', inline: true,
        defaultValue: [], visibleWhen: { key: 'Print', value: 'resident' },
        showInfoColumns: true,
        // WinForms `ucClbResident.TubeFeedOnly = True` at .vb:138.
        filterTubeFeedOnly: true },
    ],
    // Sidebar date column mirrors WinForms `DateFor` — same-date
    // shows just the date, range shows "start - end".
    buildDateFor: (v) => {
      const startFmt = formatDate(v.DateStart);
      const endFmt = formatDate(v.DateEnd);
      return startFmt === endFmt ? startFmt : formatDateRange(v.DateStart, v.DateEnd);
    },
    buildSettings: (v) => {
      const reportType = String(v.ReportType ?? 'labels');
      const isLabels = reportType === 'labels';
      const order = String(v.OrderBy ?? 'trayLine');
      const print = String(v.Print ?? 'all');
      const mealtimes = Array.isArray(v.LstMealtimes) ? (v.LstMealtimes as number[]) : [];
      const setList = (v.LstSet as number[] | undefined) ?? [];
      const patientList = (v.LstPatient as number[] | undefined) ?? [];
      // ReportType enum: Labels=0, Summary=1, List=2, Products=3
      // (`TubeFeedSettings.vb:4-9`). WinForms only picks Labels or
      // Summary in this generator; the other two enums are for
      // Facility Add/Mod use.
      const reporTypeSelected = isLabels ? 0 : 1;

      const base: Record<string, unknown> = {
        ReporTypeSelected: reporTypeSelected,
        DateStart: v.DateStart,
        DateEnd: v.DateEnd,
        LstMealtimes: mealtimes,
        PrintAll: print === 'all',
        LstSet: print === 'set' && setList.length > 0 ? setList : null,
        LstPatient: print === 'resident' && patientList.length > 0 ? patientList : null,
      };
      if (isLabels) {
        // WinForms `.vb:354-360` — order-by trio + LabelsToSkip only
        // populate in the Labels branch.
        base.OrderByTrayLine = order === 'trayLine';
        base.OrderByRoom = order === 'room';
        base.OrderByName = order === 'name';
        base.LabelsToSkip = -1;
      }
      return base;
    },
    // Mirrors WinForms `ValidateForm` (.vb:525-546). Mealtimes are
    // required in ALL visible cases (the WinForms edge case that
    // hides mealtimes for Summary+Room/Name doesn't happen here
    // because we always render them).
    validate: (v) => {
      const mealtimes = Array.isArray(v.LstMealtimes) ? (v.LstMealtimes as number[]) : [];
      if (mealtimes.length === 0) return 'Please select at least one mealtime.';
      if (v.Print === 'set') {
        const sets = (v.LstSet as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select at least one {SetName}.';
      }
      if (v.Print === 'resident') {
        const pts = (v.LstPatient as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },

  // ─── FACILITY - TUBE FEED - LIST (synthetic pKey 6900) ─────────
  // WinForms source: ucDietech_Report_Generator_TubeFeed_List.vb.
  //   TabPageText = "Tube Feed - List" (.vb:59).
  //   Init (.vb:79-98):
  //     ucDate default = _dateTimeInitialized (today)
  //     radByRoom.Text = "By {Room}"
  //   Designer default:
  //     radByName.EditValue = True (.Designer.vb:130)
  //   Print (.vb:197-214) → TubeFeedSettings:
  //     .ReporTypeSelected = List(2)   ← different from the Labels
  //                                       card which sends 0
  //     .DateStart         = ucDate.EditValue
  //     .OrderByName       = radByName.Checked
  //     .OrderByRoom       = radByRoom.Checked
  //     (No PrintAll / LstSet / LstPatient / LstMealtimes on the
  //      List variant — the report ignores them.)
  //   ValidateForm always returns True (.vb:335-338).
  //
  // Same server generator as pKey 69 (Tube Feed - Labels) — the DB
  // has no separate row for the "List" variant, so we use a
  // synthetic pKey (6900) that survives client-side only. The
  // sidebar merges orphan-config statics into the API list so this
  // card appears alongside its sibling. `resolveFkeyReport` returns
  // the real pKey (69) so the queue call routes to
  // `TubeFeedSettings` deserialization at
  // `modReportSettingsFactory.vb:77`; the settings JSON carries
  // `ReporTypeSelected=2` so the report renders the List variant.
  {
    slug: 'tube-feed-list',
    reportName: 'Tube Feed - List',
    fKey_RS_Report: 6900,
    resolveFkeyReport: () => 69,
    fields: [
      // ── Order ──────────────────────────────────────────────────
      // Default By Name (.Designer.vb:130 `radByName.EditValue = True`).
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'name',
        group: 'Order',
        options: [
          { value: 'name', label: 'By Name' },
          { value: 'room', label: 'By {Room}' },
        ] },

      // ── Date ───────────────────────────────────────────────────
      // Single date; label hidden (`LabelVisible = False` .vb:89).
      // Default today (`_dateTimeInitialized`).
      { key: 'DateStart', label: '', type: 'date', defaultValue: () => today(),
        required: true, group: 'Date' },
    ],
    buildDateFor: (v) => formatDate(v.DateStart),
    buildSettings: (v) => {
      const order = String(v.OrderBy ?? 'name');
      return {
        // TubeFeedSettings.ReportType enum: Labels=0, Summary=1,
        // List=2, Products=3 (`TubeFeedSettings.vb:4-9`).
        ReporTypeSelected: 2,
        DateStart: v.DateStart,
        OrderByName: order === 'name',
        OrderByRoom: order === 'room',
      };
    },
    // WinForms `ValidateForm` (.vb:335-338) always returns True —
    // no client-side gating.
    validate: () => null,
  },

  // ─── FACILITY - TUBE FEED - PRODUCTS (synthetic pKey 6901) ─────
  // WinForms source: ucDietech_Report_Generator_TubeFeed_Products.vb.
  //   TabPageText = "Tube Feed - Products" (.vb:59).
  //   Init (.vb:79-102):
  //     ucDate default = _dateTimeInitialized (today)
  //     UseUnitMapping = true  → radByRoom.Text = "By Unit/{Room}"
  //     UseUnitMapping = false → radByRoom.Text = "By Fl/W/{Room}"
  //   Designer default:
  //     radByName.EditValue = True (.Designer.vb:119)
  //   Print (.vb:200-214) → TubeFeedSettings:
  //     .ReporTypeSelected = Products(3)   ← distinct from Labels(0),
  //                                          Summary(1), List(2)
  //     .DateStart         = ucDate.EditValue
  //     .OrderByName       = radByName.Checked
  //     .OrderByRoom       = radByRoom.Checked
  //     (No PrintAll / LstSet / LstPatient / LstMealtimes — Products
  //      variant ignores them like List.)
  //   ValidateForm always returns True.
  //
  // Same server generator as pKey 69 — DB has no separate row for
  // this variant, so synthetic pKey 6901 flows through the same
  // orphan-config-static merge as pKey 6900 (Tube Feed - List) does.
  // `resolveFkeyReport` routes to real pKey 69; wire settings carry
  // `ReporTypeSelected=3` so the report renders the Products variant.
  {
    slug: 'tube-feed-products',
    reportName: 'Tube Feed - Products',
    fKey_RS_Report: 6901,
    resolveFkeyReport: () => 69,
    fields: [
      // ── Order ──────────────────────────────────────────────────
      // Default By Name (.Designer.vb:119). "By Unit/Room" label
      // uses the `{FloorOrUnit}` token which resolves to "Unit"
      // when UseUnitMapping is on and "Floor" otherwise — matches
      // WinForms's `radByRoom.Text` switch at .vb:93-97 (which
      // uses "Fl/W" in the non-unit-mapping case; the token gives
      // us the readable equivalent).
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'name',
        group: 'Order',
        options: [
          { value: 'name', label: 'By Name' },
          { value: 'room', label: 'By {FloorOrUnit}/{Room}' },
        ] },

      // ── Date ───────────────────────────────────────────────────
      // Single date; label hidden (`LabelVisible = False` .vb:89).
      // Default today.
      { key: 'DateStart', label: '', type: 'date', defaultValue: () => today(),
        required: true, group: 'Date' },
    ],
    buildDateFor: (v) => formatDate(v.DateStart),
    buildSettings: (v) => {
      const order = String(v.OrderBy ?? 'name');
      return {
        // TubeFeedSettings.ReportType.Products = 3
        // (`TubeFeedSettings.vb:4-9`).
        ReporTypeSelected: 3,
        DateStart: v.DateStart,
        OrderByName: order === 'name',
        OrderByRoom: order === 'room',
      };
    },
    validate: () => null,
  },

  // ─── FACILITY - TUBE FEED - ENTERAL FEEDING LABEL (pKey 134) ────
  // WinForms source: ucDietech_Report_Generator_TubeFeedFillable.vb.
  //   TabPageText = "Tube Feed Enteral Feeding Label" (.vb:58).
  //   Init (.vb:81-121):
  //     radPrintSpecificSet.Text = "Specific {SetName}"
  //     radSpecificResident.Text = "Specific {Resident}"
  //     cmbMeal populates with first char of Breakfast/Lunch/Dinner
  //       names, SelectedIndex = 0 by default
  //     radByRoomNumber.Text = "{Room} Number"
  //     ucClbResident.TubeFeedOnly = True (roster narrowed at .vb:104)
  //   Designer defaults:
  //     radPrintAll.EditValue = True         (.Designer.vb:187)
  //     radOrderByTrayLine.EditValue = True  (.Designer.vb:295)
  //   Print (.vb:222-253) → TubeFeedProductLabelSettings:
  //     .FKey_Facility       (middleware)
  //     .DatePrintedOn     = Now
  //     .OrderByTrayLine   = radOrderByTrayLine.Checked
  //     .OrderByName       = radByName.Checked
  //     .OrderByRoom       = radByRoomNumber.Checked
  //     .PrintAll          = radPrintAll.Checked
  //     .LstSet            = ucClbSet.GetCheckedSetsList (only when SpecificSet)
  //     .LstPatient        = ucClbResident.GetCheckResidents (only when SpecificResident)
  //     .MealtimeTraySort  = cmbMeal.SelectedIndex
  //     .LabelsToSkip      = -1 (default; commented-out label-selector modal)
  //   ValidateForm (.vb:319-334):
  //     - Specific Set visible + 0 checked → error "Please select at
  //       least one {SetName}"
  //     - Specific Resident visible + 0 checked → error "Please
  //       select at least one {resident}"
  {
    slug: 'tube-feed-enteral-feeding-label',
    // Dashed reportName so the Facility/Tube Feed leaf-strip removes
    // the "Tube Feed - " prefix and the card renders just
    // "Enteral Feeding Labels". WinForms `TabPageText` is
    // "Tube Feed Enteral Feeding Label" — the display-only rename
    // doesn't touch the wire.
    reportName: 'Tube Feed - Enteral Feeding Labels',
    fKey_RS_Report: 134,
    fields: [
      // ── Order By radio + inline meal dropdown ──────────────────
      // WinForms defaults to Tray Line (.Designer.vb:295). The meal
      // dropdown sits INLINE with the Tray Line radio and only
      // functions when Tray Line is picked — same pattern as
      // Beverage - By Resident's Tray Line / cmbTrayLineOrder pair
      // (.vb:600-604). Labels use main-meal first-char abbreviations
      // (WinForms .vb:107-113 pushes `strMeal(0)(0)` etc.).
      { key: 'OrderBy', label: '', type: 'radio', defaultValue: 'trayLine',
        group: 'Order By',
        options: [
          { value: 'trayLine', label: 'Tray Line' },
          { value: 'room', label: '{Room} Number' },
          { value: 'name', label: 'Name' },
        ] },
      { key: 'MealtimeTraySort', label: '', type: 'dropdown', defaultValue: 0,
        group: 'Order By', width: 65,
        // Labels are the FIRST CHARACTER of each facility-configured
        // main-meal name (WinForms `strMeal(i)(0)` at .vb:110-112).
        // The `{Breakfast_First}` / `{Lunch_First}` / `{Dinner_First}`
        // tokens resolve to the first char of the respective label
        // per `substituteTokens` in `GenericReportForm`.
        options: [
          { value: 0, label: '{Breakfast_First}' },
          { value: 1, label: '{Lunch_First}' },
          { value: 2, label: '{Dinner_First}' },
        ],
        enabledWhen: { key: 'OrderBy', value: 'trayLine' } },

      // ── Print radio ────────────────────────────────────────────
      // Default All (.Designer.vb:187). Labels use facility tokens.
      { key: 'Print', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'set', label: 'Specific {SetName}' },
          { value: 'resident', label: 'Specific {Resident}' },
        ] },

      // ── Set selector — visible only for Specific Set ───────────
      { key: 'LstSet', label: '', type: 'setSelect', inline: true,
        defaultValue: [], visibleWhen: { key: 'Print', value: 'set' } },

      // ── Resident selector — TubeFeedOnly (WinForms .vb:104) ────
      // Grid narrows to residents with `tF_Tube_Feed = true` via the
      // shared `filterTubeFeedOnly` prop.
      { key: 'LstPatient', label: '', type: 'residentSelect', inline: true,
        defaultValue: [], visibleWhen: { key: 'Print', value: 'resident' },
        showInfoColumns: true,
        filterTubeFeedOnly: true },
    ],
    // No user-visible date — WinForms sends `Now` for DatePrintedOn.
    // Sidebar uses today for the date column.
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => {
      const order = String(v.OrderBy ?? 'trayLine');
      const print = String(v.Print ?? 'all');
      const setList = (v.LstSet as number[] | undefined) ?? [];
      const patientList = (v.LstPatient as number[] | undefined) ?? [];
      return {
        OrderByTrayLine: order === 'trayLine',
        OrderByRoom: order === 'room',
        OrderByName: order === 'name',
        PrintAll: print === 'all',
        // WinForms only assigns these when the matching radio is
        // checked (`.vb:244-245`); otherwise the DTO slot stays null.
        LstSet: print === 'set' && setList.length > 0 ? setList : null,
        LstPatient: print === 'resident' && patientList.length > 0 ? patientList : null,
        MealtimeTraySort: Number(v.MealtimeTraySort ?? 0),
        // `.DatePrintedOn = Now` (.vb:239). Send ISO so Newtonsoft
        // deserializes the non-nullable DateTime slot cleanly.
        DatePrintedOn: toLocalWireDateTime(new Date()),
        // WinForms `.LabelsToSkip = -1` (DTO default; the label-
        // selector modal that would supply a real value is
        // commented out at .vb:225-233).
        LabelsToSkip: -1,
      };
    },
    // Mirrors WinForms `ValidateForm` (.vb:319-334).
    validate: (v) => {
      if (v.Print === 'set') {
        const sets = (v.LstSet as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select at least one {SetName}.';
      }
      if (v.Print === 'resident') {
        const pts = (v.LstPatient as number[] | undefined) ?? [];
        if (pts.length === 0) return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },
  // ─── MED PASS — SUMMARY (pKey 52, fKey 52) ─────────────────────
  // WinForms source: ucDietech_Report_Generator_MedPassSummary.vb.
  // Print (.vb:288-306):
  //   .FKey_Facility       = _FKey_Facility
  //   .DateFor             = ucDate.EditValue
  //   .ReportTypeSelected  = MedPassSettings.ReportType enum:
  //                            ByFloorUnit = 0 (By Floor/Unit radio)
  //                            List        = 1
  //                            Total       = 2
  //                            Label       = 3
  //   Labels-only extras (set only when radLabels.Checked):
  //     .LabelsToSkip      = from `frmLabelSelector_A5162` or the
  //                          shared label-selector when the
  //                          facility allows it (.vb:266-284) —
  //                          React sends 0 (modal not ported).
  //     .LabelType         = facility Nourishment_Labels_Type
  //     .LstMealtimes      = ucClbMealtimes.GetCheckMealtimes
  //                          (int[] — 0-based Get_MealNames PKeys,
  //                          IncludeNourishments = True → main + all
  //                          nourishments)
  //     .SpecificFloorUnit = chkSpcificUnit.Checked
  //     .SpecificResident  = radPrintResident.Checked
  //     .PatientList       = grid selected rows → PKey_Patient[]
  //                          (or floor/unit-filtered list per
  //                          .vb:233-260 when SpecificFloorUnit)
  //   report class         = MedPass (pKey 52)
  //   detailedReportName   = `settings.ReportNameOverride`
  //
  // Init (.vb:75-116):
  //   Labels radio hidden when NOT UseProduction (.vb:89)
  //   ucDate.DateInit = `Date_getFirstDayOfWeek(today + 7 days,
  //     facility.Start_Day_Med_Pass_Accept)` — next week's first
  //     day (.vb:91-92). Screenshot: today 7/14 → 7/20 (Mon).
  //   radByFloor label swaps via UseUnitMapping (.vb:96-101):
  //     "By Unit" vs "By Floor"
  //   Mealtimes checklist: IncludeNourishments = True
  //   `radPrintResident.Text = facility.Resident` (.vb:87)
  //
  // Validate (.vb:430-458): Labels-only checks — ≥1 mealtime,
  // ≥1 resident when SpecificResident, ≥1 unit when SpecificUnit.
  //
  // React port simplifications:
  //   - Labels-only radio option always shown (UseProduction gate
  //     mirrored via `enabledWhen` would be clearer; leaving
  //     always-visible for now — server ignores the option on
  //     non-production facilities).
  //   - Label-selector modal not ported (LabelsToSkip = 0).
  //   - Floor/Unit runs resolve to residents client-side in
  //     preSubmit, as WinForms does (.vb:234-260).
  {
    slug: 'med-pass-summary',
    reportName: 'Med Pass - Summary',
    fKey_RS_Report: 52,
    apiEndpoint: 'MedPass',
    fields: [
      // Report Type radio — By Unit / Labels / List / Total.
      // Values map to the MedPassSettings.ReportType enum. The
      // first radio's label swaps via UseUnitMapping (.vb:96-101).
      { key: 'ReportTypeSelected', label: '', type: 'radio',
        defaultValue: 0, // ByFloorUnit
        group: 'Report Type',
        options: () => {
          const fs = useGlobalStore.getState().facilitySettings as { useUnitMapping?: boolean } | null;
          const byFloorLabel = fs?.useUnitMapping ? 'By Unit' : 'By Floor';
          return [
            { value: 0, label: byFloorLabel },
            { value: 3, label: 'Labels' },
            { value: 1, label: 'List' },
            { value: 2, label: 'Total' },
          ];
        } },

      // Date — defaults to first day of next week per facility
      // `Start_Day_Med_Pass_Accept`. Mirrors WinForms .vb:91-92:
      //   `Date_getFirstDayOfWeek(today + 7 days, startDay)`
      // JavaScript getDay() returns 0=Sun..6=Sat matching the
      // WinForms `DayOfWeek` enum values.
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const startDay = Number(
            (fs as { start_Day_Med_Pass_Accept?: number } | null)?.start_Day_Med_Pass_Accept ?? 0,
          );
          const seed = new Date();
          seed.setDate(seed.getDate() + 7);
          const daysBack = (seed.getDay() - startDay + 7) % 7;
          seed.setDate(seed.getDate() - daysBack);
          return new Date(seed.getFullYear(), seed.getMonth(), seed.getDate());
        } },

      // Mealtimes multiCheckbox — Labels-only (WinForms hides the
      // whole `lcgPrintOptionsLabels` group when radLabels is
      // unchecked at .vb:473-481). IncludeNourishments = True so
      // main + nourishments are all offered.
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox',
        defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        visibleWhen: { key: 'ReportTypeSelected', value: 3 },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Labels-only Print radio — WinForms radPrintAll / radPrintResident /
      // chkSpcificUnit are ONE radio group (all three RadioGroupIndex 0 in
      // the Designer, radPrintAll checked), so a run prints everyone, the
      // picked residents, or the residents in the checked floors/units
      // (.vb:224-260). Each choice reveals its picker (.vb:483-501).
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print Options',
        visibleWhen: { key: 'ReportTypeSelected', value: 3 },
        options: [
          { value: 'all', label: 'All' },
          { value: 'resident', label: '{Resident}' },
          { value: 'unit', label: '{FloorOrUnit}' },
        ] },
      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Print Options', inline: true, defaultSelectAll: true,
        visibleWhen: { check: (v) => Number(v.ReportTypeSelected) === 3 && v.PrintMode === 'unit' } },
      // Residents and guests - the WinForms grid (MedPassSummary.vb:503-547).
      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Print Options', inline: true, showInfoColumns: true, medPassSummaryGrid: true,
        visibleWhen: { check: (v) => Number(v.ReportTypeSelected) === 3 && v.PrintMode === 'resident' } },
    ],
    buildDateFor: (v) => formatMedPassDateFor(v),
    // Pre-print label-selector dialog — WinForms
    // `ucDietech_Report_Generator_MedPassSummary.vb:266-284`. Two
    // gates on top of the "is this even a labels run" check:
    //   • `LabelReports_UseLabelSelector` (facility bool) — off →
    //     skip the dialog entirely, LabelsToSkip stays 0.
    //   • `labelType < 2` — MedPass-specific short-circuit. The
    //     Nourishment_Labels_Type setting has values ≥ 2 that
    //     represent OTHER paper stocks (e.g. custom formats) with
    //     no selector dialog defined. Server prints the entire
    //     sheet with no cell offset in that case.
    // When both gates pass and the report IS Labels (ReportType 3):
    //     labelType === 0 → A5162 · === 1 → generic 30-up.
    //
    // Before the selector, a Floor/Unit run resolves the checked
    // floors/units to the residents in them (.vb:234-260) — MedPass.vb:182-184
    // filters on PatientList only, so without this a single unit printed the
    // whole facility. Same stop as WinForms when nobody is in them. The rows
    // walked are the dialog's grid, guests included (.vb:503-547).
    preSubmit: async (v, { facilitySettings, showLabelSelector, activePatients, units, fetchMedPassSummaryPatients }) => {
      if (Number(v.ReportTypeSelected) !== 3) return {};
      const fs = facilitySettings as {
        labelReports_UseLabelSelector?: boolean;
        nourishment_Labels_Type?: number;
        useUnitMapping?: boolean;
        resident_Plural?: string;
      } | null;
      const extras: Record<string, unknown> = {};
      if (v.PrintMode === 'unit') {
        const grid = fetchMedPassSummaryPatients
          ? await fetchMedPassSummaryPatients()
          : (activePatients ?? []);
        const inUnits = residentsInFloorUnits(
          grid,
          (v.lstFloorUnit as number[] | undefined) ?? [],
          Boolean(fs?.useUnitMapping),
          units ?? [],
        );
        if (inUnits.length === 0) {
          const plural = String(fs?.resident_Plural ?? 'residents').toLowerCase();
          return { error: `There are not any ${plural} associated to the selected floors/units. Please widen selection.` };
        }
        extras.PatientList = inUnits;
      }
      if (!fs?.labelReports_UseLabelSelector) return extras;
      const labelType = fs.nourishment_Labels_Type ?? 0;
      if (labelType >= 2) return extras;
      const variant = labelType === 0 ? 'A5162' : 'generic30';
      const result = await showLabelSelector(variant);
      // Close/Escape → silent cancel; abort the report submit
      // without a toast. Matches WinForms's early-return after
      // `frmLabelSelector.ShowDialog() = DialogResult.Cancel`.
      if (result === 'cancel') return { error: '' };
      return { ...extras, LabelsToSkip: result };
    },
    // Mirrors WinForms Print block (.vb:288-306). Labels-only
    // fields are still SENT on non-label modes (server ignores)
    // — matches how WinForms constructs the settings blob.
    buildSettings: (v, fs) => {
      const reportType = Number(v.ReportTypeSelected);
      const isLabels = reportType === 3;
      const labelType = Number(
        (fs as { nourishment_Labels_Type?: number } | null)?.nourishment_Labels_Type ?? 0,
      );
      return {
        DateFor: v.DateFor,
        ReportTypeSelected: reportType,
        // Default 0 — overridden by the preSubmit hook above when
        // the label-selector dialog runs and the user picks a
        // non-first starting label. `preSubmitExtras` spreads on
        // top of the wireSettings in GenericReportForm.handleSubmit
        // so the wire value wins.
        LabelsToSkip: 0,
        LabelType: isLabels ? labelType : 0,
        LstMealtimes: isLabels ? ((v.LstMealtimes as number[] | undefined) ?? []) : [],
        SpecificFloorUnit: isLabels && v.PrintMode === 'unit',
        SpecificResident: isLabels && v.PrintMode === 'resident',
        // Picked residents; a Floor/Unit run gets the residents in the
        // checked floors/units from preSubmit (.vb:234-260, :304). The
        // floor/unit list itself is not on MedPassSettings.
        PatientList: isLabels && v.PrintMode === 'resident'
          ? ((v.PatientList as number[] | undefined) ?? [])
          : null,
      };
    },
    // Mirrors ValidateForm (.vb:430-458). Only Labels mode has
    // required-selection rules; other modes pass through.
    validate: (v) => {
      if (Number(v.ReportTypeSelected) !== 3) return null;
      const meals = (v.LstMealtimes as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      if (v.PrintMode === 'resident') {
        const residents = (v.PatientList as number[] | undefined) ?? [];
        if (residents.length === 0) return 'Please select at least one {Resident}.';
      }
      if (v.PrintMode === 'unit') {
        const units = (v.lstFloorUnit as number[] | undefined) ?? [];
        if (units.length === 0) return 'Please select at least one {FloorOrUnit}.';
      }
      return null;
    },
  },
  // ─── SKIN (pKey 47, fKey 47) ───────────────────────────────────
  // WinForms source: ucDietech_Report_Generator_Skin.vb.
  // Print (.vb:310-336):
  //   .FKey_Facility            = _FKey_Facility
  //   .DateFor                  = Now  (hard-coded — form has no
  //                                date picker for DateFor)
  //   .OrderByName              = radOrderName.Checked
  //   .OrderByRoom              = radOrderRoom.Checked
  //   .StartNewPageAfterEachFloor = chkStartNewPage.Checked
  //   .PrintGreatThan           = chkPrint.Checked
  //   .DateGreaterEqualThan     = dePrintDate.DateTime
  //   .LstPatient               = ucClbResident.GetCheckResidents
  //   report class              = Skin (server pKey 47 per
  //                                ReportDispatcher.cs:69)
  //   queue title               = "Skin" (.vb:333 — literal)
  //
  // Init (.vb:78-107):
  //   radOrderRoom default checked (Designer .vb:227 EditValue=True).
  //   radOrderRoom.Text = "{Room} Number" (.vb:88 — facility Room
  //     label prefix).
  //   chkStartNewPage default checked (Designer .vb:179 EditValue=True).
  //   chkStartNewPage.Text swaps via UseUnitMapping:
  //     UseUnitMapping = true  → "Start New Page After Each Unit"
  //     UseUnitMapping = false → "Start New Page After Each Floor"
  //   chkPrint default unchecked (Designer .vb:167).
  //   dePrintDate default = Now.AddDays(-89) (.vb:95); enabled only
  //     when chkPrint checked (.vb:339).
  //   ucClbResident: patient checklist — all residents show as
  //     checked in the mockup, so default-select-all.
  //
  // Validate (.vb:297-303): at least one patient must be checked.
  {
    slug: 'skin',
    reportName: 'Skin',
    fKey_RS_Report: 47,
    apiEndpoint: 'Skin',
    fields: [
      // Order radio — mirrors radOrderRoom / radOrderName pair.
      // First label uses `{Room}` token — GenericReportForm's
      // substituteTokens resolves it to the facility's Room label
      // (e.g. "Room", "Bed", "Suite").
      { key: 'Order', label: '', type: 'radio', defaultValue: 'room',
        group: 'Order',
        options: [
          { value: 'room', label: '{Room} Number' },
          { value: 'name', label: 'Name' },
        ] },

      // Options group — chkStartNewPage (default checked) + chkPrint (default unchecked) + dePrintDate.
      // Second label uses `{FloorOrUnit}` which resolves to "Unit"
      // when UseUnitMapping is true, else "Floor" (matches
      // WinForms `.vb:98-102`).
      { key: 'StartNewPageAfterEachFloor', label: 'Start New Page After Each {FloorOrUnit}',
        type: 'checkbox', defaultValue: true, group: 'Options' },

      { key: 'PrintGreatThan', label: 'Print ≥', type: 'checkbox',
        defaultValue: false, group: 'Options' },

      // dePrintDate — always visible, sits inline next to the
      // Print ≥ checkbox (matches the WinForms designer layout at
      // .vb:146-153). Disabled until PrintGreatThan is checked
      // (matches chkPrint_CheckedChanged at .vb:339 —
      // `dePrintDate.Enabled = chkPrint.Checked`).
      // Default = today - 89 (matches .vb:95).
      { key: 'DateGreaterEqualThan', label: '', type: 'date',
        group: 'Options',
        defaultValue: () => {
          const d = new Date();
          d.setDate(d.getDate() - 89);
          return d;
        },
        enabledWhen: { key: 'PrintGreatThan', value: true } },

      // Patient list — mirrors ucClbResident. All residents shown
      // checked by default (matches UI mockup). `inline: true`
      // pulls the selector OUT of the right-side column so it
      // flows below the Order + Options groups in the main
      // column (matches the WinForms layout at .vb Designer where
      // the resident checklist sits below the Options band).
      { key: 'LstPatient', label: '', type: 'residentSelect',
        defaultValue: [], group: 'Patient', inline: true,
        defaultSelectAll: true },
    ],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print settings block (.vb:314-330).
    buildSettings: (v) => ({
      DateFor: new Date(),
      OrderByName: v.Order === 'name',
      OrderByRoom: v.Order === 'room',
      StartNewPageAfterEachFloor: v.StartNewPageAfterEachFloor,
      PrintGreatThan: v.PrintGreatThan,
      DateGreaterEqualThan: v.DateGreaterEqualThan,
      LstPatient: (v.LstPatient as number[] | undefined) ?? [],
    }),
    // Mirrors ValidateForm (.vb:297-303) — at least one patient.
    validate: (v) => {
      const residents = (v.LstPatient as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  // ─── SPECIAL NEEDS (pKey 51, fKey 51) ──────────────────────────
  // WinForms source: ucDietech_Report_Generator_SpecialNeeds.vb.
  // Print (.vb:356-388 for Specific / .vb:391-429 for Summary):
  //   .FKey_Facility       = _FKey_Facility
  //   .DateFor             = Now  (hard-coded)
  //   .ReportTypeSelected  = Specific(0) | Summary(1)
  //   .OrderByName         = radOrderName.Checked
  //   .OrderByRoom         = radOrderRoom.Checked
  //   .OrderByTrayline     = radOrderByTrayLine.Checked
  //   .TrayLineMeal        = cmbTrayLineOrder.SelectedIndex
  //   .PrintAllergies      = chkPrintAllergies.Checked
  //   .PrintDietOrder      = chkPrintDietOrder.Checked
  //   .FilterTypeSelected  = FilterType enum — depends on Type +
  //                          filter radio (see below)
  //   .LstPKey_Special_Needs / .LstSpecialNeedsName —
  //     Specific → focused row's PKey only (1 item)
  //     Summary  → every checked row's PKey (1..20 items)
  //   .GroupByFloorUnit    = chkGroupByFloor.Checked
  //   report class         = SpecialNeeds (server pKey 51 per
  //                          ReportDispatcher.cs:73)
  //   queue title          = "Special Needs" (.vb:386 / .vb:427)
  //
  // Init (.vb:82-111):
  //   radOrderName default checked (Designer .vb — first radio in
  //     "Order" group is Name; see the mockup).
  //   radTypeSpecific default checked (Designer .vb:268 EditValue=True).
  //   radFilter1 default checked (Designer .vb:293 EditValue=True).
  //   radOrderRoom.Text = "{Room} Number" (.vb:92).
  //   chkGroupByFloor.Text swaps via UseUnitMapping (.vb:98-102).
  //   Special Needs grid: single-select when Specific,
  //     multi-select (checkboxes) when Summary — driven by
  //     `radTypeSummary_EditValueChanging` (.vb:451-471). React
  //     wires this via `multiSelectWhen` on the specialNeedSelect
  //     field, reading the ReportType form value.
  //   Filter radio labels also swap: Specific keeps "On Selected"
  //     / "Not On Selected"; Summary switches to "{Resident} on
  //     at least one special need" / "All {Resident_Plural}".
  //     Modeled by two visible-conditional radio fields sharing
  //     the same `Filter` value key.
  //
  // Validate (.vb:331-351):
  //   - grid must be non-empty (surfaced at load in the selector).
  //   - Summary: at least one AND at most 20 rows selected.
  //   - Specific: any single focused row counts.
  {
    slug: 'special-needs',
    reportName: 'Special Needs',
    fKey_RS_Report: 51,
    apiEndpoint: 'SpecialNeeds',
    fields: [
      // Type — Specific (0, default) / Summary (1). Drives the
      // grid's single/multi mode + the filter radio labels.
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 0,
        group: 'Type',
        options: [
          { value: 0, label: 'Specific' },
          { value: 1, label: 'Summary' },
        ] },

      // Order — Name (default) / Room Number / Tray Line. Second
      // label uses `{Room}` token. Tray Line enables the meal
      // dropdown (`cmbTrayLineOrder` — .vb:134-136).
      { key: 'Order', label: '', type: 'radio', defaultValue: 'name',
        group: 'Order',
        options: [
          { value: 'name', label: 'Name' },
          { value: 'room', label: '{Room} Number' },
          { value: 'trayline', label: 'Tray Line' },
        ] },

      // Tray Line meal picker — enabled only when Order === trayline
      // (matches radByTrayLine_CheckedChanged at .vb:134-136).
      // TrayLineMeal wire is an integer index (0=Breakfast, 1=Lunch,
      // 2=Dinner) — matches WinForms cmbTrayLineOrder.SelectedIndex.
      { key: 'TrayLineMeal', label: '', type: 'dropdown', defaultValue: 0,
        group: 'Order', width: 75,
        enabledWhen: { key: 'Order', value: 'trayline' },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Filter — Specific mode labels.
      // Uses distinct keys per mode so each field owns its own
      // default (react state can't hold two defaults on one key).
      // FilterType enum values (from SpecialNeedsSettings.vb):
      //   Specific: OnSelected(2) / NotOnSelected(3)
      //   Summary : ResidentOnAtLeastOneSN(0) / AllResidents(1)
      // buildSettings picks the active key by ReportType.
      { key: 'FilterTypeSpecific', label: '', type: 'radio', defaultValue: 2,
        group: 'Filter',
        visibleWhen: { key: 'ReportType', value: 0 },
        options: [
          { value: 2, label: 'On Selected' },
          { value: 3, label: 'Not On Selected' },
        ] },
      { key: 'FilterTypeSummary', label: '', type: 'radio', defaultValue: 0,
        group: 'Filter',
        visibleWhen: { key: 'ReportType', value: 1 },
        options: [
          { value: 0, label: '{Resident} on at least one special need' },
          { value: 1, label: 'All {Resident_Plural}' },
        ] },

      // Options — three checkboxes. Group By {FloorOrUnit} label
      // swaps via UseUnitMapping (matches .vb:98-102).
      { key: 'GroupByFloorUnit', label: 'Group By {FloorOrUnit}', type: 'checkbox',
        defaultValue: false, group: 'Options' },
      { key: 'PrintAllergies', label: 'Show Allergies', type: 'checkbox',
        defaultValue: false, group: 'Options' },
      { key: 'PrintDietOrder', label: 'Show Diet Order', type: 'checkbox',
        defaultValue: true, group: 'Options' },

      // Special Needs grid — single-select when ReportType=0
      // (Specific), multi-select when ReportType=1 (Summary).
      // Sits below the Options group as the primary content of
      // the report (matches WinForms Designer where the grid is
      // right of the option column).
      { key: 'LstPKey_Special_Needs', label: '', type: 'specialNeedSelect',
        defaultValue: [], group: 'Special Needs', inline: true,
        multiSelectWhen: { key: 'ReportType', value: 1 } },
    ],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print settings blocks (.vb:359-383 / .vb:395-424).
    buildSettings: (v, _fs, ctx) => {
      const ids = (v.LstPKey_Special_Needs as number[] | undefined) ?? [];
      const reportType = Number(v.ReportType);
      const sent = reportType === 0 ? ids.slice(0, 1) : ids;
      const filterType = reportType === 0
        ? Number(v.FilterTypeSpecific ?? 2)
        : Number(v.FilterTypeSummary ?? 0);
      return {
        DateFor: new Date(),
        ReportTypeSelected: reportType,
        OrderByName: v.Order === 'name',
        OrderByRoom: v.Order === 'room',
        OrderByTrayline: v.Order === 'trayline',
        TrayLineMeal: Number(v.TrayLineMeal ?? 0),
        PrintAllergies: v.PrintAllergies,
        PrintDietOrder: v.PrintDietOrder,
        FilterTypeSelected: filterType,
        LstPKey_Special_Needs: sent,
        // SpecialNeeds.vb:196-202 titles the report from LstSpecialNeedsName(0),
        // so an empty list throws. WinForms sends the picked rows' names
        // (.vb:380 Specific, .vb:415 Summary).
        LstSpecialNeedsName: sent.map((id) => ctx?.resolveSpecialNeedName?.(id) ?? ''),
        GroupByFloorUnit: v.GroupByFloorUnit,
      };
    },
    // Mirrors ValidateForm (.vb:331-351).
    validate: (v) => {
      const ids = (v.LstPKey_Special_Needs as number[] | undefined) ?? [];
      if (ids.length === 0) return 'Please select at least one Special Need.';
      if (Number(v.ReportType) === 1 && ids.length > 20) {
        return 'You may only select up to 20 Special Needs.  Please reduce your selected count.';
      }
      return null;
    },
  },
  // ─── SPECIAL DEVICES (pKey 62, fKey 62) ────────────────────────
  // WinForms source: ucDietech_Report_Generator_SpecialDevices.vb.
  // Print (.vb:231-280):
  //   .FKey_Facility   = _FKey_Facility
  //   .DateFor         = Now  (WinForms sends `Now` — NOT
  //                       ucDate.EditValue — despite the Date
  //                       picker being visible in the UI. See
  //                       .vb:253. React port matches: sends the
  //                       DateFor form value which starts as today.)
  //   .LabelCount      = rowsToSkip (from label-selector modal
  //                       when facility LabelReports_UseLabelSelector;
  //                       React sends 0, modal not ported)
  //   .ReportTypeSelected = SpecialDevicesSettings.ReportType:
  //                         Summary=0, Labels=1
  //   Order — four mutually-exclusive booleans:
  //     .OrderByListing / .OrderByFloor / .OrderBySet / .OrderByName
  //   .LstMealtimes    = depends on the Order + ReportType combo:
  //     Order = Listing → ucClbMealtimes.GetCheckMealtimes (multi)
  //     Order = ByFloor OR ByName:
  //       ReportType = Labels → ucClbMealtimes (multi)
  //       ReportType = Summary → LstMealtimes NOT SET (server treats as empty)
  //     Order = BySet → `[ucRadMealtime.GetSelectedMealtime]`
  //       (SINGLE-element array from the single-select radio at
  //        `ucDietech_Reports_Generator_RadMealtime.vb:35-39` —
  //        returns 0/1/2 for Breakfast/Lunch/Dinner)
  //   .LstSets         = ucClbSet.GetCheckedSetsList
  //                      (only when Order = BySet)
  //   report class     = SpecialDevices (pKey 128)
  //   detailedReportName = facility.SpecialDevice label
  //
  // Init (.vb:84-129):
  //   TabPageText = facility.SpecialDevice
  //   radBySets label = "By {SetName}" (.vb:98)
  //   radByFloor label = "By Unit"/"By Floor" per UseUnitMapping
  //     (.vb:119-124)
  //   ucClbMealtimes: `IncludeNourishments = False` — main
  //     mealtimes only (Breakfast/Lunch/Dinner)
  //   Designer defaults: radLabels.Checked (Report Type = Labels)
  //     + radListing.Checked (Order = By Listing)
  //
  // Visibility rules (SetControlVisibilities .vb:383-411):
  //   Mealtimes multi-checkbox: visible when
  //     Order = Listing
  //     OR (Order in {Floor, Name} AND ReportType = Labels)
  //   Single-mealtime radio: visible when Order = BySet
  //   Sets checklist: visible when Order = BySet
  //
  // Validate (.vb:469-484):
  //   Mealtimes visible → ≥1 mealtime checked
  //   Sets visible → ≥1 set checked
  {
    slug: 'special-devices',
    // Banner uses the facility-configured `SpecialDevice` label
    // — WinForms `.vb:277` passes the same for the queue title.
    reportName: '{SpecialDevice}',
    fKey_RS_Report: 62,
    apiEndpoint: 'SpecialDevices',
    fields: [
      // Report Type radio — Labels default (Designer .vb:150).
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 1,
        group: 'Report Type',
        options: [
          { value: 1, label: 'Labels' },
          { value: 0, label: 'Summary' },
        ] },

      // Order radio — By Listing default (Designer .vb:329).
      // Third label uses `{FloorOrUnit}`; fourth uses `{SetName}`.
      { key: 'Order', label: '', type: 'radio', defaultValue: 'listing',
        group: 'Order',
        options: [
          { value: 'listing', label: 'By Listing' },
          { value: 'floor', label: 'By {FloorOrUnit}' },
          { value: 'set', label: 'By {SetName}' },
          { value: 'name', label: 'By Name' },
        ] },

      // Date — always visible, never editable. WinForms keeps the
      // picker (.vb:100-102) only for its Lock Date sync with the other
      // report tabs and prints for `Now` whatever it shows (.vb:253).
      // Dietech Online has no Lock Date, so an editable picker here did
      // nothing: it is shown as today and greyed out, with the reason.
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        width: 250,
        defaultValue: () => today(),
        enabledWhen: { check: () => false },
        disabledReason: 'This report always prints for today.' },

      // Mealtimes multi-checkbox — visible per WinForms
      // SetControlVisibilities: Order=Listing OR
      // (Order in {floor,name} AND ReportType=Labels).
      // IncludeNourishments=False → main mealtimes only.
      // `layout: 'column'` per the mealtime-vertical rule
      // (see `feedback_multicheck_mealtime_layout` memory).
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox',
        defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        visibleWhen: {
          check: (v) => {
            const order = String(v.Order);
            if (order === 'listing') return true;
            if ((order === 'floor' || order === 'name') && Number(v.ReportType) === 1) return true;
            return false;
          },
        },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Single-mealtime radio — visible when Order=BySet. Value
      // is the 0-based index returned by `ucRadMealtime` at
      // `ucDietech_Reports_Generator_RadMealtime.vb:35-39`.
      { key: 'Mealtime', label: '', type: 'radio', defaultValue: 0,
        group: 'Mealtimes',
        visibleWhen: { key: 'Order', value: 'set' },
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Sets — multi-select, visible only when Order = BySet.
      // Auto-selects all on mount (matches WinForms grid init
      // pattern used by other set selectors).
      { key: 'LstSets', label: '', type: 'setSelect',
        defaultValue: [],
        group: '{SetName}',
        inline: true,
        defaultSelectAll: true,
        visibleWhen: { key: 'Order', value: 'set' } },
    ],
    // Footer = the day it runs for (`DateFor = Now` below), not the picker.
    buildDateFor: () => formatDate(today()),
    // Verbatim mirror of WinForms Print block (.vb:249-274).
    // Key correctness rules re-checked line-by-line:
    //   1. `DateFor = Now` — WinForms IGNORES the picked date and
    //      sends the current server time (.vb:253). The Date
    //      picker only exists so ChkLockDate can sync with other
    //      report generators (via `ucDate_EditValueChanged`);
    //      it's cosmetic at print time. React sends `new Date()`
    //      to match (written as local time by the form's
    //      stringifyWithLocalDates, so an evening print is still today).
    //   2. `LabelCount` — from the `LabelSelector` modal only
    //      when facility.LabelReports_UseLabelSelector AND
    //      radLabels.Checked (.vb:238-247). React port skips
    //      the modal and sends 0.
    //   3. Only ONE of the four Order booleans is True in
    //      WinForms — the others default to VB `Boolean = False`.
    //      React sends all four (three False, one True).
    //   4. `LstMealtimes` — the property is UNSET on the ByFloor+
    //      Summary and ByName+Summary paths (.vb:261-273 skips
    //      the assignment). VB reference type default = Nothing.
    //      React sends `null` for those paths to match on the
    //      wire, not an empty array.
    //   5. `LstSets` — only set on the BySet path (.vb:268);
    //      unset elsewhere. React sends `null` for the non-BySet
    //      paths.
    //   6. BySet path wraps `ucRadMealtime.GetSelectedMealtime`
    //      (single int 0..2 from the radio at
    //      `ucDietech_Reports_Generator_RadMealtime.vb:35-39`)
    //      in a 1-element list (.vb:267).
    buildSettings: (v) => {
      const reportType = Number(v.ReportType);
      const order = String(v.Order);
      const orderByListing = order === 'listing';
      const orderByFloor = order === 'floor';
      const orderBySet = order === 'set';
      const orderByName = order === 'name';

      // LstMealtimes null-vs-array follows the WinForms
      // conditional assignment exactly (.vb:258-273).
      let lstMealtimes: number[] | null = null;
      if (orderByListing) {
        lstMealtimes = (v.LstMealtimes as number[] | undefined) ?? [];
      } else if (orderByFloor) {
        if (reportType === 1) lstMealtimes = (v.LstMealtimes as number[] | undefined) ?? [];
        // Summary path: property untouched → null (.vb:261-264)
      } else if (orderBySet) {
        lstMealtimes = [Number(v.Mealtime)];
      } else if (orderByName) {
        if (reportType === 1) lstMealtimes = (v.LstMealtimes as number[] | undefined) ?? [];
        // Summary path: property untouched → null (.vb:269-272)
      }

      return {
        FKey_Facility: undefined, // added by GenericReportForm at submit
        DateFor: new Date(), // WinForms `Now` — .vb:253
        LabelCount: 0, // Label-selector modal not ported
        ReportTypeSelected: reportType,
        OrderByListing: orderByListing,
        OrderByFloor: orderByFloor,
        OrderBySet: orderBySet,
        OrderByName: orderByName,
        LstMealtimes: lstMealtimes,
        LstSets: orderBySet
          ? ((v.LstSets as number[] | undefined) ?? [])
          : null,
      };
    },
    // Mirrors ValidateForm (.vb:469-484): mealtimes/sets
    // requirements only apply when those pickers are visible.
    validate: (v) => {
      const reportType = Number(v.ReportType);
      const order = String(v.Order);
      // Same predicate as the visibleWhen on LstMealtimes above.
      const mealtimesVisible = order === 'listing'
        || ((order === 'floor' || order === 'name') && reportType === 1);
      if (mealtimesVisible) {
        const meals = (v.LstMealtimes as number[] | undefined) ?? [];
        if (meals.length === 0) return 'Please select at least one mealtime.';
      }
      if (order === 'set') {
        const sets = (v.LstSets as number[] | undefined) ?? [];
        if (sets.length === 0) return 'Please select at least one {SetName}.';
      }
      return null;
    },
  },

  // ─── PRESCRIBER (pKey 61, fKey 61) ─────────────────────────────
  // WinForms source: ucDietech_Report_Generator_Prescriber.vb.
  // Print (.vb:267-279):
  //   .FKey_Facility = _FKey_Facility
  //   .DateFor       = Now  (WinForms hard-codes current time —
  //                     .vb:273; the form has no date picker)
  //   report class   = Prescriber (server pKey 61)
  //   detailedReportName = g_facilitySettings.Prescriber(FKey_Facility)
  //                        — the facility-configured label (e.g.
  //                        "Doctor" per the screenshot)
  //
  // Init (.vb:78-95):
  //   TabPageText = facility.Prescriber label
  //   Shows the "no available options" message from
  //   `ResdientDGMisc.NO_OPTIONS_MESSAGE`. The form has NO input
  //   controls — Print is the only action.
  //
  // Validate (.vb:308-313): always returns True — no validation.
  //
  // React port: empty `fields` array; `description` renders the
  // "no available options" text under the banner. Card + banner
  // + queue title all resolve `{Prescriber}` to the facility
  // label via `substituteTokens` / `resolveReportLabel`.
  {
    slug: 'prescriber',
    reportName: '{Prescriber}',
    description: 'no available options...press generate to continue',
    fKey_RS_Report: 61,
    apiEndpoint: 'Prescriber',
    fields: [],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
    // Mirrors WinForms Print block (.vb:272-274). Only sends
    // DateFor = Now; FKey_Facility added by GenericReportForm.
    buildSettings: () => ({
      DateFor: new Date(),
    }),
  },
  {
    slug: 'notes',
    reportName: 'Notes',
    fKey_RS_Report: 82,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => weekAgo(), required: true },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'ReportTypeSelected', group: 'Report Type', label: 'Report Type', type: 'dropdown', defaultValue: 0, options: [{ value: 0, label: 'List' }, { value: 1, label: 'Assessment' }, { value: 2, label: 'Note' }] },
      { key: 'ShowTime', group: 'Options', label: 'Show Time', type: 'checkbox', defaultValue: false },
      { key: 'IsLandscape', group: 'Paper Size', label: 'Landscape', type: 'checkbox', defaultValue: false },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  {
    slug: 'interaction-log',
    reportName: 'System Interaction Log',
    fKey_RS_Report: 93,
    // The one Interaction Log that stands on its own: WinForms System
    // Interaction Log (ucDietech_SystemInteractionLog.vb:230-247) sends
    // IsSystemInteraction = True, DateMin / DateMax and the time zone. The
    // per-record logs (a resident, an item, a template) need FormType +
    // FKey_Generic and print from those records' screens; with
    // IsSystemInteraction off this card sent neither and printed nothing.
    fields: [
      { key: 'DateMin', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true },
      { key: 'DateMax', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.DateMin, v.DateMax),
    buildSettings: (v) => ({
      DateFor: new Date(),
      IsSystemInteraction: true,
      DateMin: v.DateMin,
      DateMax: v.DateMax,
      // LogTimeZone.User, the System Interaction Log's default (.vb:25).
      TimeZone: 1,
    }),
  },
  // NOTE: pKey 140 (Meal Pattern - Notes / EHR) lives in the EMR
  // Integration section above — see the "EHR — MEAL PATTERN NOTES"
  // block. The prior stub in this slot only rendered a single
  // ReportDate field and was overriding the full config by
  // last-wins in `REPORT_CONFIG_MAP`.

  // ─── LAB DATA / INTAKE ───
  {
    slug: 'lab-data',
    reportName: 'Lab Data',
    fKey_RS_Report: 88,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true, endKey: 'EndDate', endLabel: 'End Date' },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  {
    slug: 'intake-study',
    reportName: 'Intake Study',
    fKey_RS_Report: 94,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => weekAgo(), required: true, endKey: 'EndDate', endLabel: 'End Date' },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },

  // ─── NUTRITION — ANALYSIS (pKey 100, fKey 100) ─────────────────
  // WinForms source: ucDietech_Report_Generator_NutritionalAnalysis.vb.
  // Print (.vb:262-347):
  //   .FKey_Facility       = _FKey_Facility
  //   .DateStart           = ucDeStart.EditValue
  //   .NumberOfDays        = ucNumberOfDays.EditValue
  //   .LstMealtimes        = ucClbMealtimes.GetCheckMealtimes
  //   .FKey_Menu_Header    = ucMenuCycleInfo.PKey_Menu_Header
  //   .Menu_Name           = ucMenuCycleInfo.MenuName
  //   .ShowItemDetails     = chkShowItems.Checked
  //   .ItemTypeSelected    = radStandard(0) | radAlternate(1) | radAlwaysServed(2)
  //   .ReportTypeSelected  = radByDRI → Menu(0); else Resident(1)
  //     radByDRI branch:
  //       .FKey_RC_DRI_Group = luFKey_DRIGroup.EditValue
  //       .StringDRIName     = luFKey_DRIGroup.Text
  //       .LstDietType       = clbDietTypes.CheckedItems (PKey list)
  //       .LstConsist        = clbConsist.CheckedItems (PKey list)
  //     Else (By Patient) branch:
  //       .LstFKey_Patient   = gvTrayTickets.GetSelectedRows
  //       .LstPatientNames   = row("FullName") list
  //       .LstDietOrders     = row diet order string list
  //   .LstNutrients          = _dtNutrients.Select("Graph = TRUE")
  //   report class           = NutritionalAnalysisGrid (server
  //                            pKey 121 per ReportDispatcher.cs:143 —
  //                            NOT pKey 100's NutritionalAnalysis,
  //                            which uses a different settings shape
  //                            with pre-built comma-sep display
  //                            strings and is only invoked from
  //                            Dashboard / frmMain).
  //   queue title            = "Nutritional Analysis" (.vb:346)
  //
  // Init (.vb:97-166):
  //   radStandard (First Choice) default checked (Designer .vb:445
  //     EditValue=True). Enum: Standard=0/Alternate=1/AlwaysServed=2.
  //   radByDRI (General) default checked (Designer .vb:380
  //     EditValue=True). ReportType enum: Menu=0/Resident=1.
  //   radSpecificPatient.Text = "By {Resident}" (.vb:107).
  //   ucDeStart.DateInit = Now; auto-advances to next cycle start
  //     when today is past the current cycle's day.
  //   ucNumberOfDays: Min=1, Max=NumberOfDaysInCycle (from menu
  //     cycle), default 7 (or NumberOfDaysInCycle when <7).
  //   ucClbMealtimes.IncludeNourishments=False → main mealtimes.
  //   Default diet type = facility `FKey_Diet_Type_House` checked.
  //   Default DRI Group = facility `FKey_RC_DRI_Group_Default`.
  //   Default nutrients = facility `DefaultNutrients` (CSV of FKeys).
  //
  // Validate (.vb:460-512):
  //   - Menu cycle must be set.
  //   - ≥1 mealtime checked.
  //   - General mode: DRI Group set; ≥1 diet type; ≤5 diet types;
  //     ≤5 consistencies.
  //   - By Patient mode: ≥1 resident selected.
  //   - ≥1 nutrient checked; ≤12 nutrients.
  //
  // Lookup selectors wired below:
  //   `driGroupSelect`     — dropdown from /api/drigroup/{fac}
  //                          (defaults to facility
  //                           `FKey_RC_DRI_Group_Default`)
  //   `dietTypeSelect`     — multi-check DataGrid from
  //                          /api/diettype/{fac}. WinForms uses a
  //                          menu-cycle-scoped list via
  //                          `SQL_Diet_Type_Select_MenuCycle` —
  //                          the React port uses facility-wide
  //                          until a scoped endpoint exists.
  //                          Defaults to `FKey_Diet_Type_House`
  //                          when `defaultSelectAll: true`.
  //   `consistencySelect`  — multi-check DataGrid from
  //                          /api/consistency/{fac} (also
  //                          facility-wide vs the WinForms
  //                          menu-cycle-scoped `SQL_Consist_Select_MenuCycle`).
  //   `nutrientSelect`     — multi-check DataGrid backed by the
  //                          static `NUTRIENT_INFO` list
  //                          (`nutrientInfo.ts`) which mirrors the
  //                          WinForms compile-time `DT_NutrientInfo`
  //                          at Diet_Utils.vb:8554-8635. Facility
  //                          `DefaultNutrients` CSV auto-checks the
  //                          initial set.
  {
    slug: 'nutritional-analysis',
    reportName: 'Nutrition - Analysis',
    // pKey 121 = `NutritionalAnalysisGrid` in ReportDispatcher.cs
    // (line 143). The WinForms UI's Print_Handler creates that
    // report class + submits `NutrionalAnalysisGridReportSettings`.
    // pKey 100's `NutritionalAnalysis` report uses a different
    // settings shape (pre-built comma-sep strings) and is only
    // invoked from Dashboard / frmMain, not this generator.
    fKey_RS_Report: 121,
    apiEndpoint: 'NutritionalAnalysisGrid',
    fields: [
      // Report Type — General (default) / By Patient. Matches
      // `radByDRI` / `radSpecificPatient` at `RadioGroupIndex=1`.
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 0,
        group: 'Report Type',
        options: [
          { value: 0, label: 'General' },
          { value: 1, label: 'By {Resident}' },
        ] },

      // Date + Number of Days — inline pair matching the WinForms
      // Designer layout at `ucDeStart` + `ucNumberOfDays` (.vb:117-142).
      // NumberOfDays default is 7 (or NumberOfDaysInCycle when <7 —
      // requires menu-cycle info; hard-coded to 7 here as a fallback).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date',
        width: 225,
        defaultValue: () => today() },
      { key: 'NumberOfDays', label: 'Number of Days:', type: 'number',
        defaultValue: 7, min: 1, max: 30, group: 'Date', width: 230 },

      // Menu Cycle info display — bound to StartDate (.vb:110-115).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — main mealtimes only (`IncludeNourishments=False`
      // at .vb:146). Single selection applied to every day in the
      // range (WinForms wire is `List(Of Integer)` flat, not
      // per-day like Nourishments) — no tabs. `itemMaxWidth: 150`
      // caps each mealtime item so a facility that relabels
      // Breakfast/Lunch/Dinner to longer strings doesn't stretch
      // the group.
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox',
        defaultValue: [] as number[],
        group: 'Mealtimes', itemMaxWidth: 150,
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // DRI Group + Print — TWO separate fieldsets on the same
      // visual row via `groupRow: 'dri-print'`. DRI Group hosts the
      // dropdown; Print hosts `chkShowItems`. Matches the WinForms
      // Designer where `chkShowItems` sits to the right of
      // `luFKey_DRIGroup`.
      // DRI Group auto-selects facility `FKey_RC_DRI_Group_Default`
      // on first mount.
      { key: 'FKey_RC_DRI_Group', label: 'DRI Group', type: 'driGroupSelect',
        defaultValue: 0, group: 'DRI Group', groupRow: 'dri-print',
        inline: true,
        visibleWhen: { key: 'ReportType', value: 0 } },
      { key: 'ShowItemDetails', label: 'Show Items', type: 'checkbox',
        defaultValue: false, group: 'Print', groupRow: 'dri-print' },

      // Menu Choices — First Choice (0, default) / Alternate (1)
      // / Always Served (2). Enum from
      // `NutrionalAnalysisGridReportSettings.ItemType`.
      { key: 'ItemType', label: '', type: 'radio', defaultValue: 0,
        group: 'Menu Choices',
        visibleWhen: { key: 'ReportType', value: 0 },
        options: [
          { value: 0, label: 'First Choice' },
          { value: 1, label: 'Alternate' },
          { value: 2, label: 'Always Served' },
        ] },

      // Diet Types + Consistencies — both live in the main column
      // below Print, sharing ONE fieldset ("Diet Types /
      // Consistencies") so a CSS rule can put them side-by-side
      // in a 2-column grid within the group body. `inline: true`
      // keeps them out of the right-hand side column (reserved
      // for Nutrients). `watchField: 'StartDate'` lets each
      // selector resolve the current menu cycle from the date and
      // fetch the menu-cycle-scoped list (WinForms Priority
      // sort, matching `SQL_Diet_Type_Select_MenuCycle` +
      // `SQL_Consist_Select_MenuCycle` at .vb:581 / .vb:610).
      { key: 'LstDietType', label: 'Diet Types', type: 'dietTypeSelect',
        defaultValue: [] as number[],
        group: 'Diet Types / Consistencies', inline: true,
        defaultSelectAll: true,
        watchField: 'StartDate',
        visibleWhen: { key: 'ReportType', value: 0 } },
      { key: 'LstConsist', label: 'Consistencies', type: 'consistencySelect',
        defaultValue: [] as number[],
        group: 'Diet Types / Consistencies', inline: true,
        watchField: 'StartDate',
        visibleWhen: { key: 'ReportType', value: 0 } },

      // Residents — visible in By Patient mode. Full info-column
      // grid (Diet Order / Unit / Room / TF) plus the disabled-
      // patient affordance: rows are grayed, non-selectable, and
      // get an Info icon with a tooltip when the patient has
      //   - empty Diet Order, OR
      //   - a DoNotPrintTT special need (global or facility-
      //     scoped, fetched via /api/SpecialNeed/DoNotPrintTTPatients),
      //   - OR a diet type matching `FKey_Diet_TypeNPO`.
      // Mirrors WinForms `Fill_dtPatientGrid` +
      // `Fill_dtSpecialNeedsPatientDoNotPrint` +
      // CustomDrawCell / SelectionChanging / ToolTipController
      // handlers at .vb:634-647,712-867.
      { key: 'LstFKey_Patient', label: '', type: 'residentSelect',
        defaultValue: [], group: '{Resident_Plural}', inline: true,
        showInfoColumns: true,
        disableEmptyDietPatients: true,
        visibleWhen: { key: 'ReportType', value: 1 } },

      // Nutrients — full DT_NutrientInfo list from `nutrientInfo.ts`
      // (Fluids at top, exchanges at tail, sorted by SortOrder ASC).
      // `defaultSelectAll: true` triggers the one-shot facility-
      // default pre-check via the DRI-Group-scoped
      // `/api/DRIGroup/DefaultNutrients/{fac}/{driGroup}` — the
      // selector reads `facilitySettings.fKey_RC_DRI_Group_Default`
      // itself (matches WinForms `Fill_NutrientLists` at
      // .vb:649-685 which resolves the facility default at Init).
      // Max 12 selected. Sits in the right-hand side column.
      { key: 'LstNutrients', label: 'Nutrients', type: 'nutrientSelect',
        defaultValue: [] as number[],
        group: 'Nutrients',
        defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // Mirrors WinForms Print settings blocks
    // (.vb:262-347). Every wire field WinForms populates is
    // reproduced here — including the display strings the
    // report title reads (Menu_Name / StringDRIName /
    // StringDietConsist / LstPatientNames / LstDietOrders).
    // Names come from ctx resolvers that read the same React
    // Query caches the sibling selector components populated.
    buildSettings: (v, _fs, ctx) => {
      const numDays = Math.max(1, Number(v.NumberOfDays) || 1);
      const reportType = Number(v.ReportType ?? 0);
      const mealtimes = ((v.LstMealtimes as number[] | undefined) ?? []);
      const dietTypeIds = reportType === 0
        ? ((v.LstDietType as number[] | undefined) ?? [])
        : [];
      const consistIds = reportType === 0
        ? ((v.LstConsist as number[] | undefined) ?? [])
        : [];
      const patientIds = reportType === 1
        ? ((v.LstFKey_Patient as number[] | undefined) ?? [])
        : [];
      const driGroupId = reportType === 0 ? Number(v.FKey_RC_DRI_Group ?? 0) : 0;

      // Resolve StartDate → ISO for menu-cycle cache lookup.
      const parseIso = (d: unknown): string | null => {
        if (!d) return null;
        if (d instanceof Date) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
        return null;
      };
      const startIso = parseIso(v.StartDate);
      const fKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(startIso) ?? 0;
      const menuName = ctx?.resolveMenuName?.(startIso) ?? '';

      // StringDietConsist — comma-joined names, diet types first
      // then consistencies (matches WinForms iteration order at
      // .vb:291-312).
      const dietTypeNames = dietTypeIds
        .map((id) => ctx?.resolveDietTypeName?.(id) ?? '')
        .filter((n) => n);
      const consistNames = consistIds
        .map((id) => ctx?.resolveConsistencyName?.(id) ?? '')
        .filter((n) => n);
      const stringDietConsist = [...dietTypeNames, ...consistNames].join(', ');

      // Per-patient parallel arrays (matches .vb:330-335).
      const patientNames = patientIds.map((id) => ctx?.resolvePatientFullName?.(id) ?? '');
      const patientDietOrders = patientIds.map((id) => ctx?.resolvePatientDietOrder?.(id) ?? '');

      return {
        DateStart: v.StartDate,
        NumberOfDays: numDays,
        LstMealtimes: mealtimes,
        FKey_Menu_Header: fKeyMenuHeader,
        Menu_Name: menuName,
        ShowItemDetails: Boolean(v.ShowItemDetails),
        ItemTypeSelected: Number(v.ItemType ?? 0),
        ReportTypeSelected: reportType,
        FKey_RC_DRI_Group: driGroupId,
        StringDRIName: driGroupId > 0 ? (ctx?.resolveDriGroupName?.(driGroupId) ?? '') : '',
        LstDietType: dietTypeIds,
        LstConsist: consistIds,
        StringDietConsist: stringDietConsist,
        LstFKey_Patient: patientIds,
        LstPatientNames: patientNames,
        LstDietOrders: patientDietOrders,
        LstNutrients: (v.LstNutrients as number[] | undefined) ?? [],
      };
    },
    // Mirrors ValidateForm (.vb:460-512).
    validate: (v) => {
      const mealtimes = (v.LstMealtimes as number[] | undefined) ?? [];
      if (mealtimes.length === 0) return 'Please select at least one mealtime.';
      const reportType = Number(v.ReportType ?? 0);
      if (reportType === 0) {
        if (!v.FKey_RC_DRI_Group) return 'Please select a DRI Group.';
        const diets = (v.LstDietType as number[] | undefined) ?? [];
        if (diets.length === 0) return 'Please select at least one diet type.';
        if (diets.length > 5) return 'You may only select up to 5 Diet Types.';
        const consists = (v.LstConsist as number[] | undefined) ?? [];
        if (consists.length > 5) return 'You may only select up to 5 Consistencies.';
      } else {
        const residents = (v.LstFKey_Patient as number[] | undefined) ?? [];
        if (residents.length === 0) return 'Please select at least one {Resident}.';
      }
      const nutrients = (v.LstNutrients as number[] | undefined) ?? [];
      if (nutrients.length === 0) return 'Please select at least one nutrient.';
      if (nutrients.length > 12) return 'A maximum of 12 nutrients may be selected.';
      return null;
    },
  },
  // NOTE: prior stub for slug 'nutritional-analysis-grid' /
  // fKey 121 removed — pKey 121 IS the real Nutrition - Analysis
  // report (see the full config above with slug
  // 'nutritional-analysis'). Leaving the stub in place would
  // shadow the pKey-121 lookup via last-wins in
  // REPORT_CONFIG_BY_PKEY / MAP.
  // ─── NUTRITIONAL RECOMMENDATIONS (pKey 91) ───────────────────
  // WinForms: ucDietech_Report_Generator_NutrionalRecommendation.vb
  // (typo in filename — "Nutrional" vs "Nutritional"; class name is
  // correctly spelled).
  //
  // Init (.vb:69-104):
  //   ucDeStart: DateMin = Now - 10 years, DateInit = today
  //   ucDeEnd:   DateMin = today,           DateInit = today
  //   On DateStart change: ucDeEnd.DateMin follows (line 453-455)
  //   Group By radio → default None
  //   Recommendations Filter radio → default None
  //   Start New Page checkbox → default false
  //   Residents tab: all checked (ucClbResident default)
  //   Disciplines tab: none checked (loaded from tblDiscipline; a
  //     synthetic "(No Discipline Selected)" row is appended when
  //     any tblPatient_Recommendation has FKey_Discipline IS NULL —
  //     see .vb:382-397). In the React port we always append it;
  //     the server harmlessly matches nothing when unused.
  //
  // Print (.vb:268-314):
  //   NutritionalRecommendationsSettings payload:
  //     FKey_Facility
  //     DateStart / DateEnd
  //     GroupBySelected  : enum {None=0, Unit=1, Prescriber=2, Title=3}
  //     FilterBySelected : enum {None=0, Completed=1, Incomplete=2}
  //     StartNewPageAfterGroups : bool
  //     IsSingle : false (batch mode; template)
  //     LstFKey_Discipline : List<int> (checked disciplines)
  //     LstFKey_Patient    : List<int> (checked residents)
  //     UserID/UserName/UserFName/UserLName : filled server-side
  //
  // Validate (.vb:356-363):
  //   ≥1 resident checked.
  //
  // The "Group By - Unit" label follows the facility's
  // UseUnitMapping flag (Init.vb:77-81) — displays "Unit" when true,
  // "Floor" when false. Server enum stays `Unit=1` either way; only
  // the button label switches. React port displays "Unit" verbatim
  // for now — the WinForms label is cosmetic and the wire value
  // is the same.
  {
    slug: 'nutritional-recommendations',
    reportName: 'Nutritional Recommendations',
    fKey_RS_Report: 91,
    apiEndpoint: 'NutritionalRecommendations',
    fields: [
      // Date range — DateEnd auto-follows DateStart when it would
      // fall behind, mirroring `ucDeStart_EditValueChanged` at
      // .vb:453-455 (`ucDeEnd.DateMin = editValue`). Both defaults
      // are today at first mount; the recomputeWhen dependencies
      // keep DateEnd ≥ DateStart on subsequent edits (standard
      // date-range invariant pattern used by other reports —
      // Diet-Order Changes at .vb:816-852 is the reference impl).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To:', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      // Group By radio. Values match `NutritionalRecommendationsSettings.GroupBy`
      // enum in the report class (None=0, Unit=1, Prescriber=2, Title=3).
      { key: 'GroupBySelected', label: '', type: 'radio', defaultValue: 0,
        group: 'Group By',
        options: [
          { value: 0, label: 'None' },
          { value: 1, label: 'Unit' },
          { value: 2, label: 'Prescriber' },
          { value: 3, label: 'Title' },
        ] },

      // Filter By radio. Values match `NutritionalRecommendationsSettings.FilterBy`
      // enum (None=0, Completed=1, Incomplete=2).
      { key: 'FilterBySelected', label: '', type: 'radio', defaultValue: 0,
        group: 'Recommendations Filter',
        options: [
          { value: 0, label: 'None' },
          { value: 1, label: 'Completed Only' },
          { value: 2, label: 'Incomplete Only' },
        ] },

      { key: 'StartNewPageAfterGroups', label: 'Start New Page After Each Group',
        type: 'checkbox', defaultValue: false, group: 'Options' },

      // Residents / Disciplines rendered as tabs inside a
      // "Selection" fieldset placed below the Options group in the
      // main column. Matches the WinForms tabbed layout at the
      // bottom of the screen (see supplied screenshot). Both fields
      // share `group: 'Selection'`; the `tab` label switches which
      // one is visible. Because `tab` is set on both, the layout
      // engine keeps them in the main column instead of promoting
      // them to the right-hand side selector column (see
      // GenericReportForm sideSelectorFields filter at .tsx:577).
      { key: 'LstFKey_Patient', label: '', type: 'residentSelect',
        defaultValue: [], defaultSelectAll: true,
        group: 'Selection', tab: 'Residents' },

      // Disciplines — synthetic "(No Discipline Selected)" row
      // (PKey=0) always appended so users can include recommendations
      // whose FKey_Discipline is NULL (wizard .vb:392-397).
      { key: 'LstFKey_Discipline', label: '', type: 'disciplineSelect',
        defaultValue: [],
        group: 'Selection', tab: 'Disciplines' },
    ],
    // WinForms ValidateForm at .vb:356-363 — ≥1 resident checked.
    validate: (v) => {
      const patients = (v.LstFKey_Patient as number[] | undefined) ?? [];
      if (patients.length === 0) return 'You must select at least one resident.';
      return null;
    },
    buildDateFor: (v) => formatDateRangeSameDay(v.DateStart, v.DateEnd),
    buildSettings: (v) => ({
      DateStart: v.DateStart,
      DateEnd: v.DateEnd,
      GroupBySelected: Number(v.GroupBySelected ?? 0),
      FilterBySelected: Number(v.FilterBySelected ?? 0),
      StartNewPageAfterGroups: Boolean(v.StartNewPageAfterGroups),
      IsSingle: false,
      LstFKey_Patient: (v.LstFKey_Patient as number[] | undefined) ?? [],
      LstFKey_Discipline: (v.LstFKey_Discipline as number[] | undefined) ?? [],
    }),
  },
  {
    slug: 'problem-goal-approach',
    reportName: 'Problem/Goal/Approach',
    fKey_RS_Report: 90,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },

  // ─── COST ANALYSIS ───
  // ─── COST ANALYSIS — MENU (pKey 98, fKey 98) ───────────────────
  // WinForms source: ucDietech_Report_Generator_Cost_Menu.vb.
  // Print (.vb:352-395):
  //   .FKey_Facility      = _FKey_Facility
  //   .DateStart          = ucDate.EditValue
  //   .DateEnd            = ucDeEnd.EditValue
  //   .LstMealtimes       = ucClbMealtime.GetCheckMealtimes
  //   .FKey_Patient       = CInt(luPatient.EditValue)
  //   .StrPatientName     = luPatient.Text
  //   .ShowMenuCategories = chkShowMenuCategories.Checked
  //   .ShowNoCost         = chkShowNoCost.Checked
  //   .StrDF              = computed client-side from the selected
  //                          patient's diet flucs (.vb:354-374)
  //                          — see StrDF caveat below.
  //   report class        = CostAnalysisMenu (server pKey 98)
  //   detailedReportName  = "Cost Analysis - Menu"
  //
  // Init (.vb:85-127):
  //   Date  = today (both DateStart and DateEnd default to today)
  //   ucDeEnd.DateMin = DateStart (can't go earlier)
  //   On DateStart change: if DateStart > DateEnd, DateEnd = DateStart;
  //                        DateEnd.DateMin = DateStart
  //   Mealtimes: empty (no CheckAll)
  //
  // Validate (.vb:487-499):
  //   Patient must be selected + ≥1 mealtime.
  //
  // StrDF is the patient's diet-flucs string joined from
  // `DT_Name1..5` + `C_Name1..5`. In the React port we send it as
  // an empty string — the server has direct DB access to
  // `DT_ResidentDietFlucs` for `FKey_Patient` and can derive the
  // same value if needed. Only `BeverageSettings.ToString` /
  // `ReportNameOverride` reads it (info popup + queue title), so
  // an empty client value results in a slightly-cleaner popup and
  // the base title "Cost Analysis - Menu -" without the diet suffix.
  {
    slug: 'cost-analysis-menu',
    reportName: 'Cost Analysis - Menu',
    fKey_RS_Report: 98,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    apiEndpoint: 'CostAnalysisMenu',
    fields: [
      // Typical Patient dropdown — two-column lookup (Name + Diet
      // Order) matching WinForms `luPatient` at .vb:433-441.
      { key: 'FKey_Patient', label: 'Typical {Resident}', type: 'patientLookup',
        defaultValue: null, required: true, group: 'Typical {Resident}' },

      // Date range on one row — DateEnd auto-follows DateStart
      // when it would fall behind (mirrors ucDate_EditValueChanged
      // at .vb:280-292). See the reusable Date-range invariant
      // template rule.
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) {
            return new Date(currentEnd);
          }
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          // WinForms: DateEnd defaults to today too; on DateStart
          // change, DateEnd bumps forward if it would fall behind.
          if (existingEnd && existingEnd >= start) return existingEnd;
          return start;
        } },

      // Menu Cycle info — read-only display driven by DateStart.
      // Mirrors WinForms `ucMenuCycleInfo` at .vb:118-122 which
      // shows the active menu name + week/day info for the
      // selected date. Not sent in settings (informational only).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'DateStart' },

      // Options group — two independent flags.
      { key: 'ShowMenuCategories', label: 'Show Menu Categories', type: 'checkbox',
        defaultValue: false, group: 'Options' },
      // Default checked per user request — WinForms Designer has
      // no explicit default so it starts unchecked. React diverges
      // here because most Menu-cost runs want the zero-cost item
      // list called out.
      { key: 'ShowNoCost', label: 'Show Item List with no Cost', type: 'checkbox',
        defaultValue: true, group: 'Options' },

      // Mealtimes — all 7 mealtimes (main + nourishments).
      // WinForms `ucClbMealtime.SetItemChecks(empty)` → nothing
      // checked. React config keeps the WinForms behavior.
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.DateStart, v.DateEnd),
    // Mirrors WinForms Print settings block (.vb:377-389).
    //   - `StrDF` — WinForms computes it client-side from the
    //     patient's diet flucs (`.vb:354-374`). We resolve via
    //     `ctx.resolvePatientDietOrder` which reads the same
    //     DT_Name/C_Name join out of the residents cache. The
    //     server-side report (`.vb:144`) uses `StrDF` for the
    //     printed title "Menu Cost for a {StrDF}", and
    //     `ReportNameOverride` uses it for the queue title —
    //     sending it wrong (or empty) is user-visible.
    //   - `StrPatientName` is NOT referenced by CostAnalysisMenu.vb
    //     or CostAnalysisMenuSettings.ToString (grep-verified);
    //     safe to send empty.
    buildSettings: (v, _fs, ctx) => ({
      DateStart: v.DateStart,
      DateEnd: v.DateEnd,
      LstMealtimes: v.LstMealtimes,
      FKey_Patient: v.FKey_Patient,
      StrPatientName: '',
      ShowMenuCategories: v.ShowMenuCategories,
      ShowNoCost: v.ShowNoCost,
      StrDF: ctx?.resolvePatientDietOrder?.(v.FKey_Patient as number) ?? '',
    }),
    // Mirrors ValidateForm (.vb:487-499).
    validate: (v) => {
      if (v.FKey_Patient == null) {
        return 'Please select a {Resident}.';
      }
      if (!Array.isArray(v.LstMealtimes) || v.LstMealtimes.length === 0) {
        return 'Please select at least one mealtime.';
      }
      return null;
    },
  },
  // ─── COST ANALYSIS — NOURISHMENTS (pKey 99, fKey 99) ────────────
  // WinForms source: ucDietech_Report_Generator_Cost_Nourishment.vb.
  // Print (.vb:250-269):
  //   .FKey_Facility      = _FKey_Facility
  //   .DateStart          = ucDeStart.EditValue
  //   .DateEnd            = ucDeEnd.EditValue
  //   .ReportTypeSelected = ByFloor(0) if radByFloor.Checked else Total(1)
  //   .LstMealtimes       = ucClbMealtimeTab.GetCheckMealtimes
  //   .LstPatient         = ucClbResidents.GetCheckResidents
  //   .LstFloorUnits      = ucClb_FloorUnit.GetCheckedFloorWing_CommaSep()
  //                          ("Floor,Wing" if UseUnitMapping else "Floor,*")
  //   report class        = CostAnalysisNourishment (server pKey 99)
  //   detailedReportName  = "Cost Analysis - Nourishments"
  //
  // Init (.vb:86-144):
  //   DateStart = today; DateEnd = today; DateEnd.DateMin = DateStart
  //   radByFloor default checked (.Designer:337 EditValue = True)
  //   radByFloor.Text  = "By Unit" if UseUnitMapping else "By Floor"
  //   Mealtimes: IncludeMainMealtimes = False → nourishments only
  //              (values 3-6: EAM/AM/PM/HS)
  //              SetItemChecks(_lstMealtimesInit) empty → nothing checked
  //   Floor/Units + Residents: both default all-checked (their
  //     underlying clb controls call .CheckAll on fill)
  //   Menu Cycle info: shown based on DateStart
  //
  // radtotal_CheckedChanged (.vb:418-422): hides Floor/Units tab
  //   when Total is selected.
  //
  // Validate (.vb:487-495): ≥1 mealtime.
  {
    slug: 'cost-analysis-nourishment',
    reportName: 'Cost Analysis - Nourishments',
    fKey_RS_Report: 99,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    apiEndpoint: 'CostAnalysisNourishment',
    fields: [
      { key: 'ReportTypeSelected', label: '', type: 'radio', defaultValue: 0,
        group: 'Report Type',
        options: [
          { value: 0, label: 'By Unit' },
          { value: 1, label: 'Total' },
        ] },

      // Date Range — bidirectional invariant `DateEnd >= DateStart`.
      // Mirrors WinForms `ucDeEnd.DateMin = editValue` at .vb:475
      // (`ucDeStart_EditValueChanged`).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return start;
        } },

      // Menu Cycle info — read-only, driven by DateStart.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'DateStart' },

      // Selection tabs — Mealtimes / Floor-Units / Patients.
      // Floor-Units tab is hidden when Total (ReportTypeSelected=1)
      // is selected, matching `radtotal_CheckedChanged` at .vb:418-422.
      { key: 'LstMealtimes', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes',
        options: [
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },
      { key: 'LstFloorUnitsPKeys', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true,
        visibleWhen: { key: 'ReportTypeSelected', value: 0 } },
      { key: 'LstPatient', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRange(v.DateStart, v.DateEnd),
    // Mirrors WinForms Print settings block (.vb:252-265).
    //   - `LstFloorUnitsPKeys` (React state, pKey ints) is
    //     converted to WinForms-style "Floor,Wing" or "Floor,*"
    //     strings via `ctx.resolveFloorWing` (which reads
    //     `facilitySettings.useUnitMapping` to pick the format).
    //     Wire field name is `LstFloorUnits` per CostAnalysisNourishmentSettings.vb.
    //   - Sent for Total too. WinForms always sends the checked
    //     floor/units (.vb:264) even with the tab hidden, and
    //     CostAnalysisNourishment.vb:361 keeps only residents whose
    //     floor/unit is in the list for BOTH types, so [] printed
    //     nothing for Total.
    buildSettings: (v, _fs, ctx) => {
      const pKeys = Array.isArray(v.LstFloorUnitsPKeys) ? (v.LstFloorUnitsPKeys as number[]) : [];
      const floorWing = ctx?.resolveFloorWing?.(pKeys) ?? [];
      return {
        FKey_Facility: undefined, // added by handleSubmit
        DateStart: v.DateStart,
        DateEnd: v.DateEnd,
        ReportTypeSelected: v.ReportTypeSelected,
        LstMealtimes: v.LstMealtimes,
        LstPatient: v.LstPatient ?? [],
        LstFloorUnits: floorWing,
      };
    },
    // Mirrors ValidateForm (.vb:487-495) — only mealtime is
    // required. `{Resident}` etc. resolve via substituteTokens.
    validate: (v) => {
      if (!Array.isArray(v.LstMealtimes) || v.LstMealtimes.length === 0) {
        return 'Please select at least one mealtime.';
      }
      return null;
    },
  },

  // ─── RECIPE ───
  // ─── FACILITY - RECIPES - MENU CYCLE (pKey 43) ──────────────────
  // WinForms source: ucDietech_Report_Generator_Recipe_MenuCycle.vb.
  //   TabPageText  = "Recipes - Menu Cycle" (.vb:68).
  //   Census       = `Recipe_Census(FKey_Facility)` if > 0 else
  //                  `ResidentManager.GetActiveResidentCount`
  //                  (.vb:132-138) — falls back to 75 in the React
  //                  port when neither is available.
  //   Date range   = ucDateRange sub-control with 6 modes (Today /
  //                  Tomorrow / Day After Tomorrow / 1 Week / Menu
  //                  Cycle / Custom). Default Today (.vb:72 of
  //                  DateRange sub-control).
  //   Menu Cycle info = ucMenuCycleInfo displays the current cycle
  //                  header for the selected date; drives
  //                  `PKey_Menu_Header` and the Diet/Consistency
  //                  grid data source (.vb:151, 428).
  //   Mealtimes    = ucClbMealtimes list, no default checks (empty).
  //                  Validation requires ≥1.
  //   Diet Types + Consistencies = ONE combined grid populated from
  //                  `SQL_DietTypeConsist_Select_MenuCycle`, all rows
  //                  auto-selected on load (.vb:157). "Select Active"
  //                  button narrows to rows referenced by any
  //                  resident's diet/consistency (.vb:193-211).
  //   Yield Ranges - Portions: 3×4 spinner grid (Primary /
  //                  Alternate / Overrides). Cascade defaults driven
  //                  by Census (.vb:572-624 `SetPortionAmounts`) —
  //                  see per-field defaultValue functions below.
  //   Options      = Display Generated Date + Group Recipes, both
  //                  default checked (WinForms Designer defaults).
  // Validation (.vb:513-528):
  //   - ≥1 mealtime checked
  //   - ≥1 grid row selected
  //   - (Print path .vb:410-417) ≥1 yield spinner > 0
  //
  // Wire payload (.vb:406-462) → RecipeMenuCycleSettings:
  //   Mealtimes = bit flag from MealtimesInt (Breakfast=1, Lunch=2,
  //               Dinner=4, EAM=8, AM=16, PM=32, HS=64, PM2=128,
  //               HS2=256 — matches `Mealtime` enum)
  //   MealtimesInt = 0-based int[]
  //   StartDate, EndDate = ucDateRange.StartDate / .EndDate
  //   PKey_Menu_Header = ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header
  //   LstFKey_DietType, LstFKey_Consist = selected grid PKeys split
  //                                       by row kind
  //   Census, GroupRecipes, ShowPrintDate
  //   Primary1-4, Alternate1-4, Overrides1-4
  {
    slug: 'recipe-menu-cycle',
    reportName: 'Recipes - Menu Cycle',
    fKey_RS_Report: 43,
    fields: [
      // Census group — one spinner. Cascades to all 12 yield
      // spinners via `recomputeWhen: ['Census']`. Fallback chain
      // mirrors WinForms .vb:132-138:
      //   1. `Recipe_Census` facility setting if > 0.
      //   2. Else `ResidentManager.GetActiveResidentCount` — read
      //      from the residents cache via the 3rd `ctx` arg.
      //   3. If neither is resolvable yet (residents still loading),
      //      seed with 0 and let `recomputeOnResidents` re-run this
      //      default once the residents list resolves.
      { key: 'Census', label: '', type: 'number',
        defaultValue: (fs?: Record<string, unknown> | null, _v?: Record<string, unknown>, ctx?: { activeResidentCount?: number }) => {
          const rc = (fs as { recipe_Census?: number } | null)?.recipe_Census ?? 0;
          if (rc > 0) return rc;
          const active = ctx?.activeResidentCount ?? 0;
          return active > 0 ? active : 0;
        },
        recomputeOnResidents: true,
        min: 0, max: 9999, group: 'Census', width: 125 },

      // Date mode — 6 radio options. `Today` is the default.
      // Options 4-6 (1 Week / Menu Cycle / Custom) expose the
      // StartDate + EndDate pickers below; the first 3 hide them
      // and pin the wire dates to today / today+1 / today+2.
      { key: 'DateMode', label: '', type: 'radio', defaultValue: 'today',
        group: 'Date',
        options: () => {
          const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
          const now = new Date();
          const t1 = new Date(now); t1.setDate(t1.getDate() + 1);
          const t2 = new Date(now); t2.setDate(t2.getDate() + 2);
          return [
            { value: 'today', label: `Today (${fmt(now)})` },
            { value: 'tomorrow', label: `Tomorrow (${fmt(t1)})` },
            { value: 'dayAfter', label: `Day After Tomorrow (${fmt(t2)})` },
            { value: 'week1', label: '1 Week' },
            { value: 'menuCycle', label: 'Menu Cycle' },
            { value: 'custom', label: 'Custom' },
          ];
        } },
      // StartDate is the SINGLE source of truth for the effective
      // start date across ALL modes — mirrors WinForms
      // `ucDateRange.StartDate` getter (.vb:24-40 of DateRange.vb):
      //   today     → Now.Date
      //   tomorrow  → Now.AddDays(1).Date
      //   dayAfter  → Now.AddDays(2).Date
      //   week1     → deStart.DateTime.Date  (deStart reset to Now on
      //                                       radio click, .vb:86)
      //   menuCycle → deStart.DateTime.Date  (deStart reset to
      //                                       cycle-first-day-of-week
      //                                       math on radio click,
      //                                       .vb:88-96)
      //   custom    → deStart.DateTime.Date  (deStart reset to Now
      //                                       on radio click, .vb:102)
      // Every mode change re-runs this defaultValue (see
      // `recomputeWhen: ['DateMode']`), which is exactly what
      // WinForms `chkX_CheckedChanged` handlers do — they overwrite
      // `deStart.EditValue`. As a side effect, `menuCycleInfo`
      // watching StartDate always sees the correct effective date
      // and re-fetches the cycle info to display.
      //
      // Only visible when a range-picking mode is active — otherwise
      // the field's value still tracks the effective date but the
      // input is hidden (matches WinForms `lgDates.Visibility =
      // Never` at .vb:115 of DateRange.vb).
      { key: 'StartDate', label: 'Start', type: 'date',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const mode = String(values?.DateMode ?? 'today');
          const now = new Date();
          const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (mode === 'tomorrow') { base.setDate(base.getDate() + 1); return base; }
          if (mode === 'dayAfter') { base.setDate(base.getDate() + 2); return base; }
          // week1 / menuCycle / custom → today (WinForms resets
          // `deStart.EditValue = Now` on each radio click; the
          // menuCycle cycle-first-day math isn't ported yet — it's
          // a nice-to-have follow-up).
          return base;
        },
        required: true, group: 'Date', width: 230,
        visibleWhen: { check: (v) =>
          v.DateMode === 'week1' || v.DateMode === 'menuCycle' || v.DateMode === 'custom' },
        recomputeWhen: ['DateMode'] },
      // EndDate mirrors WinForms `ucDateRange.EndDate` getter:
      //   today/tomorrow/dayAfter → same as StartDate
      //   week1 → StartDate + 6
      //   menuCycle/custom → StartDate
      { key: 'EndDate', label: 'End', type: 'date',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const mode = String(values?.DateMode ?? 'today');
          const start = values?.StartDate;
          const s = start instanceof Date ? new Date(start.getFullYear(), start.getMonth(), start.getDate())
            : (typeof start === 'string' && /^\d{4}-\d{2}-\d{2}/.test(start))
              ? (() => { const m = start.match(/^(\d{4})-(\d{2})-(\d{2})/); return new Date(Number(m![1]), Number(m![2]) - 1, Number(m![3])); })()
              : new Date();
          if (mode === 'week1') {
            const e = new Date(s); e.setDate(e.getDate() + 6); return e;
          }
          return s;
        },
        required: true, group: 'Date', width: 230,
        visibleWhen: { check: (v) =>
          v.DateMode === 'week1' || v.DateMode === 'menuCycle' || v.DateMode === 'custom' },
        recomputeWhen: ['DateMode', 'StartDate'] },

      // Menu Cycle info — read-only display, driven by StartDate.
      // Because StartDate above always reflects the effective date
      // for the current mode (via `recomputeWhen: ['DateMode']`),
      // switching Today / Tomorrow / Day After / 1 Week / Menu
      // Cycle / Custom immediately updates the cycle info shown
      // here. Matches WinForms `ucDateRange_EditValueChanged`
      // handler at .vb:643-649 which pushes the new date into
      // `ucMenuCycleInfo.DateToUse` and re-inits.
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes — starts empty. Validation requires ≥1.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
        ] },

      // Yield Ranges - Portions (3 rows × 4 cols):
      //   Row Primary   — Primary1 = GetNearest5(census × 0.75),
      //                    Primary2 = GetNearest5(census),
      //                    Primary3 = 0, Primary4 = 0.
      //   Row Alternate — up to 3 cascading portions from
      //                    census × 0.05, × 0.10, × 0.20 (must
      //                    strictly increase); Alternate4 = 0.
      //   Row Overrides — Override1 = 1 (or 0 if census = 0),
      //                    Override2 = GetNearest5(census × 0.05),
      //                    Override3 = GetNearest5(census × 0.10)
      //                    (only if > Override2 — matches
      //                    WinForms .vb:606-621); Override4 = 0.
      { key: 'Primary1', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const c = vbCInt(Number(values?.Census ?? 75) * 0.75);
          const r = c % 5; return r <= 2 ? c - r : c + (5 - r);
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Primary2', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const c = Math.trunc(Number(values?.Census ?? 75));
          const r = c % 5; return r <= 2 ? c - r : c + (5 - r);
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Primary3', label: '', type: 'number', defaultValue: 0, min: 0, max: 9999,
        group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Primary4', label: '', type: 'number', defaultValue: 0, min: 0, max: 9999,
        group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Alternate1', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const census = Number(values?.Census ?? 75);
          const gn = (val: number) => { const r = val % 5; return r <= 2 ? val - r : val + (5 - r); };
          const a1 = gn(vbCInt(census * 0.05));
          return a1 > 0 ? a1 : 0;
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Alternate2', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const census = Number(values?.Census ?? 75);
          const gn = (val: number) => { const r = val % 5; return r <= 2 ? val - r : val + (5 - r); };
          const a1 = gn(vbCInt(census * 0.05));
          const a2 = gn(vbCInt(census * 0.10));
          return a2 > 0 && a2 > a1 ? a2 : 0;
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Alternate3', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const census = Number(values?.Census ?? 75);
          const gn = (val: number) => { const r = val % 5; return r <= 2 ? val - r : val + (5 - r); };
          const a2 = gn(vbCInt(census * 0.10));
          const a3 = gn(vbCInt(census * 0.20));
          return a3 > 0 && a3 > a2 ? a3 : 0;
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Alternate4', label: '', type: 'number', defaultValue: 0, min: 0, max: 9999,
        group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Overrides1', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const census = Number(values?.Census ?? 75);
          return census === 0 ? 0 : 1;
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Overrides2', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const census = Number(values?.Census ?? 75);
          const gn = (val: number) => { const r = val % 5; return r <= 2 ? val - r : val + (5 - r); };
          const o2 = gn(vbCInt(census * 0.05));
          return o2;
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Overrides3', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const census = Number(values?.Census ?? 75);
          const gn = (val: number) => { const r = val % 5; return r <= 2 ? val - r : val + (5 - r); };
          const o2 = gn(vbCInt(census * 0.05));
          const o3 = gn(vbCInt(census * 0.10));
          return o3 > 0 && o3 > o2 ? o3 : 0;
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges - Portions', width: 100 },
      { key: 'Overrides4', label: '', type: 'number', defaultValue: 0, min: 0, max: 9999,
        group: 'Yield Ranges - Portions', width: 100 },

      // Options — both default checked.
      { key: 'ShowPrintDate', label: 'Display Generated Date', type: 'checkbox',
        defaultValue: true, group: 'Options' },
      { key: 'GroupRecipes', label: 'Group Recipes', type: 'checkbox',
        defaultValue: true, group: 'Options' },

      // Diet Type / Consistency combined grid — side selector.
      // `dateKey: 'StartDate'` lets the selector internally
      // resolve the menu-header via `useMenuCycleInfo(startDate)`,
      // matching WinForms `ucMenuCycleInfo.DateToUse` flow.
      { key: 'DTConsist', label: '', type: 'dietTypeConsistencyGrid',
        defaultValue: { dietTypes: [], consistencies: [] },
        dateKey: 'StartDate' },
    ],
    buildDateFor: (v) => {
      const mode = String(v.DateMode ?? 'today');
      const now = new Date();
      const fmt = (d: Date) => formatDate(d);
      if (mode === 'today') return fmt(now);
      if (mode === 'tomorrow') { const t = new Date(now); t.setDate(t.getDate() + 1); return fmt(t); }
      if (mode === 'dayAfter') { const t = new Date(now); t.setDate(t.getDate() + 2); return fmt(t); }
      return formatDateRange(v.StartDate, v.EndDate);
    },
    buildSettings: (v, _fs, ctx) => {
      // StartDate/EndDate ALREADY reflect the effective range for
      // the current DateMode — the form fields' `recomputeWhen:
      // ['DateMode']` handlers keep them in lock-step with the
      // radio. So we can trust the field values directly, mirroring
      // WinForms `.StartDate = ucDateRange.StartDate` /
      // `.EndDate = ucDateRange.EndDate` at .vb:425-426 which read
      // the sub-control's getters.
      const now = new Date();
      const asDate = (x: unknown): Date => x instanceof Date ? x
        : (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}/.test(x))
          ? (() => { const m = x.match(/^(\d{4})-(\d{2})-(\d{2})/)!; return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); })()
          : now;
      const startDate: Date = asDate(v.StartDate);
      const endDate: Date = asDate(v.EndDate);

      // Resolve PKey_Menu_Header from the effective StartDate via
      // the useMenuCycleInfo cache the sibling menuCycleInfo
      // display populated. Matches WinForms
      // `ucMenuCycleInfo.MenuCycleInUseInfo.PKey_Menu_Header`
      // (.vb:428).
      const isoStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const pKeyMenuHeader = ctx?.resolvePkeyMenuHeader?.(isoStart) ?? 0;

      // Mealtime bit flag from the 0-based multi-checkbox indices.
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      const mealtimesFlag = meals.reduce((acc, i) => acc | (1 << i), 0);

      const dtc = (v.DTConsist as { dietTypes?: number[]; consistencies?: number[] } | undefined)
        ?? { dietTypes: [], consistencies: [] };

      return {
        Mealtimes: mealtimesFlag,
        MealtimesInt: meals,
        StartDate: startDate,
        EndDate: endDate,
        PKey_Menu_Header: pKeyMenuHeader,
        LstFKey_DietType: dtc.dietTypes ?? [],
        LstFKey_Consist: dtc.consistencies ?? [],
        Census: Number(v.Census ?? 0),
        GroupRecipes: Boolean(v.GroupRecipes),
        ShowPrintDate: Boolean(v.ShowPrintDate),
        Primary1: Number(v.Primary1 ?? 0),
        Primary2: Number(v.Primary2 ?? 0),
        Primary3: Number(v.Primary3 ?? 0),
        Primary4: Number(v.Primary4 ?? 0),
        Alternate1: Number(v.Alternate1 ?? 0),
        Alternate2: Number(v.Alternate2 ?? 0),
        Alternate3: Number(v.Alternate3 ?? 0),
        Alternate4: Number(v.Alternate4 ?? 0),
        Overrides1: Number(v.Overrides1 ?? 0),
        Overrides2: Number(v.Overrides2 ?? 0),
        Overrides3: Number(v.Overrides3 ?? 0),
        Overrides4: Number(v.Overrides4 ?? 0),
      };
    },
    // Mirrors WinForms `ValidateForm` (.vb:513-528) + the Print
    // gate at .vb:410-417.
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select a mealtime.';
      const dtc = (v.DTConsist as { dietTypes?: number[]; consistencies?: number[] } | undefined)
        ?? { dietTypes: [], consistencies: [] };
      if ((dtc.dietTypes?.length ?? 0) + (dtc.consistencies?.length ?? 0) === 0) {
        return 'At least one diet type or consistency must be selected.';
      }
      const yields = [
        v.Primary1, v.Primary2, v.Primary3, v.Primary4,
        v.Alternate1, v.Alternate2, v.Alternate3, v.Alternate4,
        v.Overrides1, v.Overrides2, v.Overrides3, v.Overrides4,
      ].map((x) => Number(x ?? 0));
      if (yields.every((n) => n <= 0)) return 'Please set at least one yield to greater than 0';
      return null;
    },
  },
  {
    slug: 'recipe-nutritional-information',
    reportName: 'Recipe - Nutritional Information',
    fKey_RS_Report: 65,
    fields: [],
    buildDateFor: () => formatDate(today()),
  },
  {
    slug: 'recipe-card',
    reportName: 'Recipe - Recipe Card',
    fKey_RS_Report: 66,
    fields: [
      // `inline` keeps the recipe picker in the main column. Left to the
      // default it would move to the side column, and this two-field form
      // would render as a lone "Options" fieldset next to a tall picker.
      { key: 'PKey_RC_Recipe', group: 'Selection', inline: true, label: 'Recipe', type: 'recipeSelect', defaultValue: 0 },
      { key: 'Yield', group: 'Options', label: 'Yield', type: 'number', defaultValue: 1, min: 1, max: 999 },
    ],
    buildDateFor: () => formatDate(today()),
    // A card needs a recipe: with none picked the form queued PKey 0 and
    // the report came back empty.
    validate: (v) => (Number(v.PKey_RC_Recipe ?? 0) > 0 ? null : 'Please select a recipe.'),
  },
  {
    slug: 'recipe-list',
    reportName: 'Recipe - List',
    fKey_RS_Report: 67,
    fields: [],
    buildDateFor: () => formatDate(today()),
  },
  // ─── FACILITY - RECIPES - BOOK (pKey 122, fKey 122) ─────────────
  // WinForms source: ucDietech_Report_Generator_Recipe_Book.vb.
  //   TabPageText  = "Recipes - Book" (.vb:57).
  //   Menu dropdown: `luMenuHeader` populated from `tblMenu_Header`
  //                  filtered by facility permission; default =
  //                  `mci.PKey_Menu_Header` (current menu cycle).
  //   StartDate   = Now (.vb:126).
  //   NumberOfDays: MinValue 1, MaxValue = `mci.NumberOfDaysInCycle`,
  //                 default = `NumberOfDaysInCycle` (.vb:130-136).
  //   Census      = `Recipe_Census(FKey_Facility)` if > 0 else
  //                 `ResidentManager.GetActiveResidentCount` (.vb:118-124).
  //   Yield Ranges (Primary1..4): recomputed from Census via
  //                 `SetPortionAmounts` (.vb:319-327):
  //                   Primary1 = GetNearest5(census * 0.75)
  //                   Primary2 = GetNearest5(census)
  //                   Primary3 = 0
  //                   Primary4 = 0
  //                 `GetNearest5` rounds remainder ≤2 DOWN, >2 UP.
  //   Options (all default CHECKED per Designer):
  //                 IncludeAS, IncludeHolidayOverrides, PrintIndex,
  //                 PrintTOC.
  // Validation: at least one Primary yield > 0 (.vb:294-302).
  //
  // NOTE: NumberOfDays max is hard-capped at 42 rather than derived
  // from the selected menu cycle (WinForms reads
  // `mci.NumberOfDaysInCycle` from the menu-cycle-info payload);
  // covers every realistic cycle length and avoids a menu-header ->
  // days resolver that the existing `dynamicMaxFromMenuCycle`
  // (weeks-based, date-driven) doesn't fit. Wire up a
  // `dynamicMaxFromSelectedMenu` variant if a facility needs
  // stricter enforcement.
  //
  // NOTE: Census default falls back to 75 when `recipe_Census`
  // facility setting is 0. WinForms falls back to
  // `ResidentManager.GetActiveResidentCount` — that would require a
  // `defaultValue` callback with access to the residents cache,
  // which the current field-config surface doesn't expose. Facility
  // admins can set `Recipe_Census` in tblFacility to override.
  {
    slug: 'recipe-book',
    reportName: 'Recipes - Book',
    fKey_RS_Report: 122,
    fields: [
      // Corporate facility: which facility the book is for (luFacility, .vb:98-112; this one
      // until another is picked). FKey_Facility_Selected = the pick (.vb:250-253).
      { key: 'FKey_Facility_Selected', label: '{Facility}', type: 'corporateFacilitySelect',
        defaultValue: 0, group: '{Facility}',
        visibleWhen: { check: () => isCorporateFacility() } },

      // Menu picker — WinForms `luMenuHeader` (.vb:359-415).
      // `menuCycleSelect` auto-selects the facility's Active menu
      // cycle on first render (see MenuCycleSelector.tsx:19-27,
      // driven by `tblMenu_Facility.Active` — the same signal
      // WinForms `mci.PKey_Menu_Header` derives from).
      // `inline: true` keeps the selector in the main column
      // instead of the side rail — required so the Menu group
      // sits above the Date group as the user expects.
      { key: 'PKey_Menu_Header', label: 'Menu', type: 'menuCycleSelect',
        defaultValue: 0, group: 'Menu', inline: true },

      // Date group — Start (today) + Number of Days (default 21,
      // typical menu cycle length).
      { key: 'StartDate', label: 'Start Date', type: 'date',
        defaultValue: () => today(), required: true, group: 'Date' },
      { key: 'NumberOfDays', label: 'Number of Days', type: 'number',
        defaultValue: 21, min: 1, max: 42, group: 'Date', width: 250 },

      // Census group — one spinner. Fallback chain mirrors WinForms
      // Recipe_Book .vb:118-124:
      //   1. `Recipe_Census` facility setting if > 0.
      //   2. Else `ResidentManager.GetActiveResidentCount` via
      //      the 3rd `ctx` arg (needs `recomputeOnResidents`).
      { key: 'Census', label: '', type: 'number',
        defaultValue: (fs?: Record<string, unknown> | null, _v?: Record<string, unknown>, ctx?: { activeResidentCount?: number }) => {
          const rc = (fs as { recipe_Census?: number } | null)?.recipe_Census ?? 0;
          if (rc > 0) return rc;
          const active = ctx?.activeResidentCount ?? 0;
          return active > 0 ? active : 0;
        },
        recomputeOnResidents: true,
        min: 0, max: 9999, group: 'Census', width: 125 },

      // Yield Ranges group — 4 spinners. Primary1/2 recompute from
      // Census (rounded to nearest 5 via GetNearest5 logic).
      // Primary3/4 default 0. `recomputeWhen: ['Census']` fires when
      // the census value changes — mirrors WinForms
      // `spCensus_EnabledChanged` calling `SetPortionAmounts`.
      { key: 'Primary1', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const c = vbCInt(Number(values?.Census ?? 75) * 0.75);
          const rem = c % 5;
          return rem <= 2 ? c - rem : c + (5 - rem);
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges', width: 125 },
      { key: 'Primary2', label: '', type: 'number',
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const c = Math.trunc(Number(values?.Census ?? 75));
          const rem = c % 5;
          return rem <= 2 ? c - rem : c + (5 - rem);
        },
        recomputeWhen: ['Census'], min: 0, max: 9999, group: 'Yield Ranges', width: 125 },
      { key: 'Primary3', label: '', type: 'number',
        defaultValue: 0, min: 0, max: 9999, group: 'Yield Ranges', width: 125 },
      { key: 'Primary4', label: '', type: 'number',
        defaultValue: 0, min: 0, max: 9999, group: 'Yield Ranges', width: 125 },

      // Options group — 4 checkboxes, all default CHECKED per
      // WinForms Designer defaults, then Recipes (web only).
      { key: 'IncludeAS', label: 'Include Always Served', type: 'checkbox',
        defaultValue: true, group: 'Options' },
      { key: 'IncludeHolidayOverrides', label: 'Include Holidays/Overrides', type: 'checkbox',
        defaultValue: true, group: 'Options' },
      { key: 'PrintIndex', label: 'Index', type: 'checkbox',
        defaultValue: true, group: 'Options' },
      { key: 'PrintTOC', label: 'Table of Contents', type: 'checkbox',
        defaultValue: true, group: 'Options' },
      // RecipeBookSettings.PrintRecipes. Off: only the Table of Contents and / or the Index, as
      // tables with the recipe number and code in their own columns, so the list opens in Excel
      // as rows (Kansas Heart, 2026-11-03: "Just the index"). The yields only size the recipe
      // pages, so `validate` does not ask for them while it is off. The desktop has no such box
      // and prints the recipes.
      { key: 'PrintRecipes', label: 'Recipes', type: 'checkbox',
        defaultValue: true, group: 'Options' },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
    // WinForms .vb:250-253 sets `FKey_Facility_Selected` to the
    // current facility for non-corporate mode; corporate mode uses
    // the `luFacility` dropdown value. `preSubmit` fires with the
    // resolved facilityId (same one the middleware appends as
    // `FKey_Facility` on the wire).
    preSubmit: async (v, { facilityId }) => ({ FKey_Facility_Selected: corporateFacilityFor(v.FKey_Facility_Selected, facilityId) }),
    buildSettings: (v) => ({
      // FKey_Facility_Selected — filled by preSubmit above.
      AllItems: false,
      IncludeAS: Boolean(v.IncludeAS),
      IncludeHolidayOverrides: Boolean(v.IncludeHolidayOverrides),
      PrintIndex: Boolean(v.PrintIndex),
      StartDate: v.StartDate,
      NumberOfDays: Number(v.NumberOfDays ?? 1),
      PrintTOC: Boolean(v.PrintTOC),
      PrintRecipes: v.PrintRecipes !== false,
      Census: Number(v.Census ?? 0),
      PKey_Menu_Header: Number(v.PKey_Menu_Header ?? 0),
      Primary1: Number(v.Primary1 ?? 0),
      Primary2: Number(v.Primary2 ?? 0),
      Primary3: Number(v.Primary3 ?? 0),
      Primary4: Number(v.Primary4 ?? 0),
    }),
    // Mirrors WinForms `ValidateForm` (.vb:294-302); the yields only matter for the recipe pages.
    validate: (v) => {
      if (v.PrintRecipes === false) {
        return v.PrintTOC || v.PrintIndex ? null : 'Please pick Recipes, Table of Contents or Index';
      }
      const p1 = Number(v.Primary1 ?? 0);
      const p2 = Number(v.Primary2 ?? 0);
      const p3 = Number(v.Primary3 ?? 0);
      const p4 = Number(v.Primary4 ?? 0);
      if (p1 <= 0 && p2 <= 0 && p3 <= 0 && p4 <= 0) {
        return 'Please set at least one yield to greater than 0';
      }
      return null;
    },
  },
  // ─── FACILITY - RECIPES - INGREDIENTS FOR MENU CYCLE (pKey 127) ─
  // WinForms source: ucDietech_Report_Generator_RecipesIngredientMenuCycle.vb.
  //   TabPageText  = "Ingredients for Menu Cycle" (.vb:47).
  //   Menu picker  = `luMenuHeader`, default = current in-use menu
  //                  cycle from `mci.PKey_Menu_Header` (.vb:411-415).
  //   ShowReference = `chkShowReference.Checked`, Designer default
  //                   `EditValue = True` (.Designer.vb:257).
  // Validation: menu picker must have a value (.vb:313-322).
  // Print (.vb:327-347) sends:
  //   FKey_Facility           = FKey_Facility (added by middleware)
  //   FKey_Facility_For       = FKey_Facility for non-corporate;
  //                             `luFacility.EditValue` for corporate
  //                             (the `corporateFacilitySelect` field,
  //                             this facility until another is picked)
  //   DateFor                 = Now
  //   FKey_Menu_Header        = luMenuHeader.EditValue
  //   MenuName                = luMenuHeader.Text
  //   ShowReference           = chkShowReference.Checked
  {
    slug: 'recipe-ingredients-menu-cycle',
    reportName: 'Ingredients for Menu Cycle',
    fKey_RS_Report: 127,
    fields: [
      // Corporate facility: lciFacility / luFacility (.vb:96-114).
      { key: 'FKey_Facility_For', label: '{Facility}', type: 'corporateFacilitySelect',
        defaultValue: 0, group: '{Facility}',
        visibleWhen: { check: () => isCorporateFacility() } },

      // Menu picker — `inline: true` keeps it in the main column
      // instead of the side rail. Auto-preselects the facility's
      // Active menu cycle on first render.
      { key: 'FKey_Menu_Header', label: 'Menu', type: 'menuCycleSelect',
        defaultValue: 0, group: 'Menu', inline: true },

      // Show Reference — Designer default checked.
      { key: 'ShowReference', label: 'Show Reference', type: 'checkbox',
        defaultValue: true, group: 'Options' },
    ],
    buildDateFor: () => formatDate(today()),
    preSubmit: async (v, { facilityId }) => ({ FKey_Facility_For: corporateFacilityFor(v.FKey_Facility_For, facilityId) }),
    buildSettings: (v, _fs, ctx) => {
      const pKey = Number(v.FKey_Menu_Header ?? 0);
      return {
        // FKey_Facility_For — filled by preSubmit above.
        DateFor: toLocalWireDateTime(new Date()),
        FKey_Menu_Header: pKey,
        MenuName: ctx?.resolveMenuHeaderName?.(pKey) ?? '',
        ShowReference: Boolean(v.ShowReference),
      };
    },
    // Mirrors WinForms `ValidateForm` (.vb:313-322).
    validate: (v) => {
      const pKey = Number(v.FKey_Menu_Header ?? 0);
      if (pKey <= 0) return 'Please select a menu.';
      return null;
    },
  },

  // ─── ITEM / FOOD GROUP / ANALYSIS ───
  {
    slug: 'item-usage',
    reportName: 'Item Usage',
    fKey_RS_Report: 97,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true, endKey: 'EndDate', endLabel: 'End Date' },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  {
    slug: 'item-data-grid',
    reportName: 'Item Data Grid',
    fKey_RS_Report: 96,
    fields: [],
    buildDateFor: () => formatDate(today()),
  },
  {
    slug: 'food-group-usage',
    reportName: 'Food Group Usage',
    fKey_RS_Report: 95,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true, endKey: 'EndDate', endLabel: 'End Date' },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  {
    slug: 'analysis',
    reportName: 'Analysis',
    fKey_RS_Report: 126,
    // The settings class names its date DateFor.
    fields: [
      { key: 'DateFor', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
  },

  // ─── MANAGEMENT TEAM ───
  // All management team reports share ManagementTeamSettings: StartDate, EndDate, MealtimesInt, UseProduction
  {
    slug: 'management-team',
    reportName: 'Management Team Summary',
    fKey_RS_Report: 0,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'MealtimesInt', group: 'Mealtimes', label: 'Mealtimes', type: 'multiCheckbox', defaultValue: [0, 1, 2], options: [{ value: 0, label: 'Breakfast' }, { value: 1, label: 'Lunch' }, { value: 2, label: 'Dinner' }] },
      { key: 'UseProduction', group: 'Options', label: 'Use Production Data', type: 'checkbox', defaultValue: false },
      { key: 'SortBy', group: 'Options', label: 'Sort By', type: 'dropdown', defaultValue: 'name', options: MANAGEMENT_TEAM_SORT_OPTIONS },
      { key: 'GroupItems', group: 'Options', label: 'Group Items', type: 'checkbox', defaultValue: false },
      { key: 'UseUnitMapping', group: 'Options', label: 'Use Unit Mapping', type: 'checkbox', defaultValue: false },
      { key: 'lstFloorUnit', group: '{FloorOrUnit}', label: 'Select Floor/Units', type: 'floorUnitSelect', defaultValue: [], visibleWhen: { key: 'UseUnitMapping', value: true } },
      { key: 'PatientList', group: '{Resident_Plural}', label: 'Select Residents', type: 'residentSelect', defaultValue: [] },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.StartDate, v.EndDate),
  },
  // ─── MANAGEMENT TEAM — CHARGEABLE (pKey 28/29/30 dynamic) ──────
  // WinForms source: ucDietech_Report_Generator_ManagementTeam.vb
  // with `FormType = GeneratorType.Chargeable`.
  //
  // Single card that submits to one of THREE server pkeys based
  // on the Report Type radio — WinForms Print_Handler branches at
  // .vb:867-875:
  //   radLabels  → ManagementTeamChargeableLabel   (pKey 30)
  //   radList    → ManagementTeamChargeableList    (pKey 29)
  //   radSummary → ManagementTeamChargeableSummary (pKey 28)
  // React port uses `resolveFkeyReport` to swap `fKey_RS_Report`
  // at submit time. Sidebar hides 28/29 via `PKEY_HIDE`.
  //
  // Print settings (basic + production branches at .vb:186-354):
  //   .ReportGeneratorType = GeneratorType.Chargeable (0)
  //   .ReportToPrintType   = Summary(0) / List(1) / Label(2)
  //   .StartDate           = ucDeStart.EditValue
  //   .EndDate             = ucDeEnd.EditValue
  //   .UseProduction       = facilitySettings.useProduction
  //                          (WinForms sets True in production
  //                          branch, False in basic — matches
  //                          the facility toggle)
  //   .UseUnitMapping      = facilitySettings.useUnitMapping
  //   .Mealtimes           = Diet_Reports.GetMealTimeValues(int[])
  //                          — OR'd Mealtime enum bit flags
  //                          (Breakfast=1, Lunch=2, Dinner=4,
  //                           EAM=8, AM=16, PM=32, HS=64) per
  //                          `Diet_Reports.vb:3006-3029`
  //   .MealtimesInt        = raw int[] (0-based Get_MealNames PKeys)
  //   .PatientList         = ucClbResidents.GetCheckResidents
  //                          filtered by the Floor/Units tab in
  //                          `GetCheckResidentsBasedOnUnitsSelected`
  //                          (managementTeamFloorUnitPreSubmit)
  //   .lstFloorUnit        = ucClb_FloorUnit.GetCheckedFloorUnits
  //                          (unit keys, or floor numbers without
  //                          unit mapping)
  //   .LabelType           = facility ChargeableLabel_Type (only
  //                          set for Production + Labels — .vb:296)
  //   .LabelsToSkip        = 0 (label-selector modal not ported)
  //
  // Init (.vb:95-160):
  //   Date Range: Start = today, End = today; End.DateMin = Start
  //   Menu Cycle: shown when UseProduction (facility setting) —
  //               React always renders; empty gracefully otherwise
  //   Mealtimes tab: `IncludeNourishments = True` default
  //                  (main + nourishments) — unchecked default
  //   Floor/Units tab: `.CheckAll()` on load → defaultSelectAll
  //   Residents tab: default all-checked
  //
  // Validate (.vb:1108-1122): ≥1 mealtime + ≥1 resident.
  {
    slug: 'management-team-chargeable',
    reportName: 'Management Team - Chargeable',
    // Default pkey — resolveFkeyReport swaps at submit based on
    // the Report Type radio. Static value used by the sidebar
    // card entry and as a fallback.
    fKey_RS_Report: 30,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    resolveFkeyReport: (v) => {
      // Report Type radio value maps to ReportType enum:
      //   0 = Summary → server pKey 28
      //   1 = List    → server pKey 29
      //   2 = Label   → server pKey 30
      const t = Number(v.ReportType);
      if (t === 0) return 28;
      if (t === 1) return 29;
      return 30;
    },
    fields: [
      // Report Type radio — Labels default per screenshot
      // (radLabels EditValue = True in Designer). Value matches
      // the WinForms ReportType enum ordinal.
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 2,
        group: 'Report Type',
        options: [
          { value: 2, label: 'Labels' },
          { value: 1, label: 'List' },
          { value: 0, label: 'Summary' },
        ] },

      // Date Range — bidirectional invariant `EndDate >= StartDate`.
      // Both dates default to TODAY (WinForms _dateTimeInitialized).
      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['EndDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.StartDate);
          const currentEnd = parseLocal(values?.EndDate);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'EndDate', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['StartDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      // Menu Cycle info — informational, only meaningful on
      // UseProduction facilities. WinForms hides for non-
      // production; React leaves it visible (renders empty when
      // no active cycle for the date).
      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      // Mealtimes tab — main + nourishments. Facility labels
      // resolve via applyTokens; when a facility has no name
      // for a nourishment its label falls back to the meal
      // time (e.g. "10:00 am") — same as WinForms behavior.
      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      // Floor/Units tab — WinForms `.CheckAll()` on load.
      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },

      // Residents tab — WinForms tpResidents.Text = Resident
      // facility label; tab title uses {Resident_Plural}.
      // defaultSelectAll matches WinForms `ucClbResidents` init.
      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.StartDate, v.EndDate),
    // Checked residents narrowed to the Floor/Units tab (.vb:1001-1012).
    preSubmit: managementTeamFloorUnitPreSubmit,
    // Mirrors the WinForms Print settings block for Chargeable
    // (both basic .vb:186-267 and production .vb:270-353 branches;
    // they differ only in `UseProduction` + `LabelType` field
    // set only in the production Labels path at .vb:296).
    buildSettings: (v, fs) => {
      const useProduction = Boolean(
        (fs as { useProduction?: boolean } | null)?.useProduction,
      );
      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );
      const chargeableLabelType = Number(
        (fs as { chargeableLabel_Type?: number } | null)?.chargeableLabel_Type ?? 0,
      );
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      // Same bit-flag OR loop as Diet_Reports.GetMealTimeValues
      // at `Diet_Reports.vb:3006-3029`. Values 0..6 map to
      // Mealtime enum flags 1, 2, 4, 8, 16, 32, 64.
      const flagFor = (i: number): number => {
        if (i === 0) return 1;   // Breakfast
        if (i === 1) return 2;   // Lunch
        if (i === 2) return 4;   // Dinner
        if (i === 3) return 8;   // EAM
        if (i === 4) return 16;  // AM
        if (i === 5) return 32;  // PM
        if (i === 6) return 64;  // HS
        return 0;
      };
      const mealtimesFlag = meals.reduce((acc, i) => acc | flagFor(i), 0);
      const reportType = Number(v.ReportType);
      const isLabels = reportType === 2;
      return {
        ReportGeneratorType: 0, // GeneratorType.Chargeable
        ReportToPrintType: reportType,
        StartDate: v.StartDate,
        EndDate: v.EndDate,
        UseProduction: useProduction,
        UseUnitMapping: useUnitMapping,
        Mealtimes: mealtimesFlag,
        MealtimesInt: meals,
        // The checked residents; preSubmit narrows them to the Floor/Units tab.
        PatientList: (v.PatientList as number[] | undefined) ?? [],
        lstFloorUnit: (v.lstFloorUnit as number[] | undefined) ?? [],
        // WinForms only sets LabelType when production + labels
        // (.vb:296). Basic labels + non-label modes leave it at 0.
        LabelType: (isLabels && useProduction) ? chargeableLabelType : 0,
        // LabelsToSkip: 0 — the WinForms label-selector modal
        // (`GetLabelsToSkip`) isn't ported. Facilities that use
        // OL125 label sheets would need that dialog to skip
        // already-printed rows; add later if requested.
        LabelsToSkip: 0,
      };
    },
    // Mirrors ValidateForm (.vb:1108-1122).
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const residents = (v.PatientList as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  // ─── MANAGEMENT TEAM — SPECIAL REQUEST (pKey 40/41/42) ─────────
  // WinForms source: ucDietech_Report_Generator_ManagementTeam.vb
  // with `FormType = GeneratorType.SpecialRequest`. Same UI +
  // settings shape as the other Management Team variants;
  // Special Request-specific differences:
  //   - `ReportGeneratorType = 3` (SpecialRequest — enum at .vb:9)
  //   - Server pkey map: Summary → 40, List → 41, Labels → 42
  //     (`ManagementTeamSpecialRequestSummary/List/Label.vb`)
  //   - **`UseProduction = True` always** — WinForms has no basic
  //     variant; the SpecialRequest branch hard-codes `.UseProduction = True`
  //     at .vb:693, .vb:723, .vb:750.
  //   - Labels-only fields, all sourced from facility settings:
  //     `.LabelType   = specialRequest_LabelType` (.vb:698)
  //     `.SortBy      = specialRequest_Label_SortType` (.vb:699)
  //     `.GroupItems  = specialRequest_Label_GroupItems` (.vb:695)
  //   - Labels-only calls `GetLabelsToSkip` when
  //     `specialRequest_LabelType == 0` (.vb:676-679) — React
  //     sends `LabelsToSkip: 0` (modal not ported).
  // Sibling pKeys 40/41 hidden via `PKEY_HIDE`; canonical card at
  // pKey 42 (Labels default).
  {
    slug: 'management-team-special-request',
    reportName: 'Management Team - Special Requests',
    fKey_RS_Report: 42,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    resolveFkeyReport: (v) => {
      const t = Number(v.ReportType);
      if (t === 0) return 40; // Summary
      if (t === 1) return 41; // List
      return 42;              // Labels (default)
    },
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 2,
        group: 'Report Type',
        options: [
          { value: 2, label: 'Labels' },
          { value: 1, label: 'List' },
          { value: 0, label: 'Summary' },
        ] },

      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['EndDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.StartDate);
          const currentEnd = parseLocal(values?.EndDate);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'EndDate', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['StartDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },

      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.StartDate, v.EndDate),
    // Checked residents narrowed to the Floor/Units tab (.vb:1001-1012).
    preSubmit: managementTeamFloorUnitPreSubmit,
    buildSettings: (v, fs) => {
      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );
      // Label-only facility settings — read regardless of the
      // current radio; server ignores them for Summary/List.
      const facility = fs as {
        specialRequest_LabelType?: number;
        specialRequest_Label_SortType?: number;
        specialRequest_Label_GroupItems?: boolean;
      } | null;
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      const flagFor = (i: number): number => {
        if (i === 0) return 1;
        if (i === 1) return 2;
        if (i === 2) return 4;
        if (i === 3) return 8;
        if (i === 4) return 16;
        if (i === 5) return 32;
        if (i === 6) return 64;
        return 0;
      };
      const mealtimesFlag = meals.reduce((acc, i) => acc | flagFor(i), 0);
      const reportType = Number(v.ReportType);
      const isLabels = reportType === 2;
      return {
        ReportGeneratorType: 3, // GeneratorType.SpecialRequest
        ReportToPrintType: reportType,
        StartDate: v.StartDate,
        EndDate: v.EndDate,
        // Hard-coded True — WinForms SpecialRequest branch has
        // no basic variant (.vb:693, .vb:723, .vb:750).
        UseProduction: true,
        UseUnitMapping: useUnitMapping,
        Mealtimes: mealtimesFlag,
        MealtimesInt: meals,
        // The checked residents; preSubmit narrows them to the Floor/Units tab.
        PatientList: (v.PatientList as number[] | undefined) ?? [],
        lstFloorUnit: (v.lstFloorUnit as number[] | undefined) ?? [],
        // Labels-only extras — WinForms only sets these on the
        // Labels path (.vb:695-699); on Summary/List these are
        // just default/zero in the settings blob and the server
        // ignores them.
        LabelType: isLabels ? Number(facility?.specialRequest_LabelType ?? 0) : 0,
        SortBy: isLabels ? Number(facility?.specialRequest_Label_SortType ?? 0) : 0,
        GroupItems: isLabels ? Boolean(facility?.specialRequest_Label_GroupItems) : false,
        LabelsToSkip: 0,
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const residents = (v.PatientList as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  // ─── MANAGEMENT TEAM — FIBER (pKey 31/32/33 dynamic) ───────────
  // WinForms source: ucDietech_Report_Generator_ManagementTeam.vb
  // with `FormType = GeneratorType.Fiber`.
  //
  // Same UI + settings shape as Chargeable (comment block on that
  // config has the full breakdown). Differences:
  //   - `ReportGeneratorType = 1` (Fiber)
  //   - Server pkey map: Summary → 31, List → 32, Labels → 33
  //     (WinForms .vb:490, .vb:444, .vb:399)
  //   - Labels always call `GetLabelsToSkip` regardless of
  //     UseProduction (WinForms .vb:384, .vb:524) — React port
  //     sends `LabelsToSkip: 0` (modal not ported).
  //   - No `LabelType` — that setting is Chargeable-only
  //     (.vb:296) and SpecialRequest-only (.vb:698).
  //
  // Sibling pKeys 31 (Summary) and 32 (List) hidden from sidebar
  // via `PKEY_HIDE`; the canonical card lives at pKey 33 (Labels
  // default per screenshot).
  //
  // Validate: same as Chargeable — ≥1 mealtime + ≥1 resident.
  {
    slug: 'management-team-fiber',
    reportName: 'Management Team - Fiber',
    fKey_RS_Report: 33,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    resolveFkeyReport: (v) => {
      const t = Number(v.ReportType);
      if (t === 0) return 31; // Summary
      if (t === 1) return 32; // List
      return 33;              // Labels (default)
    },
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 2,
        group: 'Report Type',
        options: [
          { value: 2, label: 'Labels' },
          { value: 1, label: 'List' },
          { value: 0, label: 'Summary' },
        ] },

      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['EndDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.StartDate);
          const currentEnd = parseLocal(values?.EndDate);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'EndDate', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['StartDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },

      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.StartDate, v.EndDate),
    // Checked residents narrowed to the Floor/Units tab (.vb:1001-1012).
    preSubmit: managementTeamFloorUnitPreSubmit,
    buildSettings: (v, fs) => {
      const useProduction = Boolean(
        (fs as { useProduction?: boolean } | null)?.useProduction,
      );
      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      const flagFor = (i: number): number => {
        if (i === 0) return 1;
        if (i === 1) return 2;
        if (i === 2) return 4;
        if (i === 3) return 8;
        if (i === 4) return 16;
        if (i === 5) return 32;
        if (i === 6) return 64;
        return 0;
      };
      const mealtimesFlag = meals.reduce((acc, i) => acc | flagFor(i), 0);
      return {
        ReportGeneratorType: 1, // GeneratorType.Fiber
        ReportToPrintType: Number(v.ReportType),
        StartDate: v.StartDate,
        EndDate: v.EndDate,
        UseProduction: useProduction,
        UseUnitMapping: useUnitMapping,
        Mealtimes: mealtimesFlag,
        MealtimesInt: meals,
        // The checked residents; preSubmit narrows them to the Floor/Units tab.
        PatientList: (v.PatientList as number[] | undefined) ?? [],
        lstFloorUnit: (v.lstFloorUnit as number[] | undefined) ?? [],
        LabelsToSkip: 0,
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const residents = (v.PatientList as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  // ─── MANAGEMENT TEAM — PROTEIN (pKey 34/35/36 dynamic) ─────────
  // WinForms source: ucDietech_Report_Generator_ManagementTeam.vb
  // with `FormType = GeneratorType.Protein`. Same UI + settings
  // shape as Chargeable / Fiber; differences:
  //   - `ReportGeneratorType = 2` (Protein)
  //   - Server pkey map: Summary → 34, List → 35, Labels → 36
  //     (WinForms .vb:494, .vb:448, .vb:403)
  // Sibling pKeys 34/35 hidden via `PKEY_HIDE`; canonical card at
  // pKey 36 (Labels default).
  {
    slug: 'management-team-protein',
    reportName: 'Management Team - Protein',
    fKey_RS_Report: 36,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    resolveFkeyReport: (v) => {
      const t = Number(v.ReportType);
      if (t === 0) return 34; // Summary
      if (t === 1) return 35; // List
      return 36;              // Labels (default)
    },
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 2,
        group: 'Report Type',
        options: [
          { value: 2, label: 'Labels' },
          { value: 1, label: 'List' },
          { value: 0, label: 'Summary' },
        ] },

      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['EndDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.StartDate);
          const currentEnd = parseLocal(values?.EndDate);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'EndDate', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['StartDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },

      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.StartDate, v.EndDate),
    // Checked residents narrowed to the Floor/Units tab (.vb:1001-1012).
    preSubmit: managementTeamFloorUnitPreSubmit,
    buildSettings: (v, fs) => {
      const useProduction = Boolean(
        (fs as { useProduction?: boolean } | null)?.useProduction,
      );
      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      const flagFor = (i: number): number => {
        if (i === 0) return 1;
        if (i === 1) return 2;
        if (i === 2) return 4;
        if (i === 3) return 8;
        if (i === 4) return 16;
        if (i === 5) return 32;
        if (i === 6) return 64;
        return 0;
      };
      const mealtimesFlag = meals.reduce((acc, i) => acc | flagFor(i), 0);
      return {
        ReportGeneratorType: 2, // GeneratorType.Protein
        ReportToPrintType: Number(v.ReportType),
        StartDate: v.StartDate,
        EndDate: v.EndDate,
        UseProduction: useProduction,
        UseUnitMapping: useUnitMapping,
        Mealtimes: mealtimesFlag,
        MealtimesInt: meals,
        // The checked residents; preSubmit narrows them to the Floor/Units tab.
        PatientList: (v.PatientList as number[] | undefined) ?? [],
        lstFloorUnit: (v.lstFloorUnit as number[] | undefined) ?? [],
        LabelsToSkip: 0,
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const residents = (v.PatientList as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },
  // ─── MANAGEMENT TEAM — THICKENER (pKey 37/38/39 dynamic) ───────
  // WinForms source: ucDietech_Report_Generator_ManagementTeam.vb
  // with `FormType = GeneratorType.Thickener`. Same UI + settings
  // shape as Chargeable / Fiber / Protein; differences:
  //   - `ReportGeneratorType = 4` (Thickener — NOT 3; 3 is
  //     SpecialRequest per the enum at .vb:5-11)
  //   - Server pkey map: Summary → 37, List → 38, Labels → 39
  //     (WinForms .vb:498, .vb:452, .vb:407)
  // Sibling pKeys 37/38 hidden via `PKEY_HIDE`; canonical card at
  // pKey 39 (Labels default).
  {
    slug: 'management-team-thickener',
    reportName: 'Management Team - Thickener',
    fKey_RS_Report: 39,
    tempMenuSupported: TEMP_MENU_NOT_SUPPORTED,
    resolveFkeyReport: (v) => {
      const t = Number(v.ReportType);
      if (t === 0) return 37; // Summary
      if (t === 1) return 38; // List
      return 39;              // Labels (default)
    },
    fields: [
      { key: 'ReportType', label: '', type: 'radio', defaultValue: 2,
        group: 'Report Type',
        options: [
          { value: 2, label: 'Labels' },
          { value: 1, label: 'List' },
          { value: 0, label: 'Summary' },
        ] },

      { key: 'StartDate', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['EndDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.StartDate);
          const currentEnd = parseLocal(values?.EndDate);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'EndDate', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['StartDate'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.StartDate) ?? new Date();
          const existingEnd = parseLocal(values?.EndDate);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      { key: '_MenuCycleInfo', label: '', type: 'menuCycleInfo',
        defaultValue: null, group: 'Menu Cycle', watchField: 'StartDate' },

      { key: 'MealtimesInt', label: '', type: 'multiCheckbox', defaultValue: [],
        group: 'Selection', tab: 'Mealtimes', layout: 'column',
        options: [
          { value: 0, label: '{Breakfast}' },
          { value: 1, label: '{Lunch}' },
          { value: 2, label: '{Dinner}' },
          { value: 3, label: '{NourishEAM}' },
          { value: 4, label: '{NourishAM}' },
          { value: 5, label: '{NourishPM}' },
          { value: 6, label: '{NourishHS}' },
        ] },

      { key: 'lstFloorUnit', label: '', type: 'floorUnitSelect', defaultValue: [],
        group: 'Selection', tab: 'Floor/Units', defaultSelectAll: true },

      { key: 'PatientList', label: '', type: 'residentSelect', defaultValue: [],
        group: 'Selection', tab: '{Resident_Plural}', defaultSelectAll: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.StartDate, v.EndDate),
    // Checked residents narrowed to the Floor/Units tab (.vb:1001-1012).
    preSubmit: managementTeamFloorUnitPreSubmit,
    buildSettings: (v, fs) => {
      const useProduction = Boolean(
        (fs as { useProduction?: boolean } | null)?.useProduction,
      );
      const useUnitMapping = Boolean(
        (fs as { useUnitMapping?: boolean } | null)?.useUnitMapping,
      );
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      const flagFor = (i: number): number => {
        if (i === 0) return 1;
        if (i === 1) return 2;
        if (i === 2) return 4;
        if (i === 3) return 8;
        if (i === 4) return 16;
        if (i === 5) return 32;
        if (i === 6) return 64;
        return 0;
      };
      const mealtimesFlag = meals.reduce((acc, i) => acc | flagFor(i), 0);
      return {
        ReportGeneratorType: 4, // GeneratorType.Thickener (enum skips 3 for SpecialRequest)
        ReportToPrintType: Number(v.ReportType),
        StartDate: v.StartDate,
        EndDate: v.EndDate,
        UseProduction: useProduction,
        UseUnitMapping: useUnitMapping,
        Mealtimes: mealtimesFlag,
        MealtimesInt: meals,
        // The checked residents; preSubmit narrows them to the Floor/Units tab.
        PatientList: (v.PatientList as number[] | undefined) ?? [],
        lstFloorUnit: (v.lstFloorUnit as number[] | undefined) ?? [],
        LabelsToSkip: 0,
      };
    },
    validate: (v) => {
      const meals = (v.MealtimesInt as number[] | undefined) ?? [];
      if (meals.length === 0) return 'Please select at least one mealtime.';
      const residents = (v.PatientList as number[] | undefined) ?? [];
      if (residents.length === 0) return 'Please select at least one {Resident}.';
      return null;
    },
  },

  // ─── MEAL PATTERN ───
  {
    slug: 'global-meal-pattern-usage',
    reportName: 'Global Meal Pattern Usage',
    fKey_RS_Report: 106,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },
  {
    slug: 'global-adjustment-usage',
    reportName: 'Global Adjustment Usage',
    fKey_RS_Report: 107,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },
  {
    slug: 'meal-pattern-aim-changes',
    reportName: 'Meal Pattern - AIM Changes',
    fKey_RS_Report: 109,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },
  {
    slug: 'meal-pattern-history',
    reportName: 'Meal Pattern History',
    fKey_RS_Report: 108,
    fields: [
      { key: 'StartDate', group: 'Date Range', label: 'Start Date', type: 'date', defaultValue: () => monthAgo(), required: true, endKey: 'EndDate', endLabel: 'End Date' },
      { key: 'EndDate', group: 'Date Range', label: 'End Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDateRange(v.StartDate, v.EndDate),
  },

  // ─── CREDITS ───
  // ─── CREDITS — CURRENT MONTH SUMMARY (pKey 111, fKey 111) ──────
  // WinForms source: ucDietech_Report_Generator_Credits_CurrentMonth.vb.
  // Print (.vb:239-266):
  //   .FKey_Facility   = _FKey_Facility
  //   .DateFor         = Now   (server ignores — uses Now.Month/Year directly)
  //   .PrintNotes      = chkPrintNotes.Checked
  //   .PrintDetails    = chkPrintDetails.Checked
  //   .LstFKey_Patients = grid.GetSelectedRows → PKey_Patient list
  //                        (only when radPrintResident is checked;
  //                        when radPrintAll is checked, LstFKey_Patients
  //                        stays NULL and the server treats that as "all")
  //   report class     = CreditsCurrentMonthSummary (server pKey 111)
  //   detailedReportName = String.Format("{0} - Current Month Summary",
  //                          Credits_MenuTitle(FKey_Facility))
  //
  // Init (.vb:96-130):
  //   radPrintAll checked by default (.Designer:337 radPrintAll.EditValue = True)
  //   chkPrintNotes checked by default (.Designer:144)
  //   chkPrintDetails unchecked by default
  //
  // Validate (.vb:332-347): if radPrintResident.Checked, ≥1 selected.
  {
    slug: 'credits-current-month-summary',
    reportName: 'Current Month Summary',
    fKey_RS_Report: 111,
    apiEndpoint: 'CreditsCurrentMonthSummary',
    fields: [
      // Print scope radio — All / Specific Resident. Values match
      // WinForms radAll (default) / radPrintResident intent.
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'specific', label: 'Specific {Resident}' },
        ] },

      // Options group — matches WinForms `chkPrintDetails` in its
      // own group. Screenshot shows it separate from the Print
      // group above.
      { key: 'PrintDetails', label: 'Print Details', type: 'checkbox',
        defaultValue: false, group: 'Options' },

      // Notes checkbox — WinForms puts it in a group labeled "Print".
      // We reuse the group name "Notes" for clarity vs the Print
      // scope group above (WinForms has duplicate group names).
      { key: 'PrintNotes', label: 'Notes', type: 'checkbox',
        defaultValue: true, group: 'Notes' },

      // Residents grid — only visible when Specific Resident is
      // picked. `showInfoColumns: true` renders Diet Order / Unit /
      // Room / TF columns alongside Name, matching the WinForms
      // grid layout at .vb:271-304 + .Designer.vb column bindings.
      { key: 'LstFKey_Patients', label: '', type: 'residentSelect',
        defaultValue: [],
        group: '{Resident_Plural}',
        inline: true,
        visibleWhen: { key: 'PrintMode', value: 'specific' },
        showInfoColumns: true },
    ],
    buildDateFor: () => {
      const d = new Date();
      return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },
    // Mirrors WinForms Print settings block (.vb:243-260).
    //   - `LstFKey_Patients` sent as `null` when All is picked so
    //     the server hits its "print every resident" branch at
    //     `.vb:101`. When Specific is picked, sends the selected PKeys.
    //   - `DateFor` set to today; server ignores it and uses
    //     `Now.Year/Month` directly (`.vb:102-103`).
    buildSettings: (v) => ({
      DateFor: new Date(),
      PrintDetails: v.PrintDetails,
      PrintNotes: v.PrintNotes,
      LstFKey_Patients: v.PrintMode === 'specific' ? (v.LstFKey_Patients ?? []) : null,
    }),
    // Mirrors ValidateForm (.vb:332-347) — Specific Resident mode
    // requires ≥1 selected. {Resident} placeholder resolves per facility.
    validate: (v) => {
      if (v.PrintMode === 'specific' && (!Array.isArray(v.LstFKey_Patients) || v.LstFKey_Patients.length === 0)) {
        return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },
  // ─── CREDITS — END OF DAY BALANCE (pKey 112, fKey 112) ─────────
  // WinForms source: ucDietech_Report_Generator_Credits_EndOfDay.vb.
  // Print (.vb:340-363):
  //   .FKey_Facility = _FKey_Facility
  //   .DateFor       = ucDeStart.EditValue  (per Fill_dtCredits, the
  //                                          date passed to
  //                                          GetCreditsForTheDay —
  //                                          not `Now` as first reading
  //                                          suggested. The Print
  //                                          block uses `Now` but the
  //                                          selected date on the form
  //                                          is what drives the credit
  //                                          fetch server-side.)
  //   .PrintCash     = chkCash.Checked
  //   .PrintCredits  = chkCredits.Checked
  //   .LstPKeys      = grid.GetSelectedRows → Menu_Header_ForeignKeyLink
  //   .LstMenuNames  = grid.GetSelectedRows → Menu_Name
  //   report class   = CreditsEndOfDayBalance (server pKey 112)
  //   detailedReportName = "{Credits} - End of Day Balance"
  //
  // Init (.vb:102-139):
  //   Date default = today; chkCash + chkCredits default checked
  //   (.Designer:124/192). If facility Credits_GuestOnlyTransactions
  //   is true, chkCredits is unchecked AND disabled (.vb:115-118).
  //
  // Menu grid populated from `ResidentCreditManager.GetCreditsForTheDay(date)`
  // via the new `GET /api/Credits/{FKey_Facility}/day-menus?date=…`
  // endpoint (wraps the same de-dupe WinForms does at .vb:440-449).
  // The selector holds `{menuId, menuName}` pairs so buildSettings
  // can populate BOTH `LstPKeys` and `LstMenuNames` off one field
  // — same twin-list WinForms writes at .vb:352-357.
  //
  // Validate (.vb:410-430):
  //   1. ≥1 of Cash / Credits checked
  //   2. There are transactions for the selected date (implicit —
  //      grid renders "No transactions…" and arm 3 fails).
  //   3. ≥1 menu selected.
  {
    slug: 'credits-end-of-day-balance',
    reportName: 'End of Day Balance',
    fKey_RS_Report: 112,
    apiEndpoint: 'CreditsEndOfDayBalance',
    fields: [
      { key: 'DateFor', label: '', type: 'date', required: true, group: 'Date',
        defaultValue: () => today() },

      { key: 'PrintCash', label: 'Cash', type: 'checkbox',
        defaultValue: true, group: 'Print' },
      // PrintCredits — defaults to true UNLESS the facility is in
      // guest-only-transactions mode, in which case WinForms
      // disables + unchecks the box (.vb:115-118). We mirror the
      // default via a function that reads the facility setting.
      { key: 'PrintCredits', label: '{Credits}', type: 'checkbox',
        defaultValue: (fs?: Record<string, unknown> | null) => {
          const guestOnly = Boolean(
            (fs as { credits_GuestOnlyTransactions?: boolean } | null | undefined)?.credits_GuestOnlyTransactions,
          );
          return !guestOnly;
        },
        group: 'Print' },

      // Menu selection grid — mirrors WinForms grid at .vb:369-449.
      // Watches DateFor and auto-selects all rows on each date change
      // (matches `gv.SelectAll()` at .vb:448). Rendered inline below
      // the Print group so the layout matches the WinForms tab order:
      // Date → Print (Cash/Credits) → Menu grid. Empty group string
      // gives a discrete block WITHOUT a header — the grid's own
      // "N of M selected" caption acts as its heading, matching the
      // WinForms tab (no separate label above the grid).
      { key: 'Menus', label: '', type: 'creditDayMenuGrid',
        watchField: 'DateFor',
        defaultValue: [] as SelectedDayMenu[],
        group: '' },
    ],
    buildDateFor: (v) => formatDate(v.DateFor),
    // Mirrors WinForms Print settings block (.vb:343-357).
    buildSettings: (v) => {
      const menus = (v.Menus as SelectedDayMenu[] | undefined) ?? [];
      return {
        DateFor: v.DateFor,
        PrintCash: v.PrintCash,
        PrintCredits: v.PrintCredits,
        LstPKeys: menus.map((m) => m.menuId),
        LstMenuNames: menus.map((m) => m.menuName),
      };
    },
    // Mirrors ValidateForm .vb:413-430.
    validate: (v) => {
      if (!v.PrintCash && !v.PrintCredits) {
        return 'Select at least one transaction type to print.';
      }
      const menus = (v.Menus as SelectedDayMenu[] | undefined) ?? [];
      if (menus.length === 0) {
        return 'Select at least one menu.';
      }
      return null;
    },
  },
  // ─── CREDITS — END OF MONTH BALANCE (pKey 113, fKey 113) ───────
  // WinForms source: ucDietech_Report_Generator_Credits_EndOfMonth.vb.
  // Print (.vb:329-362):
  //   .FKey_Facility    = _FKey_Facility
  //   .DateFor          = new Date(row.Year, row.Month, 1) — the
  //                        first of the selected past month.
  //   .LstFKey_Patients = when radPrintResident, grid.GetSelectedRows
  //                        → PKey_Patient list. Otherwise every
  //                        PKey_Patient in `_dtPatientGrid` PLUS
  //                        `PrintAll = true`.
  //   .PrintDetails     = chkPrintDetails.Checked
  //   report class      = CreditsEndOfMonthBalance (server pKey 113)
  //   detailedReportName = "{Credits} - End Of Month Balance"
  //
  // Init (.vb:101-128, .vb:377-402):
  //   luMonth dropdown: 12 rows for Now.AddMonths(-1..-12). PKey =
  //     offset (1..12). Default `luMonth.EditValue = 1` — LAST month.
  //   radPrintAll default checked (Designer.vb radPrintAll.EditValue
  //     = True). chkPrintDetails unchecked.
  //   Patient grid populated by Fill_dtPatient (.vb:407-439) using
  //     SQL_Patient_Where_Generator + FKey_Patient IS NULL +
  //     IsBulkOrder = 0 + ActAsTemplate = 0. Same filter we apply
  //     client-side via `ctx.resolveAllResidentPKeys`.
  //
  // Validate (.vb:487-501): if radPrintResident.Checked and grid
  // selection empty → `dmSelect(_strResident.ToLower)`.
  {
    slug: 'credits-end-of-month-balance',
    reportName: 'End of Month Balance',
    fKey_RS_Report: 113,
    apiEndpoint: 'CreditsEndOfMonthBalance',
    fields: [
      // Month dropdown — last 12 completed months. Value carries
      // the offset (1..12) that both `buildDateFor` and
      // `buildSettings` resolve to a first-of-month Date via
      // `offsetToMonthStart`. Default = 1 (last month) per WinForms
      // `luMonth.EditValue = 1` at .vb:401.
      { key: 'Month', label: '', type: 'dropdown',
        defaultValue: 1,
        options: last12MonthsOptions,
        width: 350,
        required: true, group: 'Month' },

      // Print scope radio — All / Specific Resident. Default 'all'
      // per WinForms radPrintAll.EditValue = True (Designer).
      { key: 'PrintMode', label: '', type: 'radio', defaultValue: 'all',
        group: 'Print',
        options: [
          { value: 'all', label: 'All' },
          { value: 'specific', label: 'Specific {Resident}' },
        ] },

      // Options — Print Details, unchecked by default per WinForms
      // `chkPrintDetails` Designer default.
      { key: 'PrintDetails', label: 'Print Details', type: 'checkbox',
        defaultValue: false, group: 'Options' },

      // Residents grid — visible only when Specific Resident is
      // picked. `showInfoColumns` matches the WinForms grid columns
      // (Name / Diet Order / Floor / Room / TF) shown in the
      // supplied screenshot.
      { key: 'LstFKey_Patients', label: '', type: 'residentSelect',
        defaultValue: [],
        group: '{Resident_Plural}',
        inline: true,
        visibleWhen: { key: 'PrintMode', value: 'specific' },
        showInfoColumns: true },
    ],
    buildDateFor: (v) => {
      const d = offsetToMonthStart(Number(v.Month));
      return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },
    // Mirrors WinForms Print settings block (.vb:334-357). When
    // radPrintAll is picked the WinForms code populates BOTH
    // PrintAll=true AND the full patient PKey list — the server
    // iterates that list (.vb server-side line 56) and would NRE
    // on null. We use `ctx.resolveAllResidentPKeys` to build the
    // same list client-side using the residents cache.
    buildSettings: (v, _fs, ctx) => {
      const monthStart = offsetToMonthStart(Number(v.Month));
      const printAll = v.PrintMode === 'all';
      const patients = printAll
        ? (ctx?.resolveAllResidentPKeys?.() ?? [])
        : ((v.LstFKey_Patients as number[] | undefined) ?? []);
      return {
        DateFor: monthStart,
        LstFKey_Patients: patients,
        PrintAll: printAll,
        PrintDetails: v.PrintDetails,
      };
    },
    // Mirrors ValidateForm (.vb:487-501) — Specific Resident mode
    // requires ≥1 selected. Token resolves per facility.
    validate: (v) => {
      if (v.PrintMode === 'specific'
          && (!Array.isArray(v.LstFKey_Patients) || v.LstFKey_Patients.length === 0)) {
        return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },
  // ─── CREDITS — ITEM USAGE (pKey 114, fKey 114) ─────────────────
  // WinForms source: ucDietech_Report_Generator_Credits_ItemUsage.vb.
  // Print (.vb:343-374):
  //   .FKey_Facility = _FKey_Facility
  //   .DateFor       = ucDeStart.EditValue
  //   .DateEnd       = ucDeEnd.EditValue
  //   .ByMenu        = radGroupByMenu.Checked
  //   .LstPKeys      = selected grid rows' Menu_Header_ForeignKeyLink
  //                    (when ByMenu) or Item_ForeignKeyLink (when
  //                    ByItem).
  //   report class   = CreditsItemUsage (server pKey 114)
  //   detailedReportName = "{Credits} - Item Usage"
  //
  // Init (.vb:102-142):
  //   Start default = today - 7 days (`_dateTimeInitialized.AddDays(-7)`)
  //   End default   = today
  //   Start min     = today - 1 year (`Now.AddYears(-1)`)
  //   End min       = start (updated on start change at .vb:435)
  //   radGroupByMenu default checked (per Designer).
  //   Grid populated once on Init via
  //   `Fill_dtMenuHeaderWithCensusRows(True)` — cached DataSet
  //   from `PrdCensusManager.GetCensusWithCredit`. Radio toggle
  //   re-uses the cache; date change refetches.
  //
  // The grid data lives behind
  // `GET /api/Credits/{FKey_Facility}/item-usage-options`
  // (new endpoint that wraps `PrdCensusManager.GetCensusWithCredit`
  // and returns twin distinct lists). `CreditItemUsageSelector`
  // holds `{byMenu, ids}` in form state so buildSettings can
  // populate both wire fields off a single form field.
  //
  // Validate (.vb:444-464):
  //   - Silent skip when the underlying data set is empty
  //     (WinForms returns false with no toast). We surface a
  //     friendly message instead so the user isn't stuck.
  //   - ≥1 selected in the grid; toast text varies by mode
  //     (`dmSelect("menu")` vs `dmSelect("item")`).
  {
    slug: 'credits-item-usage',
    reportName: 'Item Usage',
    fKey_RS_Report: 114,
    apiEndpoint: 'CreditsItemUsage',
    fields: [
      // Date Range — bidirectional invariant `DateEnd >= DateStart`.
      // Same shape as Cost Analysis - Nourishments (pKey 99).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) {
            // First open — default to today - 7 (WinForms .vb:125).
            const d = new Date();
            d.setDate(d.getDate() - 7);
            return d;
          }
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date Range',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          // First open — default to today (WinForms .vb:132).
          return new Date();
        } },

      // Group radio — By Menu default checked per Designer. Value
      // is 'menu' / 'item' string; buildSettings converts to the
      // WinForms boolean.
      { key: 'Group', label: '', type: 'radio', defaultValue: 'menu',
        group: 'Group',
        options: [
          { value: 'menu', label: 'By Menu' },
          { value: 'item', label: 'By Item' },
        ] },

      // Multi-select grid — watches DateStart / DateEnd / Group.
      // Auto-selects all on each list swap (matches WinForms
      // `gv.SelectAll()` at .vb:504). Empty group string = no
      // fieldset header; the grid's own "N of M selected" caption
      // serves as its heading, matching the WinForms tab.
      { key: 'Selection', label: '', type: 'creditItemUsageGrid',
        watchGroup: { start: 'DateStart', end: 'DateEnd', byMenu: 'Group' },
        defaultValue: { byMenu: true, ids: [] } as SelectedCreditItemUsage,
        group: '' },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.DateStart, v.DateEnd),
    // Mirrors WinForms Print settings block (.vb:347-368).
    buildSettings: (v) => {
      const sel = (v.Selection as SelectedCreditItemUsage | undefined) ?? { byMenu: true, ids: [] };
      return {
        DateFor: v.DateStart,
        DateEnd: v.DateEnd,
        ByMenu: sel.byMenu,
        LstPKeys: sel.ids,
      };
    },
    // Mirrors ValidateForm (.vb:444-464).
    validate: (v) => {
      const sel = (v.Selection as SelectedCreditItemUsage | undefined) ?? { byMenu: true, ids: [] };
      if (sel.ids.length === 0) {
        return sel.byMenu
          ? 'Select at least one menu.'
          : 'Select at least one item.';
      }
      return null;
    },
  },
  // ─── CREDITS — SUMMARY BY RESIDENT (pKey 115, fKey 115) ────────
  // WinForms source: ucDietech_Report_Generator_CreditSummary_ByResident.vb.
  // Print (.vb:229-252):
  //   .FKey_Facility          = _FKey_Facility
  //   .DateFor                = ucDeStart.EditValue
  //   .DateEnd                = ucDeEnd.EditValue
  //   .LstFKey_Patients       = grid.GetSelectedRows → PKey_Patient list
  //   .GuestOnlyTransactions  = chkPrintGuestOnly.Checked
  //   report class            = CreditsSummaryByResident (server pKey 115)
  //   detailedReportName      = "{Credits} - Summary By {Resident}"
  //
  // Init (.vb:87-126):
  //   ucDeStart: DateMin = Now - 2 years, DateInit = today, no label
  //   ucDeEnd:   DateMin = today, DateInit = today, label "To:"
  //   chkPrintGuestOnly: unchecked by default (Designer)
  //   Grid populated by Fill_dtPatient (.vb:392-424) using
  //     SQL_Patient_Where_Generator + FKey_Patient IS NULL +
  //     IsBulkOrder = 0 + ActAsTemplate = 0. Reuses the
  //     `ResidentSelector` with `showInfoColumns` — same columns
  //     WinForms shows (Name / Diet Order / Unit-or-Floor / Room / TF).
  //
  // Validate (.vb:375-386): grid must have ≥1 selection —
  //   `dmSelect(_strResdient)` toast text.
  {
    slug: 'credits-summary-by-resident',
    reportName: 'Summary by {Resident}',
    fKey_RS_Report: 115,
    apiEndpoint: 'CreditsSummaryByResident',
    fields: [
      // Date Range — bidirectional invariant `DateEnd >= DateStart`.
      // Both dates default to TODAY (WinForms .vb:111 / .vb:116)
      // — different from Item Usage's week-back default.
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) return new Date();
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To:', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },

      // Options — "Print Guest Transactions Only" unchecked default.
      { key: 'GuestOnlyTransactions', label: 'Print Guest Transactions Only', type: 'checkbox',
        defaultValue: false, group: 'Options' },

      // Residents grid — always visible (no All/Specific radio for
      // this report; the WinForms UI just shows the grid). Uses
      // `showInfoColumns` to render Diet Order / Unit / Room / TF
      // alongside Name.
      { key: 'LstFKey_Patients', label: '', type: 'residentSelect',
        defaultValue: [],
        group: '{Resident_Plural}',
        inline: true,
        showInfoColumns: true },
    ],
    buildDateFor: (v) => formatDateRangeSameDay(v.DateStart, v.DateEnd),
    // Mirrors WinForms Print settings block (.vb:236-247).
    buildSettings: (v) => ({
      DateFor: v.DateStart,
      DateEnd: v.DateEnd,
      LstFKey_Patients: (v.LstFKey_Patients as number[] | undefined) ?? [],
      GuestOnlyTransactions: v.GuestOnlyTransactions,
    }),
    // Mirrors ValidateForm (.vb:375-386) — ≥1 selected.
    validate: (v) => {
      if (!Array.isArray(v.LstFKey_Patients) || v.LstFKey_Patients.length === 0) {
        return 'Please select at least one {Resident}.';
      }
      return null;
    },
  },
  // ─── CREDITS — HISTORY (fKey 116) ──────────────────────────────
  // One resident's credit transactions for a month: the report the
  // resident Credits tab prints (CreditsHistorySettings: FKey_Patient,
  // DateFor = first of the month, DateEnd = first of the next). It used to
  // send only a date range, with no resident, so it could not run.
  {
    slug: 'credits-history',
    reportName: 'Credits - History',
    fKey_RS_Report: 116,
    fields: [
      // Always has a value (0 = this month), so not `required`: the required
      // check treats 0 as empty.
      { key: 'Month', label: '', type: 'dropdown', defaultValue: 0,
        options: thisAndLast12MonthsOptions, width: 350, group: 'Month' },
      { key: 'LstFKey_Patients', label: '', type: 'residentSelect', defaultValue: [],
        group: '{Resident}', inline: true, showInfoColumns: true },
    ],
    buildDateFor: (v) => offsetToMonthStart(Number(v.Month)).toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    buildSettings: (v, _fs, ctx) => {
      const monthStart = offsetToMonthStart(Number(v.Month));
      const nextMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
      const patientId = ((v.LstFKey_Patients as number[] | undefined) ?? [])[0];
      return {
        DateFor: monthStart,
        DateEnd: nextMonth,
        FKey_Patient: patientId,
        FullName: patientId ? ctx?.resolvePatientFullName?.(patientId) ?? '' : '',
      };
    },
    validate: (v) => {
      const ids = (v.LstFKey_Patients as number[] | undefined) ?? [];
      if (ids.length === 0) return 'Please select a {Resident}.';
      if (ids.length > 1) return 'Credit history prints one {Resident} at a time. Select one.';
      return null;
    },
  },

  // ─── PURCHASING ───
  {
    slug: 'purchasing',
    reportName: 'Purchasing',
    fKey_RS_Report: 119,
    // PurchasingSettings: StartDate + NumberOfDays (Purchasing.vb:55 reads
    // StartDate; a `ReportDate` key was dropped and the report ran for
    // 1/1/0001).
    fields: [
      { key: 'StartDate', group: 'Date', label: 'Start Date', type: 'date', defaultValue: () => today(), required: true },
      { key: 'NumberOfDays', group: 'Date', label: 'Number of Days', type: 'number', defaultValue: 1, min: 1, max: 14 },
    ],
    buildDateFor: (v) => formatDayCountRange(v.StartDate, v.NumberOfDays),
  },
  {
    slug: 'purchase-order',
    reportName: 'Purchase Order',
    fKey_RS_Report: 120,
    fields: [],
    buildDateFor: () => formatDate(today()),
  },
  {
    slug: 'order-forms',
    reportName: 'Order Forms',
    fKey_RS_Report: 123,
    fields: [],
    buildDateFor: () => formatDate(today()),
  },

  // ─── ADMINISTRATIVE ───
  {
    slug: 'facility-user-list',
    reportName: 'Facility User List',
    fKey_RS_Report: 1,
    // UserSettings has no date (DeletedUsers + FKey_Facility only); WinForms
    // prints the current users (ucDietech_Users.vb:829).
    fields: [],
    buildDateFor: () => formatDate(today()),
    buildSettings: () => ({ DeletedUsers: false }),
  },
  {
    slug: 'user-list',
    reportName: 'User List',
    fKey_RS_Report: 1,
    // See facility-user-list: UserSettings has no date.
    fields: [],
    buildDateFor: () => formatDate(today()),
    buildSettings: () => ({ DeletedUsers: false }),
  },
  {
    slug: 'facility-list',
    reportName: 'Facility List',
    fKey_RS_Report: 71,
    fields: [],
    buildDateFor: () => formatDate(today()),
  },
  // ─── MENU CYCLE USAGE (pKey 131, fKey 131) ─────────────────────
  // WinForms source: ucDietech_Report_Generator_MenuCycle.vb.
  // Print (.vb:295-307):
  //   .FKey_Facility = _FKey_Facility
  //   .DateStart     = ucDate.EditValue
  //   .DateEnd       = ucDeEnd.EditValue
  //   report class   = MenuCycleUsage (server pKey 131 per
  //                    ReportDispatcher.cs:153)
  //   queue title    = "Menu Cycle Usage" (.vb:305 — literal)
  //
  // Init (.vb:80-95):
  //   ucDate  (start): no label, DateMin = Now - 10 years,
  //                    DateInit = Now.AddYears(-1)  (one year ago)
  //   ucDeEnd:         DateMin = start, DateInit = Now (today),
  //                    label "To"
  //
  // Validate (.vb:343-346): always returns True — no validation.
  {
    slug: 'menu-cycle-usage',
    reportName: 'Menu Cycle Usage',
    fKey_RS_Report: 131,
    apiEndpoint: 'MenuCycleUsage',
    fields: [
      // Date Range — bidirectional invariant `DateEnd >= DateStart`.
      // Start default = today - 1 year (.vb:84); End default =
      // today (.vb:88).
      { key: 'DateStart', label: '', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateEnd'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const currentStart = parseLocal(values?.DateStart);
          const currentEnd = parseLocal(values?.DateEnd);
          if (!currentStart) {
            const d = new Date();
            d.setFullYear(d.getFullYear() - 1);
            return d;
          }
          if (currentEnd && currentEnd < currentStart) return new Date(currentEnd);
          return currentStart;
        } },
      { key: 'DateEnd', label: 'To', type: 'date', required: true, group: 'Date',
        recomputeWhen: ['DateStart'],
        defaultValue: (_fs?: Record<string, unknown> | null, values?: Record<string, unknown>) => {
          const parseLocal = (v: unknown): Date | null => {
            if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
            if (typeof v === 'string' && v) {
              const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
              if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
            }
            return null;
          };
          const start = parseLocal(values?.DateStart) ?? new Date();
          const existingEnd = parseLocal(values?.DateEnd);
          if (existingEnd && existingEnd >= start) return existingEnd;
          return new Date();
        } },
    ],
    buildDateFor: (v) => formatDateRange(v.DateStart, v.DateEnd),
    // Mirrors WinForms Print settings block (.vb:298-302).
    buildSettings: (v) => ({
      DateStart: v.DateStart,
      DateEnd: v.DateEnd,
    }),
  },
  {
    slug: 'override-menu-list',
    reportName: 'Override Menu List',
    fKey_RS_Report: 139,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date', defaultValue: () => today(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },

  // --- Uncovered DB reports ---
  {
    slug: 'aim-selective-menu-changes',
    reportName: 'AIM - Selective Menu Changes',
    fKey_RS_Report: 102,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date' as const, defaultValue: () => toLocalDateString(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },
  // ─── ITEM LINKING DATA GRID (fKey 129) ──────────────────────────
  // Also printed from the Item Linking editor (Tables). The fixed-layout
  // ItemLinkingDataGrid generator (ItemLinkingDataGridSettings) reads DateFor,
  // FKey_Facility, OnMenuFilter (only items on the menu cycle in use) and
  // StrStreamMaster (the desktop's saved grid layout; the website has none,
  // so it sends '').
  {
    slug: 'item-linking-data-grid',
    reportName: 'Item Linking Data Grid',
    fKey_RS_Report: 129,
    fields: [
      { key: 'OnMenuFilter', group: 'Options', label: 'Items on the menu cycle in use only', type: 'checkbox', defaultValue: false },
    ],
    buildDateFor: () => formatDate(today()),
    buildSettings: (v) => ({
      DateFor: new Date(),
      OnMenuFilter: v.OnMenuFilter === true,
      StrStreamMaster: '',
    }),
  },
  // RS 133 Production Audit and 135 Meal Pattern Notes (no generator, no
  // tblRS_Report row) were dropped 2026-10; see retiredReports.ts.
  // pKey 136 is the WinForms reportPKey_Override applied when
  // Specific Resident is picked AND ≤10 rows selected (.vb:406-409).
  // Both submit paths route through the same TrayTickets generator;
  // the override just distinguishes the "small-list" layout at
  // report render time. The React port folds this into the pKey 4
  // config's preSubmit — no separate card is needed. Stub kept so
  // any lingering API rows keyed to pKey 136 still resolve.
  {
    slug: 'tray-tickets-v2',
    reportName: 'Tray Tickets',
    fKey_RS_Report: 136,
    fields: [],
    buildDateFor: () => new Date().toLocaleDateString('en-US'),
  },
  {
    slug: 'size-mismatch-list',
    reportName: 'Size Mismatch List',
    fKey_RS_Report: 137,
    fields: [
      { key: 'ReportDate', group: 'Date', label: 'Report Date', type: 'date' as const, defaultValue: () => toLocalDateString(), required: true },
    ],
    buildDateFor: (v) => formatDate(v.ReportDate),
  },
  // RS 89, the second "Intake Study" (a server no-op), was dropped 2026-10;
  // Intake Study is RS 94. See retiredReports.ts.

  // ─── PLAN OF CARE (RS 145-149) ──────────────────────────────────
  // The desktop's Reports > Plan of Care menu (frmMain, shown when the
  // user's Plan of Care report level is above 0; see
  // PLAN_OF_CARE_REPORTS in reportCatalog.ts). Generators:
  // DietechShareUtilsVBCore/Reports/Generators/PlanOfCare. Each also
  // prints one evaluation (PKey_Patient_PlanOfCareHeader) when queued
  // from a resident's Plan of Care; these cards are the resident-list
  // entry point, which sends -1. An empty resident list prints the
  // facility's current residents, as the desktop forms do with none
  // checked.
  //
  // IADL/ADL Sheet — frmPlanOfCare_RptGen_ADL: residents + Month/Year
  // (default this month, .vb:62-78). Each resident's latest evaluation
  // dated on or before the month's last day.
  {
    slug: 'plan-of-care-iadl-adl-sheet',
    reportName: 'Plan of Care - IADL/ADL Sheet',
    fKey_RS_Report: 145,
    fields: [
      ...planOfCareMonthYearFields(),
      { key: 'LstPatient', label: '', type: 'residentSelect', defaultValue: [],
        group: '{Resident_Plural}', inline: true },
    ],
    buildDateFor: (v) => planOfCareMonthText(v.Month, v.Year),
    buildSettings: (v) => ({
      Month: Number(v.Month) || 0,
      Year: Number(v.Year) || 0,
      LstPatient: (v.LstPatient as number[] | undefined) ?? [],
      PKey_Patient_PlanOfCareHeader: -1,
    }),
  },
  // Quick Print — frmPlanOfCare_RptGen_All: the Service Plan, the
  // Evaluation and LOCD and the IADL/ADL sheet of each resident's
  // latest evaluation, pages in that order. Three check boxes, all
  // checked (.Designer.vb:205-226).
  {
    slug: 'plan-of-care-quick-print',
    reportName: 'Plan of Care - Quick Print',
    fKey_RS_Report: 146,
    fields: [
      ...planOfCareMonthYearFields(),
      { key: 'ServicePlan', label: 'Service Plan', type: 'checkbox', defaultValue: true, group: 'Reports' },
      { key: 'EvaluationAndLOCD', label: 'Evaluation and LOCD', type: 'checkbox', defaultValue: true, group: 'Reports' },
      { key: 'IADL', label: 'IADL/ADL', type: 'checkbox', defaultValue: true, group: 'Reports' },
      { key: 'LstPatient', label: '', type: 'residentSelect', defaultValue: [],
        group: '{Resident_Plural}', inline: true },
    ],
    validate: (v) =>
      v.ServicePlan || v.EvaluationAndLOCD || v.IADL ? null : 'Please check at least one report.',
    buildDateFor: (v) => planOfCareMonthText(v.Month, v.Year),
    buildSettings: (v) => ({
      Month: Number(v.Month) || 0,
      Year: Number(v.Year) || 0,
      LstPatient: (v.LstPatient as number[] | undefined) ?? [],
      PKey_Patient_PlanOfCareHeader: -1,
      ServicePlan: Boolean(v.ServicePlan),
      EvaluationAndLOCD: Boolean(v.EvaluationAndLOCD),
      IADL: Boolean(v.IADL),
    }),
  },
  // Resident Evaluation and Level of Care Determination —
  // frmPlanOfCare_Rpt_ResidentEvaluationAndLevelOfCareDetermination:
  // each resident's latest evaluation, selected service needs only.
  // (The form's "With / Without Follow-ups" select presets are not
  // ported; pick residents in the grid.)
  {
    slug: 'plan-of-care-resident-evaluation',
    reportName: 'Plan of Care - Resident Evaluation and Level of Care Determination',
    fKey_RS_Report: 147,
    fields: [
      { key: 'LstPatient', label: '', type: 'residentSelect', defaultValue: [],
        group: '{Resident_Plural}', inline: true },
    ],
    buildDateFor: () => '',
    buildSettings: (v) => ({
      LstPatient: (v.LstPatient as number[] | undefined) ?? [],
      PKey_Patient_PlanOfCareHeader: -1,
    }),
  },
  // Resident Service Plan — each resident's latest evaluation (the
  // desktop prints it per evaluation; for several residents its Quick
  // Print with only Service Plan checked does the same).
  {
    slug: 'plan-of-care-resident-service-plan',
    reportName: 'Plan of Care - Resident Service Plan',
    fKey_RS_Report: 148,
    fields: [
      { key: 'LstPatient', label: '', type: 'residentSelect', defaultValue: [],
        group: '{Resident_Plural}', inline: true },
    ],
    buildDateFor: () => '',
    buildSettings: (v) => ({
      LstPatient: (v.LstPatient as number[] | undefined) ?? [],
      PKey_Patient_PlanOfCareHeader: -1,
    }),
  },
  // Service Planning Guide — frmPlanOfCare_Rpt_ServicePlanningGuide:
  // the checked domains' problems and interventions. The form opens
  // with every domain checked (adh_CLB_CheckToggle, .vb:34); none
  // checked prints every domain, as there.
  {
    slug: 'plan-of-care-service-planning-guide',
    reportName: 'Plan of Care - Service Planning Guide',
    fKey_RS_Report: 149,
    fields: [
      { key: 'LstDomain', label: '', type: 'planOfCareDomainSelect', defaultValue: [],
        defaultSelectAll: true, group: 'Domains' },
    ],
    buildDateFor: () => '',
    buildSettings: (v) => ({
      LstDomain: (v.LstDomain as number[] | undefined) ?? [],
    }),
  },
];

// ===== PLAN OF CARE HELPERS =====

/**
 * The Month and Year look-ups of the desktop's Plan of Care report forms
 * (frmPlanOfCare_RptGen_ADL / _All MonthYear: g_clS_Months, and g_clS_Years =
 * next year down to five years before it, frmSplash.vb:1081-1085), this month
 * by default. Plain look-ups, not a date: a schedule keeps the month picked.
 */
function planOfCareMonthYearFields(): ReportFieldConfig[] {
  const next = new Date().getFullYear() + 1;
  return [
    { key: 'Month', label: '', type: 'dropdown', required: true, group: 'Date', width: 150,
      defaultValue: () => new Date().getMonth() + 1, options: MONTH_OPTIONS },
    { key: 'Year', label: '', type: 'dropdown', required: true, group: 'Date', width: 120,
      defaultValue: () => new Date().getFullYear(),
      options: [0, 1, 2, 3, 4, 5].map((o) => ({ value: next - o, label: String(next - o) })) },
  ];
}

/** "September 2026" for the print queue's Date For column; '' without a month. */
export function planOfCareMonthText(month: unknown, year: unknown): string {
  const m = Number(month), y = Number(year);
  if (!(m >= 1 && m <= 12) || !(y > 0)) return '';
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ===== LOOKUP MAPS =====

export const REPORT_CONFIG_MAP = new Map<string, ReportConfig>(
  REPORT_CONFIGS.map((c) => [c.slug, c]),
);

/**
 * Retired slugs that bookmarks, favorites and recent-report lists may still
 * hold, mapped to the config that replaced them.
 */
const LEGACY_SLUGS: Record<string, string> = {
  // Generic Tube Feed card, replaced by the WinForms port of the same generator.
  'tube-feed': 'tube-feed-labels',
  // Dropped duplicates (retiredReports.ts): intake-study-2 -> intake-study,
  // meal-pattern-notes-report -> meal-pattern-notes.
  ...RETIRED_REPORT_SLUGS,
};

/** Lookup by fKey_RS_Report (most reliable - exact PKey match) */
const REPORT_CONFIG_BY_PKEY = new Map<number, ReportConfig>();
for (const c of REPORT_CONFIGS) {
  // Only set the first config for each PKey (avoid duplicates like user-list/facility-user-list)
  if (!REPORT_CONFIG_BY_PKEY.has(c.fKey_RS_Report)) {
    REPORT_CONFIG_BY_PKEY.set(c.fKey_RS_Report, c);
  }
}

/** Lookup by normalized reportName (lowercased, trimmed) for fallback matching */
const REPORT_CONFIG_BY_NAME = new Map<string, ReportConfig>();
for (const c of REPORT_CONFIGS) {
  REPORT_CONFIG_BY_NAME.set(c.reportName.toLowerCase().trim(), c);
  // Also index by slug-derived name (e.g., "tray-tickets" → "tray tickets")
  REPORT_CONFIG_BY_NAME.set(c.slug.replace(/-/g, ' '), c);
}

/** Normalize name: strip " - " separators so "Diet Order - Changes" matches "Diet Order Changes" */
const flatten = (s: string) => s.toLowerCase().replace(/\s*-\s*/g, ' ').replace(/\s+/g, ' ').trim();

/** Lookup by flattened reportName for " - " separator tolerance */
const REPORT_CONFIG_BY_FLAT_NAME = new Map<string, ReportConfig>();
for (const c of REPORT_CONFIGS) {
  const key = flatten(c.reportName);
  if (!REPORT_CONFIG_BY_FLAT_NAME.has(key)) {
    REPORT_CONFIG_BY_FLAT_NAME.set(key, c);
  }
}

/**
 * Find config using multiple strategies (tried in order of reliability):
 * 0. PKey match (most reliable - exact numeric ID from tblRS_Report)
 * 1. Direct slug match
 * 2. Fuzzy slug match (strips hyphens - handles "traytickets" → "tray-tickets")
 * 3. ProgramName-based match (converts programName to kebab-case and matches)
 * 4. ReportName-based match (exact, then flattened, then partial)
 */
/**
 * Report generators (`fKey_RS_Report`) the website must not queue, because the
 * render would fail. The catalog hides these so users can't queue a
 * guaranteed failure. Empty today.
 *
 * It last held the five grid reports (75 Resident Data Grid, 80 Weight Entry,
 * 102 AIM Selective Menu Changes, 109 AIM Meal Pattern Changes, 129 Item
 * Linking Data Grid): the Windows report pool's WinForms grid generators
 * needed the desktop's saved grid layout, which the website cannot send. Web
 * prints of those five now go to the Linux renderer, which prints them with
 * the fixed-layout generators in DietechShareUtilsVBCore/Reports/Generators.
 * (102 and 109 still need the AIM run's change set, so they print from the AIM
 * dialogs, not from a card; see UNAVAILABLE_REPORTS in reportCatalog.ts.)
 *
 * Add an id here (and its slug to DISABLED_REPORT_SLUGS in disabledReports.ts)
 * only when the server cannot print it from what the website sends.
 */
export const DISABLED_REPORT_FKEYS: ReadonlySet<number> = new Set<number>([]);

/** True when a report's server-side generator is currently disabled (see DISABLED_REPORT_FKEYS). */
export function isReportDisabled(config: ReportConfig | undefined): boolean {
  return config?.fKey_RS_Report != null && DISABLED_REPORT_FKEYS.has(config.fKey_RS_Report);
}

export function findReportConfig(
  slug: string,
  programName?: string,
  reportName?: string,
  pKey?: number,
): ReportConfig | undefined {
  // 0. PKey match - most reliable, bypasses all name-matching issues
  // (a retired key resolves to the card that replaced it, see retiredReports.ts)
  if (pKey != null && pKey > 0) {
    const byPKey = REPORT_CONFIG_BY_PKEY.get(currentReportPKey(pKey));
    if (byPKey) return byPKey;
  }

  // Also try parsing slug as a numeric PKey
  if (/^\d+$/.test(slug)) {
    const numericPKey = parseInt(slug, 10);
    const byPKey = REPORT_CONFIG_BY_PKEY.get(currentReportPKey(numericPKey));
    if (byPKey) return byPKey;
  }

  // 1. Direct slug match (or a retired slug's replacement)
  const direct = REPORT_CONFIG_MAP.get(LEGACY_SLUGS[slug] ?? slug);
  if (direct) return direct;

  // 2. Fuzzy slug match: strip all hyphens and compare
  const normalized = slug.replace(/-/g, '');
  for (const [key, config] of REPORT_CONFIG_MAP) {
    if (key.replace(/-/g, '') === normalized) {
      return config;
    }
  }

  // 3. If a programName was provided, compute kebab slug from it and retry
  if (programName) {
    const kebab = pascalToKebab(programName);
    if (kebab) {
      const byProgramName = REPORT_CONFIG_MAP.get(kebab);
      if (byProgramName) return byProgramName;

      const normalizedPn = kebab.replace(/-/g, '');
      for (const [key, config] of REPORT_CONFIG_MAP) {
        if (key.replace(/-/g, '') === normalizedPn) {
          return config;
        }
      }
    }
  }

  // 4. Match by reportName (handles ProgramName being empty in DB)
  if (reportName) {
    // Exact match (lowercased)
    const byName = REPORT_CONFIG_BY_NAME.get(reportName.toLowerCase().trim());
    if (byName) return byName;

    // Flattened match: "Diet Order - Changes" → "diet order changes"
    const byFlat = REPORT_CONFIG_BY_FLAT_NAME.get(flatten(reportName));
    if (byFlat) return byFlat;

    // Partial: "Birthday" matching "Birthday Report" or vice versa
    const flatName = flatten(reportName);
    for (const config of REPORT_CONFIGS) {
      const flatConfig = flatten(config.reportName);
      if (flatConfig.startsWith(flatName) || flatName.startsWith(flatConfig)) {
        return config;
      }
    }
  }

  return undefined;
}
